import type { CannedOriginDto } from "./cannedReportModel.ts";
import type { ReportAudienceDto, ReportAudienceSave } from "./reportSharingModel.ts";
import { DEVICE_TOKEN_HEADER } from "./deviceCredential.ts";
import type { ReaderHealthDto } from "./readerHealthModel.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";
import type { CardPaymentsForLocationDto } from "./cardPaymentsModel.ts";
import type { DeviceDto, LocationDto, SetupCodeDto } from "./tabletsModel.ts";
import type { SalesTaxReportDto } from "./salesTaxWorksheetModel.ts";
import type { RoyaltyStatementDto } from "./royaltyStatementModel.ts";
import { signalOnSuccess } from "./startingStockMenuSignal.ts";
import { readJsonBody } from "./serviceUnavailable.ts";

// Thin fetch wrapper over the harness API (src/api/server.ts) that the
// ALPHA-06 bartender ticket UI needs: the ALPHA-01/04 catalog reads, the
// ALPHA-03 price-quote endpoint, and the four HARNESS-03 order commands
// (create order / add line, Prepared, payment stub, Close). This module
// holds no SQL and no business rule of its own, only request/response
// plumbing, mirroring harness/client/src/api.ts's own module header for
// the same reason: src/api/server.ts stays the single source of truth for
// every payload shape.
//
// Every response body here is the raw JSON the server returns (snake_case
// catalog and order-line fields, camelCase price-quote fields), not a
// reshaped DTO, for the same "read src/api/server.ts, don't guess" reason
// harness/client/src/api.ts documents.

export interface ApiResult<T> {
  status: number;
  body: T;
  /** True when the request never reached the server: fetch() itself threw
   * (offline, DNS failure, connection reset, CORS block, timeout, etc.),
   * as opposed to the server answering with a non-2xx status. status is 0
   * and body is a synthetic ApiErrorBody in this case, never the caller's
   * declared success shape. Every caller's existing `status !== 200`
   * branch already handles this the same way it handles a real HTTP
   * error (see main.ts), so this field only needs checking where "we
   * don't know what happened" must be told apart from "the server said
   * no" - the Pay flow, where retrying a payment that may have already
   * gone through is unsafe. */
  networkError: boolean;
}

export interface ApiErrorBody {
  error: string;
  message: string;
  /** Present only on a 403 {error: "approval_required"}: WHICH permission
   * needs a manager's approval, straight from the requirePermission call
   * that refused (src/domain/permissions.ts's ApprovalRequiredError, put
   * in the body by src/api/server.ts's error handler). Product-owner
   * ruling 2026-08-28: main.ts's approver picker feeds this id to
   * listApprovalCandidates below, so the list it shows is the set the
   * server will actually accept rather than a role-name guess. Optional
   * because every other error shape omits it, and because a client
   * talking to a server older than that ruling would not receive it --
   * main.ts treats its absence as "cannot determine" and degrades
   * honestly rather than silently filtering on nothing. */
  permissionId?: string;
  /** Present only on a 400 {error: "modifier_retired"}: the exact
   * modifierSelections value the caller submitted that has since been
   * retired from the catalog (src/domain/errors.ts's ModifierRetiredError).
   * VOC1-F4-F3: lets the floor sheet drop and re-quote without parsing the
   * message's prose. */
  retiredValue?: string;
  /** SPLIT1. Present only on a 409 {error: "amount_exceeds_balance"}: what the ticket
   * still owes (cents, tax included, before tip), so the pad can show the live balance. */
  remainingCents?: number;
}

/** RBAC track: the optional manager-approval fields a retry sends after a
 * first attempt on an approval_required-gated route comes back 403
 * {error: "approval_required"} (src/domain/permissions.ts's
 * requirePermission/ApprovalRequiredError, and src/api/server.ts's own
 * optionalApprovalInput, which parses exactly this pair -- both present
 * or both omitted). ui/src/main.ts's requestApproval is the one place any
 * UI call site actually supplies these, on the retry only; every route
 * below that accepts them (reopenOrder, removeSavedCard,
 * closeCountSession) works completely unchanged for every caller that
 * never passes them. */
export interface ApprovalRetryParams {
  approverUserId?: string;
  approverPin?: string;
}

const NETWORK_ERROR_MESSAGE = "Could not reach the server. Check your connection and try again.";

/** Fetch itself throws for a request that never got a response (offline,
 * DNS failure, connection reset, CORS block); it does not reject with an
 * HTTP error status the way a 4xx/5xx does, so without this catch that
 * throw propagates out of getJson/postJson as an unhandled promise
 * rejection past every caller in main.ts, none of which had a try/catch
 * of its own. Every call site here is wrapped in `void handler()`, so an
 * unhandled rejection is invisible to the bartender: no toast, no inline
 * message, nothing (see this module's header and the ALPHA-06 QA-gate
 * finding it fixes). Normalizing the throw into an ordinary ApiResult
 * with networkError: true means every caller's pre-existing
 * `status !== 200` branch surfaces it automatically, with no call site
 * changes required except the Pay path, which needs to tell this apart
 * from a definite server-side failure. */
// Architect gate correction, 2026-08-25 (Finding 3, provider-success /
// local-record boundary). src/domain/payments.ts's payOrder drives the
// real provider charge (chargeThroughAdapter/chargeThroughStoredCredential/
// chargeThroughHold) INSIDE the same SQL transaction as its own
// payment_attempts write (executeIdempotentCommand's writeDomain runs
// inside withTransaction). If the provider genuinely captures money and a
// LATER statement in that same transaction fails for any other reason
// (see idempotentCommand.ts's simulateFailureAfterDomainWrite test seam,
// which reproduces exactly this by forcing a failure AFTER the domain
// write completes), the SQL transaction rolls back -- the money the
// provider actually took stands, but payment_attempts never durably
// records it -- and the route answers with an ordinary 500
// ("internal_error"), a non-DomainError falling through Fastify's default
// error handler (src/api/server.ts's setErrorHandler).
//
// main.ts's Pay flow already keeps the failing target's own entry in
// state.payIdempotencyKeysByTarget untouched on a networkError result (the
// request never reached the server at all, so there is no way to know
// from here whether it charged before the connection dropped) -- that
// branch is correct and UNCHANGED by this function. The bug this closes is
// the OTHER branch: main.ts used to clear the key unconditionally on every
// non-networkError failure, reasoning "the server definitely answered, so
// it definitely did not charge." That reasoning is true for an ordinary
// business-rule rejection (lines changed under the ticket, nothing left
// unpaid, the ticket already
// closed -- every DomainError src/domain/payments.ts's payOrder can throw
// is thrown BEFORE any adapter call is ever made, so a structured 4xx
// domain-error response is always safe: see this codebase's own
// payOrder/collectShare, which have no code path that reaches a
// DomainError AFTER the charge). It is FALSE for the 500 case above: the
// server answered, but not with "no," with "something went wrong after I
// don't actually know what happened to the charge."
//
// Clearing the key mints a BRAND NEW adapter-level idempotency key on the
// very next retry (src/domain/payments.ts's deriveAdapterIntentIdempotencyKey
// and friends are pure functions of the DOMAIN key), which the provider's
// own idempotency cache has never seen -- a genuine second real charge.
// Preserving the key instead means the retry replays the SAME domain
// idempotency key: if the original write really did roll back with no
// persisted payment_attempts row, writeDomain's own peek finds nothing and
// re-executes from scratch, but chargeThroughAdapter derives the IDENTICAL
// adapter-level key from that same domain key every time, so the
// provider's own idempotency cache returns the original captured result
// instead of charging again (test/payments-adapter-boundary.test.ts's "a
// retried Pay after a rolled-back domain write never charges the adapter
// twice" proves this property at the domain layer; this function is what
// makes the UI actually exercise that safe path instead of the unsafe
// one).
//
// "When in doubt, keep the key": any status/body shape this function does
// not explicitly recognize as a definite pre-charge rejection defaults to
// FALSE (preserve), never the other way around.
export function shouldClearPayIdempotencyKeyOnFailure(result: {
  status: number;
  networkError: boolean;
  body?: { error?: string };
}): boolean {
  if (result.networkError) {
    // Unchanged: the request never reached the server at all.
    return false;
  }
  if (result.status >= 500) {
    // An internal_error 500 is exactly the ambiguous "the server answered,
    // but not with a definite no" case this function exists to catch: it
    // is the ONLY response shape a post-charge SQL failure inside
    // payOrder's own transaction can produce (every DomainError payOrder
    // throws happens strictly before any adapter call).
    return false;
  }
  if (result.body?.error === "payment_retry_conflict") {
    // PAY7 (docs/build-briefs/PAY7-pay-retry-after-terminal-change.md
    // Section 2.5): the one deliberate, named exception to this
    // function's own governing rule above. This 409 is thrown from
    // INSIDE an adapter call (src/domain/payments.ts's
    // chargeThroughAdapter), not before one, because an earlier
    // presentment under this same domain key may still resolve to a real
    // capture on whichever reader it actually reached. Clearing the key
    // here would mint a fresh one on the next retry and create a SECOND,
    // independent PaymentIntent -- see that brief's Section 2.5 for the
    // full reasoning. Preserve, exactly like a 5xx.
    return false;
  }
  if (
    result.body?.error === "captured_amount_mismatch" ||
    result.body?.error === "captured_amount_mismatch_not_alerted" ||
    result.body?.error === "captured_payment_reconciliation_required"
  ) {
    // These refusals follow a provider capture. PAYCAP1-S1 preserves a Pending attempt
    // and immutable discrepancy. Keep its key so the next tap replays that same refusal.
    return false;
  }
  if (result.body?.error === "idempotency_key_reused") {
    // SPLIT1 (security review A1): this key already belongs to a payment of another mode or, for a By amount payment, of
    // another amount or tip. That payment EXISTS, so the key must be kept: clearing it would let the next tap charge a
    // second time. A tip changed after an unconfirmed attempt is refused here until the original choice is made again.
    return false;
  }
  // Every DomainError src/domain/errors.ts defines carries a `code` in
  // body.error (src/api/server.ts's setErrorHandler: `{ error: error.code,
  // message: error.message }`), and every one payOrder/collectShare can
  // throw is thrown before any provider charge is attempted -- so any
  // structured non-5xx response is safe to treat as a definite,
  // pre-charge "no."  A response with no recognizable error code at all
  // (a malformed body, an unexpected shape) falls through to the safe
  // default below instead of being assumed safe.
  return result.body?.error !== undefined;
}

function networkErrorResult<T>(cause: unknown): ApiResult<T> {
  // Logged for whoever has devtools open; never surfaced to staff as-is
  // (raw fetch/DOMException text is developer language, not plain staff
  // language - the toast and inline messages always use
  // NETWORK_ERROR_MESSAGE instead).
  console.error("network request failed", cause);
  // UX29: fetch() itself threw, so this request never reached the server
  // -- the "Connection issue" signal, distinct from the browser's own
  // navigator.onLine (main.ts combines both).
  connectivityObserver?.(false);
  return {
    status: 0,
    networkError: true,
    body: {
      error: "network_error",
      message: NETWORK_ERROR_MESSAGE,
    } as T,
  };
}

// ---------------------------------------------------------------------
// Catalog (ALPHA-01 / ALPHA-04)
// ---------------------------------------------------------------------

// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth), widened by WIN1 (owner ruling 2026-08-30). One of these
// four; 'liquor' is both a cocktail ingredient and, as of Slice 3,
// directly sellable by the pour, while 'wine' is sellable only. Mirrors
// src/domain/catalog.ts's ITEM_KINDS.
//
// ITEM1 Slice 2 (docs/build-briefs/ITEM1-item-kinds-are-a-configurable-
// vocabulary.md; DEC-130, DEC-145). item_kind is now the tenant's own
// configurable vocabulary; this constant is the DEFAULT/fallback rendered
// before adminVocabularies["item_kind"] has loaded (ui/src/admin.ts's
// tenantItemKinds), mirroring CONTAINER_TYPES' own identical role just
// below. ItemKind widens to plain `string` so a real product can carry
// any further tenant-added kind beyond this fallback list. ITEM1 Slice 4
// (migration 0113) adds 'cider', mirroring src/domain/catalog.ts's own
// identical widen of ITEM_KINDS the same session: cider is sellable from
// this slice on, so the pre-load fallback should offer it too, not only
// the tenant's real vocabulary once loaded.
export const ITEM_KINDS = ["liquor", "beer", "wine", "cider"] as const;
/** PKGRM1: current configuration only. Historical stored labels remain readable. */
export function isCurrentItemKind(value: string): boolean {
  return (
    value !== "packaged" &&
    value !== "modifier" &&
    value !== "modifiers" &&
    !/^addon_options_[a-f0-9]{12}(?:_[0-9]+)?$/.test(value)
  );
}
export type ItemKind = string;
export type ProductSaleMechanism = "per_ounce" | "whole_unit";

// Amendment 2026-08-22 (eleventh)'s "size with unit and container type."
// Mirrors src/domain/catalog.ts's CONTAINER_TYPES.
export const CONTAINER_TYPES = ["bottle", "can", "keg", "other"] as const;
export type ContainerType = (typeof CONTAINER_TYPES)[number];

export interface LocationSellingContextDto {
  locationId: string | null;
  readiness: { state: "preparation" | "enforced"; version: number };
}
export interface ProductSellingDto {
  productId: string;
  locationId: string;
  state: "legacy" | "inherited" | "configured" | "unconfigured";
  version: number;
  saleMechanism: "whole_unit" | "per_ounce";
  unitPriceCents: number | null;
  perOuncePriceCents: number | null;
  pourOunceOptions: readonly string[] | null;
  shelfTier: string | null;
  active: boolean;
  hideOnMenu: boolean;
  trackStock: boolean | null;
  shelfSource: "legacy" | "inherited" | "configured" | "unconfigured";
  shelfVersion: number;
  shelfBasePriceCents: number | null;
}
export interface LocalShelfSellingDto {
  locationId: string;
  shelfTier: string;
  source: "legacy" | "inherited" | "configured" | "unconfigured";
  version: number;
  priceCents: number | null;
}
export interface LocalShelfUpdateDto {
  shelfTier: string;
  requestId: string;
  expectedVersion: number;
  source: "inherited" | "configured";
  priceCents: number | null;
}
export interface ProductSellingWriteDto {
  source: "configured";
  unitPriceCents: number | null;
  calculatedPrice?: { targetMarkupPercent: number; previewToken: string } | null;
  pourOunceOptions: number[] | null;
  shelfTier: string | null;
  active: boolean;
  hideOnMenu: boolean;
  trackStock: boolean;
}
/** Current selling facts remain separate from the physical product row. */
export function currentProductSelling(
  product: LiquorProductDto,
  locationId: string | undefined,
): ProductSellingDto | undefined {
  const selling = product.selling;
  return selling !== undefined &&
    selling.locationId === locationId &&
    selling.productId === product.id
    ? selling
    : undefined;
}
export function getLocalSellingShelves(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<
  ApiResult<
    {
      sellingContext: LocationSellingContextDto;
      shelves: Array<{
        value: string;
        label: string;
        active: boolean;
        priceCents: number | null;
        selling?: LocalShelfSellingDto;
      }>;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/location-selling/shelves${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export interface LiquorProductDto {
  /** INVVENDOR1: the vendor the item orders from at the listed location (null: none). Absent on rows not read from the list route. */
  preferredVendorName?: string | null;
  selling?: ProductSellingDto;
  stop_ordering?: boolean;
  track_stock?: boolean;
  addon_only?: boolean;
  addon_enabled?: boolean;
  addon_price_adjustment_cents?: number | null;
  addon_quantity_per_use?: string | null;
  addon_unit?: string | null;
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  /** Nullable as of inventory-track Slice 3: populated for item_kind
   * 'liquor'/'beer', null for 'packaged' (see unit_price_cents). */
  per_ounce_price_cents: number | null;
  // Declared `string[]` on src/domain/catalog.ts's LiquorProductRow (the
  // column is NUMERIC(4,2).array()), but node-postgres decodes a numeric
  // ARRAY's elements as JS numbers, unlike a scalar numeric column (which
  // it decodes as a string). This type says `(string | number)[]` rather
  // than repeating that same documented-vs-actual mismatch here; see
  // liquorMatch.ts's header for the full finding and normalizeOunces for
  // the fix every caller of this field goes through. Null for 'packaged'.
  pour_ounce_options: Array<string | number> | null;
  active: boolean;
  /** ALPHA-11. One of the four SHELF_TIER_KEYS ("tier_1".."tier_4"), or
   * null when unassigned. Used for cocktail pricing: not yet (see
   * db/schema.ts's liquorProducts.shelfTier comment); used for pour/draft
   * pricing under the shelf model as of inventory-track Slice 3. */
  shelf_tier: string | null;
  /** Inventory track Slice 3, Amendment 2026-08-22 (eleventh): the
   * "Inventory group" field-level schema. All nine nullable. Deliberately
   * NOT present: any Costing-group or Purchasing-group field (DEC-009
   * stays fenced); see src/domain/catalog.ts's InventoryItemFields.
   * INVLOC1-S2 (DEC-326): par_level and bin_label are ONE LOCATION's values, the location the list was asked for (this tablet's by
   * default). Null means that location holds no par or no bin label for the item. A response for a session with no location
   * (create, update or get with no device) carries neither field at all, which reads as "no location to speak for". */
  par_level: string | null;
  bin_label: string | null;
  size_value: string | null;
  size_unit: string | null;
  container_type: string | null;
  /** INV8 Section 2.7 (DEC-176): the tenant's own 'keg_size' vocabulary
   * value this product's keg deliveries are resolved against (INV14),
   * and the sole factor Section 2.1's count-unit derivation needs for a
   * keg-container item. Null when unset, which leaves that item's par
   * comparison permanently unresolved (excluded from the Low list) until
   * this is picked. */
  default_keg_size: string | null;
  /** ADDITEM2 (DEC-611). A keg's foam and waste in basis points (1000 = 10%); null uses the tenant's keg usable yield. */
  keg_waste_basis_points?: number | null;
  /** Basis points (e.g. 4000 = 40.00% ABV); converted to/from a
   * human-typed percent at the presentation boundary (ui/src/money.ts),
   * never displayed or accepted raw. */
  abv_basis_points: number | null;
  producer: string | null;
  country_region: string | null;
  description_notes: string | null;
  /** Amendment 2026-08-22 (twelfth). One of ITEM_KINDS. */
  item_kind: ItemKind;
  /** ITEM2-F7-S3. Saved product-level selling method. */
  saleMechanism?: ProductSaleMechanism | null;
  /** Nullable as of inventory-track Slice 3: populated only for item_kind
   * 'packaged'. See per_ounce_price_cents's own comment above. */
  unit_price_cents: number | null;
  /** B1 (docs/backlog.md, owner ruling 2026-08-27). The manufacturer's
   * code (UPC/EAN), identical wherever this bottle is sold; the future
   * scan key (B6, not built by this item). Nullable; unique per tenant
   * when present. DEC-047 retired the sibling supplier_sku field. */
  barcode: string | null;
  /** B6-F1. Bottles per case (INVCOUNT2-S1), or null when nobody set one. */
  case_size?: number | null;
  /** B6-F1. The code printed on a case (B6, migration 0387), or null. */
  case_barcode?: string | null;
  /** INV26 Slice 2 (DEC-207 item 3). Menu-visibility only: true removes
   * this item from GET .../menu-items' sellable menu; it stays in the
   * admin list, counts, and purchasing regardless. NOT NULL, defaults
   * false. See src/domain/catalog.ts's LiquorProductRow.hide_on_menu. */
  hide_on_menu: boolean;
  /** P11-F1 (DEC-372). True while the size and container type were assumed (750 mL Bottle) and no manager has confirmed them. */
  size_needs_confirmation: boolean;
  created_at: string;
  updated_at: string;
  /** VOC1-F5 Slice 1. Two derived words merged onto this already-
   * lounger-reachable route and onto GET .../menu-items's own
   * liquorProduct, never the fuller admin-only inv.stock_state.view
   * payload (no breakdown or flagConflictsWithLedger). Null for a
   * mixed-unit product. */
  quantityState: "out_of_stock" | "low" | "ok" | null;
  /** VOC1-F5 Slice 1/2. A manager's explicit 86 assertion, independent of
   * quantityState: a bottle can read "ok" on the ledger and still be
   * flagged, and the flag is what governs selection (Slice 2). */
  flagged86: boolean;
  /** INV26 Slice 1 (DEC-206 item 2). Populated ONLY on GET
   * .../liquor-products' admin list response (src/api/server.ts's list
   * route), never on GET .../menu-items or the single-product GET, which
   * both still carry the narrower VOC1-F5 shape -- optional here rather
   * than widening every route's response. onHandInCountUnit is INV8/
   * INV20's already-converted figure in the item's own count unit
   * (StockStateRow.onHandInCountUnit); countUnit is the unit it is in
   * ("bottle", "can", "keg", "oz", "unit"). Both null when the server
   * cannot resolve a count unit (no size/keg configuration) or the
   * product's ledger is genuinely mixed-unit, matching StockStateRow's
   * own null-propagation contract; the Settings inventory list sorts an
   * unresolved On hand last rather than fabricating a figure. */
  onHandInCountUnit?: string | null;
  countUnit?: string | null;
  /** KEGMATH1. Populated ONLY on the admin list response: ounces in one keg (a decimal string) for a keg item with a
   * keg size, from the keg_size vocabulary; null otherwise. A keg's Size is the serving, never the container. */
  default_keg_size_volume_oz?: string | null;
}

export interface MenuDrinkDto {
  network_publication_id?: string | null;
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  build_charge_cents: number;
  build_charge_version: number;
  requires_liquor_selection: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/** ALPHA-10 items 1 and 2: GET .../menu-drinks (list) embeds each drink's
 * full allowed-category set directly on the row (src/api/server.ts's list
 * route joins it in with one bulk query), so the alphabetical menu's
 * "From" price estimate and the manager settings screen both have it
 * without a per-drink round trip. The single-drink create/get responses
 * return the identical set as a sibling `liquorSlots` field instead (see
 * getMenuDrink's ApiResult body below); the two shapes differ because one
 * response is a list of drinks and the other is exactly one, not because
 * the underlying data differs. */
/** RECUX2-F1: the franchisor recipe's descriptive method as a location receives it. */
export interface RecipeMethodDto {
  glass: string | null;
  garnish: string | null;
  steps: string[];
}

export interface MenuDrinkListItemDto extends MenuDrinkDto {
  network_publication_id?: string | null;
  recipe_origin?: {
    kind: "own" | "corporate" | "franchisee";
    name: string;
    required: boolean;
    withdrawn: boolean;
    /** RECUX2-F2: the franchisor's Menu category and its place in the franchisor's order; null for any other drink. */
    franchisorCategory?: { name: string; position: number } | null;
  } | null;
  liquor_slots: string[];
  /** LOCFLOOR1 (sweep B17): false when the drink has no ingredients and no liquor selection (Settings marks it Needs a recipe). */
  has_recipe?: boolean;
  /** TEN19-F1 (DEC-350): the locations (A to Z) a drink from a franchisor's published recipe sells at. Empty for the location's own drinks. */
  network_locations?: string[];
}

export interface MenuDrinkComponentDto {
  id: string;
  tenant_id: string;
  menu_drink_id: string;
  component_reference: string;
  quantity: string;
  unit: string;
  /** ALPHA-07. True for at most one component per drink: the one that is
   * the drink's liquor pour. Server-computed and trustworthy; see
   * liquorMatch.ts, which used to re-derive this client-side from a
   * duplicated keyword list. */
  is_liquor_slot: boolean;
  /** CORE14. The location's own inventory item this ingredient is; null on a liquor pour and on an old typed ingredient. */
  liquor_product_id?: string | null;
  created_at: string;
}

// VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
// 2.2). A menu drink's own attached modifier, joined against the catalog
// row's CURRENT label/price/active (never a historical snapshot; see
// OrderLineModifierDto below for the append-only, copied-at-selection-time
// counterpart). Mirrors src/domain/catalog.ts's own MenuDrinkModifierDetail.
export interface AddonGroupInput {
  id?: string;
  label: string;
  required: boolean;
  optionValues: string[];
}
export interface UnresolvedIngredientChoiceDto {
  key: string;
  inventoryItemId: string | null;
  label: string;
  quantityPerUse: string | null;
  unit: string | null;
  priceAdjustmentCents: number | null;
  required: boolean;
  enabled: boolean;
  groupId: string | null;
  groupLabel: string | null;
  groupRequired: boolean;
}
export interface IngredientChoiceInput {
  id?: string;
  inventoryItemId: string;
  quantityPerUse: string;
  unit: string;
  priceAdjustmentCents: number;
  required: boolean;
  enabled: boolean;
  groupId?: string | null;
}
export interface IngredientChoiceGroupInput {
  id?: string;
  label: string;
  required: boolean;
}
export interface MenuDrinkModifierDetailDto {
  inventoryItemId?: string;
  quantityPerUse?: string | null;
  unit?: string | null;
  stockTracked?: boolean;
  enabled?: boolean;
  groupId?: string | null;
  groupLabel?: string | null;
  groupRequired?: boolean;
  value: string;
  required: boolean;
  label: string;
  priceAdjustmentCents: number;
  active: boolean;
  sortOrder: number;
}

// ---------------------------------------------------------------------
// Price quote (ALPHA-03)
// ---------------------------------------------------------------------

export interface PriceQuoteModifierInput {
  label: string;
  amountCents: number;
}

export interface PriceQuoteLineInput {
  /** Required for a cocktail line (saleKind absent); absent for a direct
   * inventory-sale line (saleKind present). See saleKind's own comment. */
  menuDrinkId?: string;
  /** FLOORQUOTE3: owned source for a new liquor selection, not a stored-price re-read. */
  changeLineOrderId?: string;
  liquorProductId?: string;
  /** Decimal string, e.g. "1.5". Required exactly when the menu drink
   * requires a liquor selection (matches src/api/server.ts's price-quote
   * validation). */
  ounces?: string;
  modifiers?: PriceQuoteModifierInput[];
  /** ALPHA-10 item 2. True when this selection came from the liquor
   * sheet's "More spirits" expansion rather than the drink's own allowed
   * categories; skips the server's category-match check only. Defaults to
   * false server-side when omitted. */
  isSubstitution?: boolean;
  /** Inventory track Slice 3 addition: one of InventorySaleKind ("pour",
   * "beer", "packaged"). When present, this line prices as a direct
   * inventory sale rather than a cocktail; liquorProductId is required,
   * menuDrinkId/isSubstitution are not read. Lets one price-quote request
   * mix cocktail and direct-sale lines so the ticket's subtotal/tax/total
   * stays one server-computed number, never client math. See
   * src/api/validation.ts's PriceQuoteLineInput.saleKind comment. */
  saleKind?: InventorySaleKind;
  /** VOC1-F5 Slice 3 (docs/build-briefs/VOC1-F5-stock-states-on-the-floor-
   * and-86-blocks-selection.md; DEC-161). Mirrors src/api/validation.ts's
   * own PriceQuoteLineInput.override86: an explicit, per-line signal that
   * the caller is attempting to select a flagged-86 liquor product with an
   * authorized override, never inferred client-side from the product's own
   * flagged86 field. Omitted (or false) leaves the Slice 2 unconditional
   * 400 in place for a flagged product. */
  override86?: boolean;
  /** VOC1-F5 Slice 3 gap (a) fix (independent-verifier finding). Mirrors
   * src/api/validation.ts's own PriceQuoteLineInput.orderLineId: sent by
   * the recompute path only (ui/src/main.ts's buildPriceQuoteLine), from
   * TicketLineState.orderLineId, always present since every line this
   * type is built for is already on the order. The server verifies this
   * id names a real, tenant-scoped order line and, when it does, treats
   * re-pricing it as already-authorized: no override86/permission check
   * is required for it. */
  orderLineId?: string;
  /** VOC1-F7 Slice 1 (docs/build-briefs/VOC1-F7-cocktail-pour-modifier.md,
   * Section 2.2; closes SEC5). Mirrors src/api/validation.ts's own
   * PriceQuoteLineInput.pourModifierApplied: an explicit, per-line signal
   * that the caller is deliberately submitting an "ounces" value other than
   * the recipe's own snapshotted liquor-slot default, never inferred from a
   * mismatch. Ignored server-side when ounces already equals the recipe
   * default; required (else 400) whenever it does not. */
  pourModifierApplied?: boolean;
  /** VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
   * 2.4; DEC-173, DEC-174). Each a 'drink_modifier' vocabulary `value`,
   * never an amount: the client never supplies a price, only which
   * catalog modifiers were selected. VOC1-F4-F2 correction: this used to
   * be cocktail-only, a saleKind line's own value silently ignored
   * (DEC-174's own scope gap for the first slice); a saleKind line now
   * resolves this identically, against its own liquor product's
   * attachments (`liquor_product_modifiers`) rather than a menu drink's.
   * Accepts a partial or empty selection unconditionally -- the
   * required-modifier gate is Prepared-time, not this call's own concern
   * (Section 2.4's own "not enforcing every required modifier is selected
   * here"). */
  modifierSelections?: string[];
}

/** VOC1-F4 (Section 2.4 step 6). One resolved catalog modifier: label and
 * amountCents are the catalog's values AT RESOLUTION TIME; required is the
 * attachment's own flag at resolution time. Mirrors src/domain/
 * lineResolution.ts's own ResolvedModifierDetail. */
export interface ResolvedModifierDetailDto {
  value: string;
  label: string;
  amountCents: number;
  required: boolean;
}

export interface PublicationBindingDto {
  publicationId: string;
  editVersion: number;
  contentRevision: number;
  targetRevision: number;
}

export interface PricedLineDto {
  publicationBinding?: PublicationBindingDto;
  menuDrinkId?: string;
  liquorProductId?: string;
  lineTotalCents: number;
  provisional: true;
  priceable: true;
  /** VOC1-F4 (Section 2.4 step 6). VOC1-F4-F2 correction: present,
   * possibly empty, for BOTH a cocktail line and a saleKind line as of
   * VOC1-F4-F2 -- this used to stay undefined for every saleKind line
   * (DEC-174's own gap), closed by that item's own resolution branch
   * inside resolveAndPriceInventorySaleLine. */
  resolvedModifiers?: ResolvedModifierDetailDto[];
}

// INV19 Slice 2 (docs/build-briefs/INV19-pour-size-price-override.md,
// "Slice 2 design"). A line whose price no longer resolves (e.g. a
// pour-size override that has resolved negative since the line was added,
// per DEC-139) rather than one that aborts the whole batch. Carries no
// lineTotalCents; the reason is the server's own message, distinct from
// the existing ALPHA-07 "needs a recipe update" wording, since a pricing
// conflict and a recipe mismatch send whoever fixes it to two different
// screens.
export interface UnpriceableLineDto {
  liquorProductId?: string;
  priceable: false;
  unpriceableReason: string;
}

export interface PriceQuoteResponse {
  lines: (PricedLineDto | UnpriceableLineDto)[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  taxRateBasisPoints: number;
  provisional: true;
}

// ---------------------------------------------------------------------
// Orders (HARNESS-03 / ALPHA-05)
// ---------------------------------------------------------------------

export interface OrderDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  status: string;
  business_date: string;
  /** ALPHA-05. Nullable: a walk-in quick order never requires a name
   * (product-requirements.md P0.3). Was missing from this DTO even
   * though src/domain/orders.ts's OrderRow always carried it (db/schema.ts's
   * orders.name); the ALPHA-06 ticket UI never needed a tab's display
   * name, but ALPHA-07's Open Tabs screen does. */
  name: string | null;
  /** TAB1 (DEC-270): who opened the tab, from the session at creation;
   * null for a tab opened before the column existed. */
  opened_by_user_id: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
}

/** UX20 (docs/build-briefs/UX20-open-tabs-guest-names.md): the tabs-list
 * row shape only, carrying each tab's guest labels in ticket order
 * alongside the ordinary order fields. Server: LIST_OPEN_TABS_SQL's
 * correlated aggregate in src/domain/tabs.ts. */
// TM1 (docs/backlog.md; DEC-230): merge_trail is a plain-language "Merged
// from ..." sentence, present only when a tab_merges row most recently
// targeted this order; null for every ordinary tab. See
// src/domain/tabs.ts's LIST_OPEN_TABS_SQL.
// TAB1 (DEC-270): opened_by_name is the opener's display name, null when
// the tab has no recorded opener.
export type OpenTabDto = OrderDto & {
  guest_labels: string[];
  merge_trail: string | null;
  opened_by_name: string | null;
  /** PL3: this tab's payment link still awaiting the guest, or null. */
  active_payment_link_id: string | null;
};

export interface OrderLineComponentInput {
  componentReference: string;
  quantity: number;
  unit: string;
  /** Inventory track (track/inventory) Slice 2 gate finding C fix. Marks
   * the ONE component that is this line's actual liquor slot (mirrors
   * ui/src/liquorMatch.ts's findLiquorSlotComponent, which
   * buildLineComponents already used to resolve it a moment earlier), so
   * src/api/server.ts's add-line route can stamp liquor_product_id by
   * this explicit flag instead of matching component text against the
   * resolved product's current name across every submitted component. An
   * omitted or false value on every component is the honest "no slot
   * resolved" case; the server never guesses. */
  isLiquorSlot?: boolean;
}

export interface OrderLineDto {
  id: string;
  tenant_id: string;
  order_id: string;
  idempotency_key: string;
  item_reference: string;
  quantity: number;
  prepared_state: string;
  /** ALPHA-10 item 2. True when this line's liquor selection was a
   * "More spirits" substitution outside the drink's own allowed
   * categories. Written once at add time; see db/schema.ts's orderLines
   * comment. */
  is_substitution: boolean;
  /** ALPHA-08 stored facts. Null for a line added before this migration
   * (or, for the liquor-specific fields, for a drink with no liquor
   * slot); populated by src/api/server.ts's add-line route for every new
   * line going forward. See db/schema.ts's orderLines comment. */
  menu_drink_id: string | null;
  liquor_product_id: string | null;
  pour_ounces: string | null;
  per_ounce_price_cents: number | null;
  build_charge_cents: number | null;
  modifiers_cents: number | null;
  line_total_cents: number | null;
  /** ALPHA-12. Null on every ticket that has never named a second guest;
   * see db/schema.ts's orderLines.guestId comment. */
  guest_id: string | null;
  /** ALPHA-08 line removal. Null while active. */
  removed_at: string | null;
  /** Inventory track Slice 3. One of "cocktail" (every pre-Slice-3 line,
   * and every line added through the unchanged cocktail add-line route),
   * "pour", "beer", "packaged". */
  line_kind: string;
  /** M5 (migration 0188). The till price change: all three are null unless
   * a manager typed a price for this drink. line_total_cents already holds
   * the typed price when set. */
  quoted_price_cents: number | null;
  price_change_cents: number | null;
  price_change_id: string | null;
  created_at: string;
}

export interface OrderLineComponentDto {
  id: string;
  tenant_id: string;
  order_line_id: string;
  component_reference: string;
  quantity: string;
  unit: string;
  created_at: string;
}

export interface PreparationOccurrenceDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_line_id: string;
  prepared_quantity: number;
  recipe_version: string | null;
  maker_employee_id: string;
  device_id: string | null;
  occurrence_type: string;
  created_at: string;
}

export interface PaymentAttemptDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_id: string;
  status: string;
  amount: string;
  currency: string;
  provider_reference: string | null;
  /** ALPHA-08. Null unless this payment was made with a tip selection;
   * see db/schema.ts's payment_attempts comment. */
  subtotal_cents: number | null;
  tax_cents: number | null;
  tip_cents: number | null;
  /** CASH1-S2. "card" for every payment before cash and for a card payment; "cash" for a cash one. Optional on the type:
   * older fixtures omit it. */
  tender?: "card" | "cash";
  /** CASH1-S2. Cash only (a bigint column, so a string over the wire); null for a card payment. */
  cash_tendered_cents?: string | number | null;
  change_cents?: string | number | null;
  created_at: string;
  updated_at: string;
}

// ALPHA-08. Per-person tip split row, append-only. See
// db/schema.ts's payment_tip_splits comment.
export interface PaymentTipSplitDto {
  id: string;
  tenant_id: string;
  payment_attempt_id: string;
  employee_id: string;
  /** The maker's users.display_name, LEFT JOINed tenant- and id-scoped;
   * null when no users row matches employee_id (a split written before
   * SEC4 holds a display name as text in employee_id itself). See
   * src/domain/payments.ts's SELECT_TIP_SPLITS_FOR_PAYMENT_SQL comment. */
  display_name: string | null;
  amount_cents: number;
  created_at: string;
}

// ALPHA-12 item 3. See src/domain/payments.ts's PaymentSplitShareInfo.
export interface PaymentSplitShareInfoDto {
  planId: string;
  shareIndex: number;
  shareCount: number;
}

export interface PaymentWithTipSplitsDto {
  paymentAttempt: PaymentAttemptDto;
  tipSplits: PaymentTipSplitDto[];
  /** ALPHA-12. Null for a whole-ticket payment or one covering more than
   * one guest's lines; see src/domain/payments.ts's PaymentWithTipSplits. */
  guestId: string | null;
  /** ALPHA-12. Non-null only for a payment produced by collecting an
   * equal-split share. */
  splitShare: PaymentSplitShareInfoDto | null;
  /** UX62-F1 (docs/build-briefs/UX62-F1-refunded-line.md). This payment's
   * own completed refund total, drinks plus tax, in integer cents; 0 when
   * never refunded. See src/domain/payments.ts's PaymentWithTipSplits
   * comment for why tip has no figure here (never clawed back). */
  refundedCents: number;
  /** CORE3. The order lines this payment's refunds returned (whole lines), so the ticket reads them as Refunded. */
  refundedOrderLineIds: string[];
}

// ALPHA-08. Pay-screen tip selection; mirrors src/api/validation.ts's
// TipSelectionInput exactly.
export type TipSelectionInput =
  | { type: "preset"; basisPoints: number }
  | { type: "custom"; amountCents: number }
  | { type: "none" };

export interface OrderCloseOperationDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_id: string;
  business_date: string;
  closed_at: string;
  created_at: string;
}

// ALPHA-07 tabs (open-tabs landing screen, rename, reopen).

export interface OrderRenameOperationDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_id: string;
  name: string;
  renamed_at: string;
  created_at: string;
}

export interface OrderReopenOperationDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_id: string;
  business_date: string;
  reopened_at: string;
  /** AU14-F4 / DEC-276: null only on a row from before the reason was required. */
  reason_id?: string | null;
  reason_label_at_action?: string | null;
  created_at: string;
}

/** AU14-F4 / DEC-276. The ticket's "Reopened" record strip, resolved on the
 * server: who reopened it, who approved, when, and why. `reason` is null for a
 * reopen from before the reason was required (the strip says "No reason
 * recorded (before this change)"). */
export interface LastReopenDto {
  at: string;
  byName: string | null;
  approvedByName: string | null;
  reason: string | null;
}

/** AU14-F15 / DEC-276. The grey "86 SOLD" strip under a drink sold past an 86,
 * resolved on the server: who sold it, who approved, when, and why. `reason` is
 * null for an override from before the reason was required (the strip says "No
 * reason recorded (before this change)"). */
export interface LineStockOverrideDto {
  at: string;
  byName: string | null;
  approvedByName: string | null;
  reason: string | null;
}

// VOC1-F4 frontend gap fix (build brief Section 2.5). One resolved
// modifier a line was rung with, copied at add-line time; never re-read
// from the catalog. Mirrors src/domain/orderLines.ts's own
// OrderLineModifierRow.
export interface OrderLineModifierDto {
  id: string;
  tenant_id: string;
  order_line_id: string;
  modifier_value: string;
  label: string;
  price_adjustment_cents: number;
  required: boolean;
  created_at: string;
}

export interface OrderLineWithComponentsDto extends OrderLineDto {
  /** M5. The server-resolved "Price changed" strip facts, null when the
   * line's price was never changed. Only the single-order read carries it. */
  priceChange?: LinePriceChangeDto | null;
  /** M5-R10. How many price changes and put-backs the line has in the ledger,
   * 0 for none. Counts a put-back line that no longer carries a strip. Only
   * the single-order read carries it. */
  priceChangeCount?: number;
  /** AU14-F15. The server-resolved "86 sold" strip facts, null when the line was
   * not sold past an 86. Only the single-order read carries it. */
  stockOverride?: LineStockOverrideDto | null;
  /** VER72-F1. True when Prepared wrote at least one inventory posting for
   * this line (read from the ledger); false for a drink with no catalog
   * product, which posts nothing (DEC-218). Only the single-order read carries it. */
  inventory_posted?: boolean;
  components: OrderLineComponentDto[];
  /** VOC1-F4 frontend gap fix (build brief Section 2.5): "A resumed tab
   * reads order_line_modifiers returned with the line directly." Empty for
   * a line that carries no modifier. */
  modifiers: OrderLineModifierDto[];
}

// ---------------------------------------------------------------------
// ALPHA-12 multi-guest tickets. See src/domain/guests.ts.
// ---------------------------------------------------------------------

// MyTime track (track/mytime) Slice 2: the resolved client view a guest
// carrying a clientRef attaches (src/api/server.ts's
// enrichGuestWithClient / src/domain/mytimeClients.ts's
// resolveGuestClientRef). Null for a plain free-text guest.
export interface ResolvedGuestClientDto {
  displayName: string;
  membershipActive: boolean;
  provisional: boolean;
  mytimeClientId: string | null;
}

export interface GuestDto {
  service_today?: boolean;
  daily_comp_claim_state?: "held" | "elsewhere" | "none";
  id: string;
  tenant_id: string;
  order_id: string;
  idempotency_key: string;
  label: string;
  /** MyTime track (track/mytime) Slice 2: additive, nullable, see
   * db/schema.ts's orderGuests header. Both null together for a plain
   * free-text guest. */
  mytime_client_ref_kind: "directory" | "provisional" | null;
  mytime_client_ref_id: string | null;
  client: ResolvedGuestClientDto | null;
  created_at: string;
}

// Auth track (track/auth): the session's bearer token, set once at login
// and cleared at logout by ui/src/main.ts's setAuthToken calls (see that
// function's own comment for why this is a module-level variable rather
// than a parameter threaded through every one of this file's ~60 exported
// functions and their ~100+ call sites in main.ts/admin.ts: every one of
// those already existed before this track, and none of them needs to
// know or care that authentication now exists, since the token travels
// on every request automatically from here). Never logged; see
// scripts/check-no-pin-in-logs.ts, which also scans ui/src/.
let authToken: string | undefined;

// AU11: ONE place that notices a session has ended, instead of ~100.
//
// Session lifetime is entirely server-side: src/domain/users.ts slides a session's
// inactivity window on each authenticated request and refuses once
// tenant_settings.session_inactivity_minutes has passed. The browser
// therefore learns the session is gone only when it next asks, and every
// call site used to render that refusal as its own local error while the
// app stayed exactly where it was. The owner met the result on 2026-08-30:
// "I was not returned to the pin code screen. Instead got to play around
// with the cached page but when I hit update it failed saying I was
// logged out."
//
// Recognising it HERE is the same reasoning this file already applies to
// the bearer token itself, which is a module-level variable precisely so
// that ~100 call sites need not know authentication exists. They need not
// know it ENDS either.
//
// Only an authentication refusal counts. A 401 is the server saying "who
// are you"; a 403 is "I know who you are and no", which is a permission
// answer a screen should render in place, not a reason to sign anybody
// out.
//
// AU12 (docs/build-briefs/AU12-lost-session-leaves-app-unauthenticated.md):
// the handler now returns whether it actually tore a session down, so the
// one-shot latch below can be spent on that fact instead of on the mere
// arrival of a 401. The earlier shape latched unconditionally, before the
// handler ran, so a 401 arriving with no session already in state (which
// main.ts's handleSessionLost has always no-op'd on) spent the one-shot
// for nothing, and every REAL lost session after it was silent for the
// rest of the page's life.
//
// AU13 (docs/build-briefs/AU13-session-lost-names-its-cause.md): the
// handler used to say only THAT the session was gone, never why, and the
// one constant message it showed asserted a cause -- inactivity -- that
// was true for exactly one of its three callers. `idle` is the local
// timer; `revoked` is a real 401 the server sent back, which covers a
// revoked session, a deactivated account, and a PIN change that swept
// other sessions, none of which this file can tell apart from each
// other; `no-token` is AU12's own case, a call caught before it ever
// reached the network. Each caller below passes the one it already
// knows, never one inferred from the response body, which is exactly the
// guess this row exists to remove.
export type SessionLostReason = "idle" | "revoked" | "no-token";

let sessionLostHandler: ((reason: SessionLostReason) => boolean) | undefined;

/** Registered once by main.ts, at page init. Resets the latch as a side
 * effect, which matters for a stale handler from a previous test/module
 * reload, but is NOT how the latch is meant to reset across an ordinary
 * shift: see resetSessionLostLatch below, which main.ts calls every time
 * a new authenticated session actually begins (sign-in, and a successful
 * resume-path probe). Without that second call site, this latch would
 * only ever reset once per page load, and a second lost session later in
 * the same page life would never be reported (the AU12 defect). */
export function setSessionLostHandler(
  handler: ((reason: SessionLostReason) => boolean) | undefined,
): void {
  sessionLostHandler = handler;
  resetSessionLostLatch();
}

// UX29 (docs/build-briefs/UX29-terminal-and-connectivity-status-on-the-
// ring-screen.md): mirrors setSessionLostHandler's own registration shape
// exactly, a second, independent observer over the same two choke points
// (networkErrorResult, noteResponse) every one of getJson/postJson/
// patchJson/putJson/deleteJson already passes through. `reached` is true
// when a real response came back from the server (any status, including a
// non-2xx one -- reaching the server and the server saying no are both
// "reached"), false only when fetch() itself threw. main.ts's own
// renderConnectivityBadge is the one caller.
let connectivityObserver: ((reached: boolean) => void) | undefined;

export function setConnectivityObserver(observer: ((reached: boolean) => void) | undefined): void {
  connectivityObserver = observer;
}

/** Call this whenever a new authenticated session begins. A lost session
 * is a one-time event PER SESSION, not per page load: the latch has to
 * be free again for the next person's session to be able to raise it. */
export function resetSessionLostLatch(): void {
  sessionLostAlreadyRaised = false;
}

let sessionLostAlreadyRaised = false;

// AU13: `reason` is a parameter, never something this function infers from
// `status` or `body`. Every real caller below (getJson/postJson/patchJson/
// putJson/deleteJson) hands back an actual server response, so all of them
// pass "revoked"; only missingTokenResult, which never sent a request at
// all, passes "no-token". noteResponse itself cannot tell a revoked
// session from a deactivated account from an expired one, so it does not
// try -- it only forwards what its caller already knows.
function noteResponse(status: number, body: unknown, reason: SessionLostReason): void {
  // UX29: fires for every real response this file gets back from the
  // server, including a plain 200, BEFORE the 401-only early return below
  // -- reaching the server is the whole signal, independent of what the
  // server said. Excluded for "no-token": missingTokenResult's own call
  // synthesizes this 401 locally and never sends a request, so recording
  // it as "reached" would be false (Section 3 of the build brief).
  if (reason !== "no-token") {
    connectivityObserver?.(true);
  }
  if (status !== 401) {
    return;
  }
  // The route that exists to TELL callers whether a session is still
  // valid is exempt: main.ts calls it on page load precisely to find out,
  // and a 401 there is an answer, not an event.
  const error = (body as { error?: unknown } | undefined)?.error;
  if (error === "authentication_required" || error === undefined) {
    if (sessionLostAlreadyRaised) {
      return;
    }
    // AU12: latch AFTER confirming the handler actually tore a session
    // down, never before. handleSessionLost returns false when
    // state.session is already undefined (nothing to do), and that must
    // leave the latch free rather than spend it, or a genuine later
    // session loss can never be reported.
    const tornDown = sessionLostHandler?.(reason) ?? false;
    if (tornDown) {
      sessionLostAlreadyRaised = true;
    }
  }
}

/** AU12 part 1: a call that requires a session must not be sent at all
 * once there is no bearer token to send it with. Before this, such a
 * call went out with no Authorization header and depended on the SERVER
 * answering 401 honestly; the roster route does not (src/api/
 * server.ts:895-897 swallows its own auth failure and answers 200 with a
 * degraded anonymous body), so a client that only reacted to a 401 could
 * never learn its session was gone from that route, on the first call or
 * any later one. Short-circuiting here removes the dependency entirely:
 * a request that is never sent cannot be answered dishonestly.
 *
 * Reuses noteResponse's own 401 handling so the session-lost latch and
 * handler behave identically whether the refusal is real or synthesized
 * here, rather than a second copy of that logic. */
function missingTokenResult<T>(): ApiResult<T> {
  const body = {
    error: "authentication_required",
    // Matches src/domain/users.ts's extractBearerToken wording exactly,
    // since this IS that condition, just caught before the request left
    // the browser instead of after the server rejected it.
    message: "sign in to continue",
  } as T;
  // AU13: this call never reached the network, so the reason is
  // "no-token", never "revoked" -- the caller knows the difference even
  // though noteResponse's own 401 handling is otherwise identical either
  // way.
  noteResponse(401, body, "no-token");
  return { status: 401, body, networkError: false };
}

/** Suppresses the handler for one call. main.ts's own page-load session
 * probe uses this: it is ASKING whether the session is valid, so a 401 is
 * its return value rather than something to react to. */
export async function withoutSessionLostHandling<T>(run: () => Promise<T>): Promise<T> {
  const previous = sessionLostHandler;
  sessionLostHandler = undefined;
  try {
    return await run();
  } finally {
    sessionLostHandler = previous;
  }
}

export function setAuthToken(token: string | undefined): void {
  authToken = token;
}

function authHeaders(): Record<string, string> {
  return authToken === undefined ? {} : { Authorization: `Bearer ${authToken}` };
}

async function getJson<T>(
  baseUrl: string,
  pathname: string,
  // AU12 part 1: false for every caller except the small, explicit set
  // enumerated at their own call sites below (listUsers's pre-login
  // roster read is the one GET that is legitimately anonymous; login
  // itself is the one anonymous POST, in postJson below). Not inferred
  // from the route or the response, so a route that happens to answer
  // anonymously is never mistaken for a route that is MEANT to be asked
  // anonymously.
  // SEC2-S4: `headers` carries the pre-login picker's X-Device-Token.
  options: { anonymousOk?: boolean; headers?: Record<string, string> } = {},
): Promise<ApiResult<T>> {
  if (authToken === undefined && options.anonymousOk !== true) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "GET",
      headers: { accept: "application/json", ...authHeaders(), ...(options.headers ?? {}) },
      // Every GET here reads live catalog, pricing, or order state a
      // bartender is about to act on (menu drink detail, liquor
      // inventory, open tabs, an order's current lines); the server sets
      // no Cache-Control of its own, and a browser-cached response
      // (found live, ALPHA-07: two e2e tests sharing one seeded menu
      // reused a stale menu-drink-detail response across BrowserContexts)
      // would silently serve stale data with no way to tell it apart
      // from fresh.
      //
      // Intentionally uncovered by an automated assertion of the header
      // or fetch option itself (see docs/qa-strategy.md's ALPHA-07
      // cache-lesson note): the browser's own HTTP cache is an
      // implementation detail Playwright cannot deterministically force
      // into a stale-hit or miss from outside, and asserting "the request
      // carried cache: no-store" would only prove this one line was
      // typed correctly, not that stale data is actually prevented. The
      // regression signal that matters is functional (a resumed order or
      // reloaded menu never shows data older than the server's last
      // answer), which the existing e2e suite already exercises
      // end-to-end every time it runs, not through a dedicated cache
      // test.
      cache: "no-store",
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  // AU13: every caller here got a genuine answer from the server, so a
  // 401 means the server actually refused the request. That is the
  // "revoked" reason, distinct from missingTokenResult's own "no-token",
  // which never asks the server anything.
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

// PO3/TF4. The first binary (non-JSON) responses this client reads: a
// generated PDF or .xlsx file. Mirrors getJson's own auth/error handling
// exactly (missingTokenResult when signed out, a network-error result on
// a failed fetch) but returns a Blob on 200 rather than parsing JSON, and
// a best-effort error message on non-200 (the server error routes still
// answer JSON on failure; only a 200 response body is the binary file).
interface BinaryFileResult {
  status: number;
  blob: Blob | undefined;
  message: string | undefined;
  networkError: boolean;
}

async function getBinaryFile(baseUrl: string, pathname: string): Promise<BinaryFileResult> {
  if (authToken === undefined) {
    return { status: 401, blob: undefined, message: "sign in to continue", networkError: false };
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "GET",
      headers: { ...authHeaders() },
      cache: "no-store",
    });
  } catch (cause) {
    return {
      status: 0,
      blob: undefined,
      message: cause instanceof Error ? cause.message : String(cause),
      networkError: true,
    };
  }
  if (response.status !== 200) {
    const parsed = (await readJsonBody(response)) as { message?: string } | undefined;
    noteResponse(response.status, parsed ?? {}, "revoked");
    return {
      status: response.status,
      blob: undefined,
      message: parsed?.message,
      networkError: false,
    };
  }
  const blob = await response.blob();
  return { status: 200, blob, message: undefined, networkError: false };
}

// PO3 (DEC-228). Downloads the one-page purchase-order PDF.
export function getPurchaseOrderPdf(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/pdf`,
  );
}

// TF4. The preview call, read before offering the download so a missing-
// SKU warning can be shown first (design/mockups/po-merge-invoice-po-
// detail.html's State B).
export interface Abc2goExportPreviewDto {
  /** TF4-F3: false when a line with an ABC2GO code has an unknown or
   * fractional bottle count; the download then refuses (409). */
  exportable: boolean;
  rowCount: number;
  /** TF4-F3: exactly what the file carries, qty in whole bottles. */
  rows: {
    itemCode: string;
    qty: number;
    productName: string;
    orderUnit: string | null;
    orderedQuantity: string;
    bottlesPerOrderUnit: string;
  }[];
  refusedLines: { liquorProductId: string; productName: string; message: string }[];
  omittedLines: { liquorProductId: string; productName: string }[];
}

export function getAbc2goExportPreview(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<Abc2goExportPreviewDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/abc2go-export/preview`,
  );
}

export function getAbc2goExportFile(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/abc2go-export`,
  );
}

// PO7-S1 (docs/build-briefs/PO7-S1-invoice-attach.md). Invoice files on a
// purchase order. The upload sends the raw file bytes (not JSON, not
// multipart) with the file's own type, one Idempotency-Key per file the
// person picked (a retry of the same pick reuses it), and the original name
// URI-encoded. Reads fetch with the bearer header and return a Blob, the
// same pattern the purchase order PDF download uses.
export interface InvoiceDuplicateOfDto {
  orderId: string;
  vendorName: string;
  placedAt: string | null;
  /** Null for a file already on another location's order: only the order id, vendor and placed date are shown. */
  attachedAt: string | null;
}

export interface InvoiceAttachmentDto {
  id: string;
  purchaseOrderId: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedByName: string | null;
  duplicateOf: InvoiceDuplicateOfDto | null;
  /** PO7-S2: where the reading of this file stands. */
  reading: InvoiceReadingDto;
}

// PO7-S2 (docs/build-briefs/PO7-S2-invoice-ocr-draft.md). The reading status
// of an attached invoice, its read-only draft, and a request to read it again.
export interface InvoiceReadingDto {
  status: "pending" | "drafted" | "failed" | "unavailable";
  message: string | null;
  retryable: boolean | null;
  draftVersion: number | null;
  duplicateInvoiceNumber: InvoiceDraftDuplicateDto | null;
}

export interface InvoiceDraftDuplicateDto {
  orderId: string;
  vendorName: string;
  placedAt: string | null;
}

export interface InvoiceDraftFieldDto<T> {
  value: T | null;
  confidence: number | null;
}

export interface InvoiceDraftLineDto {
  lineNumber: number;
  vendorItemCode: string | null;
  description: string | null;
  quantity: string | null;
  unitPriceCents: number | null;
  lineTotalCents: number | null;
  taxCents: number | null;
  confidence: number | null;
}

export interface InvoiceDraftDto {
  attachmentId: string;
  orderId: string;
  version: number;
  provider: string;
  readAt: string;
  vendorName: InvoiceDraftFieldDto<string>;
  invoiceNumber: InvoiceDraftFieldDto<string>;
  invoiceDate: InvoiceDraftFieldDto<string>;
  dueDate: InvoiceDraftFieldDto<string>;
  subtotalCents: InvoiceDraftFieldDto<number>;
  taxCents: InvoiceDraftFieldDto<number>;
  totalCents: InvoiceDraftFieldDto<number>;
  lines: InvoiceDraftLineDto[];
  duplicateInvoiceNumber: InvoiceDraftDuplicateDto | null;
}

export function getInvoiceDraft(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
): Promise<ApiResult<{ draft: InvoiceDraftDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/draft`,
  );
}

export function retryInvoiceReading(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
): Promise<ApiResult<{ status: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/reading/retry`,
    {},
  );
}

// PO7-S3 (docs/build-briefs/PO7-S3-invoice-review-and-variance.md). The review
// of an invoice's draft against its order, and a reviewer's actions on one
// line. Money is integer cents and quantities are decimal strings, exactly as
// the server derives them; nothing here is computed from typed values.
export type InvoiceReviewBucket =
  "matched" | "needs_attention" | "held_back" | "classified" | "left_out";
/** PO7-S4-F1 (DEC-299): the fixed vocabulary of invoice line kinds, picked, never typed. */
export type InvoiceNonProductKind =
  "shipping" | "fee" | "tax" | "deposit_charged" | "deposit_returned" | "credit" | "return";
export type InvoiceLineKind = "product" | InvoiceNonProductKind;
export type InvoiceReviewFlag = "cost_variance" | "received_variance" | "no_cost_baseline";

export interface InvoiceReviewLineDto {
  lineNumber: number;
  vendorItemCode: string | null;
  description: string | null;
  confidence: number | null;
  bucket: InvoiceReviewBucket;
  match: {
    status: "matched" | "uncertain" | "unmatched";
    source: "item_code" | "description" | "reviewer" | null;
    purchaseOrderLineId: string | null;
    candidatePurchaseOrderLineId: string | null;
  };
  holdBackReason: "low_confidence" | "unreadable" | null;
  productName: string | null;
  orderedQuantity: string | null;
  receivedQuantity: string | null;
  invoiceQuantity: string | null;
  invoiceQuantityOnOrderLine: string | null;
  invoiceUnitCostCents: number | null;
  invoiceLineCents: number | null;
  poUnitCostCents: number | null;
  costBaseline: "po_estimate" | "offer_cost" | null;
  differenceCents: number | null;
  differencePercentTenths: number | null;
  lineDifferenceCents: number | null;
  flags: InvoiceReviewFlag[];
  enteredByHand: boolean;
  version: number;
  /** `product` until a person picks a kind; null for a left-out line. */
  kind: InvoiceLineKind | null;
  /** Words-only guess ("Looks like this"); never applied by the server. */
  suggestedKind: InvoiceNonProductKind | null;
  amountCents: number | null;
  /** The amount as printed, signed. */
  printedAmountCents: number | null;
  effectCents: number | null;
  /** PO7-S4-F4: how the reviewer read the price, per order unit (default) or per bottle. */
  priceReading: InvoicePriceReading;
  /** Set only where the matched order line's unit holds more than one bottle. */
  bottlesPerOrderUnit: string | null;
  orderUnit: string | null;
  /** The price as read, per bottle, when the reading is per bottle. */
  readUnitCostCents: number | null;
}

export type InvoicePriceReading = "order_unit" | "per_bottle";

export interface InvoiceKindTotalDto {
  kind: InvoiceNonProductKind;
  count: number;
  effectCents: number;
}

export interface InvoiceReviewOrderLineDto {
  id: string;
  productName: string;
  vendorSku: string | null;
  orderedQuantity: string;
  receivedQuantity: string;
  unitCostCents: number | null;
}

export interface InvoiceReviewDto {
  attachmentId: string;
  orderId: string;
  draftVersion: number;
  originalFilename: string;
  vendorName: string;
  invoiceNumber: string | null;
  settings: {
    varianceBasisPoints: number;
    varianceFloorCents: number;
    confidenceThresholdBasisPoints: number;
  };
  duplicateInvoiceNumber: InvoiceDraftDuplicateDto | null;
  lines: InvoiceReviewLineDto[];
  orderLines: InvoiceReviewOrderLineDto[];
  counts: {
    matched: number;
    flagged: number;
    needsAttention: number;
    heldBack: number;
    classified: number;
    leftOut: number;
  };
  totals: {
    poEstimatedCents: number;
    poUnpricedLineCount: number;
    /** Matched products only. */
    invoiceMatchedCents: number | null;
    /** Products plus every classified line, signed by kind. */
    invoiceTotalCents: number | null;
    printedTotalCents: number | null;
    /** Printed minus the lines' total; never blocks. */
    printedDifferenceCents: number | null;
    leftOutCount: number;
    leftOutCents: number;
    heldBackCents: number;
    byKind: InvoiceKindTotalDto[];
  };
  blockingReasons: { code: string; message: string }[];
  /** Null until the invoice is confirmed (PO7-S4). */
  confirmed: InvoiceConfirmedDto | null;
  confirm: { available: boolean; reason: string; token: string };
}

// PO7-S4 (docs/build-briefs/PO7-S4-invoice-confirm-and-cost.md). A confirmed
// invoice, as the server recorded it: the order's estimate beside the final
// invoice figure, and for each line what happened to its cost.
export type InvoiceCostOutcome =
  | "posted"
  | "unchanged"
  | "no_receipt"
  | "receipt_without_cost"
  | "receipt_already_corrected"
  | "conflicting_costs"
  | "cost_unresolved";

export interface InvoiceConfirmedLineDto {
  lineNumber: number;
  purchaseOrderLineId: string;
  productName: string;
  quantity: string;
  unitCostCents: number;
  lineCents: number;
  estimateUnitCostCents: number | null;
  estimateSource: "po_estimate" | "offer_cost" | null;
  flags: string[];
  enteredByHand: boolean;
  costOutcome: InvoiceCostOutcome;
  /** PO7-S4-F4: the reading the reviewer chose, and the price as read per bottle. */
  priceReading: InvoicePriceReading;
  readUnitCostCents: number | null;
  orderUnit?: string | null;
  bottlesPerOrderUnit?: string | null;
  previousUnitCostCents: number | null;
  newUnitCostCents: number | null;
}

export interface InvoiceConfirmedDto {
  id: string;
  confirmedAt: string;
  confirmedBy: string;
  confirmedByName: string | null;
  invoiceNumber: string;
  estimatedTotalCents: number;
  estimatedUnpricedLineCount: number;
  invoiceTotalCents: number;
  printedTotalCents: number | null;
  differenceCents: number;
  receiptsCorrected: number;
  lines: InvoiceConfirmedLineDto[];
  printedDifferenceCents: number | null;
  productTotalCents: number;
  otherLines: InvoiceConfirmedOtherLineDto[];
  byKind: InvoiceKindTotalDto[];
  leftOutCount: number;
  leftOutCents: number;
}

export interface InvoiceConfirmedOtherLineDto {
  lineNumber: number;
  treatment: "counted" | "left_out";
  kind: InvoiceNonProductKind | null;
  description: string | null;
  amountCents: number;
  effectCents: number;
  enteredByHand: boolean;
}

export interface InvoiceReviewActionBody {
  action: "match" | "unmatch" | "classify" | "leave_out";
  expectedVersion: number;
  purchaseOrderLineId?: string;
  quantity?: string;
  unitPriceCents?: number;
  /** PO7-S4-F1: a classify's kind. */
  kind?: InvoiceNonProductKind;
  /** PO7-S4-F1: a classify's amount in cents when the reader misread it. */
  amountCents?: number;
  /** PO7-S4-F4: a match's price reading; omitted leaves the default (per order unit). */
  priceReading?: InvoicePriceReading;
}

export function getInvoiceReview(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
): Promise<ApiResult<{ review: InvoiceReviewDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/review`,
  );
}

/** One key per tap; a retry of the same tap reuses it, so a request that had
 * already committed is returned, never recorded twice. */
export async function postInvoiceReviewAction(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
  lineNumber: number,
  body: InvoiceReviewActionBody,
  idempotencyKey: string,
): Promise<ApiResult<{ review: InvoiceReviewDto } & Partial<ApiErrorBody>>> {
  if (authToken === undefined) {
    return missingTokenResult();
  }
  let response: Response;
  try {
    response = await fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/review/lines/${lineNumber}/actions`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": idempotencyKey,
          ...authHeaders(),
        },
        body: JSON.stringify(body),
      },
    );
  } catch (cause) {
    return networkErrorResult(cause);
  }
  const parsed = (await readJsonBody(response)) as never;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

/** Confirms a reviewed invoice. One key per tap; a retry of the same tap (and the
 * "lock the file" retry after a pending retention lock) reuses it, so a request
 * that had already committed is returned, never recorded twice. */
export async function postInvoiceConfirm(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
  reviewToken: string,
  idempotencyKey: string,
): Promise<
  ApiResult<{ review: InvoiceReviewDto; retention: "locked" | "pending" } & Partial<ApiErrorBody>>
> {
  if (authToken === undefined) {
    return missingTokenResult();
  }
  let response: Response;
  try {
    response = await fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/confirm`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": idempotencyKey,
          ...authHeaders(),
        },
        body: JSON.stringify({ reviewToken }),
      },
    );
  } catch (cause) {
    return networkErrorResult(cause);
  }
  const parsed = (await readJsonBody(response)) as never;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

export function listInvoiceAttachments(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ invoices: InvoiceAttachmentDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices`,
  );
}

export async function uploadInvoiceAttachment(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  file: File,
  contentType: string,
  idempotencyKey: string,
): Promise<
  ApiResult<
    {
      attachment: InvoiceAttachmentDto;
      replayed: boolean;
      duplicateOf: InvoiceDuplicateOfDto | null;
    } & Partial<ApiErrorBody>
  >
> {
  if (authToken === undefined) {
    return missingTokenResult();
  }
  let response: Response;
  try {
    response = await fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices`,
      {
        method: "POST",
        headers: {
          "content-type": contentType,
          "idempotency-key": idempotencyKey,
          "x-invoice-filename": encodeURIComponent(file.name),
          ...authHeaders(),
        },
        body: file,
      },
    );
  } catch (cause) {
    return networkErrorResult(cause);
  }
  const parsed = (await readJsonBody(response)) as never;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

export function getInvoiceAttachmentFile(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  attachmentId: string,
  disposition: "inline" | "attachment",
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/invoices/${encodeURIComponent(attachmentId)}/file?disposition=${disposition}`,
  );
}

async function postJson<T>(
  baseUrl: string,
  pathname: string,
  requestBody: unknown,
  // MyTime track (track/mytime) Slice 2 addition: an optional
  // AbortSignal, so Add Guest search (ui/src/main.ts's
  // handleAddGuestSearchInput) can cancel a superseded in-flight request
  // rather than let a fast typist's earlier keystroke race a later one,
  // per the proposal's own Section 3 recommendation. Every EXISTING
  // caller of postJson omits this parameter and is completely unaffected
  // (fetch's own `signal: undefined` is a no-op, never aborts).
  signal?: AbortSignal,
  // AU12 part 1: see getJson's own doc comment. login() is the one
  // anonymous POST caller.
  // SEC2-S3: `headers` carries the one extra header sign-in sends (X-Device-Token).
  options: { anonymousOk?: boolean; headers?: Record<string, string> } = {},
): Promise<ApiResult<T>> {
  if (authToken === undefined && options.anonymousOk !== true) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "POST",
      headers: { "content-type": "application/json", ...(options.headers ?? {}), ...authHeaders() },
      body: JSON.stringify(requestBody),
      signal,
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  // AU13: every caller here got a genuine answer from the server, so a
  // 401 means the server actually refused the request. That is the
  // "revoked" reason, distinct from missingTokenResult's own "no-token",
  // which never asks the server anything.
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

async function patchJson<T>(
  baseUrl: string,
  pathname: string,
  requestBody: unknown,
): Promise<ApiResult<T>> {
  // AU12 part 1: no PATCH call is ever legitimately anonymous (nothing
  // pre-login mutates anything), so this short-circuits unconditionally
  // rather than taking an anonymousOk option nothing would ever pass.
  if (authToken === undefined) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "PATCH",
      headers: { "content-type": "application/json", ...authHeaders() },
      body: JSON.stringify(requestBody),
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  // AU13: every caller here got a genuine answer from the server, so a
  // 401 means the server actually refused the request. That is the
  // "revoked" reason, distinct from missingTokenResult's own "no-token",
  // which never asks the server anything.
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

async function putJson<T>(
  baseUrl: string,
  pathname: string,
  requestBody: unknown,
): Promise<ApiResult<T>> {
  // AU12 part 1: see patchJson's own comment; no PUT call is anonymous.
  if (authToken === undefined) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "PUT",
      headers: { "content-type": "application/json", ...authHeaders() },
      body: JSON.stringify(requestBody),
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  // AU13: every caller here got a genuine answer from the server, so a
  // 401 means the server actually refused the request. That is the
  // "revoked" reason, distinct from missingTokenResult's own "no-token",
  // which never asks the server anything.
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

// PO6 Slice 2: the first DELETE this file needs
// (.../purchasing/cart/lines/:liquorProductId, removing one cart line).
async function deleteJson<T>(baseUrl: string, pathname: string): Promise<ApiResult<T>> {
  // AU12 part 1: see patchJson's own comment; no DELETE call is anonymous.
  if (authToken === undefined) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "DELETE",
      headers: { accept: "application/json", ...authHeaders() },
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  // AU13: every caller here got a genuine answer from the server, so a
  // 401 means the server actually refused the request. That is the
  // "revoked" reason, distinct from missingTokenResult's own "no-token",
  // which never asks the server anything.
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

// STK1 (docs/build-briefs/STK1-86-setter.md; DEC-269). The 86 flag's own
// routes. POST sets or clears (append-only on the server; the actor is the
// session's user, never a body field). The write is permission
// inv.stock_flag.record; the history read is inv.stock_flag.view, which a
// lounger does NOT hold (DEC-050), so a caller must treat a 403 on the GET
// as "no since/by information", never as an error worth a toast.
export interface StockFlagDto {
  id: string;
  liquorProductId: string;
  componentReference: string;
  flagged: boolean;
  note: string | null;
  actorEmployeeId: string;
  /** The setter's display name, or null for a removed account. */
  actorName: string | null;
  createdAt: string;
}

export function recordStockFlag(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId: string; flagged: boolean },
): Promise<ApiResult<{ replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/stock-flags`, {
    // A fresh key per tap: a deliberate second tap is a second write, and
    // only a retried identical request replays.
    idempotencyKey: crypto.randomUUID(),
    liquorProductId: params.liquorProductId,
    flagged: params.flagged,
  });
}

export function listStockFlags(
  baseUrl: string,
  tenantId: string,
  params: { latestOnly?: boolean } = {},
): Promise<ApiResult<{ stockFlags: StockFlagDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/stock-flags${
      params.latestOnly === true ? "?latestOnly=true" : ""
    }`,
  );
}

// CORE7 (docs/build-briefs/CORE7-86-any-item.md). The explicit 86 of a menu drink or an ingredient, per location.
// POST needs inv.stock_flag.record. GET needs record or view; who and when come back only with view (a lounger
// holds record but not view, DEC-050), as null.
export interface MenuStockDrinkDto {
  menuDrinkId: string;
  name: string;
  category: string;
  /** The drink's own flag. */
  own: boolean;
  /** The ingredient labels and mapped item names that make it read 86'd. */
  via: string[];
  /** The mapped item names inside via (an ingredient flip on this client keeps them). */
  viaItems: string[];
  /** The recipe's ingredient keys, so this client can recompute locally after an ingredient flip. */
  ingredientKeys: string[];
  /** own, or at least one entry in via. */
  flagged86: boolean;
  since: string | null;
  actorName: string | null;
}

export interface MenuStockIngredientDto {
  key: string;
  label: string;
  flagged86: boolean;
  usedByCount: number;
  since: string | null;
  actorName: string | null;
}

/** CORE7-F1: one stored set or clear of a drink's or ingredient's 86, newest first. Empty for a session without
 * inv.stock_flag.view. */
export interface MenuStockHistoryDto {
  subjectKind: "menu_drink" | "ingredient";
  /** The menu drink id, or the ingredient key. */
  subjectId: string;
  flagged: boolean;
  actorName: string | null;
  createdAt: string;
}

export function getMenuStockState(
  baseUrl: string,
  tenantId: string,
  options: { history?: boolean } = {},
): Promise<
  ApiResult<
    {
      drinks: MenuStockDrinkDto[];
      ingredients: MenuStockIngredientDto[];
      history?: MenuStockHistoryDto[];
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/menu-stock-flags${
      options.history === true ? "?history=true" : ""
    }`,
  );
}

export function recordMenuStockFlag(
  baseUrl: string,
  tenantId: string,
  params:
    | { kind: "menu_drink"; menuDrinkId: string; flagged: boolean }
    | { kind: "ingredient"; ingredientName: string; flagged: boolean },
): Promise<ApiResult<{ replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/menu-stock-flags`, {
    // A fresh key per tap, as recordStockFlag.
    idempotencyKey: crypto.randomUUID(),
    ...params,
  });
}

export function listMenuDrinks(
  baseUrl: string,
  tenantId: string,
  params: {
    /** ALPHA-10 item 3: defaults to true server-side when omitted (the
     * bartender menu's own call). The manager settings screen passes
     * false explicitly to see seasonally disabled drinks too. */
    activeOnly?: boolean;
    /** Amendment 2026-08-22 (tenth): the Drinks list's own search box,
     * mirroring listLiquorProducts' `q`. */
    q?: string;
    /** The Drinks list's own category filter chip. */
    category?: string;
  } = {},
): Promise<ApiResult<{ menuDrinks: MenuDrinkListItemDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  if (params.q !== undefined && params.q.length > 0) {
    query.set("q", params.q);
  }
  if (params.category !== undefined && params.category.length > 0) {
    query.set("category", params.category);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export async function getMenuDrink(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<
  ApiResult<
    {
      menuDrink: MenuDrinkDto;
      components: MenuDrinkComponentDto[];
      /** ALPHA-10 item 2, sibling field; see MenuDrinkListItemDto's own
       * comment on why this response shape differs from the list one. */
      liquorSlots: string[];
      /** VOC1-F4 (Section 2.2). This drink's own attached modifiers. */
      modifiers: MenuDrinkModifierDetailDto[];
      unresolvedIngredientChoices?: UnresolvedIngredientChoiceDto[];
      /** CORE14. Who publishes a drink created from a franchisor's recipe; null for the location's own drink. */
      networkSource?: { networkName: string; required: boolean; withdrawn: boolean } | null;
      /** RECUX2-F1. The franchisor's glass, garnish and numbered steps; null for the location's own drink. Read only. */
      recipeMethod?: RecipeMethodDto | null;
    } & Partial<ApiErrorBody>
  >
> {
  const result = await getJson<
    {
      menuDrink: MenuDrinkDto;
      components: MenuDrinkComponentDto[];
      /** ALPHA-10 item 2, sibling field; see MenuDrinkListItemDto's own
       * comment on why this response shape differs from the list one. */
      liquorSlots: string[];
      /** VOC1-F4 (Section 2.2). This drink's own attached modifiers. */
      modifiers: MenuDrinkModifierDetailDto[];
      unresolvedIngredientChoices?: UnresolvedIngredientChoiceDto[];
      /** CORE14. Who publishes a drink created from a franchisor's recipe; null for the location's own drink. */
      networkSource?: { networkName: string; required: boolean; withdrawn: boolean } | null;
      /** RECUX2-F1. The franchisor's glass, garnish and numbered steps; null for the location's own drink. Read only. */
      recipeMethod?: RecipeMethodDto | null;
    } & Partial<ApiErrorBody> & { ingredientChoices?: MenuDrinkModifierDetailDto[] }
  >(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/${encodeURIComponent(id)}`);
  if (result.status === 200) {
    if (!Array.isArray(result.body.ingredientChoices))
      throw new Error("Ingredient choices could not be loaded. Refresh and try again.");
    result.body.modifiers = result.body.ingredientChoices;
  }
  return result;
}

// ALPHA-10 item 3: the manager settings screen's seasonal enable/disable,
// through the same catalog update endpoint every other menu-drink field
// uses (the product owner's explicit direction, not a new dedicated
// route). Named updateMenuDrinkActive rather than a general updateMenuDrink
// because this UI never edits any other menu-drink field; a future editor
// screen can widen this to a full PATCH body when that need exists.
export function updateMenuDrinkActive(
  baseUrl: string,
  tenantId: string,
  id: string,
  active: boolean,
): Promise<ApiResult<{ menuDrink: MenuDrinkDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/${encodeURIComponent(id)}`,
    { active },
  );
}

export function listLiquorProducts(
  baseUrl: string,
  tenantId: string,
  params: {
    category?: string | string[];
    q?: string;
    activeOnly?: boolean;
    /** ALPHA-11 owner feedback (fourth amendment): the Bottles list's
     * shelf-tier filter chip. One of the four SHELF_TIER_KEYS to match
     * that tier exactly, "none" to match a bottle with no shelf assigned,
     * or omitted for no shelf-tier filtering ("All"). See
     * src/api/server.ts's parseLiquorProductShelfTierQuery for the
     * matching server-side validation. */
    shelfTier?: string;
    /** Inventory track Slice 3: the Inventory list's item-kind filter
     * chip. One of ITEM_KINDS, or omitted for "All". */
    itemKind?: string;
    /** INVLOC1-S2 (DEC-326): the location whose par, bin and stock words the rows carry. Omitted means this tablet's location.
     * Only a location the person may plan for is accepted (the server refuses another). */
    locationId?: string;
  } = {},
): Promise<
  ApiResult<
    {
      liquorProducts: LiquorProductDto[];
      sellingContext?: LocationSellingContextDto;
    } & Partial<ApiErrorBody>
  >
> {
  const query = new URLSearchParams();
  if (params.category !== undefined) {
    // ALPHA-10 item 2: the liquor sheet's default view browses/searches
    // across a drink's WHOLE allowed-category set at once (e.g.
    // vodka+tequila for an espresso martini), so this accepts either one
    // category or an array, comma-joined for the request query string;
    // see src/api/server.ts's parseListQuery for the matching server-side
    // split. A single string is sent exactly as before (no behavior
    // change for every existing single-category caller).
    const categoryValue = Array.isArray(params.category)
      ? params.category.join(",")
      : params.category;
    if (categoryValue.length > 0) {
      query.set("category", categoryValue);
    }
  }
  if (params.q !== undefined && params.q.length > 0) {
    query.set("q", params.q);
  }
  // ALPHA-11: the manager console's Bottles list passes activeOnly: false
  // explicitly to see deactivated bottles too, mirroring listMenuDrinks'
  // own activeOnly parameter for the manager settings screen.
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  if (params.shelfTier !== undefined && params.shelfTier.length > 0) {
    query.set("shelfTier", params.shelfTier);
  }
  if (params.itemKind !== undefined && params.itemKind.length > 0) {
    query.set("itemKind", params.itemKind);
  }
  if (params.locationId !== undefined && params.locationId.length > 0) {
    query.set("locationId", params.locationId);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

/** VOC1-F3: the liquor sheet's Recent strip. The ids of up to five distinct liquors the signed-in employee used at
 * this tablet's location in the last seven days, for the given spirit types, newest first. The employee and the location are
 * the session's; nothing here names either. */
export function listRecentLiquors(
  baseUrl: string,
  tenantId: string,
  categories: readonly string[],
): Promise<
  ApiResult<
    { recent: Array<{ liquorProductId: string; lastSelectedAt: string }> } & Partial<ApiErrorBody>
  >
> {
  const query = new URLSearchParams({ category: categories.join(",") });
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/recent?${query.toString()}`,
  );
}

// ALPHA-08: single-liquor-product-by-id read, for tab-resume reconstruction
// (main.ts's reconstructOrderLine), which now knows a resumed line's exact
// liquor_product_id (a stored fact) and just needs that product's display
// name, rather than searching/matching by name the way the pre-ALPHA-08
// heuristic had to.
export async function getLiquorProduct(
  baseUrl: string,
  tenantId: string,
  id: string,
  locationId?: string,
): Promise<
  ApiResult<
    {
      liquorProduct: LiquorProductDto;
      sellingContext?: LocationSellingContextDto;
      /** VOC1-F4-F2 (Section 2.2). This product's own attached
       * modifiers, mirroring GET .../menu-drinks/:id's identical
       * `modifiers` field (VOC1-F4 Section 2.2). Used both by the
       * Inventory item editor's own attachment picker and by the floor's
       * per-product lazy fetch (main.ts's activeInventorySaleModifiers)
       * to learn whether a tile needs the modifiers sheet at all. */
      modifiers: MenuDrinkModifierDetailDto[];
      unresolvedIngredientChoices?: UnresolvedIngredientChoiceDto[];
    } & Partial<ApiErrorBody>
  >
> {
  const result = await getJson<
    {
      liquorProduct: LiquorProductDto;
      sellingContext?: LocationSellingContextDto;
      /** VOC1-F4-F2 (Section 2.2). This product's own attached
       * modifiers, mirroring GET .../menu-drinks/:id's identical
       * `modifiers` field (VOC1-F4 Section 2.2). Used both by the
       * Inventory item editor's own attachment picker and by the floor's
       * per-product lazy fetch (main.ts's activeInventorySaleModifiers)
       * to learn whether a tile needs the modifiers sheet at all. */
      modifiers: MenuDrinkModifierDetailDto[];
      unresolvedIngredientChoices?: UnresolvedIngredientChoiceDto[];
    } & Partial<ApiErrorBody> & { ingredientChoices?: MenuDrinkModifierDetailDto[] }
  >(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
  if (result.status === 200) {
    if (!Array.isArray(result.body.ingredientChoices))
      throw new Error("Ingredient choices could not be loaded. Refresh and try again.");
    result.body.modifiers = result.body.ingredientChoices;
  }
  return result;
}

// INV19 Slice 1 (docs/build-briefs/INV19-pour-size-price-override.md;
// ADR-010 revised text; DEC-139). A per-item, per-pour-size price
// override that follows its shelf tier: deltaCents is signed, and
// effectivePriceCents is ALWAYS computed server-side
// (resolvePourPriceOverrideEffectiveCents), never recomputed here, per
// ADR-010 Section 5's "exactly one place" requirement.
export interface PourPriceOverrideDto {
  pourSizeValue: string;
  deltaCents: number;
  active: boolean;
  /** Null only when the product carries no shelf tier at all (an edge
   * case the write path already refuses for an ACTIVE override; an
   * inactive one may still exist without a tier). */
  effectivePriceCents: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PourPriceOverridesDto {
  tierValue: string | null;
  tierLabel: string | null;
  tierPriceCents: number | null;
  overrides: PourPriceOverrideDto[];
}

export function getPourPriceOverrides(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
  locationId?: string,
): Promise<ApiResult<PourPriceOverridesDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(liquorProductId)}/pour-price-overrides${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export function putPourPriceOverride(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
  pourSizeValue: string,
  params: { deltaCents: number; active: boolean },
): Promise<ApiResult<{ override: PourPriceOverrideDto } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(liquorProductId)}/pour-price-overrides/${encodeURIComponent(pourSizeValue)}`,
    params,
  );
}

// ---------------------------------------------------------------------
// ALPHA-11 manager console: full catalog CRUD (create/edit bottles and
// drinks) beyond ALPHA-10 item 3's active-only toggle. Every route below
// already existed server-side (src/api/server.ts) as part of the ALPHA-01
// catalog domain; this is the first UI caller of the create/full-patch
// shapes.
// ---------------------------------------------------------------------

// Inventory track Slice 3, Amendment 2026-08-22 (eleventh). Shared by
// Create/UpdateLiquorProductInput below (identical fields), matching
// src/domain/catalog.ts's InventoryItemFields split for the same reason.
export interface InventoryItemFieldsInput {
  trackStock?: boolean;
  addonOnly?: boolean;
  addon?: {
    enabled?: boolean;
    priceAdjustmentCents?: number | null;
    quantityPerUse?: number | string | null;
    unit?: string | null;
  };
  /** INVLOC1-S2 (DEC-326): par level and bin label are per location. One entry per location the person changed; a null clears
   * that value, an omitted key leaves it. The server resolves each location by the planning rule and refuses the whole
   * request if any is refused. There is no tenant-wide parLevel or binLabel any more. */
  stockSettings?: StockSettingEntryInput[];
  sizeValue?: number;
  sizeUnit?: string;
  containerType?: string;
  /** Already-converted basis points; the human-percent conversion is
   * ui/src/money.ts's job, at the form boundary, never here. */
  abvBasisPoints?: number;
  producer?: string;
  countryRegion?: string;
  descriptionNotes?: string;
  /** INV8 Section 2.7 (DEC-176). One of the tenant's own 'keg_size'
   * vocabulary values, or a value the product already carried even if
   * since retired (validateDefaultKegSize's own retirement-safety rule).
   * Wired through both create and update the same way containerType
   * already is. */
  defaultKegSize?: string;
  /** ADDITEM2 (DEC-611). Basis points, 0 to 5000. On an update, null clears it back to the tenant's keg usable yield. */
  kegWasteBasisPoints?: number | null;
}

/** One location's change to one item's par and bin (src/api/validation.ts's optionalStockSettings). */
export interface StockSettingEntryInput {
  stopOrdering?: boolean;
  locationId: string;
  parLevel?: number | null;
  binLabel?: string | null;
}

/** A location the person may plan for (GET .../planning-locations). */
export interface PlanningLocationDto {
  id: string;
  name: string;
  /** True for the location of this tablet. The server puts it first. */
  isThisTablet: boolean;
}

/** One item's par and bin at one location the person may plan for (GET .../liquor-products/:id/stock-settings). */
export interface LocationStockSettingsDto {
  stopOrdering?: boolean;
  locationId: string;
  locationName: string;
  isThisTablet: boolean;
  parLevel: string | null;
  binLabel: string | null;
}

export function getPlanningLocations(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ locations: PlanningLocationDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/planning-locations`);
}

export function getLiquorProductStockSettings(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ stockSettings: LocationStockSettingsDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}/stock-settings`,
  );
}

export interface CreateLiquorProductInput extends InventoryItemFieldsInput {
  locationId?: string;
  sellingRequestId?: string;
  sellingVersion?: number;
  ingredientChoices?: IngredientChoiceInput[];
  ingredientChoiceGroups?: IngredientChoiceGroupInput[];
  name: string;
  category: string;
  /** Amendment 2026-08-22 (twelfth). Omitted defaults server-side to
   * 'liquor'. */
  itemKind?: string;
  /** ITEM2-F7-S3: explicit per-product method, required after policy activation. */
  saleMechanism?: ProductSaleMechanism;
  /** Calculated per-ounce price when that selling method applies. */
  perOuncePriceCents?: number;
  pourOunceOptions?: number[];
  /** The whole-unit price when that selling method applies. */
  unitPriceCents?: number;
  shelfTier?: string;
  /** B1 (docs/backlog.md, owner ruling 2026-08-27). The manufacturer's
   * code (UPC/EAN). Optional; unique per tenant when present. DEC-047
   * retired the sibling supplierSku field. */
  barcode?: string;
  /** INV26 Slice 2 (DEC-207 item 3). Omitted defaults server-side to
   * false (on the menu). */
  hideOnMenu?: boolean;
}

export function createLiquorProduct(
  baseUrl: string,
  tenantId: string,
  input: CreateLiquorProductInput,
): Promise<ApiResult<{ liquorProduct: LiquorProductDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/liquor-products`, input);
}

export interface UpdateLiquorProductInput extends InventoryItemFieldsInput {
  locationId?: string;
  sellingRequestId?: string;
  sellingVersion?: number;
  sellingSettings?: ProductSellingWriteDto;
  ingredientChoices?: IngredientChoiceInput[];
  ingredientChoiceGroups?: IngredientChoiceGroupInput[];
  name?: string;
  category?: string;
  itemKind?: string;
  /** ITEM2-F7-S3: explicit per-product method, required after policy activation. */
  saleMechanism?: ProductSaleMechanism;
  calculatedPrice?: { targetMarkupPercent: number; previewToken: string };
  perOuncePriceCents?: number;
  pourOunceOptions?: number[];
  unitPriceCents?: number;
  shelfTier?: string;
  /** Review 9 correction (architect gate, Critical): mirrors
   * updateMenuDrink's own `active` field, now that
   * src/domain/catalog.ts's updateLiquorProduct accepts it (a
   * deactivated bottle used to be uneditable AND unreactivatable through
   * this route). */
  active?: boolean;
  /** B1. Same field as CreateLiquorProductInput.barcode. */
  barcode?: string;
  /** B6-F1. Bottles per case, 1 to 100000; `null` clears it, omitted leaves it. */
  caseSize?: number | null;
  /** B6-F1. A string sets the case barcode, `null` clears it, omitted leaves it. */
  caseBarcode?: string | null;
  /** B6-F1. True after the person confirmed replacing a different stored case barcode. */
  replaceCaseBarcode?: boolean;
  /** VOC1-F4-F2 (Section 2.2). REPLACES this product's whole modifier
   * attachment set when supplied, mirroring UpdateMenuDrinkInput's own
   * identical field (VOC1-F4 Section 2.2); omitted leaves the product's
   * existing attachments unchanged. Not echoed back on this route's own
   * response (`{liquorProduct}`, unchanged shape); read it back with a
   * fresh GET. */
  modifiers?: { value: string; required: boolean }[];
  addonGroups?: AddonGroupInput[];
  /** INV26 Slice 2 (DEC-207 item 3). Omitted leaves the item's current
   * menu visibility unchanged, the same COALESCE convention every other
   * optional field on this route uses. */
  hideOnMenu?: boolean;
}

export function updateLiquorProduct(
  baseUrl: string,
  tenantId: string,
  id: string,
  input: UpdateLiquorProductInput,
): Promise<ApiResult<{ liquorProduct: LiquorProductDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}`,
    input,
  );
}

export function confirmLiquorSize(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ liquorProduct: LiquorProductDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}/confirm-size`,
    {},
  );
}

export function deactivateLiquorProduct(
  baseUrl: string,
  tenantId: string,
  id: string,
  selling?: { locationId: string; sellingRequestId: string; sellingVersion: number },
): Promise<ApiResult<{ liquorProduct: LiquorProductDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}/deactivate`,
    selling ?? {},
  );
}

// ---------------------------------------------------------------------
// INV4: vendors (docs/backlog.md, owner ruling 2026-09-03; migrations
// 0075/0076; docs/build-briefs/INV4-vendor-management.md). Same shape as
// the liquor-products block above: full CRUD, retire (never delete) via
// a deactivate route.
// ---------------------------------------------------------------------

export interface VendorDto {
  id: string;
  tenant_id: string;
  name: string;
  account_reference: string | null;
  /** Whether a vendor-wide number is on file. A reader without inv.vendor.manage gets this and no number (lead ruling 2026-10-09). */
  has_account_reference?: boolean;
  notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  /** CONFIGURATION money (CLAUDE.md's money rule), integer cents. */
  minimum_order_cents: number | null;
  delivery_days: string[] | null;
  /** 24-hour "HH:MM". */
  order_cutoff_time: string | null;
  /** VEN2: the vendor's own ordering site, always stored with an
   * explicit http/https scheme. */
  order_website_url: string | null;
  order_method?: VendorOrderMethod | null;
  order_file_layout?: VendorOrderFileLayout | null;
  price_file_layout?: VendorPriceFileLayout | null;
  /** AUTOPAR1: days between the location's orders with this vendor, or null when no pace is set. */
  order_pace_days: number | null;
  /** AUTOPAR1: the usual order weekday ("monday"), or null for any day. */
  order_pace_weekday: string | null;
  /** AUTOPAR1: days from placing an order to its delivery. */
  lead_days: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export type VendorOrderMethod = "upload_file" | "email" | "website" | "phone";
export type VendorOrderFileLayout = "abc2go_item_code_qty";
export type VendorPriceFileLayout = "nc_abc";

export interface VendorFieldsInput {
  orderMethod?: VendorOrderMethod | null;
  orderFileLayout?: VendorOrderFileLayout | null;
  priceFileLayout?: VendorPriceFileLayout | null;
  accountReference?: string;
  notes?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  minimumOrderCents?: number;
  deliveryDays?: string[];
  orderCutoffTime?: string;
  orderWebsiteUrl?: string;
}

export interface CreateVendorInput extends VendorFieldsInput {
  name: string;
}

export interface UpdateVendorInput extends VendorFieldsInput {
  name?: string;
  active?: boolean;
}

export function listVendors(
  baseUrl: string,
  tenantId: string,
  params: { q?: string; activeOnly?: boolean } = {},
): Promise<
  // INVLOC1 Slice 5: `planningLocationCount` is how many locations the person may plan for (the vendor card offers "Account number by
  // location" only above one). Optional so an older server's answer still parses.
  ApiResult<{ vendors: VendorDto[]; planningLocationCount?: number } & Partial<ApiErrorBody>>
> {
  const query = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) {
    query.set("q", params.q);
  }
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export function getVendor(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ vendor: VendorDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(id)}`,
  );
}

export function createVendor(
  baseUrl: string,
  tenantId: string,
  input: CreateVendorInput,
): Promise<ApiResult<{ vendor: VendorDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/vendors`, input);
}

export function updateVendor(
  baseUrl: string,
  tenantId: string,
  id: string,
  input: UpdateVendorInput,
): Promise<ApiResult<{ vendor: VendorDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(id)}`,
    input,
  );
}

// PO10 (DEC-294; design/mockups/vendor-price-list-import*): the vendor price
// list import. A preview (multipart file, nothing saved but the check), then an
// apply of what the preview showed. The server computes every row; the screen
// draws them and sends back only which ones to leave out.
export type PriceListGroup = "change" | "no_change" | "problem" | "not_in_file" | "sku_fill";

/** DEC-451: one place in the file an item with no NC code might be. */
export interface PriceListSkuCandidateDto {
  ncCode: string;
  brandName: string | null;
  sizeLabel: string | null;
  priceCents: number | null;
  rowNumber: number;
  nameExact: boolean;
  sizeExact: boolean;
}
export type PriceListProblemCode =
  | "bad_price"
  | "price_too_high"
  | "duplicate_code_in_file"
  | "duplicate_offer_code"
  | "order_unit_not_bottle"
  | "size_mismatch";

export interface PriceListRowDto {
  group: PriceListGroup;
  offerId: string;
  productId: string;
  productName: string;
  ncCode: string;
  fileBrandName: string | null;
  fileSizeLabel: string | null;
  itemSizeLabel: string | null;
  sizeChecked: boolean;
  currentCostCents: number | null;
  newCostCents: number | null;
  filePriceCents: number | null;
  /** Signed tenths of a percent (251 is 25.1%); null for a first price. */
  changePercentTenths: number | null;
  firstPrice: boolean;
  held: boolean;
  problem: { code: PriceListProblemCode; message: string } | null;
  fileRowNumbers: number[];
  filePriceTexts: string[];
  lastUpdatedAt: string;
  /** Only on a sku_fill row (its offerId is "" when the item has no offer). */
  skuFill?: { defaultOn: boolean; candidates: PriceListSkuCandidateDto[] };
}

export interface PriceListNoCodeDto {
  productId: string;
  productName: string;
  reason: "no_offer" | "offer_has_no_code";
}

export interface PriceListIgnoredRowDto {
  ncCode: string;
  brandName: string | null;
  sizeLabel: string | null;
  newCostCents: number | null;
  rowNumber: number;
}

export interface PriceListSummaryDto {
  changes: number;
  up: number;
  down: number;
  firstPrice: number;
  held: number;
  ready: number;
  noChange: number;
  problems: number;
  notInFile: number;
  noCode: number;
  ignored: number;
  skuFill?: number;
  skuFillReady?: number;
}

export interface PriceListPreviewDto {
  previewToken: string;
  expiresAt: string;
  file: { name: string; sizeBytes: number; sha256: string; rowCount: number };
  columns: {
    codeColumn: string;
    priceColumn: string;
    sizeColumn: string | null;
    brandColumn: string | null;
    found: string[];
  };
  summary: PriceListSummaryDto;
  gate: {
    ok: boolean;
    codedOffers: number;
    foundOffers: number;
    minPercent: number;
    bootstrap?: true;
  };
  rows: PriceListRowDto[];
  noCode: PriceListNoCodeDto[];
}

export interface PriceListChangedSinceCheckDto {
  offerId: string;
  productName: string;
  ncCode: string;
  reason:
    | "price_changed"
    | "deactivated"
    | "code_changed"
    | "unit_changed"
    | "offer_added"
    | "code_added"
    | "code_taken";
  currentCostCents: number | null;
  newCostCents: number | null;
  previewedCostCents: number | null;
}

export interface PriceListApplyResultDto {
  importId: string;
  replayed: boolean;
  createdAt: string;
  vendorId: string;
  fileName: string;
  updated: number;
  skippedByUser: number;
  heldNotTaken: number;
  problems: number;
  changedSinceCheck: PriceListChangedSinceCheckDto[];
  noChange: number;
  skuFilled?: number;
  summary: PriceListSummaryDto;
}

export interface PriceListLastImportDto {
  id: string;
  createdAt: string;
  userDisplayName: string;
  fileName: string;
  updated: number;
}

async function postForm<T>(
  baseUrl: string,
  pathname: string,
  form: FormData,
): Promise<ApiResult<T>> {
  if (authToken === undefined) {
    return missingTokenResult<T>();
  }
  let response: Response;
  try {
    // No content-type header: the browser sets multipart/form-data with its boundary.
    response = await fetch(`${baseUrl}${pathname}`, {
      method: "POST",
      headers: { ...authHeaders() },
      body: form,
    });
  } catch (cause) {
    return networkErrorResult<T>(cause);
  }
  const parsed = (await readJsonBody(response)) as T;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

const priceListBase = (tenantId: string, vendorId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(vendorId)}/price-list`;

export function previewVendorPriceList(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  file: File,
  options: { codeColumn?: string; priceColumn?: string; locationId?: string } = {},
): Promise<ApiResult<PriceListPreviewDto & Partial<ApiErrorBody> & { found?: string[] }>> {
  const form = new FormData();
  if (options.locationId !== undefined) form.append("locationId", options.locationId);
  if (options.codeColumn !== undefined) form.append("codeColumn", options.codeColumn);
  if (options.priceColumn !== undefined) form.append("priceColumn", options.priceColumn);
  form.append("file", file, file.name);
  return postForm(baseUrl, `${priceListBase(tenantId, vendorId)}/preview`, form);
}

export function applyVendorPriceList(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  input: {
    locationId?: string;
    previewToken: string;
    idempotencyKey: string;
    excludedOfferIds: string[];
    includedHeldOfferIds: string[];
    skuFills?: Array<{ productId: string; ncCode: string }>;
  },
): Promise<ApiResult<PriceListApplyResultDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `${priceListBase(tenantId, vendorId)}/apply`, input);
}

export function listVendorPriceListIgnored(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  previewToken: string,
  cursor: number,
  limit: number,
): Promise<
  ApiResult<
    {
      rows: PriceListIgnoredRowDto[];
      total: number;
      nextCursor: number | null;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `${priceListBase(tenantId, vendorId)}/previews/${encodeURIComponent(previewToken)}/ignored?cursor=${cursor}&limit=${limit}`,
  );
}

export function getVendorPriceListChangesCsv(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  importId: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `${priceListBase(tenantId, vendorId)}/imports/${encodeURIComponent(importId)}/changes.csv`,
  );
}

export function getVendorLastPriceListImport(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
): Promise<ApiResult<{ import: PriceListLastImportDto | null } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `${priceListBase(tenantId, vendorId)}/last-import`);
}

export function deactivateVendor(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ vendor: VendorDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(id)}/deactivate`,
    {},
  );
}

// INV4: vendor item offers. One (vendor, liquor_product) purchasing
// relationship. See src/domain/vendors.ts's own module header for the
// full field write-up.
export interface VendorItemOfferDto {
  id: string;
  tenant_id: string;
  vendor_id: string;
  liquor_product_id: string;
  vendor_sku: string | null;
  order_unit: string | null;
  pack_size: string | null;
  units_per_case: number | null;
  /** CONFIGURATION money (CLAUDE.md's money rule): the cost of one
   * order_unit from this vendor. */
  cost_cents: number | null;
  preferred: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VendorItemOfferFieldsInput {
  vendorSku?: string;
  orderUnit?: string;
  packSize?: number;
  unitsPerCase?: number;
  costCents?: number;
  preferred?: boolean;
}

export interface CreateVendorItemOfferInput extends VendorItemOfferFieldsInput {
  locationId?: string;
  requestId?: string;
  vendorId: string;
  liquorProductId: string;
}

export interface VendorTermsSetupContextDto {
  state: "preparation" | "enforced";
  locations: Array<{ id: string; name: string; active: boolean }>;
}
export interface EnforceVendorTermsInput {
  requestId: string;
  reviewedLocationIds: readonly string[];
}
export interface EnforceVendorTermsResultDto {
  state: "enforced";
  version: number;
  requestId: string;
  replayed: boolean;
}
export function getVendorTermsSetupContext(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<VendorTermsSetupContextDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/location-terms-setup`,
  );
}
export function enforceVendorTermsSetup(
  baseUrl: string,
  tenantId: string,
  input: EnforceVendorTermsInput,
): Promise<ApiResult<EnforceVendorTermsResultDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/location-terms-setup/enforce`,
    input,
  );
}

/** F4 effective terms are distinct from the shared relationship and package. */
export interface EffectiveLocationVendorOfferTermsDto {
  locationId: string;
  offerId: string;
  vendorId: string;
  productId: string;
  localTermsId: string | null;
  localVersion: number | null;
  provenance: "current_legacy_offer" | "legacy_snapshot" | "configured" | "unconfigured";
  vendorSku: string | null;
  costCents: number | null;
  orderUnit: string | null;
  packSize: string | null;
  unitsPerCase: number | null;
  legacyUpdatedAt: string;
}
export interface LocationVendorOfferTermsContextDto {
  locationId: string;
  state: "preparation" | "enforced";
}
export interface SaveLocationVendorOfferTermsInput {
  locationId: string;
  requestId: string;
  expectedVersion: number | null;
  vendorSku: string | null;
  costCents: number | null;
}
export type SaveLocationVendorOfferTermsResultDto = {
  locationId: string;
  offerId: string;
  requestId: string;
  expectedVersion: number | null;
  replayed: boolean;
} & (
  | { outcome: "applied"; terms: EffectiveLocationVendorOfferTermsDto }
  | { outcome: "not_applied"; reason: "preparation" | "revision_changed" | "basis_changed" }
);
export function getLocationVendorOfferTerms(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  offerIds: string[],
): Promise<
  ApiResult<
    {
      context: LocationVendorOfferTermsContextDto;
      terms: EffectiveLocationVendorOfferTermsDto[];
    } & Partial<ApiErrorBody>
  >
> {
  const query = new URLSearchParams({ locationId, offerIds: offerIds.join(",") });
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/location-terms?${query}`,
  );
}
export function saveLocationVendorOfferTerms(
  baseUrl: string,
  tenantId: string,
  offerId: string,
  input: SaveLocationVendorOfferTermsInput,
): Promise<ApiResult<SaveLocationVendorOfferTermsResultDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/${encodeURIComponent(offerId)}/location-terms`,
    input,
  );
}

export interface UpdateVendorItemOfferInput extends VendorItemOfferFieldsInput {
  active?: boolean;
}

/** INVLOC1 Slice 5: one location's own preferred vendor offer for one item (GET .../vendor-item-offers/preferred-by-location). */
export interface PreferredOfferLocationDto {
  locationId: string;
  locationName: string;
  isThisTablet: boolean;
  /** The location's own choice, or null when the location uses the franchisee-wide default. */
  chosenOfferId: string | null;
  /** The offer the cart, suggested orders and receiving use at this location today. */
  resolvedOfferId: string | null;
  resolvedFrom: "location" | "default" | "none";
}

export interface PreferredOffersByLocationDto {
  defaultOfferId: string | null;
  preferredByLocation: PreferredOfferLocationDto[];
}

export function getPreferredOffersByLocation(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
): Promise<ApiResult<PreferredOffersByLocationDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/preferred-by-location?liquorProductId=${encodeURIComponent(liquorProductId)}`,
  );
}

export function putPreferredOffersByLocation(
  baseUrl: string,
  tenantId: string,
  body: {
    liquorProductId: string;
    entries: Array<{ locationId: string; vendorItemOfferId: string | null }>;
  },
): Promise<ApiResult<PreferredOffersByLocationDto & Partial<ApiErrorBody>>> {
  vendorOfferWriteCount += 1;
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/preferred-by-location`,
    body,
  );
}

/** INVLOC1 Slice 5: a vendor's account number at each location the person may plan for. */
export interface VendorLocationAccountDto {
  locationId: string;
  locationName: string;
  isThisTablet: boolean;
  /** Null for a reader without inv.vendor.manage, who sees "On file" instead. */
  accountReference: string | null;
  hasNumber: boolean;
}

export interface VendorLocationAccountsDto {
  vendorAccountReference: string | null;
  hasVendorAccountReference: boolean;
  accounts: VendorLocationAccountDto[];
}

export function getVendorLocationAccounts(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
): Promise<ApiResult<VendorLocationAccountsDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(vendorId)}/location-accounts`,
  );
}

export function putVendorLocationAccounts(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  entries: Array<{ locationId: string; accountReference: string | null }>,
): Promise<ApiResult<VendorLocationAccountsDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(vendorId)}/location-accounts`,
    { entries },
  );
}

export function listVendorItemOffers(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId?: string; vendorId?: string; activeOnly?: boolean } = {},
): Promise<ApiResult<{ vendorItemOffers: VendorItemOfferDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.liquorProductId !== undefined) {
    query.set("liquorProductId", params.liquorProductId);
  }
  if (params.vendorId !== undefined) {
    query.set("vendorId", params.vendorId);
  }
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

/** INVVENDOR1: counts vendor option writes (create, edit, deactivate, preferred change, location terms) so the Inventory list knows its Vendor column went stale. */
let vendorOfferWriteCount = 0;
export function vendorOfferWrites(): number {
  return vendorOfferWriteCount;
}

export function createVendorItemOffer(
  baseUrl: string,
  tenantId: string,
  input: CreateVendorItemOfferInput,
): Promise<ApiResult<{ vendorItemOffer: VendorItemOfferDto } & Partial<ApiErrorBody>>> {
  vendorOfferWriteCount += 1;
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers`, input);
}

/** BULKVENDOR1: one item the bulk Set vendor could not change, with the server's plain sentence. */
export interface BulkSetVendorFailureDto {
  liquorProductId: string;
  itemName: string;
  reason: string;
}

/** BULKVENDOR1: the answer of POST .../vendor-item-offers/bulk-set-vendor. */
export interface BulkSetVendorResultDto {
  vendorId: string;
  vendorName: string;
  locationId: string;
  locationName: string;
  changed: number;
  alreadySet: number;
  offersAdded: number;
  failed: BulkSetVendorFailureDto[];
}

/** BULKVENDOR1: set one vendor as the vendor of many items (at most 200) at one location. */
export function bulkSetItemVendor(
  baseUrl: string,
  tenantId: string,
  input: { vendorId: string; locationId?: string; liquorProductIds: string[] },
): Promise<ApiResult<BulkSetVendorResultDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/bulk-set-vendor`,
    input,
  );
}

/** RECIPETABLE1: one drink the bulk edit could not change, with the server's plain sentence. */
export interface BulkMenuDrinkFailureDto {
  menuDrinkId: string;
  drinkName: string;
  reason: string;
}

/** RECIPETABLE1: the answer of POST .../menu-drinks/bulk-edit. */
export interface BulkMenuDrinkResultDto {
  changed: number;
  alreadySet: number;
  failed: BulkMenuDrinkFailureDto[];
}

/** RECIPETABLE1: the one field a bulk edit changes, with its value. */
export type BulkMenuDrinkChangeInput =
  | { field: "category"; category: string }
  | { field: "active"; active: boolean }
  | { field: "requiresLiquor"; requiresLiquorSelection: false }
  | { field: "requiresLiquor"; requiresLiquorSelection: true; liquorSlots: string[] }
  | { field: "buildCharge"; buildChargeCents: number };

/** RECIPETABLE1: change one field of many drinks (at most 200) with the drink editor's own write. */
export function bulkEditMenuDrinks(
  baseUrl: string,
  tenantId: string,
  input: BulkMenuDrinkChangeInput & { menuDrinkIds: string[] },
): Promise<ApiResult<BulkMenuDrinkResultDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/bulk-edit`, input);
}

/** BULKEDIT1: the answer of POST .../liquor-products/bulk-edit. */
export interface BulkEditResultDto {
  field: "shelfTier" | "active" | "stock86";
  changed: number;
  alreadySet: number;
  failed: BulkSetVendorFailureDto[];
}

/** BULKEDIT1: set one shelf tier, one active state or one 86 state on many items (at most 200). */
export function bulkEditItems(
  baseUrl: string,
  tenantId: string,
  input:
    | { field: "shelfTier"; shelfTier: string; locationId?: string; liquorProductIds: string[] }
    | { field: "active"; active: boolean; locationId?: string; liquorProductIds: string[] }
    | { field: "stock86"; flagged: boolean; locationId?: string; liquorProductIds: string[] },
): Promise<ApiResult<BulkEditResultDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/bulk-edit`,
    input,
  );
}

export function updateVendorItemOffer(
  baseUrl: string,
  tenantId: string,
  id: string,
  input: UpdateVendorItemOfferInput,
): Promise<ApiResult<{ vendorItemOffer: VendorItemOfferDto } & Partial<ApiErrorBody>>> {
  vendorOfferWriteCount += 1;
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/${encodeURIComponent(id)}`,
    input,
  );
}

export function deactivateVendorItemOffer(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ vendorItemOffer: VendorItemOfferDto } & Partial<ApiErrorBody>>> {
  vendorOfferWriteCount += 1;
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendor-item-offers/${encodeURIComponent(id)}/deactivate`,
    {},
  );
}

// PO6 Slice 1 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md):
// read-only order list and detail. Camel-cased, unlike VendorDto above,
// because src/domain/purchasing.ts's listPurchaseOrders/getPurchaseOrder
// build and return camelCase objects directly (see toOrderSummary), so
// this is the shape the JSON body actually carries, not a reshaping this
// file invents.
export interface PurchaseOrderSummaryDto {
  id: string;
  vendorId: string;
  vendorName: string;
  status: string;
  /** An ESTIMATE derived server-side from the placed lines' snapshotted
   * offer costs; never recomputed here. See ui/src/purchasingDisplay.ts's
   * formatOrderEstimatedTotal, the one place this is turned into a
   * dollar string, straight from this field. */
  estimatedTotalCents: number;
  lineCount: number;
  placedByEmployeeId: string;
  /** PO6-S1-F2: a courtesy label resolved server-side (src/domain/
   * purchasing.ts's toOrderSummary), never recomputed here. Null when the
   * id does not resolve to a users row for this tenant; the UI shows
   * "Former employee" in that case, never the raw id. */
  placedByDisplayName: string | null;
  approvedByEmployeeId: string | null;
  /** Same shape as placedByDisplayName; null whenever approvedByEmployeeId
   * itself is null (no approver at all), or when it does not resolve. */
  approvedByDisplayName: string | null;
  placedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
}

/** One purchase_order_lines row, projected only by getPurchaseOrder
 * (src/domain/purchasing.ts's PurchaseOrderLine, PO6-S2-F1). The list
 * route's own PurchaseOrderSummaryDto above carries no lines; only the
 * detail route does. */
export interface PurchaseOrderLineDto {
  id: string;
  liquorProductId: string;
  productName: string;
  orderedQuantity: string;
  /** PO3. The vendor's code for this item, captured at placement; null
   * where the offer carried none. See src/domain/purchasing.ts's
   * PurchaseOrderLine.vendorSkuAtPlacement. */
  vendorSkuAtPlacement: string | null;
  /** TF4-F3: the order unit the quantity is in, and the bottles it holds. */
  orderUnit: string | null;
  bottlesPerOrderUnit: string | null;
  bottleCount: number | null;
  unitCostCentsAtPlacement: number | null;
  extendedCostCents: number | null;
}

/** getPurchaseOrder's own return shape: the same summary fields
 * PurchaseOrderSummaryDto has, plus this order's lines. */
export interface PurchaseOrderDetailDto extends PurchaseOrderSummaryDto {
  cancellationReasons?: readonly string[];
  approvalBlockers?: string[];
  lines: PurchaseOrderLineDto[];
}

/** One row of a purchase order's status_changes ledger. `variance` is
 * present only on the row that entered or resolved `exception`
 * (src/domain/purchasing.ts's own PurchaseOrderStatusChange comment); its
 * shape there is an array of the order's per-line receiving state at that
 * moment (src/domain/receiving.ts's LineReceivingState), which is the one
 * place this route's response carries a per-line ordered quantity. */
export interface PurchaseOrderStatusChangeDto {
  fromStatus: string | null;
  toStatus: string;
  actorEmployeeId: string;
  reason: string | null;
  createdAt: string;
  exceptionReason: string | null;
  resolution: string | null;
  variance: unknown;
}

export function listPurchaseOrders(
  baseUrl: string,
  tenantId: string,
  params: { status?: string } = {},
): Promise<ApiResult<{ orders: PurchaseOrderSummaryDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.status !== undefined) {
    query.set("status", params.status);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export function getPurchaseOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<
  ApiResult<
    {
      order: PurchaseOrderDetailDto;
      statusHistory: PurchaseOrderStatusChangeDto[];
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}`,
  );
}

/** PO6 Slice 3 (docs/build-briefs/PO6-S3-approval.md). POAPPROVE1 (DEC-459): a person whose own purchasing.order.approve
 * grant is allowed sends only the idempotency key. Otherwise carries a
 * SECOND person's own credentials (approverUserId/approverPin): unlike
 * the approval_required routes above, this route requires them on every
 * call, never only on a retry, because approving IS the second person's
 * own act (src/api/server.ts's own POST .../approve, which calls
 * requireSecondPersonApproval directly rather than waiting for
 * requirePermission's built-in approval_required branch). Returns the
 * order alone (never statusHistory), so a caller that also needs the
 * refreshed status ledger re-reads getPurchaseOrder, exactly as
 * ui/src/main.ts's own handleSubmitPurchasingOrderApproveClick does. */
export function approvePurchaseOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string } & ApprovalRetryParams,
): Promise<ApiResult<{ order: PurchaseOrderDetailDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/approve`,
    params,
  );
}

// PO6 Slice 2 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S2-cart-and-placement.md): the cart, draft
// proposals, and placement. Every DTO below is the camelCase shape
// src/domain/purchasing.ts's own CartLineView/DraftProposal/PlacedOrder
// return directly (see toView/generateDraftProposals/placeCart), not a
// reshaping this file invents.

/** One of the reasons a cart line cannot be placed yet
 * (src/domain/purchasing.ts's CartLineBlocker). A line can carry more
 * than one at once. */
export type CartLineBlockerDto =
  "missing_vendor" | "vendor_inactive" | "missing_contact" | "missing_package" | "missing_cost";

export interface CartLineDto {
  liquorProductId: string;
  productName: string;
  quantity: string;
  vendorId: string | null;
  vendorName: string | null;
  vendorItemOfferId: string | null;
  vendorSku: string | null;
  orderUnit: string | null;
  packSize: string | null;
  unitsPerCase: number | null;
  /** TF4-F3: bottles in one order unit, and this line's whole bottle count. */
  bottlesPerOrderUnit?: string | null;
  bottleCount?: number | null;
  /** Straight from the preferred vendor offer's own cost_cents column;
   * never typed and never combined with quantity into a total by this
   * file (ui/src/purchasingDisplay.ts's own module header states the
   * invariant, matching PO6-S1's estimatedTotalCents rule). */
  unitCostCents: number | null;
  /** PO6-S2-F2: quantity x unitCostCents for this line, computed by the
   * server (src/domain/purchasing.ts lineEstimateCentsOf). Null when the
   * offer has no price. This file never multiplies. */
  lineEstimateCents: number | null;
  blockers: CartLineBlockerDto[];
}

/** PO8: src/domain/purchasing.ts's CartVendorSchedule, computed on the
 * server in the tenant's zone. */
export interface CartVendorScheduleDto {
  kind: "open" | "past_cutoff" | "days_only" | "cutoff_only" | "past_cutoff_only" | "none";
  orderByDay: string | null;
  orderByTime: string | null;
  deliveryDay: string | null;
  deliveryDays: string[];
}

/** PO6-S2-F2 and PO8: src/domain/purchasing.ts's CartVendorSummary. */
export interface CartVendorSummaryDto {
  vendorId: string | null;
  vendorName: string | null;
  estimatedTotalCents: number;
  pricedLineCount: number;
  unpricedLineCount: number;
  totalIsComplete: boolean;
  minimumOrderCents: number | null;
  minimumStatus: "none" | "met" | "under" | "maybe_under";
  shortfallCents: number | null;
  minimumFillPercent: number | null;
  schedule: CartVendorScheduleDto;
}

export interface CartEstimateDto {
  totalCents: number;
  isComplete: boolean;
  unpricedLineCount: number;
  vendorCount: number;
  lineCount: number;
  underMinimumVendorCount: number;
  maybeUnderMinimumVendorCount: number;
}

/** What GET and PUT on the cart, and DELETE on a cart line, return. */
export interface CartDto {
  cartId: string;
  locationId: string;
  lines: CartLineDto[];
  vendorSummaries: CartVendorSummaryDto[];
  cartEstimate: CartEstimateDto;
}

export function getCart(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ cartLines: CartDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/cart`);
}

export function setCartLine(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId: string; quantity: string },
): Promise<ApiResult<{ cartLines: CartDto } & Partial<ApiErrorBody>>> {
  return putJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/cart/lines`, params);
}

export function removeCartLine(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
): Promise<ApiResult<{ cartLines: CartDto } & Partial<ApiErrorBody>>> {
  return deleteJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/cart/lines/${encodeURIComponent(liquorProductId)}`,
  );
}

/** Criterion 4's three derivable sources (src/domain/purchasing.ts's
 * ProposalSource); search needs no proposal, a person just picks the
 * product via getCart/setCartLine above. */
export type ProposalSourceDto = "below_par" | "low_stock" | "out_of_stock";

export interface DraftProposalDto {
  liquorProductId: string;
  productName: string;
  onHand: string;
  parLevel: string | null;
  sources: ProposalSourceDto[];
  suggestedQuantity: string | null;
  /** INV8 (DEC-176), Section 2.5: par compared in the item's own count
   * unit ("unit", "keg", "bottle", "can"), or null when unresolvable
   * (no container config, or an unrecognized sale mechanism/container
   * type). Optional on this DTO rather than required: a pre-INV8 fixture
   * that never set it stays a valid DraftProposalDto (see
   * test/purchasing-display.test.ts's own `proposal()` fixture, which
   * this brief's frontend slice does not touch); every real response from
   * GET .../purchasing/proposals carries it. */
  countUnit?: string | null;
  /** On-hand converted into countUnit; null exactly when countUnit is
   * null. The comparable figure a below_par-sourced row should show as
   * "On hand", never the raw onHand field above. Same optionality
   * reasoning as countUnit. */
  onHandInCountUnit?: string | null;
  /** PO9 (TF4-F3). The shortfall converted by the server into the preferred
   * offer's ORDER unit, rounded up to whole order units; this, never
   * suggestedQuantity (bottles), is what a cart line stages. Null when there
   * is no preferred offer or it cannot be converted. Optional for the same
   * pre-existing-fixture reason as countUnit. */
  order?: ProposalOrderDto | null;
  /** Why order is null when a preferred offer exists but cannot be converted. */
  orderBlockedReason?: string | null;
}

export interface ProposalOrderDto {
  vendorItemOfferId: string;
  orderUnit: string;
  bottlesPerOrderUnit: string;
  orderQuantity: string;
  bottlesCovered: number;
  /** LOCORDER1: the preferred offer's vendor and its cost per order unit at this location. Optional like countUnit above. */
  vendorName?: string | null;
  unitCostCents?: number | null;
}

/** READS ONLY (src/domain/purchasing.ts's own generateDraftProposals
 * comment): cannot create an order or even fill the cart by itself. */
export function getDraftProposals(
  baseUrl: string,
  tenantId: string,
  params: { sources?: ProposalSourceDto[] } = {},
): Promise<ApiResult<{ proposals: DraftProposalDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.sources !== undefined && params.sources.length > 0) {
    query.set("sources", params.sources.join(","));
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/proposals${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

/** One order placeCart actually created, straight from its own response
 * (src/domain/purchasing.ts's PlacedOrder). estimatedTotalCents here is
 * the only total the cart screen ever shows for a just-placed order: it
 * comes from this response, never computed from the lines that produced
 * it. */
export interface PlacedOrderDto {
  approverNames?: readonly string[];
  id: string;
  vendorId: string;
  vendorName: string;
  lineCount: number;
  status: string;
  estimatedTotalCents: number;
}

/** Places the cart. With `vendorId`, places only that vendor's ready
 * lines and leaves the rest of the cart intact (criterion 7: a vendor
 * order can be saved for later); without it, every ready vendor in the
 * cart places as its own order (criterion 6). Refuses with 400
 * (CartNotReadyError) before writing anything if any candidate line is
 * blocked (criterion 8). */
export function placeCart(
  baseUrl: string,
  tenantId: string,
  params: { idempotencyKey: string; vendorId?: string },
): Promise<ApiResult<{ orders: PlacedOrderDto[] } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/cart/place`,
    params,
  );
}

// PO6 Slice 4 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S4-receiving.md). The receiving screen: what one
// order's own lines still owe (GET .../receiving,
// src/domain/receiving.ts's getOrderReceivingView/LineReceivingState) and
// recording a receipt against a line (the existing POST
// .../inventory/receipts route, extended by PO4-F1 with an optional
// purchaseOrderLineId). No new route: both already exist and are read
// directly here, camelCase because that is the shape their own JSON
// bodies already carry (see receiving.ts's own toLineState/
// getOrderReceivingView).

/** One purchase_order_lines row's own receiving state: what was ordered,
 * what has been received net of any correction of that receipt (Slice 3b
 * widened the underlying query to net corrections in; not this file's
 * concern, just what it returns), and what remains. All three quantities
 * are the database's own NUMERIC(12,4) strings, never re-derived here. */
export interface LineReceivingStateDto {
  purchaseOrderLineId: string;
  liquorProductId: string;
  productName: string;
  orderedQuantity: string;
  receivedQuantity: string;
  outstandingQuantity: string;
  receiptCount: number;
  /** PO7-S4-F3: the line's order unit ("case") and the bottles in one, as
   * placed. The three quantities above are in this unit. */
  orderUnit?: string | null;
  bottlesPerOrderUnit?: string | null;
}

/** PO13: one receipt that delivered a different product than the line ordered. */
export interface OrderSubstitutionDto {
  purchaseOrderLineId: string;
  receiptId: string;
  orderedProductId: string;
  orderedProductName: string;
  deliveredProductId: string;
  deliveredProductName: string;
  quantity: string;
  unit: string;
  receivedOrderUnitQuantity: string | null;
}

/** getOrderReceivingView's own return shape. `latestExceptionReason`/
 * `latestResolution` are null unless this order has ever entered Slice
 * 3b's `exception` state; this screen shows them read-only and does not
 * act on them (resolving an exception is Slice 5's own scope). */
export interface OrderReceivingViewDto {
  /** UX105: the order's own state, read in the same call as its lines. */
  status: string;
  lines: LineReceivingStateDto[];
  /** PO13: receipts on this order that delivered a different product than the line ordered. */
  substitutions: OrderSubstitutionDto[];
  fullyReceived: boolean;
  latestExceptionReason: string | null;
  latestResolution: string | null;
}

export function getOrderReceivingView(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<OrderReceivingViewDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/receiving`,
  );
}

/** The subset of `inventory_receipts` this screen actually reads back off
 * a successful POST, for its own confirmation toast. The route returns
 * the full row (see src/domain/inventory.ts's InventoryReceiptRow); only
 * `id`, `quantity`, and `unit` are typed here because nothing else this
 * screen does reads the rest, the same "type what is actually read"
 * shape LiquorProductOnHandDto and other partial DTOs in this file
 * already use. */
export interface RecordedInventoryReceiptDto {
  id: string;
  quantity: string;
  unit: string;
}

/** Records a receipt. `purchaseOrderLineId`, supplied here, is what makes
 * this the SAME write PO4-F1 already built and tested: the domain fits
 * the quantity against that line's own outstanding remainder
 * (src/domain/receiving.ts's assertReceiptFits) and refuses with 400
 * (ValidationError) rather than posting anything if it does not fit, and
 * settles the order's own status from its receipts afterward. `source`
 * is omitted, defaulting to "manual": a delivery entered by a person
 * standing at it, never "imported" or "outside_system". `unitCostCents`
 * is the one other optional field this screen supplies (ADR-003 Decision
 * 3: received cost is configuration-shaped, entered once per receipt
 * line against an invoice, and stays typed). */
export function recordStockReceipt(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    /** The raw quantity/unit path. Mutually exclusive with
     * receivedContainerCount below; the domain itself is what enforces
     * that (src/domain/inventory.ts, INV8-F1 Section 2), never this
     * client. */
    quantity?: string;
    unit?: string;
    /** INV8-F1 Section 2: the product's own configured container (bottle
     * or can), submitted alone rather than with quantity/unit. Resolved
     * server-side through the product's own configured size. */
    receivedContainerCount?: string;
    /** PO7-S4-F3: the delivery stated in the line's own order unit ("2" for
     * 2 cases); the server posts the bottles. Excludes quantity and unit. */
    receivedOrderUnits?: string;
    /** CASECOST1: the cost of one order unit as invoiced, whole cents. */
    costPerOrderUnitCents?: number;
    /** Absent on a delivery with no order line (B6: an item scanned that is not on the order). */
    purchaseOrderLineId?: string;
    /** B6: the vendor of a delivery with no order line (the order's own vendor), and a note naming the order. */
    vendorId?: string;
    note?: string;
    /** PO13: the ordered product, when `liquorProductId` is a different product that arrived in its place. */
    substitutedForLiquorProductId?: string;
    unitCostCents?: number;
    /** INV8-F1 Section 3: cents per container, mutually exclusive with
     * unitCostCents; converted server-side to cents per ounce before the
     * write. */
    costPerContainerCents?: number;
  },
): Promise<
  ApiResult<{ receipt: RecordedInventoryReceiptDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts`, params);
}

// PO6 Slice 6 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S6-outside-system-orders.md). Recording an order
// placed outside this product: the SAME POST .../inventory/receipts route
// above, no new route, called with source "outside_system" and
// deliberately no purchaseOrderLineId (this delivery has no purchase
// order behind it at all -- src/domain/inventory.ts's own doc comment on
// that field: "stock arriving with no purchase order behind it is not an
// error, it is a delivery nobody raised a PO for").

/** The subset of the receipt this screen's own "Recorded this visit" list
 * reads back, the same "type what is actually read" convention
 * RecordedInventoryReceiptDto follows; source_reference is included here
 * (unlike that type) because this screen shows it, that one does not. */
export interface RecordedOutsideSystemReceiptDto {
  id: string;
  quantity: string;
  unit: string;
  source_reference: string | null;
  /** INV35-B1-F1 (DEC-300). The vendor picked on this delivery, or null. */
  vendor_id: string | null;
}

export function recordOutsideSystemReceipt(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    // See recordStockReceipt above for why quantity/unit and
    // receivedContainerCount, and unitCostCents and costPerContainerCents,
    // are each a mutually exclusive pair the domain enforces (INV8-F1
    // Sections 2 and 3), never this client.
    quantity?: string;
    unit?: string;
    receivedContainerCount?: string;
    unitCostCents?: number;
    costPerContainerCents?: number;
    sourceReference?: string;
    /** INV35-B1-F1 (DEC-300). A vendor id picked from the sheet, never a
     * name; omitted when no vendor was picked. */
    vendorId?: string;
  },
): Promise<
  ApiResult<{ receipt: RecordedOutsideSystemReceiptDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts`, {
    ...params,
    source: "outside_system",
  });
}

// INV35-B1-F1 (docs/build-briefs/INV35-B1-F1-delivery-vendor-picker.md; DEC-300). What the
// "Pick a vendor" sheet reads: GET .../inventory/receipt-vendors, gated on inv.receipt.record
// alone (src/domain/receiptVendors.ts), so a person who can record a delivery can pick a vendor
// without holding inv.vendor.view.

export interface ReceiptVendorOptionDto {
  id: string;
  name: string;
  /** False when the vendor is turned off in Settings. */
  active: boolean;
}

export interface ReceiptVendorListDto {
  /** Every vendor of the tenant, A to Z, turned-off ones flagged. */
  vendors: ReceiptVendorOptionDto[];
  /** Up to three vendor ids most recently picked on a delivery, newest first, active only. */
  usedLately: string[];
  /** The vendor of the product's preferred offer, or null. Never preselected. */
  usualVendorId: string | null;
}

export function listReceiptVendors(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId?: string } = {},
): Promise<ApiResult<ReceiptVendorListDto & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.liquorProductId !== undefined) {
    query.set("liquorProductId", params.liquorProductId);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/receipt-vendors${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

// B6 (docs/build-briefs/B6-barcode-receive.md). Scan a barcode to receive. Three routes, all on inv.receipt.record: what a
// scanned code belongs to, the items the link sheet lists, and saving a code to an item.

/** How a scanned bottle is posted: as containers (receivedContainerCount), as units, or as plain bottles. Decided by the server. */
export type BarcodeReceiveAs = "container" | "unit" | "bottle";

export interface BarcodeMatchDto {
  liquorProductId: string;
  name: string;
  itemKind: string;
  /** False when the item is turned off in Settings. */
  active: boolean;
  matchedBy: "bottle" | "case";
  receiveAs: BarcodeReceiveAs;
  /** Bottles in a full case, or null when none is set. */
  caseSize: number | null;
}

export function lookupBarcode(
  baseUrl: string,
  tenantId: string,
  code: string,
): Promise<ApiResult<{ match: BarcodeMatchDto | null } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/barcode-scan/lookup?code=${encodeURIComponent(code)}`,
  );
}

export interface BarcodeItemDto {
  id: string;
  name: string;
  itemKind: string;
  barcode: string | null;
  caseBarcode: string | null;
  caseSize: number | null;
  receiveAs: BarcodeReceiveAs;
}

export function listBarcodeItems(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ items: BarcodeItemDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/barcode-scan/items`);
}

export function linkBarcode(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId: string; code: string; kind: "bottle" | "case"; replace?: boolean },
): Promise<
  ApiResult<
    {
      link: {
        liquorProductId: string;
        kind: "bottle" | "case";
        code: string;
        replaced: boolean;
        unchanged: boolean;
        caseSize: number | null;
      };
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/barcode-scan/link`,
    params,
  );
}

// PO6 Slice 5 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md,
// Decision 4 slice 5; docs/build-briefs/PO6-S5-corrections-and-
// exceptions.md). Correcting a posted receipt (POST/GET .../inventory/
// receipts/:receiptId/corrections) and entering/resolving a purchase
// order's own `exception` state (POST .../purchasing/orders/:orderId/
// transition, PO4 Slice 3b). Every route below already exists; nothing
// here is new business behavior.

/** PO4 Slice 3b's own fixed vocabulary
 * (src/domain/purchasing.ts's PURCHASE_EXCEPTION_REASONS), duplicated
 * here the SAME way CartLineBlockerDto above duplicates
 * src/domain/purchasing.ts's CartLineBlocker: a TypeScript `as const`
 * array, not a database table, so there is no GET route this file could
 * read it from instead. */
export const PURCHASE_EXCEPTION_REASONS = [
  "short_delivery",
  "over_delivery",
  "damaged",
  "wrong_item",
  "cost_mismatch",
  "vendor_cancelled",
] as const;
export type PurchaseExceptionReasonDto = (typeof PURCHASE_EXCEPTION_REASONS)[number];

/** PO4 Slice 3b's own PURCHASE_EXCEPTION_RESOLUTIONS, same duplication
 * shape as PURCHASE_EXCEPTION_REASONS above. */
export const PURCHASE_EXCEPTION_RESOLUTIONS = [
  "delivery_completed",
  "accepted_short",
  "awaiting_delivery",
] as const;
export type PurchaseExceptionResolutionDto = (typeof PURCHASE_EXCEPTION_RESOLUTIONS)[number];

/** POST .../purchasing/orders/:orderId/transition. This file calls it
 * only for Slice 3b's own two commands: entering `exception` (supply
 * `toStatus: "exception"` and `exceptionReason`) and resolving it
 * (supply `resolution` and the `toStatus`
 * ui/src/purchasingDisplay.ts's computeExceptionResolutionTarget
 * computed -- a mismatched toStatus is refused with 400, never silently
 * corrected). Every other transition this route accepts is out of this
 * slice's scope and this file never calls it for one. */
export function transitionPurchaseOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    toStatus: string;
    exceptionReason?: PurchaseExceptionReasonDto;
    resolution?: PurchaseExceptionResolutionDto;
    reason?: string;
  },
): Promise<ApiResult<{ order: PurchaseOrderDetailDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/transition`,
    params,
  );
}

/** The full `inventory_receipts` row (src/domain/inventory.ts's
 * InventoryReceiptRow), snake_case because GET .../inventory/receipts
 * returns the raw database row verbatim, not a reshaping this file
 * invents -- confirmed against test/receiving-api.test.ts's own
 * `plain.body.receipt.purchase_order_line_id` assertion, not assumed.
 * Only the fields this screen actually reads are typed here, the same
 * "type what is read" convention RecordedInventoryReceiptDto above
 * already uses. */
export interface InventoryReceiptDto {
  id: string;
  liquor_product_id: string;
  purchase_order_line_id: string | null;
  quantity: string;
  unit: string;
  unit_cost_cents: number | null;
  created_at: string;
  received_at: string;
  // PO6 Slice 6 (docs/build-briefs/PO6-S6-outside-system-orders.md): the
  // two fields that tell an outside-system receipt apart from every other
  // source, added because this slice's own e2e coverage reads them back
  // to prove the write it made, not assumed to already be here.
  source: string;
  source_reference: string | null;
  // INV8-F1 Section 2/3/4a: non-null exactly on a container-based receipt
  // (keg, bottle, or can). received_container_count is what this screen
  // reads to decide whether a "Correct" control is offered at all
  // (Section 4a: a container receipt cannot be corrected yet).
  received_container_type: string | null;
  received_container_count: string | null;
  received_cost_per_container_cents: number | null;
}

/** No route returns receipts scoped to an order or an order line
 * (docs/build-briefs/PO6-S5-corrections-and-exceptions.md's own
 * "Findings" section); this screen filters the product-scoped read
 * below by each line's own purchaseOrderLineId, which it already has
 * from getPurchaseOrder/getOrderReceivingView, rather than a new route. */
export function listReceipts(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId?: string } = {},
): Promise<ApiResult<{ receipts: InventoryReceiptDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.liquorProductId !== undefined) {
    query.set("liquorProductId", params.liquorProductId);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

/** The correction fact itself (src/domain/receiptCorrections.ts's
 * InventoryReceiptCorrectionRow), same raw-row convention as
 * InventoryReceiptDto above. */
export interface InventoryReceiptCorrectionDto {
  id: string;
  inventory_receipt_id: string;
  quantity_delta: string;
  /** INV8-F1-F1: the signed delta in the ORIGINAL receipt's own
   * container unit (bottles, cans or kegs), non-null only for a
   * correction of a receipt recorded through the container-count path.
   * quantity_delta above still carries the resolved ounce-equivalent
   * delta unconditionally. */
  container_count_delta: string | null;
  corrected_unit_cost_cents: number | null;
  reason: string;
  actor_employee_id: string;
  created_at: string;
}

export function listReceiptCorrections(
  baseUrl: string,
  tenantId: string,
  receiptId: string,
): Promise<ApiResult<{ corrections: InventoryReceiptCorrectionDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts/${encodeURIComponent(receiptId)}/corrections`,
  );
}

/** Corrects a posted receipt by SELECTING it (the caller already has its
 * id from listReceipts above) and stating what it should have been,
 * never a typed delta and never a typed total (CLAUDE.md's money rule;
 * ADR-003 Decision 3). `quantityDelta` here is the SIGNED delta the
 * route itself accepts (src/domain/receiptCorrections.ts's own
 * parseQuantity), computed by ui/src/purchasingDisplay.ts's
 * computeReceiptCorrectionQuantityDelta from the original receipt's own
 * quantity and what a person entered as the corrected quantity -- this
 * function never receives a value a person typed AS a delta.
 * `correctedUnitCostCents`, when supplied, is the corrected price
 * ITSELF (configuration-shaped, ADR-003 Decision 3), not a delta either.
 * INV8-F1-F1: `containerCountDelta` is the same shape as `quantityDelta`
 * but in the original receipt's own container unit (bottles, cans or
 * kegs), computed by the identical computeReceiptCorrectionQuantityDelta
 * helper; exactly one of the two applies, depending on whether the
 * target receipt was entered through the container-count path, and the
 * route/domain layer decides which and refuses the other. inv.receipt.correct
 * is approval_required for an Assistant Manager
 * (src/domain/permissions.ts, read directly), so this accepts the same
 * ApprovalRetryParams every other approval_required route in this file
 * does: omitted on the first attempt, supplied only on a retry after a
 * 403 approval_required. */
export function correctReceipt(
  baseUrl: string,
  tenantId: string,
  receiptId: string,
  params: {
    idempotencyKey: string;
    quantityDelta?: string;
    containerCountDelta?: string;
    correctedUnitCostCents?: number;
    reason: string;
  } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      correction: InventoryReceiptCorrectionDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts/${encodeURIComponent(receiptId)}/corrections`,
    params,
  );
}

export interface CreateMenuDrinkComponentInput {
  componentReference: string;
  quantity: number;
  unit: string;
  isLiquorSlot?: boolean;
  /** CORE14. The location's own inventory item this ingredient is. */
  liquorProductId?: string;
}

export interface CreateMenuDrinkInput {
  ingredientChoices?: IngredientChoiceInput[];
  ingredientChoiceGroups?: IngredientChoiceGroupInput[];
  name: string;
  category: string;
  buildChargeCents: number;
  requiresLiquorSelection: boolean;
  liquorSlots?: string[];
  components?: CreateMenuDrinkComponentInput[];
}

export function createMenuDrink(
  baseUrl: string,
  tenantId: string,
  input: CreateMenuDrinkInput,
): Promise<
  ApiResult<
    {
      menuDrink: MenuDrinkDto;
      components: MenuDrinkComponentDto[];
      liquorSlots: string[];
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/menu-drinks`, input);
}

export interface UpdateMenuDrinkInput {
  ingredientChoices?: IngredientChoiceInput[];
  ingredientChoiceGroups?: IngredientChoiceGroupInput[];
  name?: string;
  category?: string;
  requiresLiquorSelection?: boolean;
  liquorSlots?: string[];
  buildChargeCents?: number;
  active?: boolean;
  /** VOC1-F4 (Section 2.2). REPLACES the drink's entire modifier
   * attachment set when supplied, mirroring liquorSlots' own
   * replace-not-merge contract; omitted leaves the existing set unchanged. */
  modifiers?: { value: string; required: boolean }[];
  addonGroups?: AddonGroupInput[];
  /** CORE14. REPLACES the drink's whole recipe when supplied; omitted leaves it unchanged. */
  components?: CreateMenuDrinkComponentInput[];
}

// Full-field PATCH, distinct from updateMenuDrinkActive above (which the
// seasonal-toggle list still uses for its one-field case): the manager
// console's drink editor needs every field, not only active.
export function updateMenuDrink(
  baseUrl: string,
  tenantId: string,
  id: string,
  input: UpdateMenuDrinkInput,
): Promise<ApiResult<{ menuDrink: MenuDrinkDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/${encodeURIComponent(id)}`,
    input,
  );
}

export function deactivateMenuDrink(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ menuDrink: MenuDrinkDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/${encodeURIComponent(id)}/deactivate`,
    {},
  );
}

// Keep retired state field names internal until their callers are migrated binding by binding.
// Only canonical ingredient IDs cross the HTTP boundary; client-supplied adjustments never do.
function ingredientSelectionRequest<
  T extends { modifierSelections?: readonly string[]; modifiers?: unknown },
>(
  input: T,
): Omit<T, "modifierSelections" | "modifiers"> & { ingredientSelections?: readonly string[] } {
  const { modifierSelections, modifiers, ...rest } = input;
  void modifiers;
  return {
    ...rest,
    ...(modifierSelections === undefined ? {} : { ingredientSelections: modifierSelections }),
  };
}
export async function priceQuote(
  baseUrl: string,
  tenantId: string,
  lines: PriceQuoteLineInput[],
): Promise<ApiResult<PriceQuoteResponse & Partial<ApiErrorBody>>> {
  const result = await postJson<PriceQuoteResponse & Partial<ApiErrorBody>>(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/price-quote`,
    { lines: lines.map(ingredientSelectionRequest) },
  );
  if (result.status === 200)
    for (const line of result.body.lines)
      if (line.priceable) {
        const wire = line as typeof line & {
          resolvedIngredientChoices?: ResolvedModifierDetailDto[];
        };
        line.resolvedModifiers = wire.resolvedIngredientChoices;
      }
  return result;
}

export function createOrder(
  baseUrl: string,
  tenantId: string,
  params: { idempotencyKey: string; businessDate?: string; name?: string },
): Promise<ApiResult<{ order: OrderDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/orders`, params);
}

// ALPHA-07: Open Tabs landing screen. See src/domain/tabs.ts.

export function listOpenTabs(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ tabs: OpenTabDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tabs`);
}

/** PERFSIGNIN1: one open ticket's figures for the Open Tabs list. */
export interface OpenTabSummaryDto {
  orderId: string;
  drinkCount: number;
  /** null when the ticket's total could not be priced. */
  totalCents: number | null;
  unpricedCount: number;
  /** True once every line is covered by a payment; always false for an empty ticket. */
  fullyPaid: boolean;
  /** LOCFLOOR1 (sweep B1): what a Pay right now would charge, tax included, tip excluded (after discounts, comps and
   * part-payments); null when the ticket could not be priced. totalCents above is the whole ticket. */
  owedWithTaxCents?: number | null;
  /** RCPT1-S3: present only on a ticket whose card is authorized and waiting for a tip written on its receipt. */
  awaitingTipCents?: number;
}

/** PERFSIGNIN1: the figures for every open ticket of this location in one request, in place of the order read, payment
 * preview and price quote the list used to make for each ticket. See src/domain/openTabSummaries.ts. */
export function listOpenTabSummaries(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ summaries: OpenTabSummaryDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tabs/summaries`);
}

/** REFUNDFIND1: one ticket closed on this location's current business day, from the server (whoever closed it). */
export interface RecentlyClosedTabDto {
  id: string;
  name: string | null;
  guestLabels: string[];
  closedAt: string;
  closedByName: string | null;
  /** What was charged, tip included. */
  totalCents: number;
  tipCents: number;
}

export function listRecentlyClosedTabs(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ tabs: RecentlyClosedTabDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tabs/recently-closed`);
}

export function renameOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; name: string },
): Promise<
  ApiResult<
    {
      renameOperation: OrderRenameOperationDto;
      order: OrderDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/rename`,
    params,
  );
}

export function reopenOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  // AU14-F4 / DEC-276: a reason (an active "reopen" action reason) is required.
  params: { idempotencyKey: string; reasonId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      reopenOperation: OrderReopenOperationDto;
      order: OrderDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/reopen`,
    params,
  );
}

// TM1 (docs/backlog.md; DEC-230, owner ruling "merge only"). One ledger row
// per tab that closed (TAB2, DEC-340); target_order_id is the tab that stays
// open and receives everything. See src/domain/tabMerge.ts.
export interface TabMergeDto {
  id: string;
  source_order_id: string;
  target_order_id: string;
  business_date: string;
  lines_moved_count: number;
  guests_moved_count: number;
  comps_moved_count: number;
  source_hold_outcome: "none" | "released" | "stranded" | "release_failed";
  source_hold_id: string | null;
  created_at: string;
}

// TAB2 (DEC-340): every tab in sourceOrderIds closes into targetOrderId in one all-or-nothing merge,
// one ledger row per source. targetOrderId is the receiving tab and rides in the path.
export function mergeManyTabs(
  baseUrl: string,
  tenantId: string,
  targetOrderId: string,
  params: { idempotencyKey: string; sourceOrderIds: string[] },
): Promise<
  ApiResult<
    {
      merges: TabMergeDto[];
      sourceOrders: OrderDto[];
      targetOrder: OrderDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(targetOrderId)}/merge-many`,
    params,
  );
}

// ALPHA-07: reads an existing order back with its lines and each line's
// component snapshot, for the Open Tabs "tap to resume" flow. See
// src/domain/orders.ts's getOrderWithLines.
export function getOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<
  ApiResult<
    {
      order: OrderDto;
      lines: OrderLineWithComponentsDto[];
      /** ALPHA-12. Empty for the ordinary single-implicit-guest ticket. */
      guests: GuestDto[];
      /** AU14-F4 / DEC-276: the most recent reopen, or null when never reopened. */
      lastReopen: LastReopenDto | null;
      /** ID1: the ID Verified row's state for this ticket. */
      idVerification: IdVerificationViewDto;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}`,
  );
}

// ID1 (docs/build-briefs/ID1-id-verified-ticket-action.md; PRD P0.10). The optional ID Verified action on a ticket with
// alcohol. The record is who, when and the method; there is no ID data to send or read.
export type IdVerificationMethodKey =
  "drivers_license" | "state_id" | "passport" | "military_id" | "other_government_id";

export type IdVerificationModeKey = "off" | "optional" | "required";
export type IdCheckSkipReasonKey =
  "known_regular" | "staff_on_shift" | "owner_manager_guest" | "scanner_down" | "other";

export interface IdVerificationViewDto {
  /** ID1-F2 (DEC-398): the location's mode. */
  mode: IdVerificationModeKey;
  enabled: boolean;
  hasAlcohol: boolean;
  /** True when the server would refuse Pay right now: Required, alcohol, no check and no skip. */
  payBlocked: boolean;
  methods: Array<{ key: IdVerificationMethodKey; label: string }>;
  skipReasons: Array<{ key: IdCheckSkipReasonKey; label: string }>;
  skip: {
    id: string;
    reason: IdCheckSkipReasonKey;
    reasonLabel: string;
    employeeName: string;
    approverName: string;
    skippedAt: string;
  } | null;
  current: {
    id: string;
    method: IdVerificationMethodKey;
    methodLabel: string;
    employeeName: string;
    verifiedAt: string;
    count: number;
  } | null;
}

export function postIdVerification(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; method: IdVerificationMethodKey; change: boolean },
): Promise<
  ApiResult<{ idVerification: IdVerificationViewDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/id-verification`,
    params,
  );
}

/** ID1-F2 (DEC-398). Skip the ID check on a ticket at a Required location: a picked reason, plus a manager's PIN through the
 * approval flow (the approver fields ride the same request and key). */
export function postIdCheckSkip(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    reason: IdCheckSkipReasonKey;
    approverUserId?: string;
    approverPin?: string;
  },
): Promise<
  ApiResult<{ idVerification: IdVerificationViewDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/id-check-skip`,
    params,
  );
}

export interface IdVerificationSettingDto {
  locationId: string;
  mode: IdVerificationModeKey;
  enabled: boolean;
  modes: Array<{ key: IdVerificationModeKey; label: string }>;
  methods: Array<{ key: IdVerificationMethodKey; label: string }>;
}

export function getIdVerificationSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<IdVerificationSettingDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/id-verification`,
  );
}

export function putIdVerificationSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: { mode: IdVerificationModeKey },
): Promise<ApiResult<IdVerificationSettingDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/id-verification`,
    body,
  );
}

// ALPHA-12: name a guest on a ticket. See src/domain/guests.ts's addGuest.
// MyTime track (track/mytime) Slice 2: clientRefKind/clientRefId are
// optional, additive (the Add Guest search UI's "attach" and "new
// client" paths); a plain free-text guest omits both, byte-for-byte
// unchanged from ALPHA-12.
export function addGuest(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    label: string;
    clientRefKind?: "directory" | "provisional";
    clientRefId?: string;
  },
): Promise<ApiResult<{ guest: GuestDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/guests`,
    params,
  );
}

// ---------------------------------------------------------------------
// MyTime track (track/mytime) Slice 2: Add Guest client search and
// provisional-client creation. See src/domain/mytimeClients.ts's own
// module header (searchClients / createProvisionalClient) and docs/
// decision-proposals/mytime-client-sync-proposal.md Sections 3 and 4.
// ---------------------------------------------------------------------

export interface ClientSearchResultDto {
  originLocationName?: string | null;
  lastVisitAtUtc?: string | null;
  lastVisitLocationName?: string | null;
  lastVisitTimeZone?: string | null;
  possibleDuplicate?: boolean;

  id: string;
  displayName: string;
  nameSearchKey: string;
  phoneSearchKey: string | null;
  membershipActive: boolean;
  originLocationRef: string | null;
  lastSyncedAt: string | null;
  provisional: boolean;
}

export interface ClientSearchResponseDto {
  results: ClientSearchResultDto[];
  freshness: { lastSyncedAt: string | null };
}

/** POST .../clients/search. Server-enforced 3-character minimum (main.ts
 * debounces client-side and never sends fewer, but the server is the
 * real gate; see src/domain/mytimeClients.ts's own CLIENT_SEARCH_MIN_
 * QUERY_LENGTH). Callers pass an AbortSignal so a fast typist's
 * superseded in-flight request can be cancelled rather than raced. */
export function searchClients(
  baseUrl: string,
  tenantId: string,
  params: { query: string },
  signal?: AbortSignal,
): Promise<ApiResult<ClientSearchResponseDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/clients/search`,
    params,
    signal,
  );
}

export interface CreateProvisionalClientResponseDto {
  id: string;
  displayName: string;
  phoneSearchKey: string;
  matchedExisting: boolean;
  source: "directory" | "provisional";
}

/** POST .../clients/provisional. Name and phone both required (the
 * phone-ask rule); see src/domain/mytimeClients.ts's
 * createProvisionalClient. */
export function createProvisionalClient(
  baseUrl: string,
  tenantId: string,
  params: { displayName: string; phone: string },
): Promise<ApiResult<CreateProvisionalClientResponseDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/clients/provisional`, params);
}

// ALPHA-12: the explicit, append-only guest-reattribution command. See
// src/domain/guests.ts's changeLineGuest.
export function changeLineGuest(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: { idempotencyKey: string; guestId: string },
): Promise<
  ApiResult<{ change: unknown; orderLine: OrderLineDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/change-guest`,
    params,
  );
}

// CORE6 (docs/build-briefs/CORE6-move-one-drink-to-another-tab.md): move ONE drink to another open tab, until a
// payment covers it. The drink arrives with no guest. See src/domain/lineMove.ts.
export function moveLineToTab(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: { idempotencyKey: string; targetOrderId: string },
): Promise<
  ApiResult<
    {
      move: { id: string; order_line_id: string; from_order_id: string; to_order_id: string };
      orderLine: OrderLineDto;
      replayed: boolean;
      /** Present only when the target tab's hold had something actionable (see addLine). */
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/move-to-tab`,
    params,
  );
}

export function addLine(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    publicationBinding?: PublicationBindingDto;
    itemReference: string;
    quantity: number;
    components: OrderLineComponentInput[];
    /** ALPHA-10 item 2. See OrderLineDto.is_substitution. */
    isSubstitution?: boolean;
    /** ALPHA-08. Required by src/api/server.ts's add-line route, which
     * resolves and prices the line server-side (src/domain/
     * lineResolution.ts) before persisting it; see that route's own
     * comment. */
    menuDrinkId: string;
    liquorProductId?: string;
    ounces?: string;
    modifiers?: PriceQuoteModifierInput[];
    /** ALPHA-12. Required by the server whenever the ticket currently has
     * two or more named guests, optional otherwise (auto-attributed with
     * one guest, omitted entirely with none); see src/domain/guests.ts's
     * resolveLineGuestId. */
    guestId?: string;
    /** VOC1-F5 Slice 3 (DEC-161). See PriceQuoteLineInput.override86's own
     * comment; identical contract at this route. */
    override86?: boolean;
    /** AU14-F15 / DEC-276. The reason picked on the 86 sheet; the server
     * requires it whenever override86 is true. */
    override86ReasonId?: string;
    /** VOC1-F7 Slice 1/2. See PriceQuoteLineInput.pourModifierApplied's own
     * comment; identical contract at this route, and required by it exactly
     * when ounces differs from the recipe's own default. */
    pourModifierApplied?: boolean;
    /** VOC1-F4 (Section 2.4). See PriceQuoteLineInput.modifierSelections'
     * own comment; identical contract at this route. */
    modifierSelections?: string[];
  },
): Promise<
  ApiResult<
    {
      orderLine: OrderLineDto;
      components: OrderLineComponentDto[];
      replayed: boolean;
      /** Payments-track Slice 4 (DEC-002 Section 3). Present only when
       * there is something actionable to show: the running ticket just
       * crossed the configured increment threshold and the hold's
       * authorized amount grew ("incremented"), or an increment attempt
       * was actually made and failed ("declined" / "ceiling_reached").
       * Absent for every ticket with no active hold, or one that has not
       * grown enough yet to trigger a check; see src/api/server.ts's own
       * runHoldIncrementCheckBestEffort. */
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines`,
    ingredientSelectionRequest(params),
  );
}

// ---------------------------------------------------------------------
// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth/thirteenth): direct inventory sales (pour, draft beer,
// packaged), the non-recipe counterpart to priceQuote/addLine above. See
// src/domain/inventorySale.ts's module header.
// ---------------------------------------------------------------------

// ITEM2 Slice 5 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
// in-settings.md Section 3.4; DEC-158). Widened from a closed five-literal
// union to `string`, mirroring ItemKind's own identical widen at ITEM1: a
// tenant-added kind's own sale-kind literal is its item-kind value itself
// (Section 3.1's "pour" is the one many-to-one exception), which cannot be
// named as a union member at compile time without a code change per kind,
// exactly what this item exists to remove. "pour" (liquor) and "packaged"
// (the seeded whole-unit kind) remain legal values of this now-open string
// type; nothing about their own special-cased handling elsewhere changes.
export type InventorySaleKind = string;

export interface InventorySaleQuoteResponse {
  lineTotalCents: number;
  perOuncePriceCents?: number;
  pourOunces?: string;
  provisional: true;
  /** VOC1-F4-F2 (Section 2.4 step 5). Present, possibly empty, so the
   * floor sheet can render each selected modifier's live price before
   * Add to Ticket commits, mirroring the batched price-quote route's own
   * identical field. */
  resolvedModifiers?: ResolvedModifierDetailDto[];
}

export async function quoteInventorySale(
  baseUrl: string,
  tenantId: string,
  params: {
    saleKind: InventorySaleKind;
    liquorProductId: string;
    /** Decimal string; required for "pour"/"beer", ignored for
     * "packaged". */
    ounces?: string;
    modifiers?: PriceQuoteModifierInput[];
    /** VOC1-F5 Slice 3 (DEC-161). See PriceQuoteLineInput.override86's own
     * comment; identical contract at this route. */
    override86?: boolean;
    /** VOC1-F4-F2 (Section 2.4). See PriceQuoteLineInput.modifierSelections'
     * own comment; the identical field, resolved against this liquor
     * product's own attachments instead of a menu drink's. */
    modifierSelections?: string[];
  },
): Promise<ApiResult<InventorySaleQuoteResponse & Partial<ApiErrorBody>>> {
  const result = await postJson<
    InventorySaleQuoteResponse &
      Partial<ApiErrorBody> & { resolvedIngredientChoices?: ResolvedModifierDetailDto[] }
  >(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory-sale-quote`,
    ingredientSelectionRequest(params),
  );
  if (result.status === 200) result.body.resolvedModifiers = result.body.resolvedIngredientChoices;
  return result;
}

export function addInventorySaleLine(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    saleKind: InventorySaleKind;
    liquorProductId: string;
    ounces?: string;
    modifiers?: PriceQuoteModifierInput[];
    guestId?: string;
    /** VOC1-F5 Slice 3 (DEC-161). See PriceQuoteLineInput.override86's own
     * comment; identical contract at this route. */
    override86?: boolean;
    /** AU14-F15 / DEC-276. The reason picked on the 86 sheet; the server
     * requires it whenever override86 is true. */
    override86ReasonId?: string;
    /** VOC1-F4-F2 (Section 2.4). See PriceQuoteLineInput.modifierSelections'
     * own comment; the identical field, resolved against this liquor
     * product's own attachments instead of a menu drink's. */
    modifierSelections?: string[];
  },
): Promise<
  ApiResult<
    {
      orderLine: OrderLineDto;
      components: OrderLineComponentDto[];
      replayed: boolean;
      /** Same best-effort signal addLine above carries, for the same
       * reason: a pour, a draft beer and a can grow the running ticket
       * exactly as a cocktail does. */
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines/inventory-sale`,
    ingredientSelectionRequest(params),
  );
}

// Amendment 2026-08-22 (twelfth): "All appear in the alphabetical menu
// alongside cocktails (with type-appropriate tiles)." One typed,
// discriminated-union read combining every sellable thing this tenant
// has. See src/api/server.ts's GET .../menu-items for the exact shape.
// ITEM2 Slice 5 (Section 3.4): the non-cocktail branch's `kind` widens
// from a closed five-literal union to `string`, the same widen
// InventorySaleKind takes above, since it carries the identical value
// (the item kind's own value, or "pour" for liquor). `itemKindLabel` is
// this item's own new field (src/api/server.ts's GET .../menu-items):
// the kind's CONFIGURED label, since the floor's primary role cannot
// fetch GET .../vocabularies itself to learn a tenant-added kind's own
// display text (see that route's own comment for why).
export type MenuItemDto =
  | {
      kind: "cocktail";
      id: string;
      name: string;
      menuDrink: MenuDrinkListItemDto;
      /** UX121: the liquor categories the drink is made from (its slots and recipe components), for the Base filter. */
      baseCategories: string[];
      /** CORE7: the drink reads 86'd at this tablet's location (its own flag, an 86'd ingredient or mapped item). */
      flagged86: boolean;
      /** CORE7: the ingredient or item names that make it 86'd, empty when only its own flag is set. */
      flagged86Via: string[];
      /** LOCFLOOR1 (sweep B17): false when the drink has no ingredients and no liquor selection, so the floor cannot ring it. */
      hasRecipe?: boolean;
    }
  | {
      kind: string;
      id: string;
      name: string;
      liquorProduct: LiquorProductDto;
      itemKindLabel: string;
      /** CORE12. A straight pour's flat price under shelf pricing, computed on the server by the
       * sale's own tier and override rules; null when not shelf priced or when the sale would refuse
       * the item. Absent on every other kind. */
      shelfPourPriceCents?: number | null;
      /** UX110. The shelf tier's display name (shelf_tier vocabulary label) for a straight pour with a
       * tier assigned; null when none is assigned. Absent on every other kind. */
      shelfTierLabel?: string | null;
    };

/** TEN11-F3 (DEC-414): the header that marks a request the person did not cause (an automatic refresh). A support
 * session's idle clock ignores it; every other caller of the API ignores it entirely. */
export const BACKGROUND_REQUEST_HEADERS: Record<string, string> = { "X-Bms-Background": "1" };

export function getMenuItems(
  baseUrl: string,
  tenantId: string,
  options: { background?: boolean } = {},
): Promise<ApiResult<{ items: MenuItemDto[]; pricingModel?: string } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-items`,
    options.background === true ? { headers: BACKGROUND_REQUEST_HEADERS } : {},
  );
}

/** UX125. The ranking behind Popular Now: item ids (a drink id or a product id, as the tile carries), best seller first. */
export function getPopularMenuItems(
  baseUrl: string,
  tenantId: string,
  options: { background?: boolean } = {},
): Promise<ApiResult<{ items: { id: string; quantity: number }[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/popular-menu-items`,
    options.background === true ? { headers: BACKGROUND_REQUEST_HEADERS } : {},
  );
}

// ALPHA-08. Remove a line before any payment has captured on its order.
// See src/domain/lineEditing.ts.
export function removeLine(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    { removal: unknown; orderLine: OrderLineDto; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/remove`,
    params,
  );
}

// ---------------------------------------------------------------------
// M0: void a PREPARED line, with a mandatory reason, and the tenant's own
// reason vocabulary behind the picker. Owner ruling 2026-08-27: "The only
// way to void out a drink is to say what happened." See
// src/domain/lineEditing.ts's voidPreparedLine and
// src/domain/voidReasons.ts.
//
// voidReasonId is REQUIRED in this signature, not optional: there is no
// call shape that voids without saying why. ApprovalRetryParams is here
// because money.line.void_prepared resolves approval_required for the
// Assistant Manager tier, so this is one of the calls main.ts's
// requestApproval drives.
// ---------------------------------------------------------------------

export interface VoidReasonDto {
  id: string;
  tenant_id: string;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrderLineVoidDto {
  id: string;
  tenant_id: string;
  order_line_id: string;
  order_id: string;
  void_reason_id: string;
  void_reason_label_at_void: string;
  note: string | null;
  voided_at: string;
}

/** M1: undo a mis-tapped Prepared. The ONE line command that returns
 * inventory, because the drink it reverses was never made; the server
 * refuses it on a covered line and on anything with no unreversed
 * preparation. Same approval-retry contract as voidOrderLine below. */
export function unprepareOrderLine(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: { idempotencyKey: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      occurrence: { id: string; occurrence_type: string };
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/unprepare`,
    params,
  );
}

// M4: refund a captured payment by selecting drinks (DEC-035). The read
// returns everything the picker renders, tax shares included, so the
// client only ever SUMS server-derived per-line figures; the write sends
// order-line ids and nothing else. No money amount crosses this
// boundary in either direction as a client-authored value.

export interface RefundableLineDto {
  order_line_id: string;
  item_reference: string;
  line_total_cents_at_capture: number;
  tax_share_cents: number;
  /** DUALPRICE1: this drink's share of the payment's Card price (0 without dual pricing). Optional so an older payload reads as 0. */
  card_price_share_cents?: number;
  /** REFDISC3 (DEC-308 a): the guest paid nothing for this line, so it cannot
   * be refunded and the sheet shows it disabled. Server-derived. */
  comped: boolean;
  refunded: boolean;
}

/** REFDISC2 (DEC-305): one card of an equal split, with what it paid and what
 * is left to refund to it. Every figure is server-derived. */
export interface RefundCardDto {
  paymentAttemptId: string;
  shareIndex: number;
  shareCount: number;
  guestLabel: string;
  cardBrand: string | null;
  cardLast4: string | null;
  capturedCents: number;
  tipCents: number;
  refundedCents: number;
  refundableRemainingCents: number;
  opened: boolean;
}

export interface RefundableLinesDto {
  paymentAttemptId: string;
  orderId: string;
  status: string;
  capturedTotalCents: number;
  taxCents: number;
  alreadyRefundedCents: number;
  /** SPLIT1 (Q4). True when this payment was taken by amount: it has no drinks to select and is refunded by an amount. */
  amountPayment: boolean;
  cancelledSplitCredit?: boolean;
  amountRefundable?: boolean;
  /** SPLIT1 (Q4). What a refund of this payment can still return (the server's ceiling). */
  refundableCents: number;
  /** REFUNDALL1. The tip captured on this payment, what earlier refunds returned of it, and what a refund can still return. */
  tipCents: number;
  tipRefundedCents: number;
  tipRefundableCents: number;
  cardBrand: string | null;
  cardLast4: string | null;
  /** Non-null only for a share of a completed equal split. */
  equalSplit: { payments: RefundCardDto[] } | null;
  lines: RefundableLineDto[];
}

export interface PaymentRefundDto {
  id: string;
  /** The ticket the refund is on (REFUNDRECEIPT1: the receipt choice names it). */
  order_id?: string;
  payment_attempt_id?: string;
  status: string;
  amount_cents: number;
  tax_cents: number;
  /** REFUNDALL1. The tip returned inside this refund; zero unless the manager chose to refund it. */
  tip_cents?: number;
  local_only: boolean;
}

export function listRefundableLines(
  baseUrl: string,
  tenantId: string,
  paymentAttemptId: string,
): Promise<ApiResult<RefundableLinesDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-attempts/${encodeURIComponent(paymentAttemptId)}/refundable-lines`,
  );
}

/** REF1 (DEC-011): refundReasonId is REQUIRED in this signature, matching
 * voidOrderLine's own voidReasonId below -- there is no call shape from
 * the floor that refunds without saying why. ApprovalRetryParams is here
 * for completeness, though it is now inert on this specific route:
 * money.refund resolves allowed for the Assistant Manager as of REF1
 * (DEC-011: "the role IS the control"), so no tier this call reaches from
 * the floor is ever asked for a PIN here anymore. */
export function refundPaymentLines(
  baseUrl: string,
  tenantId: string,
  paymentAttemptId: string,
  params: {
    idempotencyKey: string;
    orderLineIds: string[];
    refundReasonId: string;
    /** REFUNDALL1. Also return the payment's remaining tip. The server derives the amount. */
    includeTip?: boolean;
  } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      refund: PaymentRefundDto;
      refundLines: { order_line_id: string }[];
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-attempts/${encodeURIComponent(paymentAttemptId)}/refund-lines`,
    params,
  );
}

/** SPLIT1 (owner ruling Q4, 2026-10-10): refund up to the amount of a payment taken by amount. The cents are keyed
 * on the pad (the second owner-approved typed-amount exception); the server validates the ceiling and derives the tax. */
export function refundAmountPayment(
  baseUrl: string,
  tenantId: string,
  paymentAttemptId: string,
  params: {
    idempotencyKey: string;
    amountCents: number;
    refundReasonId: string;
    includeTip?: boolean;
  } & ApprovalRetryParams,
): Promise<ApiResult<{ refund: PaymentRefundDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-attempts/${encodeURIComponent(paymentAttemptId)}/refund-amount`,
    params,
  );
}

// ---------------------------------------------------------------------
// REF1 (DEC-011, 2026-08-29): the tenant's own refund reason vocabulary,
// a THIRD configurable reason list alongside void reasons and discount
// reasons. Structural twin of the VoidReasonDto shape and functions
// above.
// ---------------------------------------------------------------------

export interface RefundReasonDto {
  id: string;
  tenant_id: string;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

/** Defaults to active reasons only, which is what the floor picker wants.
 * Location settings passes activeOnly:false so an Admin can see and
 * reactivate a retired one. */
export function listRefundReasons(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean },
): Promise<ApiResult<{ refundReasons: RefundReasonDto[] } & Partial<ApiErrorBody>>> {
  const query = params?.activeOnly === false ? "?activeOnly=false" : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/refund-reasons${query}`);
}

export function createRefundReason(
  baseUrl: string,
  tenantId: string,
  params: { label: string },
): Promise<ApiResult<{ refundReason: RefundReasonDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/refund-reasons`, params);
}

/** Rename, deactivate and reactivate, all three. There is deliberately no
 * deleteRefundReason: a reason that past refunds reference is retired,
 * never removed, and no delete route exists to call. */
export function updateRefundReason(
  baseUrl: string,
  tenantId: string,
  refundReasonId: string,
  params: { label?: string; active?: boolean },
): Promise<ApiResult<{ refundReason: RefundReasonDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/refund-reasons/${encodeURIComponent(refundReasonId)}`,
    params,
  );
}

// ---------------------------------------------------------------------
// PL1: the card-not-present payment LINK (docs/build-briefs/
// PL1-payment-link.md; DEC-011 items 6 and 7). The create body carries
// only an idempotencyKey; the amount is server-derived (the money
// rule), never sent by this client.
// ---------------------------------------------------------------------

export interface PaymentLinkDto {
  id: string;
  order_id: string;
  provider_name: string;
  provider_reference: string;
  url: string;
  amount_cents: number;
  currency: string;
  status: string;
  payment_attempt_id: string | null;
  created_at: string;
  updated_at: string;
}

export function createPaymentLink(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ link: PaymentLinkDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/payment-links`,
    params,
  );
}

export function listPaymentLinks(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ links: PaymentLinkDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/payment-links`,
  );
}

export function reconcilePaymentLink(
  baseUrl: string,
  tenantId: string,
  paymentLinkId: string,
): Promise<
  ApiResult<
    {
      link: PaymentLinkDto;
      paymentAttempt?: { id: string; status: string; amount: string };
      coveredOrderLineIds?: string[];
      settlementClose?: { kind: string; message?: string };
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-links/${encodeURIComponent(paymentLinkId)}/reconcile`,
  );
}

export function cancelPaymentLink(
  baseUrl: string,
  tenantId: string,
  paymentLinkId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ link: PaymentLinkDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-links/${encodeURIComponent(paymentLinkId)}/cancel`,
    params,
  );
}

export function voidOrderLine(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: { idempotencyKey: string; voidReasonId: string; note?: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      lineVoid: OrderLineVoidDto;
      orderLine: OrderLineDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/void`,
    params,
  );
}

/** Defaults to active reasons only, which is what the floor picker wants.
 * Location settings passes activeOnly:false so an Admin can see and
 * reactivate a retired one. */
export function listVoidReasons(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean },
): Promise<ApiResult<{ voidReasons: VoidReasonDto[] } & Partial<ApiErrorBody>>> {
  const query = params?.activeOnly === false ? "?activeOnly=false" : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/void-reasons${query}`);
}

export function createVoidReason(
  baseUrl: string,
  tenantId: string,
  params: { label: string },
): Promise<ApiResult<{ voidReason: VoidReasonDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/void-reasons`, params);
}

/** Rename, deactivate and reactivate, all three. There is deliberately no
 * deleteVoidReason: a reason that past voids reference is retired, never
 * removed, and no delete route exists to call. */
export function updateVoidReason(
  baseUrl: string,
  tenantId: string,
  voidReasonId: string,
  params: { label?: string; active?: boolean },
): Promise<ApiResult<{ voidReason: VoidReasonDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/void-reasons/${encodeURIComponent(voidReasonId)}`,
    params,
  );
}

// ALPHA-08. Change a line's liquor selection before any payment has
// captured on its order. See src/domain/lineEditing.ts.
export function changeLineLiquor(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: {
    idempotencyKey: string;
    publicationBinding?: PublicationBindingDto;
    liquorProductId: string;
    ounces?: string;
    isSubstitution?: boolean;
    /** VOC1-F5 Slice 3 (DEC-161). Mirrors src/api/validation.ts's own
     * add-line override86 field: set true only when this selection went
     * through the override confirm sheet in "Change liquor" mode
     * (ui/src/main.ts's submitLiquorChange). The route resolves
     * inv.stock_flag.override for the acting session before letting a
     * flagged selection through; omitted (or false) leaves the
     * unconditional 400 in place for a flagged product. */
    override86?: boolean;
    /** AU14-F15 / DEC-276. The reason picked on the 86 sheet; the server
     * requires it whenever override86 is true. */
    override86ReasonId?: string;
    /** VOC1-F7 Slice 1/2. See PriceQuoteLineInput.pourModifierApplied's own
     * comment; identical contract at this route, and required by it exactly
     * when ounces differs from the recipe's own default. */
    pourModifierApplied?: boolean;
    /** M5 (DEC-282). The bartender's explicit yes to "this puts the price
     * back to the menu price" on a drink whose price was changed at the
     * till. Without it the server answers 409 price_change_in_force. */
    confirmPriceRestore?: boolean;
  },
): Promise<
  ApiResult<
    {
      change: unknown;
      orderLine: OrderLineDto;
      replayed: boolean;
      /** A liquor change can raise the line's price, which grows the
       * running ticket without adding a line to it. */
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/change-liquor`,
    params,
  );
}

export function prepareLine(
  baseUrl: string,
  tenantId: string,
  orderLineId: string,
  params: {
    idempotencyKey: string;
    preparedQuantity: number;
    recipeVersion?: string;
    deviceId?: string;
    occurrenceType?: "ordinary" | "remake";
  },
): Promise<
  ApiResult<
    {
      occurrence: PreparationOccurrenceDto;
      inventoryPostings: unknown[];
      /** VER72-F1: false when Prepared posted nothing (no catalog product). */
      inventoryPosted?: boolean;
      replayed: boolean;
      /** CORE1. Present when the Prepared tap settled the ticket and the server closed it (CL2 trigger 8). */
      settlementClose?: { kind: string; message?: string };
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/order-lines/${encodeURIComponent(orderLineId)}/prepared`,
    params,
  );
}

// Architect gate correction, 2026-08-21: this no longer takes an "amount"
// at all. src/api/server.ts's payments route never reads one from the
// request body (Critical finding: omitting "tip" used to let any caller
// pay any amount); subtotal, tax, and the final amount are always
// server-computed from the order's own current uncovered lines. See
// src/domain/payments.ts's payOrder module header for the full story.

/** CL2 (owner ruling 2026-08-28). Present on the response of any command
 * that could have settled the ticket, and only when there is something to
 * say: "closed" means the ticket closed itself and the floor should go
 * back to Open Tabs, "blocked" and "failed" mean it did not and the
 * message says why. Absent for the ordinary case where money is still
 * owed. Server side: src/api/server.ts's runSettlementCloseBestEffort. */
export interface SettlementCloseDto {
  kind: "closed" | "blocked" | "failed";
  message?: string;
}

export function payOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    currency?: string;
    requestedOutcome?: "Captured" | "Failed";
    /** ALPHA-08. The real pay-confirm sheet always sends one, including
     * the explicit {"type":"none"} shape for "no tip"; see
     * docs/decision-proposals/provisional-tips-config.md rule 3. */
    tip?: TipSelectionInput;
    /** ALPHA-12 "pay my drinks". Present only when this payment covers one
     * guest's own uncovered lines rather than the whole ticket; see
     * src/domain/payments.ts's PayOrderParams.guestId. */
    guestId?: string;
    /** SPLIT1 "By guest", the No guest row: pays the drinks attributed to no guest. */
    unassigned?: boolean;
    /** SPLIT1 "By amount". The server decides the pre-tax and tax parts and the
     * tip base; only `cents` (typed on the pad) is a client figure, and it is a
     * request ceiling. See src/domain/amountPayments.ts's AmountSelection. The
     * body field is `byAmount`: the payments route has always ignored `amount`. */
    byAmount?: AmountSelectionDto;
    /** Payments-track Slice 3, "Use saved card". Present only when the
     * guest chose to charge a card already on file; see src/domain/
     * payments.ts's PayOrderParams.savedCardId. */
    savedCardId?: string;
    /** Terminal-assignment slice. The registered terminal this physical
     * device IS (session.ts's TerminalConfig.paymentTerminalId), sent
     * with every card-present pay attempt; see src/domain/payments.ts's
     * PayOrderParams.terminalId. */
    terminalId?: string;
    acknowledgementId?: string;
    /** CASH1-S2-U1. A cash payment: `tender: "cash"` with what was handed over, never a tip. The amount is Exact, a
     * quick-cash amount the server computes, or a counted breakdown; the server refuses any other figure. */
    tender?: "cash";
    cash?: { tenderedCents: number; denominations?: Record<string, number> };
    /** RCPT1-S3. The tip written on the receipt, in cents, taken when this payment captures the ticket's receipt authorization. */
    receiptTipCents?: number;
  },
): Promise<
  ApiResult<
    {
      paymentAttempt: PaymentAttemptDto;
      /** TERM6-F1: why a Failed attempt failed, when the server could say. Absent on a replay. */
      failureKind?: "declined" | "customer_canceled" | "reader_error";
      tipSplits: PaymentTipSplitDto[];
      /** ALPHA-08 delta payments (owner ruling 2026-08-21): the order_line
       * ids this payment attempt actually covered, so the UI can show
       * "this is what was just charged" without a second round trip. */
      coveredOrderLineIds: string[];
      replayed: boolean;
      settlementClose?: SettlementCloseDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/payments`,
    params,
  );
}

// ALPHA-08 delta payments + design/QA gate finding G (no client-side tip/
// total arithmetic): a read-only preview of what Pay would charge right
// now (the order's active uncovered lines, subtotal, tax, and the
// resolved tip/total for every configured preset plus the caller's own
// tip choice). See src/domain/payments.ts's getPaymentPreview. Called
// whenever the ticket rail needs to know the CURRENT uncovered set (on
// resume, after any line edit, after a payment) and again whenever the
// pay-confirm sheet's tip selection changes, so every dollar figure shown
// to the bartender before Confirm comes from this response, never
// computed in this file.
export interface PaymentPreviewLineDto {
  orderLineId: string;
  itemReference: string;
  /** The line's own GROSS price. */
  lineTotalCents: number | null;
  /** Discounts slice A: what the discounts on this ticket take off THIS
   * line. Zero when nothing is discounted. */
  discountCents: number;
}

export interface PaymentPreviewPresetOptionDto {
  basisPoints: number;
  tipCents: number;
  totalCents: number;
}

/** Architect-gate MEDIUM finding, 2026-08-21: non-undefined exactly when an
 * equal-split plan is actively collecting on this order. See src/domain/
 * payments.ts's ActiveSplitPlanPreview comment: when set, the UI must
 * prefer resuming the split over the payable total the rest of this
 * response still (harmlessly) computes, since Pay would immediately 409. */
export interface ActiveSplitPlanPreviewDto {
  planId: string;
  shareCount: number;
  sharesCollected: number;
  label: string;
}

/** SPLIT1. Exactly one shape; `cents` is the one figure a person types. */
export type AmountSelectionDto =
  | { type: "cents"; cents: number }
  | { type: "remaining" }
  | { type: "half" }
  | { type: "guest"; guestId: string };

/** SPLIT1. One guest group among the uncovered lines (guestId null is "No guest"). */
export interface GuestShareDto {
  guestId: string | null;
  orderLineIds: string[];
  drinkCount: number;
  subtotalCents: number;
  subtotalBeforeDiscountCents: number;
  taxCents: number;
  /** The amount before tip: what a By guest payment for this group charges. */
  totalCents: number;
}

export interface PayModeAvailabilityDto {
  available: boolean;
  reason?: "amount_payment_taken";
}

/** DUALPRICE1. Present only when dual pricing is on. The preview's own figures stay the CASH figures; a card payment pays
 * cardTotalCents plus any tip. unsupportedReason is set when the card price is not built for the chosen way of paying. */
export interface DualPricingPreviewDto {
  basisPoints: number;
  cardPriceCents: number;
  cardTaxCents: number;
  cardTotalCents: number;
  cashTotalCents: number;
  unsupportedReason?: string;
}

export interface PaymentPreviewDto {
  dualPricing?: DualPricingPreviewDto;
  cartPauseEnabled?: boolean;
  /** TERM10 (DEC-410): true when the guest picks the tip on the card reader, so the pay sheet does not ask. Optional on
   * the type for the same reason as tipMode. */
  readerTipOnReader?: boolean;
  /** LABELCLAR1: the location's TIPFULL1 setting, so the Pay sheet can say what the tip is figured on. */
  tipOnPreDiscountPrice?: boolean;
  /** RCPT1-S3: where this location takes card tips. Optional on the type because a share preview adapted into this shape has none. */
  tipMode?: "reader" | "receipt";
  uncoveredLines: PaymentPreviewLineDto[];
  /** AFTER discounts: what Pay will actually charge, before tax and tip. */
  subtotalCents: number;
  /** Discounts slice A. BEFORE discounts, and the number every tip preset
   * below is computed on. Owner ruling 2026-08-28: "For tips, calculate
   * based on the total ticket value before discounts." Equal to
   * subtotalCents when nothing is discounted. */
  subtotalBeforeDiscountCents: number;
  /** The one summed row the pay sheet shows beside Subtotal and Tax. Zero
   * when nothing is discounted, and the row is hidden rather than showing
   * a zero. */
  discountCents: number;
  /** Computed on the DISCOUNTED subtotal. Owner ruling: "yes tax on whats
   * actually paid." */
  taxCents: number;
  tipCents: number;
  totalCents: number;
  presetOptions: PaymentPreviewPresetOptionDto[];
  provisional: true;
  activeSplitPlan?: ActiveSplitPlanPreviewDto;
  /** SPLIT1. Optional on the type because a share preview adapted into this shape (main.ts) has none. */
  guestShares?: GuestShareDto[];
  /** SPLIT1. What the ticket still owes, tax included, before tip. */
  balanceCents?: number;
  amountPayments?: { count: number; settledCents: number };
  /** SPLIT1. Which pay modes are open; N/A is shown from this, never inferred. */
  payModes?: {
    byGuest: PayModeAvailabilityDto;
    equal: PayModeAvailabilityDto;
    amount: PayModeAvailabilityDto;
  };
  /** SPLIT1. Present when the request carried `amount`, or the ticket already has amount payments. */
  amountPayment?: {
    amountCents: number;
    subtotalCents: number;
    taxCents: number;
    tipBaseCents: number;
    remainingAfterCents: number;
    final: boolean;
  };
  /** DOCSBUG5. True when a charge cannot reach real money (the simulated
   * provider, or a provider holding a test key). Absent or false means a
   * real card may be charged, and the Pay sheet words itself that way. */
  testEnvironment?: boolean;
}

export type ReaderCartDisplayDto =
  | {
      status: "shown";
      acknowledgementId: string;
      terminalId: string;
      preTipTotalCents: number;
      currency: string;
    }
  | { status: "refused"; reason: "unavailable" | "reader_busy" | "unsupported" | "stale" }
  | { status: "unknown"; reason: "unconfirmed" };
export function showOrderReaderCart(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  body: {
    displayRequestId: string;
    terminalId: string;
    guestId?: string;
    unassigned?: boolean;
    byAmount?: AmountSelectionDto;
  },
): Promise<ApiResult<ReaderCartDisplayDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/reader-cart`,
    body,
  );
}
export function showShareReaderCart(
  baseUrl: string,
  tenantId: string,
  shareId: string,
  body: {
    displayRequestId: string;
    terminalId: string;
  },
): Promise<ApiResult<ReaderCartDisplayDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/split-payment-shares/${encodeURIComponent(shareId)}/reader-cart`,
    body,
  );
}

export function getPaymentPreview(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params?: {
    tip?: TipSelectionInput;
    guestId?: string;
    unassigned?: boolean;
    byAmount?: AmountSelectionDto;
  },
): Promise<ApiResult<PaymentPreviewDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/payment-preview`,
    params ?? {},
  );
}

// ALPHA-08. Payment-status read: every payment attempt on an order (amount,
// when, per-person tip split rows), for the pay-warning sheet's "this tab
// was already paid" state and the closeout summary's reopened-tab
// activity. See src/domain/payments.ts's listPaymentsForOrder.
export function getPayments(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ payments: PaymentWithTipSplitsDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/payments`,
  );
}

// ALPHA-08. Pay-screen tip presets, config-driven. See src/config/tips.ts.
export function getTipConfig(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ presetsBasisPoints: number[]; provisional: true } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tip-config`);
}

// ---------------------------------------------------------------------
// ALPHA-12 equal split payments. See src/domain/splitPayments.ts.
// ---------------------------------------------------------------------

export interface SplitShareRowDto {
  id: string;
  tenant_id: string;
  plan_id: string;
  order_id: string;
  share_index: number;
  subtotal_cents: number;
  tax_cents: number;
  status: string;
  payment_attempt_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SplitShareDto extends SplitShareRowDto {
  progress:
    | { state: "pending"; drinksCents: number; taxCents: number; beforeTipCents: number }
    | {
        state: "captured";
        drinksCents: number;
        taxCents: number;
        tipCents: number;
        totalChargedCents: number;
      };
}

export interface SplitPlanDto {
  id: string;
  tenant_id: string;
  order_id: string;
  idempotency_key: string;
  share_count: number;
  subtotal_cents_at_creation: number;
  tax_cents_at_creation: number;
  completed_at: string | null;
  /** Architect-gate correction, 2026-08-21. See db/schema.ts's
   * paymentSplitPlans.cancelledAt comment. */
  cancelled_at: string | null;
  created_at: string;
}

export interface EqualShareCentsDto {
  shareIndex: number;
  subtotalCents: number;
  taxCents: number;
  amountCents: number;
}

// Read-only preview: no persistence. See src/domain/splitPayments.ts's
// getSplitPreview.
export function getSplitPreview(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { shareCount: number },
): Promise<
  ApiResult<
    {
      subtotalCents: number;
      taxCents: number;
      shares: EqualShareCentsDto[];
      provisional: true;
      activeSplitPlan?: ActiveSplitPlanPreviewDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/split-preview`,
    params,
  );
}

// The real, persisted "Start equal split" command. See
// src/domain/splitPayments.ts's createSplitPlan.
export function createSplitPlan(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; shareCount: number },
): Promise<
  ApiResult<
    { plan: SplitPlanDto; shares: SplitShareDto[]; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/split-payments`,
    params,
  );
}

// Every split plan for an order, most recent first, each with its shares.
// For "N of M shares collected" progress and the reopened/payment-detail
// view's per-share activity. See src/domain/splitPayments.ts's
// listSplitPlansForOrder.
export function listSplitPayments(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<
  ApiResult<{ plans: { plan: SplitPlanDto; shares: SplitShareDto[] }[] } & Partial<ApiErrorBody>>
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/split-payments`,
  );
}

// Read-only preview of what collecting ONE share would charge, for a given
// tip selection. See src/domain/splitPayments.ts's getSharePaymentPreview.
export interface SharePaymentPreviewDto {
  cartPauseEnabled?: boolean;
  shareIndex: number;
  shareCount: number;
  subtotalCents: number;
  taxCents: number;
  tipCents: number;
  totalCents: number;
  presetOptions: PaymentPreviewPresetOptionDto[];
  status: string;
  provisional: true;
  /** DOCSBUG5: see PaymentPreviewDto.testEnvironment. */
  testEnvironment?: boolean;
  /** TERM10: see PaymentPreviewDto.readerTipOnReader. */
  readerTipOnReader?: boolean;
}

export function getSharePaymentPreview(
  baseUrl: string,
  tenantId: string,
  shareId: string,
  params?: { tip?: TipSelectionInput },
): Promise<ApiResult<SharePaymentPreviewDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/split-payment-shares/${encodeURIComponent(shareId)}/preview`,
    params ?? {},
  );
}

// Architect-gate CRITICAL correction, 2026-08-21 ("abandoned split
// deadlock"): abandons an active split plan, converting its already-
// collected shares into real line coverage and unblocking every other pay
// mode on the order. See src/domain/splitPayments.ts's cancelSplitPlan.
export interface CancelSplitPlanOperationDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  plan_id: string;
  order_id: string;
  captured_share_count: number;
  share_count: number;
  collected_cents: number;
  covered_line_count: number;
  cancelled_at: string;
  created_at: string;
}

export function cancelSplitPlan(
  baseUrl: string,
  tenantId: string,
  planId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    {
      cancelOperation: CancelSplitPlanOperationDto;
      plan: SplitPlanDto;
      coveredOrderLineIds: string[];
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/split-payments/${encodeURIComponent(planId)}/cancel`,
    params,
  );
}

// Collect one share: its own sequential simulated payment. See
// src/domain/splitPayments.ts's collectShare.
export function collectShare(
  baseUrl: string,
  tenantId: string,
  shareId: string,
  params: {
    idempotencyKey: string;
    currency?: string;
    requestedOutcome?: "Captured" | "Failed";
    tip?: TipSelectionInput;
    /** Terminal-assignment slice. See payOrder's own terminalId comment
     * above; identical meaning for a split-collect share's own charge. */
    terminalId?: string;
    acknowledgementId?: string;
    /** CASH1-S2-U1. A cash payment: `tender: "cash"` with what was handed over, never a tip. The amount is Exact, a
     * quick-cash amount the server computes, or a counted breakdown; the server refuses any other figure. */
    tender?: "cash";
    cash?: { tenderedCents: number; denominations?: Record<string, number> };
  },
): Promise<
  ApiResult<
    {
      paymentAttempt: PaymentAttemptDto;
      /** TERM6-F1: why a Failed attempt failed, when the server could say. Absent on a replay. */
      failureKind?: "declined" | "customer_canceled" | "reader_error";
      tipSplits: PaymentTipSplitDto[];
      share: SplitShareRowDto;
      plan: SplitPlanDto;
      coveredOrderLineIds: string[];
      replayed: boolean;
      settlementClose?: SettlementCloseDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/split-payment-shares/${encodeURIComponent(shareId)}/collect`,
    params,
  );
}

// CL6 (DEC-010): the activity half of the closeout, bucketed by the
// ORDER's own service date. See src/domain/closeout.ts's
// CloseoutActivityByServiceDate.
export interface CloseoutActivityByServiceDateDto {
  drinksSold: number;
  tabsClosed: number;
  voidCount: number;
  voidValueCents: number;
  // DSC35-S4. Scheduled specials a manager removed (count) and what they
  // were taking (cents). S8a renders it.
  removedSpecialCount: number;
  removedSpecialCents: number;
  // CORE1. Tabs walked out on with this service date and what they owed.
  walkoutCount: number;
  walkoutOwedCents: number;
  /** CORE5-F4 (PROVISIONAL). The part walked after its tab's day was final. */
  walkoutLateCount: number;
  walkoutLateOwedCents: number;
}

// CL6 (DEC-010): the money half, bucketed by each payment's/refund's own
// stamped collection date. See src/domain/closeout.ts's
// CloseoutRevenueByCollectionDate for the exact scope (Captured payments
// only for discounts/comps, non-Failed refunds) and why comp is a
// labeled SUBSET of discount rather than a separate total.
export interface CloseoutRevenueByCollectionDateDto {
  grossBeforeTaxCents: number;
  /** DUALPRICE1: the dual-pricing Card price collected, its own line beside sales before tax. 0 without dual pricing. */
  cardPriceCents?: number;
  taxCents: number;
  tipsCents: number;
  totalCollectedCents: number;
  discountCount: number;
  discountReductionCents: number;
  compCount: number;
  compReductionCents: number;
  refundCount: number;
  refundAmountCents: number;
}

// CL6. One carried-over open tab inside a finalization record; see
// src/domain/closeout.ts's CarriedOpenTab.
export interface CarriedOpenTabDto {
  orderId: string;
  name: string | null;
  serviceDate: string;
  ageDays: number;
  totalCents: number;
  outstandingCents: number;
  /** CARRYOVER1: tax included, as Pay would charge; absent on a day closed before CARRYOVER1. */
  owedWithTaxCents?: number;
  holdBacked: boolean;
  holdAtRisk: boolean;
  holdAtRiskReason: string | null;
  /** PAY24: see src/domain/closeout.ts's CarriedOpenTab.holdProviderMismatch. */
  holdProviderMismatch: boolean;
}

// CL6. A finalized day's immutable record; see src/domain/closeout.ts's
// CloseoutFinalization.
/** CASH1-S4. One session closed on the business day, as its close event recorded it. */
export interface CloseoutCashSessionDto {
  sessionId: string;
  deviceId: string;
  deviceName: string | null;
  custodianUserId: string;
  custodianName: string | null;
  openedAt: string;
  closedAt: string;
  countedCents: number;
  /** CASH1-S4-F1. The next four are absent unless the caller holds drawer.view_expected (the blind count). */
  expectedCents?: number;
  /** Counted minus expected, signed. */
  varianceCents?: number;
  approverUserId?: string | null;
  /** CLOSEFIX1. Absent on a session finalized before the name was recorded. */
  approverName?: string | null;
  reasonLabel?: string | null;
}

/** CASH1-S4. A drawer with no close, opened on or before the business day. */
export interface CloseoutCashOpenDrawerDto {
  sessionId: string;
  deviceId: string;
  deviceName: string | null;
  custodianUserId: string;
  custodianName: string | null;
  openedAt: string;
}

/** CASH1-S4. The closeout Cash block. There is no cash tip line: no tips on cash. */
export interface CloseoutCashBlockDto {
  businessDate: string;
  floatCents: number;
  cashSalesCents: number;
  cashRefundsCents: number;
  paidInCents: number;
  paidOutCents: number;
  dropsCents: number;
  countedCents: number;
  /** CASH1-S4-F1. Absent unless the caller holds drawer.view_expected (the blind count). */
  expectedCents?: number;
  overShortCents?: number;
  sessions: CloseoutCashSessionDto[];
  openDrawers: CloseoutCashOpenDrawerDto[];
}

export interface CloseoutFinalizationDto {
  id: string;
  businessDate: string;
  finalizedAt: string;
  /** Null only on a day the system closed by itself (AUTOCLOSE1, DEC-496). */
  actorUserId: string | null;
  closedAutomatically: boolean;
  actorDisplayName: string | null;
  activityByServiceDate: Omit<
    CloseoutActivityByServiceDateDto,
    | "removedSpecialCount"
    | "removedSpecialCents"
    | "walkoutCount"
    | "walkoutOwedCents"
    | "walkoutLateCount"
    | "walkoutLateOwedCents"
  > & { walkoutCount: number | null; walkoutOwedCents: number | null };
  revenueByCollectionDate: CloseoutRevenueByCollectionDateDto;
  carriedOpenTabs: CarriedOpenTabDto[];
  /** CARRYOVER1 (DEC-556): what this close carried; owedWithTaxCents is null for a day closed before CARRYOVER1. */
  carriedOver: { count: number; owedWithTaxCents: number | null };
  openExceptionIds: string[];
  /** CASH1-S4. The Cash block the day recorded; null for a day finalized before it (N/A). */
  cash: CloseoutCashBlockDto | null;
  /** PAY1-S4-F2-F8-F5 slice 4. What the day recorded about Stripe's settlement; null for a day finalized before it
   * or with connected accounts off. */
  settlement: {
    status: "matches" | "differs" | "incomplete" | "account_unresolved" | "not_fetched";
    findings: ("differs" | "dispute" | "failed_payout" | "account_unresolved")[];
    provider: { capturedCents: number; refundedCents: number } | null;
    local: { capturedCents: number; refundedCents: number } | null;
    capturedDiffCents: number | null;
    refundedDiffCents: number | null;
    declaration: {
      capturedDiffCents: number;
      refundedDiffCents: number;
      reasonId: string;
      reasonLabel: string;
      declaredByUserId: string;
      declaredAt: string;
    } | null;
  } | null;
}

// ALPHA-08 + CL6. Closeout summary: the live aggregation for a business
// day, split into DEC-010's two named views. See src/domain/closeout.ts.
/** M5 (DEC-282). One drink with a price changed at the till, still in force,
 * counted on the day the drink was rung. */
export interface CloseoutPriceChangeRowDto {
  orderLineId: string;
  orderId: string;
  orderName: string | null;
  itemReference: string;
  at: string;
  wasCents: number;
  nowCents: number;
  /** nowCents minus wasCents: negative when lowered. */
  differenceCents: number;
  setBy: string | null;
  /** Null when the person who set it could approve alone. */
  approvedBy: string | null;
  reason: string;
  halfOffOrMore: boolean;
  wentUp: boolean;
  /** M5-R10. Ledger rows (changes and put-backs) for this drink, the History
   * button's badge. */
  changeCount: number;
  /** M5-F1-F1 (DEC-311). The cost snapshotted when the price was set; null
   * on an older record or when no figure was known. */
  drinkCostCents: number | null;
  /** "lower_bound" means "at least" that figure. Null on an older record. */
  drinkCostBasis: "full" | "lower_bound" | "unknown" | null;
  /** The price in force is below the SNAPSHOTTED cost (never recomputed). */
  belowCost: boolean;
}

export interface CloseoutPriceChangesDto {
  count: number;
  loweredCount: number;
  loweredCents: number;
  raisedCount: number;
  raisedCents: number;
  netCents: number;
  flagOffBasisPoints: number;
  rows: CloseoutPriceChangeRowDto[];
}

export interface CloseoutSummaryDto {
  businessDate: string;
  /** AU14-F4, AU14-F15 / DEC-276. Actions that now require a reason, on the
   * business night they happened, newest first: reopens and 86 sales. A row's
   * `reason` is null for one from before the reason was required. */
  actionsWithReason: {
    /** AU14-F5 / DEC-276. Manual discounts taken off, with the reason, newest first. */
    discountRemovals: {
      count: number;
      cents: number;
      rows: {
        at: string;
        orderId: string;
        ticketName: string | null;
        discountName: string;
        reductionCents: number;
        removedBy: string | null;
        approvedBy: string | null;
        reason: string;
      }[];
    };
    stockOverrides: {
      count: number;
      rows: {
        at: string;
        orderId: string;
        ticketName: string | null;
        productName: string | null;
        overriddenBy: string | null;
        approvedBy: string | null;
        reason: string | null;
      }[];
    };
    reopens: {
      count: number;
      alreadyPaidCount: number;
      rows: {
        at: string;
        orderId: string;
        ticketName: string | null;
        reopenedBy: string | null;
        approvedBy: string | null;
        reason: string | null;
      }[];
    };
  };
  /** M5. Not a discount; already inside Sales before tax. */
  priceChanges: CloseoutPriceChangesDto;
  activityByServiceDate: CloseoutActivityByServiceDateDto;
  revenueByCollectionDate: CloseoutRevenueByCollectionDateDto;
  /** CLOSEFIX1. True only when the payment provider is the simulated one; the note and chip show only then. */
  paymentsSimulated: boolean;
  /** DSC35-S8a. Scheduled specials that reduced a captured payment collected
   * on this date (same basis as the Discounts card). Empty rows: no block. */
  scheduledSpecials: {
    rows: { name: string; rule: string; drinkCount: number; takenOffCents: number }[];
    totalDrinkCount: number;
    totalCents: number;
  };
  /** DSC35-S8a. Discounts = manager + scheduled + comps, server-derived. */
  discountBreakdown: {
    byAmountCents?: number;
    totalCount: number;
    totalCents: number;
    managerCount: number;
    managerCents: number;
    scheduledCount: number;
    scheduledCents: number;
    compCount: number;
    compCents: number;
  };
  /** DSC35-S8a. Removed specials by reason, service-date basis. */
  removedSpecialsByReason: { reason: string; count: number; cents: number }[];
  /** Design/QA gate finding F: per-maker tip totals for the day. A maker
   * with no tip today does not appear at all (never a synthesized zero
   * row); see src/domain/closeout.ts's SELECT_TIPS_BY_MAKER_SQL comment. */
  tipsByMaker: { employeeId: string; displayName: string | null; amountCents: number }[];
  /** RCPT1-S4 / RCPT1-S3: authorized receipt sales not yet captured, not sales and not tips. A live read. */
  awaitingTip?: {
    count: number;
    amountCents: number;
    tickets: {
      orderId: string;
      name: string | null;
      amountCents: number;
      businessDate: string;
      captureBeforeAt: string | null;
      carriedToBusinessDate: string | null;
    }[];
  };
  reopenedTabActivity: {
    orderId: string;
    name: string | null;
    reopenedAtTimestamps: string[];
    /** ALPHA-12 item 3: per-guest and per-share activity, alongside the
     * amount and time this list already carried. */
    capturedPayments: {
      amountCents: number | null;
      amount: string;
      capturedAt: string;
      guestId: string | null;
      splitShare: PaymentSplitShareInfoDto | null;
    }[];
  }[];
  openTabsAtDayEnd: {
    orderId: string;
    name: string | null;
    createdAt: string;
    /** SHIFT1: who opened the tab; null when none is recorded. */
    openedByUserId: string | null;
    openedByName: string | null;
    itemCount: number;
    /** The ticket total (every active line, covered or not). See
     * outstandingCents for how much is actually still owed. */
    totalCents: number;
    /** Re-gate item 5: the uncovered balance, i.e. what a real Pay right
     * now would actually charge (src/domain/closeout.ts's own comment).
     * Equal to totalCents when nothing has been captured yet. */
    outstandingCents: number;
    /** LOCFLOOR1 (sweep B1): outstandingCents plus tax on it, the amount a Pay right now would charge before any tip.
     * The figure to show where a screen says how much is still owed; outstandingCents stays the pre-tax balance. */
    owedWithTaxCents: number;
    reopened: boolean;
    /** Architect-gate CRITICAL correction ("closeout honesty while a plan
     * is ACTIVE"): non-null exactly when an equal-split plan is actively
     * collecting on this order; outstandingCents above already accounts
     * for what its captured shares collected. See src/domain/closeout.ts's
     * OpenTabAtDayEnd.activeSplit comment. */
    activeSplit: { sharesCollected: number; shareCount: number; label: string } | null;
  }[];
  unpriceableLines: {
    orderLineId: string;
    orderId: string;
    orderName: string | null;
    itemReference: string;
  }[];
  /** CL7. Payment attempts DECIDED as Failed: no block follows, the guest
   * can be charged again with an ordinary fresh Pay. Scoped by this
   * business date's own orders. See src/domain/closeout.ts's
   * FailedPaymentAttempt. */
  failedPayments: {
    paymentAttemptId: string;
    orderId: string;
    orderName: string | null;
    amount: string;
    attemptedAt: string;
  }[];
  /** CL7. Payment attempts still Pending: a genuinely non-terminal
   * provider status that BLOCKS every further Pay, split-collect, and
   * Close on its order until a manager confirms the real outcome
   * (DEC-022). Tenant-wide, not scoped to this business date: a live
   * blocking condition, not a day's history. See src/domain/closeout.ts's
   * PendingReconciliationPaymentAttempt. */
  pendingReconciliationPayments: {
    paymentAttemptId: string;
    orderId: string;
    orderName: string | null;
    amount: string;
    attemptedAt: string;
  }[];
  /** PAY26. Money collected for a Walkout in an old processor's own dashboard, as its own line (never part of
   * totalCollectedCents). `clean` is false while a Walkout cleared as collected there has no record. */
  outsideCollections: {
    rows: {
      walkoutExceptionId: string;
      orderId: string;
      orderName: string | null;
      outcome: "collected" | "not_collected";
      amountCents: number;
      recordedAt: string;
      recordedByDisplayName: string | null;
    }[];
    collectedCents: number;
    unrecorded: {
      walkoutExceptionId: string;
      orderId: string;
      orderName: string | null;
      amountCents: number;
    }[];
    clean: boolean;
  };
  /** PAY1-S4-F2-F8-F5 slice 3. Stripe's settlement for this location and date beside what was recorded, from the latest
   * stored snapshot. Null with connected accounts off. `clean` is false unless it matches with no dispute and no failed
   * payout. See src/domain/settlementCloseout.ts. */
  settlement: {
    status: "matches" | "differs" | "incomplete" | "account_unresolved" | "not_fetched";
    clean: boolean;
    findings: ("differs" | "dispute" | "failed_payout" | "account_unresolved")[];
    fetchedAt: string | null;
    provider: { capturedCents: number; refundedCents: number } | null;
    local: { capturedCents: number; refundedCents: number } | null;
    capturedDiffCents: number | null;
    refundedDiffCents: number | null;
    fees: { applicationFeeCents: number; processingFeeCents: number } | null;
    disputes: { count: number; cents: number } | null;
    payouts: { paidCents: number; failedCount: number } | null;
  } | null;
  /** CL7. The terminal REGISTRATION roster, not a live health signal;
   * reachability and pairing are not recorded anywhere in this codebase
   * (CL8, docs/backlog.md). See src/domain/closeout.ts's
   * TerminalRegistrationStatus. */
  terminalStatus: {
    /** CL8. `health` is the reader's live reachability, or null when it was not read. */
    activeTerminals: { terminalId: string; displayName: string; health: ReaderHealthDto | null }[];
    deactivatedToday: { terminalId: string; displayName: string; deactivatedAt: string }[];
  };
  /** CL7. The MyTime client-directory sync's own freshness read (MT1),
   * reused rather than re-derived; see MyTimeSyncFreshnessDto below for
   * the identical shape. */
  syncStatus: MyTimeSyncFreshnessDto;
  /** CL6. What finalizing right now would record as carried over, each
   * tab with its hold-at-risk flag: shown in the Finalize confirmation
   * sheet before anything is written. */
  carryoverPreview: CarriedOpenTabDto[];
  /** CL6. Non-null once this date has been finalized. */
  finalization: CloseoutFinalizationDto | null;
  /** CASH1-S4. The location's cash switch, and the Cash block (live, or the recorded one for a finalized day, null for N/A). */
  cashEnabled: boolean;
  cash: CloseoutCashBlockDto | null;
  /** CORE11. Gross sales, discounts, comps, refunds and net sales for the day, read on the server
   * through the same figures Sales by day uses, so the two agree for the same business day. */
  netSales: {
    ticketsPaid: number;
    grossSalesCents: number;
    discountCents: number;
    compCents: number;
    refundBeforeTaxCents: number;
    netSalesCents: number;
  };
  /** CORE13. Whether Finalize would be refused as "not over yet", and when it opens: the date after
   * the business date, at the cutoff, in the tenant's own zone. */
  finalizeAvailability: {
    dayIsOver: boolean;
    availableOnDate: string;
    cutoffTimeLocal: string;
    timezone: string;
  } | null;
}

/** CORE13, EXP1-S1. The closeout as an audited file, CSV or Excel; needs report.closeout.view and report.export. */
export function getCloseoutCsv(
  baseUrl: string,
  tenantId: string,
  businessDate: string,
  locationId?: string,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/closeout-summary.${format}?businessDate=${encodeURIComponent(businessDate)}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export function getCloseoutSummary(
  baseUrl: string,
  tenantId: string,
  businessDate: string,
  locationId?: string,
): Promise<ApiResult<CloseoutSummaryDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/closeout-summary?businessDate=${encodeURIComponent(businessDate)}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

// M12 (docs/build-briefs/M12-a-manager-can-see-void-history.md; DEC-101;
// DEC-149). Mirrors CloseoutSummaryDto/getCloseoutSummary's own shape
// exactly. voidReasonLabelAtVoid is the write-once snapshot, never a live
// void_reasons.label; itemReference/liquorComponentReference are the
// removed line's own write-once snapshots, never a live menu_drinks/
// liquor_products lookup. actorUserId is null exactly for a void
// recorded before M12 shipped; actorDisplayName is null in that same
// case and only that case, since a deactivated actor's account row is
// never deleted and still resolves a name.
export interface VoidHistoryEntryDto {
  id: string;
  orderLineId: string;
  orderId: string;
  voidReasonLabelAtVoid: string;
  note: string | null;
  voidedAt: string;
  itemReference: string;
  lineTotalCents: number | null;
  liquorComponentReference: string | null;
  actorUserId: string | null;
  actorDisplayName: string | null;
}

export interface VoidHistoryDto {
  businessDate: string;
  entries: VoidHistoryEntryDto[];
}

export function getVoidHistory(
  baseUrl: string,
  tenantId: string,
  businessDate: string,
): Promise<ApiResult<VoidHistoryDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/void-history?businessDate=${encodeURIComponent(businessDate)}`,
  );
}

/** EXP1-S4. The day's voids as an audited file; needs report.void_history.view and report.export. */
export function getVoidHistoryFile(
  baseUrl: string,
  tenantId: string,
  businessDate: string,
  format: GateExportFormat,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/void-history?businessDate=${encodeURIComponent(businessDate)}&format=${format}`,
  );
}

// RPT1 (docs/build-briefs/RPT1-owner-reports-slice-1.md; DEC-268): the three
// owner reports. Mirrors src/domain/salesReports.ts's report shapes. Money is
// integer cents; the screen formats it. A tie-out that fails never blocks a
// report: it arrives as tieOut.ok false with the difference named.
export interface ReportContextDto {
  today: string;
  timezone: string;
  cutoffLocal: string;
  /** INV33: the business night by the clock alone (the inventory reports' today). */
  clockToday?: string;
}

export interface ReportMetaDto {
  from: string;
  to: string;
  timezone: string;
  cutoffLocal: string;
  generatedAt: string;
  paymentsSimulated: boolean;
}

export interface ReportTieOutDto {
  ok: boolean;
  differences: Array<{ businessDate: string | null; check: string; differenceCents: number }>;
}

export interface SalesDayRowDto {
  businessDate: string;
  status: "final" | "open";
  tickets: number;
  grossCents: number;
  discountCents: number;
  compCents: number;
  refundCents: number;
  netCents: number;
  avgTicketCents: number | null;
  taxCents: number;
  tipsCents: number;
  /** CASH1-S4. Collected by each tender on the day. */
  cardCents: number;
  cashCents: number;
}

/** DSC21. One discount's cost at one location, with who owns its definition. */
export interface DiscountKindRowDto {
  discountId: string;
  name: string;
  kind: DiscountKind;
  applications: number;
  costCents: number;
}

export interface DiscountKindReportDto {
  meta: ReportMetaDto & { locationId: string; locationName: string };
  rows: DiscountKindRowDto[];
  totals: { applications: number; costCents: number; localCents: number; corporateCents: number };
}

export interface SalesByDayReportDto {
  meta: ReportMetaDto;
  days: SalesDayRowDto[];
  totals: Omit<SalesDayRowDto, "businessDate" | "status">;
  prior: {
    from: string;
    to: string;
    netCents: number;
    tickets: number;
    tipsCents: number;
    avgTicketCents: number | null;
  } | null;
  memo: {
    voids: { count: number; valueCents: number };
    /** CORE1: tabs walked out on in the range and what they owed. Not in net sales. */
    walkouts: {
      count: number;
      owedCents: number;
      legacyFinalized: boolean;
      lateCount: number;
      lateOwedCents: number;
    };
    openTabsCarried: { count: number; totalCents: number };
    pendingPayments: number;
    failedPayments: number;
  };
  tieOut: ReportTieOutDto;
}

export interface SalesItemRowDto {
  menuDrinkId: string | null;
  name: string;
  category: string;
  quantity: number;
  netCents: number;
  shareOfNet: number;
}

export interface SalesCategoryRowDto {
  category: string;
  itemCount: number;
  quantity: number;
  netCents: number;
  shareOfNet: number;
}

export interface SalesByItemReportDto {
  meta: ReportMetaDto;
  rows: SalesItemRowDto[];
  categories: SalesCategoryRowDto[];
  totals: { itemCount: number; quantity: number; netCents: number };
  tieOut: ReportTieOutDto;
}

export interface TipsEmployeeRowDto {
  employeeId: string;
  displayName: string;
  totalCents: number;
  byDay: Record<string, number>;
}

export interface TipsByEmployeeReportDto {
  meta: ReportMetaDto;
  /** False for the self view: only the caller's own tips came back. */
  everyone: boolean;
  employees: TipsEmployeeRowDto[];
  dayTotals: Record<string, number>;
  totalCents: number;
  bestDay: { businessDate: string; cents: number } | null;
  prior: { from: string; to: string; totalCents: number };
  tipsShareOfGross: number | null;
  tieOut: ReportTieOutDto;
}

// RPTLAYOUT1-F1-S2 (owner ruling 2026-10-19, "retire duplicates"): Sales by item, Tips by employee, Stock on hand and value,
// Count variance and Receipts and spend left the location console; the canned reports on the Reports home replace them.
export type ReportId =
  | "sales-by-day"
  | "cost-margin"
  // ABCRPT1: the state ABC spirits used vs drinks sold report.
  | "abc-spirits"
  | "sales-tax"
  | "menu-profitability"
  // PAY2-S2 (DEC-404, DEC-405): this bar's own monthly royalty statement.
  | "royalty-statement"
  // CRB1 (DEC-431): the custom report builder.
  | "custom";

// INV32 (DEC-289): the Stock on hand and value report. Mirrors
// src/domain/stockValueReport.ts. Quantities are plain decimal strings in the
// item's own unit, money is whole cents, and a unit cost is a decimal cents
// string (a sub-cent cost exists).
export type StockValueStateDto = "ok" | "low" | "out" | "mixed";
export type StockValueFilterStateDto = "ok" | "low" | "out" | "flagged86";
export type StockValueSortDto = "category" | "value" | "par";

export interface StockValueItemDto {
  liquorProductId: string;
  name: string;
  category: string;
  categoryKey: string;
  onHand: string | null;
  onHandUnit: string | null;
  ounces: string | null;
  unitCostCents: string | null;
  unitCostPer: string | null;
  valueCents: number | null;
  par: string | null;
  parUnit: string | null;
  state: StockValueStateDto;
  belowPar: boolean;
  belowZero: boolean;
  flagged86: boolean;
  mixedUnits: boolean;
  breakdown: Array<{ unit: string; quantity: string }>;
  /** MGRDAYMAJ1 (manager-day-12): never had stock history at a location still entering starting stock. Not Out, not counted. */
  noStartingStock: boolean;
}

export interface StockValueReportDto {
  meta: { asOf: string; timeZone: string; businessDate: string };
  totals: {
    valueCents: number;
    countedItems: number;
    items: number;
    noStartingStock: number;
    notCounted: { noCost: number; mixedUnits: number };
    belowPar: number;
    belowZero: number;
  };
  byCategory: Array<{
    category: string;
    categoryKey: string;
    items: number;
    valueCents: number;
    notCounted: number;
  }>;
  items: StockValueItemDto[];
}

export interface StockValueQueryDto {
  category?: string;
  state?: StockValueFilterStateDto;
  belowPar?: boolean;
  noCost?: boolean;
  belowZero?: boolean;
  sort?: StockValueSortDto;
}

export function getStockValueReport(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<StockValueReportDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/reports/stock-value`);
}

// INV33 (docs/build-briefs/INV33-usage-and-waste.md; DEC-290): mirrors
// src/domain/usageWasteReport.ts. Quantities are decimal strings in the item's
// own unit (ounces, or units); money is integer cents.
export interface UsageWasteContainersDto {
  start: string | null;
  received: string | null;
  used: string | null;
  wasted: string | null;
  vendorReturned: string | null;
  counts: string | null;
  other: string | null;
  end: string | null;
}

export interface UsageWasteReasonOfItemDto {
  reasonId: string;
  label: string;
  retired: boolean;
  quantity: string;
  entries: number;
  costCents: number | null;
}

export interface UsageWasteItemDto {
  liquorProductId: string;
  name: string;
  category: string;
  unit: "oz" | "unit";
  containerUnit: string | null;
  ouncesPerContainer: string | null;
  mixedUnits: boolean;
  start: string | null;
  received: string | null;
  receiptFixes: string | null;
  receivedNet: string | null;
  used: string | null;
  wasted: string | null;
  vendorReturned: string | null;
  counts: string | null;
  other: {
    startingStock: string | null;
    putBack: string | null;
    reversals: string | null;
    conversions: string | null;
  };
  otherTotal: string | null;
  end: string | null;
  containers: UsageWasteContainersDto | null;
  usedCostCents: number | null;
  wasteCostCents: number | null;
  costState: "costed" | "no_cost" | "no_activity" | "mixed";
  wasteEntries: number;
  wasteByReason: UsageWasteReasonOfItemDto[];
  vendors: string[];
  breakdown: Array<{ unit: string; quantity: string }>;
}

export interface UsageWasteReportDto {
  meta: {
    from: string;
    to: string;
    timeZone: string;
    cutoffLocal: string;
    today: string;
    open: boolean;
    firstRecordOn: string | null;
    updatedAt: string;
  };
  totals: {
    usedOz: string;
    usedUnits: string;
    wastedOz: string;
    wastedUnits: string;
    receivedOz: string;
    receivedUnits: string;
    countsOz: string;
    countsUnits: string;
    countsShortItems: number;
    countsOverItems: number;
    usedCostCents: number;
    wasteCostCents: number;
    wasteEntries: number;
    items: number;
    costedItems: number;
    notCosted: { noCost: number; mixedUnits: number };
    unmatchedUsage: { lines: number; ounces: string; names: string[] };
  };
  byReason: Array<{
    reasonId: string;
    label: string;
    retired: boolean;
    entries: number;
    ounces: string;
    units: string;
    costCents: number | null;
    uncostedEntries: number;
  }>;
  byCategory: Array<{
    category: string;
    items: number;
    usedOz: string;
    usedUnits: string;
    wastedOz: string;
    wastedUnits: string;
    receivedOz: string;
    receivedUnits: string;
    countsOz: string;
    countsUnits: string;
    usedCostCents: number;
    wasteCostCents: number;
  }>;
  items: UsageWasteItemDto[];
}

// INV34 (docs/build-briefs/INV34-cost-and-margin.md; DEC-291): mirrors
// src/domain/costMarginReport.ts. Money is integer cents; percents are basis
// points (1% = 100); quantities are decimal strings.
export type CostMarginDrinkState = "ok" | "partial" | "nocost" | "noparts";

export interface CostMarginPartDto {
  name: string;
  unit: string;
  quantity: string;
  perDrink: string | null;
  containers: string | null;
  costCents: number;
  priced: boolean;
}

export interface CostMarginDrinkDto {
  menuDrinkId: string | null;
  name: string;
  category: string;
  sold: number;
  rungUpCents: number;
  discountCents: number;
  compCents: number;
  memberBenefitCents: number;
  refundCents: number;
  salesCents: number;
  costedSalesCents: number;
  leftOutSalesCents: number;
  costedSold: number;
  state: CostMarginDrinkState;
  costCents: number | null;
  remakeCostCents: number | null;
  remakes: number;
  costPerDrinkCents: number | null;
  marginCents: number | null;
  marginBp: number | null;
  pourCostBp: number | null;
  missingParts: string[];
  untrackedParts: string[];
  priceChangedLines: number;
  parts: CostMarginPartDto[];
}

export interface CostMarginReportDto {
  meta: {
    from: string;
    to: string;
    timeZone: string;
    cutoffLocal: string;
    today: string;
    open: boolean;
    updatedAt: string;
  };
  totals: {
    salesCents: number;
    sold: number;
    rungUpCents: number;
    discountCents: number;
    compCents: number;
    memberBenefitCents: number;
    refundCents: number;
    costedSalesCents: number;
    costCents: number;
    remakeCostCents: number;
    remakes: number;
    marginCents: number;
    marginBp: number | null;
    pourCostBp: number | null;
    drinks: number;
    costedDrinks: number;
    partlyCostedDrinks: number;
    untrackedDrinks: number;
    untrackedPartNames: string[];
    left: { drinks: number; salesCents: number };
  };
  byCategory: Array<{
    category: string;
    drinks: number;
    sold: number;
    salesCents: number;
    costedSalesCents: number;
    costCents: number;
    marginCents: number;
    marginBp: number | null;
    pourCostBp: number | null;
  }>;
  drinks: CostMarginDrinkDto[];
  partlyCosted: CostMarginDrinkDto[];
  leftOut: CostMarginDrinkDto[];
  days: Array<{
    date: string;
    sold: number;
    salesCents: number;
    costedSalesCents: number;
    costCents: number;
    marginCents: number;
    marginBp: number | null;
    pourCostBp: number | null;
  }>;
  actualVsTheoretical: {
    theoreticalCents: number;
    wasteCents: number;
    shortfallCents: number;
    actualCents: number;
    gapCents: number;
    gapBp: number | null;
    itemsCounted: number;
    itemsWithoutCost: number;
    itemsTotal: number;
    tie: {
      matchedCents: number;
      madeNotPaidCents: number;
      madeNotPaidLines: number;
      madeRemovedCents: number;
      madeEarlierCents: number;
      madeLaterCents: number;
      leftOutCents: number;
      roundingCents: number;
      usedCents: number;
    };
  };
}

/** What the on-screen filters ask the server for. The drinks CSV follows the same ones. */
export interface CostMarginQuery {
  locationId?: string;
  category?: string;
  sort?: string;
  onlyBelow?: boolean;
  onlyNoCost?: boolean;
  onlyPartial?: boolean;
  onlyRemade?: boolean;
  onlyDiscounts?: boolean;
}

function costMarginQueryString(query: CostMarginQuery): string {
  const parts: string[] = [];
  if (query.locationId !== undefined)
    parts.push(`locationId=${encodeURIComponent(query.locationId)}`);
  if (query.category !== undefined && query.category !== "") {
    parts.push(`category=${encodeURIComponent(query.category)}`);
  }
  if (query.sort !== undefined && query.sort !== "worst") {
    parts.push(`sort=${encodeURIComponent(query.sort)}`);
  }
  for (const key of [
    "onlyBelow",
    "onlyNoCost",
    "onlyPartial",
    "onlyRemade",
    "onlyDiscounts",
  ] as const) {
    if (query[key] === true) {
      parts.push(`${key}=true`);
    }
  }
  return parts.length === 0 ? "" : `&${parts.join("&")}`;
}

export function getCostMarginReport(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  query: CostMarginQuery,
): Promise<ApiResult<CostMarginReportDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `${reportPath(tenantId, "cost-margin", from, to)}${costMarginQueryString(query)}`,
  );
}

// RPT2 (docs/build-briefs/RPT2-menu-profitability.md; PRD P0.13): mirrors src/domain/menuProfitability.ts. Money is
// integer cents, shares and pour costs are basis points (1% = 100). A null figure is N/A, never 0.
export type MenuProfitabilityQuadrant = "star" | "workhorse" | "puzzle" | "drag";

export interface MenuProfitabilityDrinkDto {
  menuDrinkId: string | null;
  name: string;
  category: string;
  sold: number;
  salesCents: number;
  shareBp: number;
  state: "ok" | "partial" | "nocost" | "noparts";
  partlyCosted: boolean;
  costed: boolean;
  avgPriceCents: number | null;
  costPerDrinkCents: number | null;
  pourCostBp: number | null;
  marginPerDrinkCents: number | null;
  totalMarginCents: number | null;
  quadrant: MenuProfitabilityQuadrant | null;
  onTarget: boolean | null;
  priceToHitTargetCents: number | null;
}

export interface MenuProfitabilityReportDto {
  meta: CostMarginReportDto["meta"] & { locationId?: string };
  target: { pourCostBp: number; isDefault: boolean; minBp: number; maxBp: number; stepBp: number };
  totals: {
    sold: number;
    salesCents: number;
    costedDrinks: number;
    partlyCostedDrinks: number;
    uncostedDrinks: number;
    totalMarginCents: number;
    avgMarginPerDrinkCents: number | null;
  };
  quadrants: Array<{
    quadrant: MenuProfitabilityQuadrant;
    drinks: number;
    totalMarginCents: number;
  }>;
  drinks: MenuProfitabilityDrinkDto[];
}

export interface MenuProfitabilityTargetDto {
  locationId: string;
  pourCostBp: number;
  isDefault: boolean;
  minBp: number;
  maxBp: number;
  stepBp: number;
}

export function getMenuProfitabilityReport(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  locationId?: string,
): Promise<ApiResult<MenuProfitabilityReportDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `${reportPath(tenantId, "menu-profitability", from, to)}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

/** The Menu profitability CSV. The server needs report.export and audits it. */
export function getMenuProfitabilityCsv(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  locationId?: string,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `${reportPath(tenantId, "menu-profitability", from, to)}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}&format=${format}`,
  );
}

export function putMenuProfitabilityTarget(
  baseUrl: string,
  tenantId: string,
  body: { targetPourCostBp: number; locationId?: string },
): Promise<ApiResult<MenuProfitabilityTargetDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/menu-profitability/target`,
    body,
  );
}

// CRB1 (docs/build-briefs/CRB1-custom-report-builder-core.md; docs/design/custom-report-builder-architecture.md): the custom
// report builder, franchisee side. Mirrors src/domain/reportBuilder/. A spec names catalog fields and bound values only; the
// server compiles it, re-checks every field against the session's permissions, and audits the run before the rows return.
export interface CustomReportFieldDto {
  id: string;
  dataset: string;
  name: string;
  label: string;
  kind: "dimension" | "measure";
  type: "money" | "number" | "decimal" | "percent" | "date" | "text" | "boolean" | "enum";
  aggregations: string[];
  sensitivity: string;
  values?: string[];
  /** CRB3: the words to show for an enum value. */
  valueLabels?: Record<string, string>;
  ratio?: { numerator: string; denominator: string; scale?: number };
  /** CRB3: a share measure: a measure's sum over every group's, as a percent. */
  share?: { of: string };
}

export interface CustomReportCatalogDto {
  catalog: {
    catalogVersion: string;
    compositeEnabled?: boolean;
    datasets: Array<{
      id: string;
      label: string;
      grain: string;
      maxRangeDays: number;
      /** False for a snapshot as of the run (Stock on hand): the dates do not apply. */
      dated: boolean;
      fields: CustomReportFieldDto[];
      composite?: { dateBasis: string; dimensions: Array<{ key: string; label: string }> };
    }>;
  };
  locations: Array<{ id: string; name: string }>;
  businessDate: string;
  timezone: string;
  presets: string[];
  limits: { maxRangeDays: number; maxShownRows: number };
}

export type CustomReportCell = string | number | boolean | null;

export interface CustomReportColumnDto {
  key: string;
  label: string;
  kind: "dimension" | "measure";
  field: string;
  agg: string | null;
  type: "text" | "date" | "boolean" | "money" | "number" | "decimal" | "percent";
}

/** CRB3: the same report over the prior range, aligned to the rows: one cell per measure column, in column order. */
export interface CustomReportCompareDto {
  kind: "prior_period" | "same_period_last_year";
  from: string;
  to: string;
  rows: CustomReportCell[][];
  /** Basis points; null where there is no prior to compare with. */
  changeBp: Array<Array<number | null>>;
  totals: CustomReportCell[] | null;
  totalsChangeBp: Array<number | null> | null;
}

export interface CustomReportResultDto {
  meta: {
    title: string;
    dataset: string;
    datasetLabel: string;
    from: string;
    to: string;
    timezone: string;
    timezones: string[];
    locations: Array<{ id: string; name: string }>;
    generatedAt: string;
    catalogVersion: string;
    specHash: string;
    rowCount: number;
    truncated: boolean;
    rowCap: number;
    sources?: Array<{ id: string; dataset: string; dateBasis: string }>;
  };
  columns: CustomReportColumnDto[];
  rows: CustomReportCell[][];
  dimensionLabels?: Array<Array<string | null>>;
  totals: CustomReportCell[] | null;
  spec: unknown;
  compare?: CustomReportCompareDto;
}

export function getCustomReportCatalog(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<CustomReportCatalogDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/reports/custom/catalog`);
}

export function runCustomReport(
  baseUrl: string,
  tenantId: string,
  spec: unknown,
): Promise<ApiResult<CustomReportResultDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/reports/custom/run`, { spec });
}

/** The export of a custom report in any gate format. The server needs report.custom.build and report.export and audits it. */
export async function exportCustomReport(
  baseUrl: string,
  tenantId: string,
  spec: unknown,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  if (authToken === undefined) {
    return { status: 401, blob: undefined, message: "sign in to continue", networkError: false };
  }
  let response: Response;
  try {
    response = await fetch(
      `${baseUrl}/tenants/${encodeURIComponent(tenantId)}/reports/custom/export?format=${format}`,
      {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ spec }),
        cache: "no-store",
      },
    );
  } catch (cause) {
    return {
      status: 0,
      blob: undefined,
      message: cause instanceof Error ? cause.message : String(cause),
      networkError: true,
    };
  }
  if (response.status !== 200) {
    const parsed = (await readJsonBody(response)) as { message?: string } | undefined;
    noteResponse(response.status, parsed ?? {}, "revoked");
    return {
      status: response.status,
      blob: undefined,
      message: parsed?.message,
      networkError: false,
    };
  }
  return { status: 200, blob: await response.blob(), message: undefined, networkError: false };
}

// CRB2 (docs/build-briefs/CRB2-custom-report-saved-reports.md; design 4.1): a franchisee's saved custom reports. Mirrors
// src/domain/reportBuilder/definitions.ts. Visibility is private (the creator) or shared (the franchisee); every version is kept.
export type SavedReportVisibility = "private" | "shared";
export type SavedReportTabId = "all" | "mine" | "scheduled";

export interface SavedReportSummaryDto {
  id: string;
  title: string;
  dataset: string;
  datasetLabel: string;
  visibility: SavedReportVisibility;
  mine: boolean;
  createdBy: { id: string; name: string };
  currentVersion: number;
  editedAt: string;
  editedBy: { id: string; name: string };
  locations: { kind: "all" } | { kind: "list"; names: string[] };
  schedule: { label: string; paused: boolean; nextAt?: string } | null;
}

export interface SavedReportListDto {
  tab: SavedReportTabId;
  items: SavedReportSummaryDto[];
  counts: { all: number; mine: number; scheduled: number };
  permittedLocationCount: number;
}

export interface SavedReportVersionDto {
  cannedOrigin?: CannedOriginDto | null;
  versionNumber: number;
  title: string;
  specHash: string;
  catalogVersion: string;
  source: string;
  savedAt: string;
  savedBy: { id: string; name: string };
}

export interface SavedReportDetailDto extends SavedReportSummaryDto {
  cannedOrigin?: CannedOriginDto | null;
  audience?: ReportAudienceDto;
  spec: Record<string, unknown>;
  specHash: string;
  copiedFrom: { definitionId: string; version: number } | null;
  versions: SavedReportVersionDto[];
  canEdit: boolean;
  canShare: boolean;
  written?: boolean;
}

function savedPath(tenantId: string, leaf = ""): string {
  return `/tenants/${encodeURIComponent(tenantId)}/reports/custom/saved${leaf}`;
}

export function listSavedReports(
  baseUrl: string,
  tenantId: string,
  tab: SavedReportTabId,
  query: string,
): Promise<ApiResult<SavedReportListDto & Partial<ApiErrorBody>>> {
  const params = new URLSearchParams({ tab });
  if (query.trim() !== "") params.set("q", query.trim());
  return getJson(baseUrl, `${savedPath(tenantId)}?${params.toString()}`);
}

export function getSavedReport(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}`));
}

export function getSavedReportVersion(
  baseUrl: string,
  tenantId: string,
  id: string,
  version: number,
): Promise<
  ApiResult<SavedReportVersionDto & { spec: Record<string, unknown> } & Partial<ApiErrorBody>>
> {
  return getJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}/versions/${version}`));
}

export function createSavedReport(
  baseUrl: string,
  tenantId: string,
  spec: unknown,
  visibility: SavedReportVisibility,
  requestId: string,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, savedPath(tenantId), { spec, visibility, requestId });
}

export function saveSavedReportVersion(
  baseUrl: string,
  tenantId: string,
  id: string,
  spec: unknown,
  baseVersion: number,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}/versions`), {
    spec,
    baseVersion,
  });
}

export function listSavedReportAudienceChoices(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ items: Array<{ id: string; name: string }> } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/custom/audience-choices`,
  );
}
export function setSavedReportAudience(
  baseUrl: string,
  tenantId: string,
  id: string,
  audience: ReportAudienceSave,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return patchJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}`), { audience });
}

export function setSavedReportVisibility(
  baseUrl: string,
  tenantId: string,
  id: string,
  visibility: SavedReportVisibility,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return patchJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}`), { visibility });
}

export function archiveSavedReport(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<{ id: string; archived: true } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}/archive`), {});
}

export function copySavedReport(
  baseUrl: string,
  tenantId: string,
  id: string,
  requestId: string,
): Promise<ApiResult<SavedReportDetailDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, savedPath(tenantId, `/${encodeURIComponent(id)}/copy`), { requestId });
}

export function runSavedReport(
  baseUrl: string,
  tenantId: string,
  id: string,
  version?: number,
): Promise<ApiResult<CustomReportResultDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    savedPath(tenantId, `/${encodeURIComponent(id)}/run`),
    version === undefined ? {} : { version },
  );
}

/** The export of a saved custom report in any gate format. The server needs report.custom.view and report.export. */
export async function exportSavedReport(
  baseUrl: string,
  tenantId: string,
  id: string,
  version: number | undefined,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  if (authToken === undefined) {
    return { status: 401, blob: undefined, message: "sign in to continue", networkError: false };
  }
  let response: Response;
  try {
    response = await fetch(
      `${baseUrl}${savedPath(tenantId, `/${encodeURIComponent(id)}/export`)}?format=${format}`,
      {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(version === undefined ? {} : { version }),
        cache: "no-store",
      },
    );
  } catch (cause) {
    return {
      status: 0,
      blob: undefined,
      message: cause instanceof Error ? cause.message : String(cause),
      networkError: true,
    };
  }
  if (response.status !== 200) {
    const parsed = (await readJsonBody(response)) as { message?: string } | undefined;
    noteResponse(response.status, parsed ?? {}, "revoked");
    return {
      status: response.status,
      blob: undefined,
      message: parsed?.message,
      networkError: false,
    };
  }
  return { status: 200, blob: await response.blob(), message: undefined, networkError: false };
}

/** The Cost and margin CSVs: the drinks file, or `days` for the by-day file. */
export function getCostMarginCsv(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  query: CostMarginQuery,
  file: "drinks" | "days",
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  const days = file === "days" && format === "csv";
  return getBinaryFile(
    baseUrl,
    `${reportPath(tenantId, "cost-margin", from, to)}${days ? (query.locationId === undefined ? "" : `&locationId=${encodeURIComponent(query.locationId)}`) : costMarginQueryString(query)}` +
      `${days ? "&export=days" : ""}&format=${format}`,
  );
}

// INV35-A1 (docs/build-briefs/INV35-A-count-variance.md; DEC-292): mirrors
// src/domain/countVarianceReport.ts. Quantities are decimal strings in the
// item's own unit (ounces, or units); money is integer cents. Every count
// carries the STORED expected and difference.
export interface CountVarianceContainersDto {
  last: string | null;
  received: string | null;
  used: string | null;
  wasted: string | null;
  returned: string | null;
  other: string | null;
  expected: string;
  counted: string;
  difference: string;
}

export type CountStatusDto = "short" | "over" | "ok" | "first";

export interface CountVarianceItemCountDto {
  sessionId: string;
  closedBusinessDate: string;
  closedAt: string;
  cutoffAt: string;
  inRange: boolean;
  blind: boolean;
  modeChanged: boolean;
  counterName: string;
  first: boolean;
  overlapped: boolean;
  countUnit: string;
  last: string | null;
  received: string | null;
  used: string | null;
  wasted: string | null;
  returned: string | null;
  other: string | null;
  expected: string;
  counted: string;
  difference: string;
  unexplained: string | null;
  status: CountStatusDto;
  costCents: number | null;
  containers: CountVarianceContainersDto | null;
}

export interface CountVarianceItemDto {
  liquorProductId: string;
  name: string;
  category: string;
  unit: "oz" | "unit";
  containerUnit: string | null;
  ouncesPerContainer: string | null;
  mixedUnits: boolean;
  state: "compared" | "first" | "notCounted" | "never";
  countsInRange: number;
  blindCounts: number;
  hasFirstCountInRange: boolean;
  lastCountedOn: string | null;
  counts: CountVarianceItemCountDto[];
  net: string | null;
  netContainers: string | null;
  shortTotal: string | null;
  shortContainers: string | null;
  overTotal: string | null;
  overContainers: string | null;
  shortCounts: number;
  overCounts: number;
  netCents: number | null;
  shortCents: number | null;
  overCents: number | null;
  countsChanged: string | null;
  streak: number;
  trend: Array<{
    sessionId: string;
    closedBusinessDate: string;
    difference: string;
    containers: string | null;
    status: CountStatusDto;
  }>;
  blindShort: boolean;
  lastOver: boolean;
  theoreticalCents: number | null;
  wasteCents: number | null;
  actualCents: number | null;
  gapBasisPoints: number | null;
  costState: "costed" | "no_cost" | "none";
}

export interface CountVarianceCountRowDto {
  sessionId: string;
  closedBusinessDate: string;
  closedAt: string;
  cutoffAt: string;
  blind: boolean;
  modeChanged: boolean;
  counterName: string;
  scopeLabel: string;
  itemsCounted: number;
  itemsActive: number;
  firstCounts: number;
  overlappedItems: number;
  shortCents: number;
  overCents: number;
  uncostedItems: number;
}

export interface CountVarianceReportDto {
  meta: {
    from: string;
    to: string;
    timeZone: string;
    cutoffLocal: string;
    today: string;
    open: boolean;
    firstCountOn: string | null;
    openCounts: { count: number; latestStartedAt: string | null; latestCounterName: string | null };
    trendLooksBackPastRange: true;
    updatedAt: string;
  };
  totals: {
    shortCents: number;
    overCents: number;
    shortOz: string;
    shortUnits: string;
    overOz: string;
    overUnits: string;
    shortContainers: string;
    overContainers: string;
    itemsShort: number;
    itemsOver: number;
    streakItems: number;
    longestStreak: { name: string; streak: number } | null;
    countsClosed: number;
    itemsCounted: number;
    itemsTotal: number;
    blindCounts: number;
    firstCounts: { items: number; oz: string; units: string };
    overlappedCounts: number;
    costedItems: number;
    notCosted: { noCost: number; mixedUnits: number };
  };
  counts: CountVarianceCountRowDto[];
  items: CountVarianceItemDto[];
  tie: {
    shortOz: string;
    shortUnits: string;
    overOz: string;
    overUnits: string;
    firstOz: string;
    firstUnits: string;
    overlapOz: string;
    overlapUnits: string;
    netOz: string;
    netUnits: string;
  };
}

/** What the on-screen filters ask the server for. The CSV follows the same ones. */
export interface CountVarianceQuery {
  locationId?: string;
  category?: string;
  sort?: string;
  sessionId?: string;
  onlyStreak?: boolean;
  onlyShort?: boolean;
  onlyOver?: boolean;
  onlyBlind?: boolean;
  onlyFirst?: boolean;
  onlyNotCounted?: boolean;
  onlyNoCost?: boolean;
}

// INV35-B1 (DEC-292): the Receipts and spend report. Mirrors
// src/domain/receiptsSpendReport.ts. Money is whole cents, a price an ounce is
// a decimal cents string, quantities are plain decimal strings.
export interface ReceiptCorrectionDto {
  id: string;
  madeAt: string;
  madeBusinessDate: string;
  receiptId: string;
  by: string | null;
  quantityDelta: string;
  containerCountDelta: string | null;
  newPriceCents: number | null;
  reason: string;
  effectCents: number | null;
}

export interface ReceiptsDeliveryDto {
  receiptId: string;
  receivedAt: string;
  keyedAt: string;
  receivedBusinessDate: string;
  vendorId: string | null;
  vendorName: string | null;
  /** INV35-B1-F1: the vendor was picked when the delivery was received, with no purchase order. */
  vendorPickedWithoutOrder: boolean;
  /** False when the vendor is turned off today, null with no vendor. */
  vendorActive: boolean | null;
  purchaseOrderId: string | null;
  orderPlacedAt: string | null;
  liquorProductId: string;
  name: string;
  category: string;
  unit: string;
  quantityAsDelivered: string;
  quantity: string;
  containerLabel: string | null;
  containersAsDelivered: string | null;
  containers: string | null;
  ounces: string | null;
  containerReceipt: boolean;
  priced: boolean;
  priceBottleCents: number | null;
  pricePerOunceCents: string | null;
  priceBottleAfterCents: number | null;
  pricePerOunceAfterCents: string | null;
  spendAsDeliveredCents: number | null;
  spendCents: number | null;
  correctionsEffectCents: number;
  receivedBy: string | null;
  note: string | null;
  substitutedFor: string | null;
  priceWentUp: boolean;
  priceUpBasisPoints: number | null;
  corrections: ReceiptCorrectionDto[];
  averageBeforeCents: number | null;
  averageAfterCents: number | null;
}

export interface ReceiptsVendorDto {
  vendorId: string | null;
  name: string;
  /** False when the vendor is turned off today (INV35-B1-F1), null for No vendor recorded. */
  active: boolean | null;
  deliveries: number;
  /** Of `deliveries`, how many were entered without a purchase order with this vendor picked. */
  noOrderDeliveries: number;
  lines: number;
  items: number;
  spentCents: number;
  shareBasisPoints: number;
  correctionsCents: number;
  noPriceLines: number;
}

export interface CostHistoryPointDto {
  at: string;
  businessDate: string;
  cause: "delivery" | "correction" | "conversion";
  priceChanged: boolean;
  vendorName: string | null;
  priceCents: number | null;
  bottles: string | null;
  averageBeforeCents: number | null;
  averageAfterCents: number | null;
}

export interface CostHistoryItemDto {
  liquorProductId: string;
  name: string;
  category: string;
  containerLabel: string | null;
  ouncesPerContainer: string | null;
  nowCents: number | null;
  nowPerOunceCents: string | null;
  startCents: number | null;
  changeCents: number | null;
  changeBasisPoints: number | null;
  costUp: boolean;
  costDown: boolean;
  priceCorrected: boolean;
  noPriceYet: boolean;
  lastDelivery: { priceCents: number; businessDate: string } | null;
  deliveries: number;
  trendCents: number[];
  points: CostHistoryPointDto[];
}

export interface ReceiptsCorrectionRowDto extends ReceiptCorrectionDto {
  liquorProductId: string;
  name: string;
  category: string;
  vendorName: string | null;
  receivedAt: string;
  receivedBusinessDate: string;
  priceBeforeCents: number | null;
}

export interface ReceiptsSpendReportDto {
  meta: {
    from: string;
    to: string;
    timeZone: string;
    cutoffLocal: string;
    today: string;
    open: boolean;
    firstRecordOn: string | null;
    updatedAt: string;
  };
  totals: {
    spentCents: number;
    spendAsDeliveredCents: number;
    correctionsCents: number;
    correctionCount: number;
    deliveries: number;
    lines: number;
    items: number;
    bottles: string;
    units: string;
    ounces: string;
    noPriceLines: number;
    noVendorLines: number;
    costsUp: number;
    biggestCostUp: { liquorProductId: string; name: string; changeBasisPoints: number } | null;
    priceUpLines: number;
  };
  vendors: ReceiptsVendorDto[];
  deliveries: ReceiptsDeliveryDto[];
  deliveriesInRange: number;
  costHistory: CostHistoryItemDto[];
  costItemsInRange: number;
  corrections: ReceiptsCorrectionRowDto[];
}

/** What the on-screen filters ask the server for. Each CSV follows its own table's filters. */
export interface ReceiptsSpendQuery {
  locationId?: string;
  vendorId?: string;
  sort?: string;
  onlyCorrected?: boolean;
  onlyPriceUp?: boolean;
  onlyNoPrice?: boolean;
  onlySubstituted?: boolean;
  category?: string;
  costSort?: string;
  onlyCostUp?: boolean;
  onlyCostDown?: boolean;
  onlyCostCorrected?: boolean;
  onlyCostNone?: boolean;
}

/** TAX2-S2, TAX2-S2-F1: the Sales tax worksheet. `period` is exactly one of `{ month }`, `{ quarter }`, `{ year }` or `{ from, to }`
 * (ui/src/salesTaxWorksheetModel.ts periodQuery); with none the server answers the last full period of the location's saved filing
 * frequency. `basis` is the date a sale is counted by; with none the server uses the location's saved one. */
export function getSalesTaxWorksheetReport(
  baseUrl: string,
  tenantId: string,
  query: {
    period?: Record<string, string>;
    basis?: "business_date" | "calendar_date";
    locationId?: string;
  },
): Promise<ApiResult<SalesTaxReportDto & Partial<ApiErrorBody>>> {
  const parts = Object.entries(query.period ?? {}).map(
    ([name, value]) => `${name}=${encodeURIComponent(value)}`,
  );
  if (query.basis !== undefined) parts.push(`basis=${encodeURIComponent(query.basis)}`);
  if (query.locationId !== undefined)
    parts.push(`locationId=${encodeURIComponent(query.locationId)}`);
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/sales-tax${parts.length === 0 ? "" : `?${parts.join("&")}`}`,
  );
}

// ABCRPT1: mirrors src/domain/abcSpiritsReport.ts. Quantities are bottles as decimal strings; a figure with `missing` set has
// no value and says why.
export interface AbcFigureDto {
  ounces: string | null;
  bottles: string | null;
  missing: string | null;
}
export type AbcAgreementDto = "agree" | "differ" | "unknown";
export interface AbcSpiritsRowDto {
  liquorProductId: string;
  brand: string;
  itemName: string;
  sizeLabel: string | null;
  ouncesPerBottle: string | null;
  beginning: AbcFigureDto;
  purchased: AbcFigureDto;
  usedComputed: AbcFigureDto;
  lostOrBroken: AbcFigureDto;
  otherWaste: AbcFigureDto;
  ending: AbcFigureDto;
  usedCounted: AbcFigureDto;
  difference: AbcFigureDto;
  agreement: AbcAgreementDto;
}
export interface AbcSizeRowDto {
  sizeLabel: string | null;
  items: number;
  beginning: AbcFigureDto;
  purchased: AbcFigureDto;
  usedComputed: AbcFigureDto;
  lostOrBroken: AbcFigureDto;
  ending: AbcFigureDto;
  usedCounted: AbcFigureDto;
  difference: AbcFigureDto;
  agreement: AbcAgreementDto;
}
export interface AbcSpiritsReportDto {
  meta: {
    month: string;
    from: string;
    to: string;
    partialMonth: boolean;
    timeZone: string;
    today: string;
    locationId: string;
    locationName: string;
    updatedAt: string;
    countWindowDays: number;
  };
  profile: {
    stateCode: string;
    stateName: string;
    formName: string;
    recordsName: string;
    retentionYears: number;
    fields: {
      averageDrinkPrice: string;
      averageShotSize: string;
      beginning: string;
      purchased: string;
      used: string;
      lostStolenBroken: string;
      ending: string;
    };
  };
  rows: AbcSpiritsRowDto[];
  sizes: AbcSizeRowDto[];
  figures: {
    drinksSold: number;
    drinkSalesCents: number;
    averageDrinkPriceCents: number | null;
    averageDrinkPriceMissing: string | null;
    pours: number;
    pouredOunces: string;
    averageShotOunces: string | null;
    averageShotMissing: string | null;
  };
  notes: {
    itemsWithoutActivity: number;
    mixedUnitItems: string[];
    unmatchedUsageOunces: string;
    itemsWithoutSize: number;
  };
}

/** ABCRPT1: one location's month. `month` left out is the last full month. */
export function getAbcSpiritsReport(
  baseUrl: string,
  tenantId: string,
  query: { month?: string; locationId?: string },
): Promise<ApiResult<AbcSpiritsReportDto & Partial<ApiErrorBody>>> {
  const parts: string[] = [];
  if (query.month !== undefined) parts.push(`month=${encodeURIComponent(query.month)}`);
  if (query.locationId !== undefined)
    parts.push(`locationId=${encodeURIComponent(query.locationId)}`);
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/abc-spirits${parts.length === 0 ? "" : `?${parts.join("&")}`}`,
  );
}

/** ABCRPT1: the month as a file through the export gate (CSV, Excel, PDF). */
export function getAbcSpiritsFile(
  baseUrl: string,
  tenantId: string,
  month: string,
  locationId: string | undefined,
  format: GateExportFormat = "csv",
  sheet?: "size" | "figures",
): Promise<BinaryFileResult> {
  const parts = [`month=${encodeURIComponent(month)}`, `format=${format}`];
  if (sheet !== undefined && format === "csv") parts.push(`export=${sheet}`);
  if (locationId !== undefined) parts.push(`locationId=${encodeURIComponent(locationId)}`);
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/abc-spirits?${parts.join("&")}`,
  );
}

/** The worksheet file for a period (the same four forms as the screen); it carries both date bases, so no basis is sent. */
export function getSalesTaxWorksheetCsv(
  baseUrl: string,
  tenantId: string,
  period: Record<string, string>,
  locationId?: string,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  const parts = Object.entries(period).map(
    ([name, value]) => `${name}=${encodeURIComponent(value)}`,
  );
  parts.push(`format=${format}`);
  if (locationId !== undefined) parts.push(`locationId=${encodeURIComponent(locationId)}`);
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/sales-tax?${parts.join("&")}`,
  );
}

/** TAX2-S2-F1: saves the location's filing frequency and/or date basis (admin.tax_rate.manage). Either field may be left out. */
export function putSalesTaxFilingSettings(
  baseUrl: string,
  tenantId: string,
  input: {
    filingFrequency?: "monthly" | "quarterly" | "annual";
    dateBasis?: "business_date" | "calendar_date";
    locationId?: string;
  },
): Promise<
  ApiResult<
    {
      settings: {
        locationId: string;
        frequency: "monthly" | "quarterly" | "annual";
        dateBasis: "business_date" | "calendar_date";
      };
    } & Partial<ApiErrorBody>
  >
> {
  return putJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tax-filing-settings`, input);
}

/** PAY2-S2: this location's own royalty statement for a month ("YYYY-MM"; none asks for the last full month). `statement` is
 * null for a location that is not a franchise location. */
export function getRoyaltyStatement(
  baseUrl: string,
  tenantId: string,
  query: { month?: string; locationId?: string },
): Promise<ApiResult<{ statement?: RoyaltyStatementDto | null } & Partial<ApiErrorBody>>> {
  const parts: string[] = [];
  if (query.month !== undefined) parts.push(`month=${encodeURIComponent(query.month)}`);
  if (query.locationId !== undefined)
    parts.push(`locationId=${encodeURIComponent(query.locationId)}`);
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/royalty-statement${parts.length === 0 ? "" : `?${parts.join("&")}`}`,
  );
}

/** PAY2-S8: this location's royalty ledger (charges, adjustments, statement versions, disputes). `ledger` is null for a location that
 * is not a franchise location. */
export function getRoyaltyCharges(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<ApiResult<{ ledger?: unknown } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/royalty-charges${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

/** PAY2-S8: the royalty charges file in the chosen format. */
export function getRoyaltyChargesFile(
  baseUrl: string,
  tenantId: string,
  format: GateExportFormat,
  sheet?: string,
  locationId?: string,
  month?: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/royalty-charges?format=${format}${sheet === undefined ? "" : `&export=${encodeURIComponent(sheet)}`}${month === undefined ? "" : `&month=${encodeURIComponent(month)}`}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

/** PAY2-S8: a location's Admin disputes one of its own royalty charges. */
export function postRoyaltyDispute(
  baseUrl: string,
  tenantId: string,
  chargeId: string,
  body: { reason: string; note?: string },
): Promise<ApiResult<{ dispute?: { id: string; status: string } } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/royalty-charges/${encodeURIComponent(chargeId)}/dispute`,
    body,
  );
}

export function getReportContext(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<ApiResult<ReportContextDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/context${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

/** EXP1-S6: what the Export menu needs to know about this deployment. The client id is PUBLIC; null means the Google
 * Sheet export is off. */
export function getExportOptions(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ googleSheetsClientId: string | null } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/export-options`);
}

/** Locations this person may select in closeout and money reports. */
export function getReportLocations(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ locations: PlanningLocationDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/reports/locations`);
}

function reportPath(
  tenantId: string,
  report: ReportId | "tips-by-employee",
  from: string,
  to: string,
): string {
  return (
    `/tenants/${encodeURIComponent(tenantId)}/reports/${report}` +
    `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
  );
}

export function getSalesByDayReport(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  locationId?: string,
): Promise<ApiResult<SalesByDayReportDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `${reportPath(tenantId, "sales-by-day", from, to)}&compare=prior${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export function getDiscountKindReport(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  locationId?: string,
): Promise<ApiResult<DiscountKindReportDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/discounts?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
      (locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`),
  );
}

/** EXP1-S4. The discount card as an audited file; needs report.sales_by_day.view and report.export. */
export function getDiscountKindFile(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
  format: GateExportFormat,
  locationId?: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/reports/discounts?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&format=${format}` +
      (locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`),
  );
}

export function getTipsByEmployeeReport(
  baseUrl: string,
  tenantId: string,
  from: string,
  to: string,
): Promise<ApiResult<TipsByEmployeeReportDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, reportPath(tenantId, "tips-by-employee", from, to));
}

/** The CSV download. The server audits it and needs report.export; the file
 * comes back as a Blob, saved by an object-URL anchor like the purchase
 * order documents (the request carries the bearer token, which a plain link
 * could not). */
export function getReportCsv(
  baseUrl: string,
  tenantId: string,
  report: ReportId,
  from: string,
  to: string,
  locationId?: string,
  format: GateExportFormat = "csv",
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `${reportPath(tenantId, report, from, to)}&format=${format}${locationId === undefined ? "" : `&locationId=${encodeURIComponent(locationId)}`}`,
  );
}

// CL6 (DEC-010, P0.15): finalize a business day. Gated server-side by
// report.closeout.finalize; never blocks on open tabs (they are recorded
// as carried). 409 with code business_day_not_over or
// business_day_already_finalized on the two refusals.
export function finalizeBusinessDay(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    businessDate: string;
    locationId?: string;
    /** PAY1-S4-F2-F8-F5 slice 4: the settlement difference the screen showed, with the picked reason. */
    settlementDeclaration?: {
      reasonId: string;
      capturedDiffCents: number;
      refundedDiffCents: number;
    };
  },
): Promise<
  ApiResult<{ finalization: CloseoutFinalizationDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/closeout-finalizations`, {
    idempotencyKey: params.idempotencyKey,
    businessDate: params.businessDate,
    ...(params.locationId === undefined ? {} : { locationId: params.locationId }),
    ...(params.settlementDeclaration === undefined
      ? {}
      : { settlementDeclaration: params.settlementDeclaration }),
  });
}

export function closeOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    {
      closeOperation: OrderCloseOperationDto;
      order: OrderDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/close`,
    params,
  );
}

/** CORE2. POST .../orders/:orderId/cancel (permission pos.tab.cancel): takes a ticket with no active line and no payment
 * off Open Tabs, recorded with the actor and the time. */
export function cancelOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ order: OrderDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/cancel`,
    params,
  );
}

export interface OrderTipResolutionDto {
  id: string;
  orderId: string;
  resolution: string;
  resolvedByUserId: string;
  resolvedAt: string;
}

// A3 (docs/decisions-awaiting-owner.md, ANSWERED 2026-08-29): the floor's
// explicit no-tip action. Only reachable on a ticket already at a zero
// balance whose tip question is still unaddressed; the server refuses
// (409) otherwise, the same OrderBalanceOutstandingError shape Close
// itself raises. settlementClose carries "closed" here far more often
// than any other command's own settlementClose does, because resolving
// the tip is usually the ONLY thing standing between this ticket and
// closing.
export function resolveNoTip(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    {
      resolution: OrderTipResolutionDto;
      order: OrderDto;
      replayed: boolean;
      settlementClose?: SettlementCloseDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/resolve-no-tip`,
    params,
  );
}

// ---------------------------------------------------------------------
// SIG1 (docs/build-briefs/SIG1-signature-and-receipt.md): the touch-drawn
// signature and DEC-016's printed/emailed/none receipt choice, both fired
// at the moment a ticket closes (DEC-016's own "prompt at closeout"). Raw
// server rows, snake_case, per this file's own header convention.
// ---------------------------------------------------------------------

export interface SignatureStrokeDto {
  points: { x: number; y: number }[];
}

export interface PaymentSignatureDto {
  id: string;
  tenant_id: string;
  order_id: string;
  payment_attempt_id: string | null;
  strokes: SignatureStrokeDto[];
  captured_by_user_id: string;
  captured_at: string;
}

export function captureSignature(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; paymentAttemptId?: string; strokes: SignatureStrokeDto[] },
): Promise<
  ApiResult<{ signature: PaymentSignatureDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/signature`,
    params,
  );
}

export type ReceiptDeliveryMethodDto = "printed" | "emailed" | "none";

export interface ReceiptDeliveryDto {
  id: string;
  tenant_id: string;
  order_id: string;
  payment_attempt_id: string | null;
  /** REFUNDRECEIPT1. Set when this receipt is for a refund. */
  refund_id?: string | null;
  delivery_method: ReceiptDeliveryMethodDto;
  recipient_email: string | null;
  /** RCPT2: derived from the attempts for an emailed choice. sent only once the provider accepted. */
  delivery_status: ReceiptDeliveryStatusDto;
  /** RCPT2: the newest attempt for an emailed choice; null for printed and none. */
  email?: ReceiptEmailStateDto | null;
  recorded_by_user_id: string;
  recorded_at: string;
}

export type ReceiptDeliveryStatusDto =
  "not_applicable" | "pending_delivery" | "sending" | "sent" | "failed";

export interface ReceiptEmailStateDto {
  status: "sending" | "sent" | "failed" | null;
  reason: string | null;
  attemptCount: number;
  lastAddress: string | null;
  lastAttemptAt: string | null;
}

export type ReceiptChoiceKeyDto = "printed" | "emailed" | "none";
export type ReceiptModeKey = "printed_and_email" | "email_only" | "ask";

export interface ClientReceiptEmailContextDto {
  identity: string;
  revision: number;
  canManage: boolean;
}
export interface ClientReceiptEmailDto extends ClientReceiptEmailContextDto {
  suppressed?: boolean;
  email: string | null;
  masked: string | null;
}
export interface SaveClientReceiptEmailPreference {
  saveClientEmail?: boolean;
  clientEmailIdentity?: string;
  clientEmailRevision?: number;
}
export interface ReceiptOptionsDto {
  mode: ReceiptModeKey;
  choices: Array<{ key: ReceiptChoiceKeyDto; label: string }>;
  defaultChoice: ReceiptChoiceKeyDto | null;
  /** RCPT2-F5: the masked address an Emailed choice goes to without asking, or null when the sheet asks. */
  knownClientEmail?: { masked: string } | null;
  clientEmail?: ClientReceiptEmailContextDto | null;
}

/** RCPT2: the choices this ticket's location offers for a receipt (Email only offers no Printed). RCPT2-F5: for the payment named. */
export function getReceiptOptions(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  paymentAttemptId?: string,
  forRefund = false,
): Promise<ApiResult<ReceiptOptionsDto & Partial<ApiErrorBody>>> {
  const parts = [
    ...(paymentAttemptId === undefined
      ? []
      : [`paymentAttemptId=${encodeURIComponent(paymentAttemptId)}`]),
    ...(forRefund ? ["forRefund=true"] : []),
  ];
  const query = parts.length === 0 ? "" : `?${parts.join("&")}`;
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-options${query}`,
  );
}

/** RCPT2: the receipt choices recorded on a ticket, for a manager (money.signature.view). */
export function getTicketReceipts(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ receipts: ReceiptDeliveryDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/signature-and-receipt`,
  );
}

/** RCPT2: resend an emailed receipt, to a corrected address when one is given (money.receipt.resend). */
export function resendReceipt(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  receiptId: string,
  params: { idempotencyKey: string; recipientEmail?: string },
): Promise<
  ApiResult<
    {
      receipt: ReceiptDeliveryDto | null;
      attempt: { outcome: string | null; reason: string | null };
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipts/${encodeURIComponent(receiptId)}/resend`,
    params,
  );
}

export interface ReceiptSettingDto {
  locationId: string;
  mode: ReceiptModeKey;
  modes: Array<{ key: ReceiptModeKey; label: string }>;
  choices: Array<{ key: ReceiptChoiceKeyDto; label: string }>;
  defaultChoice: ReceiptChoiceKeyDto | null;
}

export function getReceiptSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<ReceiptSettingDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/receipt-settings`,
  );
}

export function putReceiptSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: { mode: ReceiptModeKey },
): Promise<ApiResult<ReceiptSettingDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/receipt-settings`,
    body,
  );
}

export function recordReceiptChoice(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: SaveClientReceiptEmailPreference & {
    idempotencyKey: string;
    paymentAttemptId?: string;
    /** REFUNDRECEIPT1. A Succeeded refund of this ticket; paymentAttemptId then names its payment. */
    refundId?: string;
    deliveryMethod: ReceiptDeliveryMethodDto;
    recipientEmail?: string;
  },
): Promise<ApiResult<{ receipt: ReceiptDeliveryDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-choice`,
    params,
  );
}

// ---------------------------------------------------------------------
// ALPHA-11 tenant settings: the manager console's Location settings
// screen. See src/domain/tenantSettings.ts's module header for the
// precedence rule (stored row wins once bootstrapped; env is bootstrap
// only). Every dollar figure below is provisional, per DEC-004 (tax) and
// DEC-006 (rounding) staying open; the response's own `provisional: true`
// is the same label price-quote and payment-preview already carry.
// ---------------------------------------------------------------------

export interface ShelfTierSettingDto {
  tier: string;
  name: string;
  priceCents: number;
  /** B7-S3. Whether this tier can still be assigned to a new item; a
   * retired (false) tier keeps pricing anything already assigned to it
   * (DEC-128 Ruling 1). */
  active: boolean;
}

// Inventory track Slice 3, Amendment 2026-08-22 (thirteenth). Mirrors
// src/domain/tenantSettings.ts's PRICING_MODELS exactly, the same
// "TypeScript list plus a matching SQL CHECK" shape ITEM_KINDS/
// CONTAINER_TYPES above already use.
export const PRICING_MODELS = ["per_ounce", "shelf"] as const;
export type PricingModel = (typeof PRICING_MODELS)[number];

// VOC1-F9 (docs/build-briefs/VOC1-F9-price-rounding-mode-location-
// configurable.md Section 2.1). Hand-mirrored against src/domain/
// tenantSettings.ts's own ROUNDING_MODES, the identical "kept in sync by
// hand since one is a browser-bundled DTO layer and the other a
// database-backed validator with no shared import between them"
// relationship PRICING_MODELS above already has with its own domain twin.
export const ROUNDING_MODES = ["half-up-cent"] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

export interface TenantSettingsDto {
  taxRateBasisPoints: number;
  /** VOC1-F9: real, editable settings field now (PATCH .../settings and
   * POST .../settings/save both accept it). "half-up-cent" is the only
   * value ROUNDING_MODES/the database CHECK constraint accept today. */
  roundingMode: string;
  tipPresetsBasisPoints: number[];
  shelfTiers: ShelfTierSettingDto[];
  /** Payments-track Slice 4 (DEC-002). Null until a manager sets a
   * placeholder value here (the approval record's own rule for a new
   * location with no sales history yet); a tab may only be held once
   * this is set. */
  holdAmountCents: number | null;
  /** DEC-002 Section 3's own configurable threshold; 8000 (80%) by
   * default. */
  holdIncrementThresholdBasisPoints: number;
  /** PD2. PROVISIONAL defaults 2000 (20%) and 2500 ($25.00). */
  discountApprovalPercentBasisPoints: number;
  discountApprovalAmountCents: number;
  /** CARDCLOSE1 (DEC-555). True (the default) captures and closes card tickets at the end of the business day. */
  closeCardTicketsAtDayEnd: boolean;
  /** DUALPRICE1. Off by default. dualPricingBasisPoints is the card price difference (270 = 2.70%), 0 to 400. */
  dualPricingEnabled: boolean;
  dualPricingBasisPoints: number;
  /** TIPFULL1. True suggests tips on the reader on the price before discounts; false (the default) is today's behavior. */
  tipOnPreDiscountPrice: boolean;
  /** DEC-002 Section 4's own flagged, held-back value; 0 by default,
   * and rejected above 0 unless abandonedTabGratuityDisclosureConfirmed
   * is true (src/domain/tenantSettings.ts's own disclosure-gate check). */
  abandonedTabGratuityBasisPoints: number;
  abandonedTabGratuityDisclosureConfirmed: boolean;
  /** Inventory track Slice 3, Amendment 2026-08-22 (thirteenth). One of
   * "per_ounce"/"shelf"; governs neat/rocks pour and draft-beer pricing.
   * See src/domain/tenantSettings.ts's PRICING_MODELS. */
  pricingModel: string;
  /** B5 (docs/backlog.md; owner ruling 2026-08-27, refined 2026-09-01):
   * "one pour size per location, no exceptions, no per-bottle override."
   * Decimal-ounces string (e.g. "1.75"). Read ONLY for a straight liquor
   * pour (saleKind "pour") when pricingModel above is "shelf"; governs
   * depletion, never price. Does not apply to beer, wine, or a
   * cocktail's own recipe-driven liquor-slot depletion. */
  standardPourOunces: string;
  /** Auth track: pin-policy-proposal.md item 5. Minutes of inactivity
   * before a session signs itself out. Settings-rebuild SET1/SET2/SET3:
   * this field always came back from the server; only the Operations
   * group editor is new. */
  sessionInactivityMinutes: number;
  /** PIN policy, per tenant (migration 0043). */
  pinMinLength: number;
  pinMaxLength: number;
  pinLockoutAttempts: number;
  pinLockoutMinutes: number;
  /** Payments-track cutover checklist item 4. "HH:MM" in 24-hour local
   * time; when the business day rolls over for closeout. */
  businessDayCutoffTimeLocal: string;
  /** The IANA time zone name businessDayCutoffTimeLocal is measured
   * against. */
  businessDayTimezone: string;
  /** CL6-F1-F3 (DEC-286). When a manager confirmed the time zone and cutoff
   * (an ISO instant), and the confirming person's display name; both null
   * until confirmed (the name is also null for a confirmation made by the
   * migration backfill). Live card payments are refused while null. */
  businessDayConfirmedAt: string | null;
  businessDayConfirmedByName: string | null;
  /** ONB1. The sales tax rate twin of the two fields above: when an admin confirmed or recorded the rate and who;
   * both null until then (the name is also null for a confirmation made by the migration backfill). Live card
   * payments are refused while the date is null. */
  taxRateConfirmedAt: string | null;
  taxRateConfirmedByName: string | null;
  /** PAY-LOC1 (docs/backlog.md) review finding. Needed only so a real
   * Stripe Terminal Location can be created; every field null until an
   * admin enters it. */
  locationAddress: LocationAddressDto;
  /** PAY-LOC1-F1 (docs/backlog.md). Present only on a PATCH response, and
   * only when the local address save succeeded but the card-reader
   * location at the payment provider either could not be updated (retry
   * by saving again) or was deliberately left unchanged (an incomplete
   * address is never pushed to the provider). A plain, already-authored
   * message; never a raw error. Absent for every other outcome. */
  locationAddressSyncWarning?: string;
}

export interface LocationAddressDto {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string;
}

export function getSettings(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<
  ApiResult<
    {
      settings: TenantSettingsDto;
      provisional: true;
      sellingContext?: LocationSellingContextDto;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/settings${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export type LocationSettingKey =
  | "pricing_model"
  | "standard_pour_ounces"
  | "hold_amount_cents"
  | "purchase_approval_threshold_cents"
  | "business_day_cutoff_time_local"
  | "business_day_timezone"
  | "tip_presets_basis_points"
  | "discount_approval_percent_basis_points"
  | "discount_approval_amount_cents"
  | "close_card_tickets_at_day_end"
  | "dual_pricing_enabled"
  | "dual_pricing_basis_points"
  | "tip_on_pre_discount_price";

export interface LocationSettingsDto {
  locationId: string;
  taxRateBasisPoints: number;
  pricingModel: string;
  standardPourOunces: string;
  holdAmountCents: number | null;
  purchaseApprovalThresholdCents: number;
  discountApprovalPercentBasisPoints: number;
  discountApprovalAmountCents: number;
  closeCardTicketsAtDayEnd: boolean;
  dualPricingEnabled: boolean;
  dualPricingBasisPoints: number;
  tipOnPreDiscountPrice: boolean;
  businessDayCutoffTimeLocal: string;
  businessDayTimezone: string;
  tipPresetsBasisPoints: number[];
  overriddenKeys: LocationSettingKey[];
}

function locationSettingsPath(
  tenantId: string,
  locationId: string,
  settingKey?: LocationSettingKey,
): string {
  const base = `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/settings`;
  return settingKey === undefined ? base : `${base}/${encodeURIComponent(settingKey)}`;
}

export function getLocationSettings(baseUrl: string, tenantId: string, locationId: string) {
  return getJson<{ settings: LocationSettingsDto } & Partial<ApiErrorBody>>(
    baseUrl,
    locationSettingsPath(tenantId, locationId),
  );
}

export function putLocationSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  key: LocationSettingKey,
  value: unknown,
) {
  return putJson<{ settings: LocationSettingsDto } & Partial<ApiErrorBody>>(
    baseUrl,
    locationSettingsPath(tenantId, locationId, key),
    { value },
  );
}

export function revertLocationSetting(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  key: LocationSettingKey,
) {
  return deleteJson<{ settings: LocationSettingsDto } & Partial<ApiErrorBody>>(
    baseUrl,
    locationSettingsPath(tenantId, locationId, key),
  );
}

// P1(b) fix (Codex review, PR #59 against 2dcb2df). A read-only tier list
// for an actor who holds admin.liquor_product.view but is denied
// admin.tenant_settings.view (inventory_manager, assistant_manager), so
// ensureShelfTierSettingsLoaded (admin.ts) does not have to fall back to
// a synthetic tier_1..tier_4 list that cannot see a retired or newly
// added tier. Gated server-side on admin.liquor_product.view, never
// admin.tenant_settings.view.
export function getLiquorProductShelfTiers(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ shelfTiers: ShelfTierSettingDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/liquor-products/shelf-tiers`);
}

/** VER57-F4 (DEC-273). The pour sizes alone, per item kind, for a role that
 * may manage liquor items but is denied tenant settings. Ounce strings,
 * ascending; no prices, labels, or anything editable. */
export function getLiquorProductPourOptions(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ pourOptionsByItemKind: Record<string, string[]> } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/liquor-products/pour-options`);
}

/** ADDITEMNEXT1 (DEC-592). The active item categories per item kind scope, for a role that may manage liquor items but is
 * denied tenant settings. Value and label only. */
export function getLiquorProductItemCategories(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      itemCategoriesByScope: Record<string, { value: string; label: string }[]>;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/item-categories`,
  );
}

export interface UpdateTenantSettingsInput {
  taxRateBasisPoints?: number;
  tipPresetsBasisPoints?: number[];
  // B7 Slice 1 (migration 0103) removed "shelfTiers" from this route's own
  // body: rename/reprice a tier through updateVocabularyValue instead
  // (PATCH .../vocabularies/shelf_tier/:value). The RESPONSE still carries
  // shelfTiers (see TenantSettingsDto below), unchanged shape.
  holdAmountCents?: number;
  holdIncrementThresholdBasisPoints?: number;
  discountApprovalPercentBasisPoints?: number;
  discountApprovalAmountCents?: number;
  closeCardTicketsAtDayEnd?: boolean;
  dualPricingEnabled?: boolean;
  dualPricingBasisPoints?: number;
  tipOnPreDiscountPrice?: boolean;
  abandonedTabGratuityBasisPoints?: number;
  abandonedTabGratuityDisclosureConfirmed?: boolean;
  /** VOC1-F9. One of ROUNDING_MODES above ("half-up-cent" today). */
  roundingMode?: string;
  pricingModel?: string;
  standardPourOunces?: string;
  sessionInactivityMinutes?: number;
  pinMinLength?: number;
  pinMaxLength?: number;
  pinLockoutAttempts?: number;
  pinLockoutMinutes?: number;
  businessDayCutoffTimeLocal?: string;
  businessDayTimezone?: string;
  /** CL6-F1-F3 (DEC-286). True records the deliberate "Confirm time zone and
   * cutoff" tap. Must be sent with BOTH values above, so the values confirmed
   * are exactly the values saved. */
  confirmBusinessDay?: boolean;
  /** M5 (DEC-282). The "half off or more" flag threshold for a till price
   * change, in basis points of the menu price (1 to 10000). */
  priceChangeFlagOffBasisPoints?: number;
  /** PAY-LOC1 review finding. Omitted means unchanged; an empty string
   * clears the field (stored as NULL). */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export function updateSettings(
  baseUrl: string,
  tenantId: string,
  input: UpdateTenantSettingsInput,
): Promise<ApiResult<{ settings: TenantSettingsDto; provisional: true } & Partial<ApiErrorBody>>> {
  return patchJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/settings`, input);
}

/** ONB1: the deliberate "Confirm sales tax rate" tap. Idempotent on the server. */
export function confirmTaxRate(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      confirmed: boolean;
      taxRateConfirmedAt: string | null;
      taxRateConfirmedByName: string | null;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tax-rates/confirm`, {});
}

// SET19 (docs/build-briefs/SET19-transactional-settings-save.md). One
// transactional server call covering every shelf tier row and the rest
// of the Location settings form together, replacing the interim
// per-tier-PATCH-then-combined-PATCH sequence: a partial failure can no
// longer leave a shelf tier price committed while the save reports as
// failed. shelfTiers is required (may be empty); each row carries the
// tier's stable value and at least one of label/priceCents, the same
// shape PATCH .../vocabularies/shelf_tier/:value already accepts.
export interface SaveLocationSettingsInput extends UpdateTenantSettingsInput {
  locationId?: string;
  localShelfUpdates?: LocalShelfUpdateDto[];
  shelfTiers: { value: string; label?: string; priceCents?: number }[];
}

export function saveLocationSettings(
  baseUrl: string,
  tenantId: string,
  input: SaveLocationSettingsInput,
): Promise<
  ApiResult<
    {
      settings: TenantSettingsDto;
      provisional: true;
      sellingContext?: LocationSellingContextDto;
      localShelves?: LocalShelfSellingDto[];
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/settings/save`, input);
}

// PO2, owner ruling 2026-09-29 (DEC-247). Its own pair of calls, not
// folded into getSettings/updateSettings above: the server route is
// gated on purchasing.approval_threshold.manage, a permission
// independent of admin.location_settings.manage, so this cannot share
// UpdateTenantSettingsInput/TenantSettingsDto's own request/response
// shape without implying the same permission governs both.
export function getPurchaseApprovalThreshold(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ thresholdCents: number } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/approval-threshold`);
}

export function updatePurchaseApprovalThreshold(
  baseUrl: string,
  tenantId: string,
  thresholdCents: number,
): Promise<ApiResult<{ thresholdCents: number } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/approval-threshold`,
    {
      thresholdCents,
    },
  );
}

// ---------------------------------------------------------------------
// Payments-track Slice 3: terminal management (Settings > Payments >
// Terminals). See src/domain/terminals.ts.
// ---------------------------------------------------------------------

export interface TerminalDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  display_name: string;
  provider_name: string;
  provider_reference: string;
  active: boolean;
  deactivated_at: string | null;
  created_at: string;
  /** TERM4: the location this reader belongs to. `location_name` is on the Settings list only. */
  location_id: string;
  location_name?: string | null;
  /** TERM2: the provider's live report on this reader, on the Settings list
   * only (GET .../terminals with no filter). Null or absent when the provider
   * did not answer. */
  reader?: {
    deviceType: string | null;
    serialNumber: string | null;
    connection: "online" | "offline" | "unknown";
    simulated: boolean;
  } | null;
}

export function registerTerminal(
  baseUrl: string,
  tenantId: string,
  // TERM2: registrationCode is the pairing code a physical reader shows on its
  // screen. Omitted (never an empty string) registers the simulated reader.
  params: {
    idempotencyKey: string;
    displayName: string;
    registrationCode?: string;
    /** TERM4: omitted means the signed-in tablet's own location (DEC-322). */
    locationId?: string;
  },
): Promise<ApiResult<{ terminal: TerminalDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/terminals`, params);
}

export function listTerminals(
  baseUrl: string,
  tenantId: string,
  // Terminal-assignment slice: activeOnly lets the terminal-setup picker
  // (main.ts) request only pickable terminals, matching src/api/
  // server.ts's own `?activeOnly=true` query param; Settings > Payments >
  // Terminals (admin.ts) still calls this with no params, unchanged, so
  // it keeps showing every terminal including inactive ones.
  // PAY6: chargeableOnly is the floor picker's own, stricter ask -- every
  // chargeable terminal is active, but not every active terminal is
  // chargeable (its own provider_name can have outlived a later provider
  // cutover). The comparison provider name is resolved server-side, so
  // this client never learns or sends one.
  params?: { activeOnly?: boolean; chargeableOnly?: boolean },
): Promise<
  ApiResult<{ terminals: TerminalDto[]; testEnvironment?: boolean } & Partial<ApiErrorBody>>
> {
  const query =
    params?.chargeableOnly === true
      ? "?chargeableOnly=true"
      : params?.activeOnly === true
        ? "?activeOnly=true"
        : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/terminals${query}`);
}

/** TERM1: the floor picker's own list (GET .../terminals/selectable,
 * floor.terminal.select). Active and chargeable terminals only, and only
 * what a picker tile needs: no provider_reference, no registration fields. */
export interface SelectableTerminalDto {
  id: string;
  display_name: string;
  provider_name: string;
}

export function listSelectableTerminals(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ terminals: SelectableTerminalDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/terminals/selectable`);
}

/** CL8: one reader's live health for the floor's reader pill (GET .../terminals/:terminalId/health,
 * floor.terminal.select). The location is the session's; a reader of another location answers 404. */
export function getReaderHealth(
  baseUrl: string,
  tenantId: string,
  terminalId: string,
): Promise<ApiResult<{ health: ReaderHealthDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/health`,
  );
}

/** TERM3: the signed-in user's last chosen terminal, when it is still active and
 * chargeable (GET .../me/terminal). Null otherwise; never a deactivated one.
 * TERMREADER1: the card reader this registered device last used comes first, whoever is signed in. */
export function getRememberedTerminal(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      terminal: { id: string; display_name: string; provider_name: string } | null;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/terminal`);
}

/** TERM3: remembers the signed-in user's terminal choice (PUT .../me/terminal). */
export function rememberTerminal(
  baseUrl: string,
  tenantId: string,
  params: { terminalId: string },
): Promise<ApiResult<{ changed: boolean } & Partial<ApiErrorBody>>> {
  return putJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/terminal`, params);
}

/** TERM2: stops a payment waiting on a reader (POST .../cancel-reader-action).
 * `outcome` is what the server read back from the provider. */
export function cancelReaderAction(
  baseUrl: string,
  tenantId: string,
  terminalId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    {
      outcome: "canceled" | "completed" | "unresolved" | "nothing_pending";
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/cancel-reader-action`,
    params,
  );
}

export function deactivateTerminal(
  baseUrl: string,
  tenantId: string,
  terminalId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ terminal: TerminalDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/deactivate`,
    params,
  );
}

export interface TerminalRenameDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  terminal_id: string;
  previous_display_name: string;
  new_display_name: string;
  renamed_at: string;
  created_at: string;
}

// Amendment 2026-08-22 (ninth): renameTerminal. See src/domain/
// terminals.ts's own doc comment.
export function renameTerminal(
  baseUrl: string,
  tenantId: string,
  terminalId: string,
  params: { idempotencyKey: string; displayName: string },
): Promise<
  ApiResult<
    { terminal: TerminalDto; rename: TerminalRenameDto; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/terminals/${encodeURIComponent(terminalId)}/rename`,
    params,
  );
}

// ---------------------------------------------------------------------
// Payments-track Slice 3: card-on-file, per docs/payment-provider-
// evaluation.md's PROV-105 launch gate. See src/domain/cardOnFile.ts.
// ---------------------------------------------------------------------

export interface SavedCardDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_guest_id: string;
  source_payment_attempt_id: string;
  provider_name: string;
  card_reference: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  consent_method: string;
  consent_obtained_at: string;
  consent_evidence_reference: string;
  removed_at: string | null;
  created_at: string;
}

/** The consent screen's own "Save this card" confirm. See src/domain/
 * cardOnFile.ts's vaultCardFromCapture: consentAgreed must be true (an
 * unchecked-by-default checkbox unless the guest explicitly checks it). */
export function vaultCardFromCapture(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  guestId: string,
  params: {
    idempotencyKey: string;
    sourcePaymentAttemptId: string;
    consentAgreed: boolean;
    consentEvidenceReference: string;
  },
): Promise<
  ApiResult<
    { savedCard: SavedCardDto; replayed: boolean; alreadySaved?: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/guests/${encodeURIComponent(guestId)}/saved-cards`,
    params,
  );
}

/** CARDDEFAULT2. Whether the card behind a Captured payment is already saved for this guest's client: `saved`, `not_saved`,
 * or `unknown` when the provider gave no fingerprint. Saves nothing. */
export function getSavedCardMatch(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  guestId: string,
  paymentAttemptId: string,
): Promise<
  ApiResult<
    { status: "saved" | "not_saved" | "unknown"; savedCardId?: string } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/guests/${encodeURIComponent(guestId)}/saved-card-match/${encodeURIComponent(paymentAttemptId)}`,
  );
}

/** One listed saved card. CARDDEFAULT2: `guestIds` are the ticket guests it belongs to and `chargeable` is whether a
 * charge today would pass the server's provider and account checks. */
export interface SavedCardEntryDto {
  savedCard: SavedCardDto;
  guestLabel: string;
  guestIds: string[];
  chargeable: boolean;
}

/** Every non-removed saved card belonging to any guest on this order.
 * Powers "Use saved card" at Pay (shown only when this list is
 * non-empty). */
export function listSavedCardsForOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ savedCards: SavedCardEntryDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/saved-cards`,
  );
}

/** Removal deletes the provider token for real, first-class, per
 * PROV-105's own retention ruling; see src/domain/cardOnFile.ts's
 * removeSavedCard. */
export function removeSavedCard(
  baseUrl: string,
  tenantId: string,
  savedCardId: string,
  params: { idempotencyKey: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      savedCard: SavedCardDto;
      replayed: boolean;
      providerDetachPending?: boolean;
      providerDetachWarning?: string;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/saved-cards/${encodeURIComponent(savedCardId)}/remove`,
    params,
  );
}

// ---------------------------------------------------------------------
// Payments-track Slice 4 (DEC-002, docs/decision-register.md). "Keep tab
// open" with a hold: offered only when a tab has no card on file
// (mirroring listSavedCardsForOrder's own empty-list gate above), per
// DEC-002 Section 1's own trigger. See src/domain/tabs.ts's module
// header for the full domain write-up.
// ---------------------------------------------------------------------

export interface OrderHoldDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  order_id: string;
  provider_name: string;
  adapter_intent_id: string;
  provider_reference: string;
  hold_unit_cents: number;
  authorized_amount_cents: number;
  increment_count: number;
  capture_before_at: string | null;
  /** PAY14: "set_aside" is a hold taken off the tab without any adapter
   * call (a simulated hold cleared automatically, owner answer Q3 = B). */
  status: "active" | "captured" | "released" | "set_aside";
  captured_amount_cents: number | null;
  captured_payment_attempt_id: string | null;
  released_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HoldIncrementOutcomeDto {
  /** "check_failed" (M0, 2026-08-28) is the server's own honest report
   * that the check did not complete at all: a provider fault, a timeout,
   * anything neither a decline nor the attempt ceiling. It is separate
   * from "declined" on purpose, because nothing on either side knows a
   * card refused anything, and the two need different words in front of
   * the bartender. See src/api/server.ts's HoldIncrementSignal. */
  kind:
    | "incremented"
    | "declined"
    | "ceiling_reached"
    | "check_failed"
    /** PAY14: the hold was placed through a payment processor this
     * location no longer uses; the badge shows it needs a manager. */
    | "hold_provider_mismatch"
    /** PAY14 (Q3 = B): a test (simulated) hold was just set aside; the
     * badge slot shows the test-hold note. */
    | "hold_set_aside";
  hold?: OrderHoldDto;
  message?: string;
}

/** DEC-002 Section 1's own gate (no card on file, tab kept open) is
 * re-checked server-side (HoldNotAllowedError, 409) regardless of what
 * this call site already knows; see src/domain/tabs.ts's openTabWithHold.
 * Rejects with 400 (ValidationError) when this tenant has no
 * holdAmountCents configured yet in Settings (the approval record's own
 * "a manager must set one" rule for a new location). */
export function openTabHold(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  // Terminal-assignment slice: terminalId replaces the former plain
  // terminalReference field this route used to accept directly. The
  // server resolves it tenant-scoped to the real registered terminal's
  // own provider_reference itself (src/domain/payments.ts's
  // resolveTerminalReferenceForCharge, called from src/api/server.ts's
  // hold route); this client never resolves or names a raw provider
  // reference.
  params: { idempotencyKey: string; terminalId?: string },
): Promise<
  ApiResult<
    {
      hold: OrderHoldDto;
      replayed: boolean;
      /** Present when the ticket was ALREADY past the increment threshold
       * at the moment this hold became active, so the server raised it
       * straight away rather than waiting for a drink that might never be
       * ordered. */
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/hold`,
    params,
  );
}

/** A plain read: null when this order has never had a hold. Powers the
 * ticket rail's own hold-status display. PAY14 (Section 4.5): the server
 * also reports `providerMismatch` (an active hold from a payment processor
 * this location no longer uses; its manager exception row already
 * exists) and `testHoldClearedNow` (this very read set a test hold
 * aside). */
export function getHoldForOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<
  ApiResult<
    {
      hold: OrderHoldDto | null;
      /** HOLDPERM1: the order's location's hold amount, readable by the floor role; null when none is set. */
      holdAmountCents?: number | null;
      providerMismatch?: boolean;
      testHoldClearedNow?: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/hold`,
  );
}

// ---------------------------------------------------------------------
// Inventory track (track/inventory) Slice 2: the Settings > Counts screen.
// Every route below already existed server-side from Slice 1
// (src/api/server.ts); this is the first UI caller. See
// src/domain/inventory.ts and src/domain/counts.ts's own module headers
// for the full domain write-up.
// ---------------------------------------------------------------------

export interface CountSectionDto {
  id: string;
  tenant_id: string;
  name: string;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export function listCountSections(
  baseUrl: string,
  tenantId: string,
  params: { activeOnly?: boolean } = {},
): Promise<ApiResult<{ countSections: CountSectionDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sections${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export function createCountSection(
  baseUrl: string,
  tenantId: string,
  input: { name: string; sortOrder?: number },
): Promise<ApiResult<{ countSection: CountSectionDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/count-sections`, input);
}

export function updateCountSection(
  baseUrl: string,
  tenantId: string,
  id: string,
  input: { name?: string; sortOrder?: number; active?: boolean },
): Promise<ApiResult<{ countSection: CountSectionDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sections/${encodeURIComponent(id)}`,
    input,
  );
}

export interface OnHandResultDto {
  componentReference: string;
  quantityOnHand: string | null;
  unit: string | null;
  mixedUnits: boolean;
  breakdown: { unit: string; quantity: string }[];
}

export interface LiquorProductOnHandDto extends OnHandResultDto {
  liquorProduct: LiquorProductDto;
}

export function listOnHand(
  baseUrl: string,
  tenantId: string,
  params: { activeOnly?: boolean } = {},
): Promise<
  ApiResult<
    {
      liquorProducts: LiquorProductOnHandDto[];
      otherComponents: OnHandResultDto[];
    } & Partial<ApiErrorBody>
  >
> {
  const query = new URLSearchParams();
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/on-hand${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

// TF3 (docs/backlog.md, owner ruling 2026-08-28): the single-product
// on-hand read, straight from GET .../liquor-products/:id/on-hand
// (src/domain/inventory.ts's getOnHandForLiquorProduct), the same
// append-only-ledger-derived figure listOnHand above returns for every
// product at once. This wrapper exists because the Edit Bottle window
// needs exactly one product's on-hand, not the whole tenant's.
export function getLiquorProductOnHand(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<LiquorProductOnHandDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(id)}/on-hand`,
  );
}

// INV1 (docs/build-briefs/INV1-movement-types.md). The waste reason
// vocabulary, a structural twin of RefundReasonDto/listRefundReasons
// above.
export interface WasteReasonDto {
  id: string;
  tenant_id: string;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

export function listWasteReasons(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean },
): Promise<ApiResult<{ wasteReasons: WasteReasonDto[] } & Partial<ApiErrorBody>>> {
  const query = params?.activeOnly === false ? "?activeOnly=false" : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/waste-reasons${query}`);
}

// INV1: the missing P0.7 movement types. A fourth append-only stream
// alongside receipts/adjustments; see src/domain/inventoryMovements.ts's
// own module header.
export interface InventoryMovementDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  liquor_product_id: string;
  component_reference: string;
  // DOCSBUG4: the list route returns every type the ledger stream holds, so
  // the Recent movements list can say why a row cannot be reversed.
  movement_type:
    | "waste"
    | "return_to_stock"
    | "vendor_return"
    | "reversal"
    | "opening_balance"
    | "receipt_correction"
    | "transfer_out"
    | "transfer_in";
  quantity_delta: string;
  unit: string;
  reason_id: string | null;
  reason_label_at_movement: string | null;
  // INV4-F2 (migration 0151): a real vendor reference plus its snapshotted
  // name, the same reference-plus-snapshot pair reason_id/
  // reason_label_at_movement already carries above. Both null on a
  // movement recorded before this item, or a vendor return that never
  // named a vendor.
  vendor_id: string | null;
  vendor_name_at_movement: string | null;
  note: string | null;
  related_movement_id: string | null;
  // AU14-F14 (migration 0231): on a reversal written after that migration, the
  // reason picked from the reversal list and its label as it read then; null on
  // an earlier reversal and on every other type.
  action_reason_id: string | null;
  action_reason_label_at_movement: string | null;
  actor_employee_id: string;
  created_at: string;
}

export function recordWasteMovement(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    quantity: string;
    unit: string;
    wasteReasonId: string;
    actorEmployeeId: string;
  },
): Promise<
  ApiResult<{ movement: InventoryMovementDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/movements/waste`,
    params,
  );
}

export function recordReturnToStockMovement(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    quantity: string;
    unit: string;
    actorEmployeeId: string;
  },
): Promise<
  ApiResult<{ movement: InventoryMovementDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/movements/return-to-stock`,
    params,
  );
}

export function recordVendorReturnMovement(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    quantity: string;
    unit: string;
    // INV4-F2-F1: the floor sheet's own vendor chip picker sends this.
    // Optional at this client layer for the same backward-compatibility
    // reason INV4-F2's backend keeps it optional (docs/build-briefs/
    // INV4-F2-vendor-return-names-a-vendor.md); the UI itself requires a
    // selection before Save (saveInventoryMovement in admin.ts).
    vendorId?: string;
    note?: string;
    actorEmployeeId: string;
  },
): Promise<
  ApiResult<{ movement: InventoryMovementDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/movements/vendor-return`,
    params,
  );
}

// DOCSBUG4 (DEC-274): undo one movement. No quantity is sent: the server
// derives the reversal by negating the row. AU14-F14 (DEC-316 a): a reason id
// from the reversal list is required, and rides every attempt. An
// approval_required role retries with the approver fields (ApprovalRetryParams)
// through requestApproval.
export function reverseInventoryMovement(
  baseUrl: string,
  tenantId: string,
  movementId: string,
  params: { idempotencyKey: string; reasonId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<{ movement: InventoryMovementDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/movements/${encodeURIComponent(movementId)}/reverse`,
    params,
  );
}

// INV31 (docs/build-briefs/INV31-set-starting-stock.md; DEC-288). The Set
// starting stock screen's three calls. The write is INV7's opening-balance
// route, unchanged: the actor is the signed-in user, never a body field.
export type OpeningBalanceHistory = "none" | "opening_balance" | "other";

export interface OpeningBalanceStatusDto {
  products: { liquorProductId: string; history: OpeningBalanceHistory }[];
  startsEmpty: boolean;
  /** INV38: this location's initial stock is in (set once, never cleared). Set starting stock is hidden for good. */
  initialStockCompleted: boolean;
}

export function getOpeningBalanceStatus(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<OpeningBalanceStatusDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/opening-balance/status`,
  );
}

export function recordOpeningBalanceMovement(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    quantity: string;
    unit: string;
    note?: string;
  },
): Promise<
  ApiResult<{ movement: InventoryMovementDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  // INV38: saving starting stock can complete the set.
  return signalOnSuccess(
    postJson(
      baseUrl,
      `/tenants/${encodeURIComponent(tenantId)}/inventory/movements/opening-balance`,
      params,
    ),
  );
}

export function setStartsEmpty(
  baseUrl: string,
  tenantId: string,
  startsEmpty: boolean,
): Promise<ApiResult<{ startsEmpty: boolean } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/opening-balance/starts-empty`,
    { startsEmpty },
  );
}

// INV41: one row of an item's stock history (every table that changes on-hand), signed in the item's count unit.
export interface StockHistoryDto {
  source: "postings" | "receipts" | "adjustments" | "movements";
  id: string;
  kind:
    "receipt" | "sale" | "sale_undone" | "count_adjustment" | InventoryMovementDto["movement_type"];
  quantityDelta: string;
  unit: string;
  /** Signed, in countUnit; null when the item's configuration cannot convert the row. */
  quantity: string | null;
  countUnit: string | null;
  createdAt: string;
  actorEmployeeId: string | null;
  note: string | null;
}

export function listStockHistory(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId: string; limit?: number },
): Promise<ApiResult<{ history: StockHistoryDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams({ liquorProductId: params.liquorProductId });
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/stock-history?${query.toString()}`,
  );
}

export function listInventoryMovements(
  baseUrl: string,
  tenantId: string,
  params: { liquorProductId?: string } = {},
): Promise<ApiResult<{ movements: InventoryMovementDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.liquorProductId !== undefined) {
    query.set("liquorProductId", params.liquorProductId);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/inventory/movements${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export interface CountSessionDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  status: string;
  opened_by_employee_id: string;
  closed_by_employee_id: string | null;
  closed_at: string | null;
  created_at: string;
  // INV2 (docs/backlog.md, PRD P0.7).
  assigned_counter_employee_id: string;
  /** PO6-S1-F3 (docs/backlog.md). A courtesy label resolved server-side
   * through a LEFT JOIN on users (src/domain/counts.ts's own
   * assigned_counter_display_name); null exactly when
   * assigned_counter_employee_id does not resolve to a current users row
   * for this tenant. Never a fallback for the id above: use
   * resolveActorDisplayName (ui/src/actorDisplay.ts) to get a plain
   * "Former employee" label instead of rendering this raw id. */
  assigned_counter_display_name: string | null;
  /** UX116-F1. Who closed the session, resolved server-side like the counter's name; null while open or when the
   * id does not resolve for this tenant. */
  closed_by_display_name: string | null;
  blind_mode: boolean;
  cutoff_at: string;
}

/** Closed inventory projection for the count workflow. It deliberately
 * carries only what the walk needs; expected on-hand exists only when the
 * server has authorized and resolved a quantity-visible session. */
export interface CountWalkItemDto {
  id: string;
  name: string;
  countUnits: string[];
  /** INV37. Ounces in one bottle, or null when the item has no usable
   * bottle size; the walk starts a sized item on Bottles. */
  bottleOunces: string | null;
  expectedOnHand?: {
    quantityOnHand: string | null;
    unit: string | null;
    mixedUnits?: boolean;
    /** INV37. quantityOnHand in bottles, two decimals, or null. */
    inBottles?: string | null;
    /** INV43. The INV42 expected figure per countable unit, for an item
     * held in more than one unit that has a bottle size. */
    expectedByUnit?: { oz: string; bottle: string };
  };
}

export interface CountSessionWalkDto {
  session: CountSessionDto;
  observations: CountObservationDto[];
  items: CountWalkItemDto[];
}

export interface CountSessionReviewRowDto {
  /** INVDESIGN1-S2: the item, so Recount can reopen it. */
  liquorProductId: string;
  /** INVDESIGN1-S2: whole cents of what was expected and what was counted, valued like Stock value. Absent without the
   * Stock value right, null for an item with no usable cost. */
  expectedValueCents?: number | null;
  countedValueCents?: number | null;
  liquorProductName: string;
  countedQuantity: string;
  expectedQuantity: string;
  unit: string;
  sectionName: string | null;
  previewVariance: string;
  /** INV37. The same figures in bottles when the line was counted in
   * bottles; null otherwise. */
  bottleView: {
    countedQuantity: string;
    expectedQuantity: string;
    previewVariance: string;
    countedOunces: string | null;
  } | null;
}

export function listCountItems(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<ApiResult<{ items: CountWalkItemDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-items${locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`}`,
  );
}

export function getCountSessionWalk(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
): Promise<ApiResult<CountSessionWalkDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/walk`,
  );
}

/** INVDESIGN1-S2: one area of the Inventory home (src/domain/inventoryHome.ts). */
export interface HomeAreaDto {
  id: string | null;
  name: string;
  itemIds: string[];
  countedInOpenCount: number;
  openCountLast: { at: string; by: string | null } | null;
  closedCountLast: { at: string; by: string | null } | null;
  itemsWithStartingStock: number;
}

export interface InventoryHomeAreasDto {
  startingStockPending: boolean;
  startsEmpty: boolean;
  initialStockCompleted: boolean;
  itemCount: number;
  namedAreaCount: number;
  areas: HomeAreaDto[];
  openCount: {
    id: string;
    createdAt: string;
    blind: boolean;
    assignedCounterEmployeeId: string;
    assignedCounterName: string | null;
    countedItems: number;
  } | null;
  lastClosedCount: { id: string; closedAt: string; closedByName: string | null } | null;
}

export function getInventoryHomeAreas(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<InventoryHomeAreasDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/home-areas`);
}

export function getCountSessionReview(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
): Promise<ApiResult<{ rows: CountSessionReviewRowDto[]; valued?: boolean } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/review`,
  );
}

export function startCountSession(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    openedByEmployeeId: string;
    /** INV2. Defaults to openedByEmployeeId server-side when omitted. */
    assignedCounterEmployeeId?: string;
    /** INV2. Defaults to false (quantity-visible) server-side when
     * omitted. */
    blindMode?: boolean;
    /** INV2. ISO-8601. Defaults to session start server-side when
     * omitted. */
    cutoffAt?: string;
  },
): Promise<ApiResult<{ session: CountSessionDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/count-sessions`, params);
}

// INV2 (docs/backlog.md, PRD P0.7): "reassignment requires an audited
// manager action." Mirrors closeCountSession's own ApprovalRetryParams
// shape immediately below: the first, no-approval attempt either
// succeeds outright (admin/manager) or comes back 403 approval_required
// (assistant_manager/inventory_manager), and ui/src/main.ts's
// requestApproval drives the retry with a real manager's PIN.
export interface CountSessionReassignmentDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  count_session_id: string;
  previous_counter_employee_id: string;
  new_counter_employee_id: string;
  reassigned_by_employee_id: string;
  created_at: string;
}

export function reassignCountSession(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
  params: {
    idempotencyKey: string;
    newCounterEmployeeId: string;
    reassignedByEmployeeId: string;
  } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      reassignment: CountSessionReassignmentDto;
      session: CountSessionDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/reassign`,
    params,
  );
}

// INV2: "any later change is audited." No approval fields: the criterion
// asks only for the audit row, which setCountSessionMode itself always
// writes, and this route is gated on the same inv.count_session.run
// permission startCountSession's own route already requires.
export interface CountSessionModeChangeDto {
  id: string;
  tenant_id: string;
  idempotency_key: string;
  count_session_id: string;
  previous_blind_mode: boolean;
  new_blind_mode: boolean;
  changed_by_employee_id: string;
  created_at: string;
}

export function setCountSessionMode(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
  params: { idempotencyKey: string; blindMode: boolean; changedByEmployeeId: string },
): Promise<
  ApiResult<
    {
      modeChange: CountSessionModeChangeDto;
      session: CountSessionDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/mode`,
    params,
  );
}

export function listCountSessions(
  baseUrl: string,
  tenantId: string,
  params: { status?: string } = {},
): Promise<ApiResult<{ sessions: CountSessionDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams();
  if (params.status !== undefined) {
    query.set("status", params.status);
  }
  const queryString = query.toString();
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions${queryString.length > 0 ? `?${queryString}` : ""}`,
  );
}

export interface CountObservationDto {
  liquor_product_id: string;
  counted_quantity: string;
  unit: string;
  /** INV37. What the counter typed when a Bottles entry was stored in
   * ounces; null when nothing was converted. */
  entered_quantity?: string | null;
  entered_unit?: string | null;
  count_section_id: string | null;
  liquor_product_name: string;
}

export function getCountSession(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<
  ApiResult<
    { session: CountSessionDto; observations: CountObservationDto[] } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(id)}`,
  );
}

export function recordCount(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    countedQuantity: string;
    unit: string;
    countSectionId?: string;
    recordedByEmployeeId: string;
  },
): Promise<
  ApiResult<
    { line: CountObservationDto & { id: string }; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/counts`,
    params,
  );
}

// ---------------------------------------------------------------------
// INVCOUNT2-S2 (docs/build-briefs/INVCOUNT2-S2-count-table-screen.md): the count table's reads and writes over the
// INVCOUNT2-S1 routes (DEC-409). Nothing here sends or stores anything but identifiers and whole numbers.
// ---------------------------------------------------------------------

export interface CountTableLastCountDto {
  /** The last closed count in bottles (two decimals, trimmed), or null when the item has no bottle size. */
  inBottles: string | null;
  quantity: string;
  unit: string;
  sessionId: string;
  countedAt: string;
}

export interface CountTableItemDto {
  id: string;
  name: string;
  countUnits: string[];
  bottleOunces: string | null;
  countSectionId: string | null;
  countSectionName: string | null;
  position: number | null;
  caseSize: number | null;
  itemKind: string;
  category: string;
  sizeValue: string | null;
  sizeUnit: string | null;
  containerType: string | null;
  /** INVDESIGN1-S1: a keg's default keg size value (for example "sixth_barrel"), or null. Catalog identity only. */
  defaultKegSize?: string | null;
  /** INVPOLISH1: this location's par in the item's count unit, or null. A target, not stock, so a blind count carries it too. */
  par?: string | null;
  /** Present ONLY in a quantity-visible session. Absent, never null, in a blind one. */
  lastCount?: CountTableLastCountDto | null;
}

export interface CountTableDto {
  session: CountSessionDto;
  sections: { id: string; name: string; sort_order: number }[];
  items: CountTableItemDto[];
  observations: CountObservationDto[];
}

export function getCountSessionTable(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
): Promise<ApiResult<CountTableDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/table`,
  );
}

export function recordCountTableEntry(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
  params: {
    idempotencyKey: string;
    liquorProductId: string;
    cases: number;
    bottles: number;
    tenths: number;
    countSectionId?: string;
  },
): Promise<
  ApiResult<
    {
      line: CountObservationDto & { id: string };
      replayed: boolean;
      totalBottles: string;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/table-counts`,
    params,
  );
}

export interface CountUncountedItemDto {
  id: string;
  name: string;
  countSectionId: string | null;
  countSectionName: string | null;
}

export function getCountSessionUncounted(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
): Promise<ApiResult<{ items: CountUncountedItemDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/uncounted`,
  );
}

export interface CountPlacementsDto {
  locationId: string;
  sections: { id: string; name: string; sortOrder: number }[];
  items: {
    id: string;
    name: string;
    countSectionId: string | null;
    countSectionName: string | null;
    position: number | null;
    caseSize: number | null;
  }[];
}

export function listCountPlacements(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<CountPlacementsDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/count-placements`);
}

export type CountPlacementMove = "up" | "down" | "top" | "bottom";

export function setCountPlacement(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
  params: { countSectionId: string | null },
): Promise<ApiResult<Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-placements/${encodeURIComponent(liquorProductId)}`,
    params,
  );
}

export function moveCountPlacement(
  baseUrl: string,
  tenantId: string,
  liquorProductId: string,
  direction: CountPlacementMove,
): Promise<ApiResult<Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-placements/${encodeURIComponent(liquorProductId)}/move`,
    { direction },
  );
}

export interface CountSessionVarianceDto {
  id: string;
  tenant_id: string;
  count_session_id: string;
  liquor_product_id: string;
  component_reference: string;
  count_section_id: string | null;
  counted_quantity: string;
  on_hand_at_start_quantity: string;
  variance_quantity: string;
  unit: string;
  created_at: string;
}

export interface InventoryAdjustmentDto {
  id: string;
  tenant_id: string;
  count_session_id: string;
  liquor_product_id: string;
  component_reference: string;
  quantity_delta: string;
  unit: string;
  adjustment_type: string;
  created_at: string;
}

export function closeCountSession(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
  params: { idempotencyKey: string; closedByEmployeeId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      closeOperation: { id: string; variance_count: number; adjustment_count: number };
      session: CountSessionDto;
      variances: CountSessionVarianceDto[];
      adjustments: InventoryAdjustmentDto[];
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  // INV38: a closed count completes the location's initial stock.
  return signalOnSuccess(
    postJson(
      baseUrl,
      `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/close`,
      params,
    ),
  );
}

export function getCountSessionVariances(
  baseUrl: string,
  tenantId: string,
  countSessionId: string,
): Promise<
  ApiResult<
    {
      variances: CountSessionVarianceDto[];
      /** INVPOLISH1: each counted item's container type by product id, so a keg's figure reads in kegs. */
      containerTypes?: Record<string, string | null>;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/count-sessions/${encodeURIComponent(countSessionId)}/variances`,
  );
}

/** INVPOLISH1: one Past counts row's summary. Dollars are the stock-value valuation's, and absent without the right to read it. */
export interface CountHistorySummaryDto {
  sessionId: string;
  areaCount: number;
  itemCount: number;
  itemTypes: { containerType: string | null; count: number }[];
  value: {
    expectedCents: number;
    countedCents: number;
    differenceCents: number;
    percent: number | null;
    unvalued: number;
  } | null;
}

export function getCountHistorySummaries(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ summaries: CountHistorySummaryDto[]; valued: boolean } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/count-history-summaries`);
}

// ---------------------------------------------------------------------
// T14 (docs/backlog.md): the MyTime ambiguous-reconciliation queue.
// src/domain/mytimeClientSync.ts's Slice 3 section is the domain design
// write-up; src/api/server.ts's own "T14" comment block is the two
// routes this calls.
// ---------------------------------------------------------------------

export interface ReconciliationCandidateMatchDto {
  mytimeClientId: string;
  displayName: string;
  phoneSearchKey: string | null;
  membershipActive: boolean;
}

export interface ReconciliationCandidateDto {
  id: string;
  provisionalClientId: string;
  reason: string;
  createdAt: string;
  provisionalDisplayName: string;
  provisionalPhoneSearchKey: string;
  candidateMatches: ReconciliationCandidateMatchDto[];
}

export function listReconciliationCandidates(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ candidates: ReconciliationCandidateDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/mytime-reconciliation-candidates`,
  );
}

export interface ReconciliationResolutionDto {
  candidateId: string;
  provisionalClientId: string;
  outcome: "linked" | "kept_separate";
  mytimeClientId: string | null;
}

/** Never a typed id: `mytimeClientId`, when present, is always one the
 * caller copied straight from a `ReconciliationCandidateDto.candidateMatches`
 * entry this same screen already fetched, per CLAUDE.md's "selected from
 * the lines it belongs to, never typed" rule applied to an identity
 * decision instead of a money one. */
export function resolveReconciliationCandidate(
  baseUrl: string,
  tenantId: string,
  candidateId: string,
  params: { resolution: "link"; mytimeClientId: string } | { resolution: "keep_separate" },
): Promise<ApiResult<{ resolution: ReconciliationResolutionDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/mytime-reconciliation-candidates/${encodeURIComponent(candidateId)}/resolve`,
    params,
  );
}

// ---------------------------------------------------------------------
// MT1 (docs/backlog.md), P0.11: the MyTime client-sync freshness check.
// src/domain/mytimeClientSync.ts's getMyTimeClientSyncFreshness has the
// full design write-up. Read-only: no companion write function exists,
// by design (this surface never mutates sync state).
// ---------------------------------------------------------------------

export interface MyTimeSyncFreshnessDto {
  thresholdHours: number;
  now: string;
  lastSucceededBatchDate: string | null;
  lastSucceededAt: string | null;
  lastAttemptBatchDate: string | null;
  lastAttemptAt: string | null;
  lastAttemptStatus: "running" | "succeeded" | "failed" | null;
  lastAttemptErrorMessage: string | null;
  hoursSinceLastSuccess: number | null;
  stale: boolean;
}

export function getMyTimeSyncFreshness(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ freshness: MyTimeSyncFreshnessDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/mytime-sync-freshness`);
}

// ---------------------------------------------------------------------
// AU8 (docs/backlog.md): the manager-exceptions queue's floor-facing
// surface. src/domain/managerExceptions.ts is the domain design write-up;
// src/api/server.ts's own "AU2b/AU8" comment block is the two routes this
// calls (GET .../manager-exceptions, POST .../manager-exceptions/:id/
// resolve). Both already existed before this slice (AU2b), gated on
// admin.manager_exception.view/.resolve; only the floor UI reaching them
// is new. The response shape is the raw DB row (snake_case), the same
// convention OrderHoldDto above already documents, not a reshaped camelCase
// DTO.
// ---------------------------------------------------------------------

export interface ManagerExceptionDto {
  id: string;
  tenant_id: string;
  order_id: string;
  /** Set on a drawer_open_at_close row (also settlement_difference), which has no ticket (order_id is then null). */
  location_id?: string | null;
  business_date?: string | null;
  /** Set on a settlement_difference row: "differs", "dispute", "failed_payout" or "account_unresolved". */
  settlement_finding?: string | null;
  payment_attempt_id: string | null;
  kind:
    | "capture_shortfall_uncredited"
    | "settlement_difference"
    | "drawer_open_at_close"
    | "receipt_capture_failed"
    | "receipt_tip_missed"
    | "drawer_kick_ambiguous_printer"
    | "printer_password_expired"
    | "royalty_collection_attention"
    | "test_sale_refund_failed"
    | "business_day_capture_short"
    | "walkout_capture_blocked_by_exception"
    | "hold_provider_mismatch"
    // PAY21 (owner ruling, Kerry, 2026-09-29): the close committed, but the
    // separate hold-release transaction failed for a generic reason.
    | "hold_release_failed_after_close"
    // PAY10 (DEC-225, owner ruling "Nightly check", 2026-09-29): the
    // nightly provider-charge reconciliation pass found a genuinely
    // captured provider charge with no matching payment_attempts row.
    | "unmatched_provider_charge"
    // PAY29 (DEC-226, owner ruling "Auto-release it", 2026-09-29): the
    // automatic reversal of a hold's own orphaned increment itself
    // failed at the provider.
    | "hold_increment_release_failed"
    // PAY34-F1, PAY34-F2 (migration 0289): the payment provider and our record disagree.
    | "pending_payment_provider_captured"
    | "failed_payment_provider_captured"
    | "captured_payment_provider_failed"
    // SPLIT1 (migration 0293): a ticket now worth less than was collected by amount; a manager has to look at it.
    | "amount_credits_exceed_ticket";
  outstanding_cents: number;
  message: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_employee_id: string | null;
  resolution_note: string | null;
  /** PAY14 (Section 4.5), widened by PAY21: present on hold_provider_mismatch
   * and hold_release_failed_after_close rows only. */
  hold?: ManagerExceptionHoldFactsDto;
}

/** PAY14 (Section 4.5): the order's most recent hold, as the queue row
 * needs it. camelCase, unlike the raw row around it, because the server
 * builds it rather than reading a column. */
export interface ManagerExceptionHoldFactsDto {
  authorizedAmountCents: number;
  /** A display name ("Stripe"), or null: never the raw provider name. */
  providerDisplayName: string | null;
  captureBeforeAt: string | null;
  /** The hold is active and no longer matches the configured processor. */
  stranded: boolean;
  walkoutBlockedCents: number | null;
  /** The IANA time zone captureBeforeAt is shown in (D4-R). */
  locationTimeZone: string;
}

/** PAY14 Slice B (Section 7.2): what the manager did with a hold from the
 * old payment processor, required when resolving a hold_provider_mismatch
 * row. */
export type HoldDispositionDto =
  "released_at_old_provider" | "left_to_expire" | "collected_at_old_provider";

/** Every OPEN (unresolved) exception for this tenant, newest first. Callers
 * gate the call itself on admin.manager_exception.view (hasPermission) so a
 * session that cannot see this queue never issues the request at all,
 * matching admin.ts's own reconciliationQueueVisible convention. */
export function listManagerExceptions(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ managerExceptions: ManagerExceptionDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/manager-exceptions`);
}

/** UX80-F1: the account menu's badge counts in one read. Each key is present only when the session
 * may see that count; an absent key is never a zero. */
export function getMenuCounts(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      counts: {
        cartLines?: number;
        ordersAwaitingApproval?: number;
        ordersAwaitingYourApproval?: number;
        clientsToMatch?: number;
        countsInProgress?: number;
        unfinalizedBusinessDate?: string;
      };
    } & Partial<ApiErrorBody>
  >
> {
  // TEN11-F3: the counts refresh on a timer, so a support session's idle clock must not count it as the person's action.
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/menu-counts`, {
    headers: BACKGROUND_REQUEST_HEADERS,
  });
}

/** PAY34-F1, PAY34-F2: what the manager did about a payment the provider and our record disagree about. Required
 * on the three payment disagreement kinds and refused on every other kind. */
export type PaymentResolutionActionDto =
  "record_captured" | "mark_failed" | "handled_at_provider" | "acknowledged";

/** Marks one exception resolved. resolutionNote is genuinely free text (a
 * manager's own account of how the money was handled, no fixed vocabulary
 * to pick from). It is optional except on a
 * walkout_capture_blocked_by_exception row, where the server requires a
 * note that is not only spaces (PAY14 Slice B, D9). holdDisposition is
 * required on a hold_provider_mismatch row and ignored on every other
 * kind (PAY14 Slice B, Section 7.2). */
export function resolveManagerExceptionRequest(
  baseUrl: string,
  tenantId: string,
  exceptionId: string,
  params: {
    resolutionNote?: string;
    holdDisposition?: HoldDispositionDto;
    paymentResolution?: PaymentResolutionActionDto;
  },
): Promise<
  ApiResult<
    { managerException: ManagerExceptionDto; holdSetAside?: unknown } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/manager-exceptions/${encodeURIComponent(exceptionId)}/resolve`,
    params,
  );
}

/** PAY26. Records what became of a Walkout collected in an old processor's dashboard. The amount is the
 * server's, never sent. */
export function recordOutsideCollectionRequest(
  baseUrl: string,
  tenantId: string,
  exceptionId: string,
  body: { outcome: "collected" | "not_collected"; note?: string },
): Promise<ApiResult<{ record: { id: string } } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/manager-exceptions/${encodeURIComponent(exceptionId)}/outside-collection`,
    body,
  );
}

/** PAY35. What a manager's Check payment found. See src/domain/pendingPaymentRecheck.ts. */
export type CheckPaymentKindDto =
  "resolved_failed" | "still_processing" | "provider_captured" | "unavailable" | "not_pending";

/** PAY35. Re-reads the payment provider for one Pending payment attempt, now. */
export function checkPaymentAttemptRequest(
  baseUrl: string,
  tenantId: string,
  paymentAttemptId: string,
): Promise<
  ApiResult<
    { check: { paymentAttemptId: string; kind: CheckPaymentKindDto } } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/payment-attempts/${encodeURIComponent(paymentAttemptId)}/check`,
    {},
  );
}

// ---------------------------------------------------------------------
// Auth track (track/auth), Slice 1: PIN login (the tile picker), the
// self-service profile (My Profile), and manager user management
// (Settings > Users). See src/domain/users.ts and src/api/server.ts's
// own "Auth track" section for the server side. UserDto never carries a
// PIN or a PIN hash; see src/api/server.ts's toUserResponse, the one
// place a UserRow is shaped for any HTTP response.
// ---------------------------------------------------------------------

export interface UserDto {
  id: string;
  displayName: string;
  /** The legacy single-role value, kept for one release past the RBAC
   * migration (src/domain/users.ts's own AUTH_ROLES header). Prefer
   * `roles` for anything display- or logic-facing; this field is not
   * removed from the wire shape yet, but Settings > Users never reads it
   * directly anymore.
   *
   * SEC3 (DEC-100), 2026-09-08: OPTIONAL, for the same reason `roles`
   * just below is. GET .../users' pre-login shape (an unauthenticated
   * caller, src/api/server.ts's toPreLoginUserResponse) no longer sends
   * `role` at all, per the owner's ruling that the sign-in screen shows
   * names only. Every other route returning a user still populates this;
   * `login`'s and `getSession`'s own response types restore it to
   * required there by explicit composition, since a successful login or
   * an authenticated session read always carries it at runtime. */
  role?: string;
  /** RBAC track (DEC-023): the user's full role set. A user always holds
   * at least one (src/domain/permissions.ts's setUserRoles enforces
   * this); Settings > Users' multi-select chip group is the surface that
   * edits this.
   *
   * Gate finding 5, 2026-08-27: OPTIONAL, because GET .../users answers
   * in two shapes and only one of them carries this. A caller holding
   * admin.user.view gets the full record; every other caller (anonymous
   * pre-login, or an authenticated role without that permission) gets
   * src/api/server.ts's own toPreLoginUserResponse minimal shape, which
   * is id/displayName/role and nothing else. The type said `string[]`
   * unconditionally, which is how ui/src/approvalFlow.ts came to answer
   * "is this person a manager" from the stale legacy `role` column
   * instead. Every route that returns a user OTHER than that roster read
   * populates this. */
  roles?: string[];
  /** U2: the four profile fields. OPTIONAL for exactly the same reason
   * `roles` above is: GET .../users answers in two shapes, and the
   * minimal one (an anonymous pre-login caller, or an authenticated
   * caller without admin.user.view) deliberately carries none of them.
   * email and phone are staff PII, so that omission is the point, not an
   * oversight. Every route returning the full record populates all four,
   * with null meaning "not set." */
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  active: boolean;
  mustChangePin: boolean;
  failedPinAttempts: number;
  lockedUntil: string | null;
  defaultThemeId: string | null;
  createdAt: string;
}

/** SEC3 (DEC-100), 2026-09-09: `role` on every route in this file that
 * requires an authenticated caller with `admin.user.view` or a stronger
 * admin.user.* permission (Settings > Users and its own mutations:
 * `listUsersForAdmin`, `createUser`, `deactivateUser`, `reactivateUser`,
 * `resetPin`, `unlockUser`, `setUserRoles`, `updateUserProfile`). SEC3
 * only ever drops `role` for the caller with NO session at all
 * (src/api/server.ts's case 1, `toAnonymousUserResponse`); every route
 * this type describes requires a session before the server answers at
 * all, so it can never land on that case. Restored to required here by
 * explicit type composition, the same pattern `login`'s and
 * `getSession`'s own response types above use, never a non-null
 * assertion, a `?? ""` fallback, or a filter that silently drops
 * `undefined`. */
export type AdminUserDto = Omit<UserDto, "role"> & {
  role: string;
  /** LOC2: the locations the person works at (user_locations). Present on the full directory (Settings > Users). */
  locationIds?: string[];
};

/** Product-owner ruling 2026-08-28 (CLAUDE.md's permission-driven rule):
 * the approver picker's own read. Answers "which OTHER active users
 * resolve `allowed` on this exact permission", which is precisely the set
 * src/domain/permissions.ts's requirePermission will accept as an
 * approver, computed server-side by the same algorithm rather than
 * guessed in the browser from a role name. Returns only id and
 * displayName, all a tile renders.
 *
 * Reachable only by a caller whose OWN grant on `permissionId` is
 * "approval_required" (see the route's own header in src/api/server.ts
 * for why that is the gate). Any non-200 means this device could not
 * determine the answer, which main.ts surfaces as plain language on the
 * picker instead of a filtered list it cannot stand behind. */
export interface ApprovalCandidateDto {
  id: string;
  displayName: string;
}

/**
 * DSC32. `discountId` asks a DIFFERENT question than `permissionId`
 * above: "who can approve THIS discount", answered regardless of the
 * CALLER's own posture (unlike the permissionId shape, which is
 * reachable only when the caller's own grant is exactly
 * approval_required). Pass exactly one of the two; `discountId` takes
 * the DSC32 route branch that checks money.discount.approve for the
 * candidate set. */
export function listApprovalCandidates(
  baseUrl: string,
  tenantId: string,
  target: { permissionId: string } | { discountId: string },
): Promise<ApiResult<{ candidates: ApprovalCandidateDto[] } & Partial<ApiErrorBody>>> {
  const query = new URLSearchParams(
    "permissionId" in target
      ? { permissionId: target.permissionId }
      : { discountId: target.discountId },
  );
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/approval-candidates?${query.toString()}`,
  );
}

/** The tile-picker's own read. Unauthenticated by design (it IS the
 * pre-login screen); auto-bootstraps the tenant's first manager the first
 * time it finds zero users (src/domain/users.ts's ensureBootstrapManager).
 * activeOnly defaults to true server-side when the query param is
 * omitted; the picker never wants to show a deactivated employee's tile,
 * so this always passes true explicitly. */
function usersRosterPath(tenantId: string, params: { activeOnly?: boolean }): string {
  const query = new URLSearchParams();
  if (params.activeOnly !== undefined) {
    query.set("activeOnly", params.activeOnly ? "true" : "false");
  }
  const queryString = query.toString();
  return `/tenants/${encodeURIComponent(tenantId)}/users${queryString.length > 0 ? `?${queryString}` : ""}`;
}

/** AU12 part 1: anonymousOk, because main.ts's own loadUserPicker calls
 * this BEFORE anyone has signed in (the pre-login tile picker) as well
 * as after a session ends (SEC3's own "the roster fetch after logout/an
 * inactivity timeout carries no Authorization header" tests). The two
 * authenticated fallback reads elsewhere in main.ts (the approval-picker
 * and purchasing-approval degrade paths) already carry a real token when
 * they call this, so anonymousOk here never masks a genuinely lost
 * session for THEM: the flag only changes behavior when there is no
 * token to send, which for those two call sites never happens. */
export function listUsers(
  baseUrl: string,
  tenantId: string,
  params: { activeOnly?: boolean } = {},
  // SEC2-S4 (DEC-320): the pre-login picker is refused without this tablet's device credential, sent as a header
  // (never in the URL). A call made with a session (the approval pickers) needs none: the session is the credential.
  deviceToken?: string,
): Promise<ApiResult<{ users: UserDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, usersRosterPath(tenantId, params), {
    anonymousOk: true,
    ...(deviceToken === undefined ? {} : { headers: { [DEVICE_TOKEN_HEADER]: deviceToken } }),
  });
}

/** ui/src/admin.ts's own two reads of this SAME route (Settings > Users'
 * full roster, and the count-reassignment picker's staff list): both are
 * only ever reachable from behind `session()`, which throws rather than
 * proceed with no session (admin.ts:1458-1460), so `role` is always
 * present -- see `AdminUserDto`'s own comment above. This is a fourth
 * call site the SEC3 brief's "three call sites" trace did not find; see
 * docs/backlog.md and the brief's own record for the correction. */
export function listUsersForAdmin(
  baseUrl: string,
  tenantId: string,
  params: { activeOnly?: boolean } = {},
): Promise<ApiResult<{ users: AdminUserDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, usersRosterPath(tenantId, params));
}

// Independent-review Finding B, 2026-08-26: mirrors src/domain/
// permissions.ts's own Grant/PermissionSnapshot types exactly (this
// module holds no SQL and reshapes nothing server-side already decided,
// per its own header). A resolved decision per catalog permission id,
// the SAME answer resolveEffectivePermission gives every server route --
// see that module's resolveEffectivePermissionSnapshot for the full
// reasoning this exists to serve, and ui/src/session.ts's
// hasAnyPermission for how the UI turns this into a plain visibility
// decision. Presentation only: nothing in this file or main.ts uses this
// to authorize a request, only to decide what to SHOW; every request the
// UI makes is still independently authorized server-side regardless of
// what this snapshot says.
export type PermissionGrant = "allowed" | "approval_required" | "denied";
export type PermissionSnapshot = Record<string, PermissionGrant>;

/** What a successful sign-in answers with: the PIN login, and the welcome link's PIN choice (ONBW1-S1), which answers
 * the same so the device handles both as one sign-in. */
export interface SignInBody {
  token: string;
  // SEC3 (DEC-100): UserDto.role became optional for the pre-login
  // roster shape only; a successful login response is never that
  // shape, so `role` is restored to required here by explicit type
  // composition rather than a non-null assertion or an invented
  // fallback.
  user: Omit<UserDto, "role"> & { role: string };
  permissions: PermissionSnapshot;
  /** AU11: the tenant's inactivity window, so the client can mirror
   * the server's session-lifetime rule locally instead of polling for it. */
  sessionInactivityMinutes: number;
  /** T16/DEC-133: the tenant's resolved PIN-length policy, so the
   * shared PIN pad can honour a configured length above the old
   * hardcoded 4-6 range instead of silently refusing digits past 6. */
  pinMinLength: number;
  pinMaxLength: number;
}

/** AU12 part 1: anonymousOk. This IS how a token is obtained, so it is
 * necessarily called with none. */
export function login(
  baseUrl: string,
  tenantId: string,
  params: { userId: string; pin: string },
  // SEC2-S3/S4: this tablet's device credential. Sent as a header (never in the body, never in a URL). Required:
  // the server refuses sign-in without a valid one (device_not_recognized).
  deviceToken: string,
): Promise<ApiResult<SignInBody & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/auth/login`,
    params,
    undefined,
    {
      anonymousOk: true,
      headers: { [DEVICE_TOKEN_HEADER]: deviceToken },
    },
  );
}

/** Validates the CURRENTLY-SET bearer token (setAuthToken, above) against
 * the server; src/api/server.ts's GET .../auth/session route calls
 * requireAuthenticatedUser and 401s on an unknown/expired/idle-timed-out
 * token. main.ts's init calls this on page load for a terminal that has a
 * stored TerminalConfig and a stored Session, so a mid-shift page refresh
 * resumes straight to Open Tabs on a still-valid token instead of forcing
 * a re-login, and falls back to the user picker on a 401. */
export function getSession(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      // SEC3 (DEC-100): UserDto.role became optional for the pre-login
      // roster shape only; GET .../auth/session is authenticated-only
      // and never that shape, so `role` is restored to required here by
      // explicit type composition rather than a non-null assertion or an
      // invented fallback.
      user: Pick<UserDto, "id" | "displayName" | "mustChangePin" | "defaultThemeId"> & {
        role: string;
        roles: string[];
      };
      permissions: PermissionSnapshot;
      /** AU11: see the login response's own note. */
      sessionInactivityMinutes: number;
      /** T16/DEC-133: see the login response's own note. */
      pinMinLength: number;
      pinMaxLength: number;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/auth/session`);
}

/** Best-effort: main.ts calls this before clearing the local session
 * regardless of the response (an already-expired or unknown token is a
 * harmless no-op server-side; see src/domain/users.ts's logout). */
export function logoutRequest(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ ok: true } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/auth/logout`, {});
}

export function changePin(
  baseUrl: string,
  tenantId: string,
  params: { idempotencyKey: string; oldPin: string; newPin: string; newPinConfirm: string },
): Promise<ApiResult<{ user: UserDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/change-pin`, params);
}

/** My Profile's default-theme picker AND the topbar quick theme switcher
 * (both write through to the same profile field once a session exists;
 * see ui/src/main.ts's setTheme). */
export function setMyTheme(
  baseUrl: string,
  tenantId: string,
  themeId: string | null,
): Promise<ApiResult<{ user: UserDto } & Partial<ApiErrorBody>>> {
  return patchJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/theme`, { themeId });
}

/** TEN17: the brand color scheme of the network this tenant belongs to, for My Profile's Color scheme list. The
 * body is `{ brand: null }` for a tenant in no network or a network with no brand saved. Colors and fonts only. */
export function getNetworkBrand(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ brand: unknown } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/network-brand`);
}

/** TEN17-F1: the logo of the network this tenant belongs to, by its content hash (the URL changes when the logo
 * does). A 404 when the hash is not the tenant's own network's logo. Sent with the session token, so the picture is
 * never readable without one; the page turns the blob into a URL. */
export function getNetworkBrandLogo(
  baseUrl: string,
  tenantId: string,
  sha256: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/me/network-brand/logo/${encodeURIComponent(sha256)}`,
  );
}

/** TEN17-F5: the uploaded font the brand of the network this tenant belongs to uses, by its id and content hash. A
 * 404 when the id and hash are not that network's font in use. Sent with the session token; the page registers the
 * bytes as a font (no URL the browser could fetch without a session). */
export function getNetworkBrandFont(
  baseUrl: string,
  tenantId: string,
  fontId: string,
  sha256: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/me/network-brand/fonts/${encodeURIComponent(fontId)}/${encodeURIComponent(sha256)}`,
  );
}

/** USR4 (docs/build-briefs/USR4-my-profile-details.md): My Profile's
 * "Your details" section. Same undefined-vs-changed contract as the
 * manager's PATCH .../users/:userId route -- an omitted field is left
 * alone, an explicit null clears it -- but this route reads no target
 * id at all; it always acts on the caller's own session. */
export function updateMyProfile(
  baseUrl: string,
  tenantId: string,
  params: {
    displayName?: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  },
): Promise<ApiResult<{ user: UserDto } & Partial<ApiErrorBody>>> {
  return patchJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/profile`, params);
}

// NOTIFPREF1: the signed-in person's own email choices (src/notificationsPage.ts). The address is the one on their own
// profile, taken from the session by the server; these calls name only a kind and a value.
export function getMyNotificationPreferences(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<unknown>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/me/notification-preferences`);
}
export function putMyNotificationPreference(
  baseUrl: string,
  tenantId: string,
  params: { kind: string; enabled: boolean },
): Promise<ApiResult<unknown>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/me/notification-preferences`,
    params,
  );
}

// Manager-only (Settings > Users); the server enforces this again
// independent of anything the UI hides, per Amendment 2026-08-21 sixth.
// Every response below carries `user: AdminUserDto` rather than plain
// `UserDto` -- see that type's own comment: `role` is always present,
// since none of these routes ever answer an unauthenticated caller.

// U7 (DEC-103, DEC-107): the create route accepts the same optional
// profile fields PATCH .../users/:userId already accepts (`firstName`,
// `lastName`, and `email`/`phone` as the JSON body keys, matching that
// route's own convention), plus an optional `chosenPin`, mirroring
// resetPin's own `chosenPin` contract below. `chosenPin` omitted mints a
// generated temporary PIN as today (`temporaryPin` in the response,
// `mustChangePin: true` at sign-in); a valid `chosenPin` is final, no
// forced change. Nothing about a chosen PIN's VALUE is ever logged; see
// scripts/check-no-pin-in-logs.ts's closed identifier list, which already
// names `chosenPin`.
export function createUser(
  baseUrl: string,
  tenantId: string,
  params: {
    idempotencyKey: string;
    displayName: string;
    role: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    chosenPin?: string;
  },
): Promise<
  ApiResult<
    { user: AdminUserDto; temporaryPin?: string; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/users`, params);
}

export function deactivateUser(
  baseUrl: string,
  tenantId: string,
  userId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ user: AdminUserDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/deactivate`,
    params,
  );
}

// U6 (docs/backlog.md), DEC-041 item 4: the Active toggle's missing ON
// half. Same permission as deactivateUser above (admin.user.deactivate);
// a display-name collision with an active account comes back as a real
// 409 (error: "duplicate_active_name") the caller surfaces rather than
// retries with a suffix.
export function reactivateUser(
  baseUrl: string,
  tenantId: string,
  userId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ user: AdminUserDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/reactivate`,
    params,
  );
}

// Settings redesign gap (migration 0044): the tenant vocabularies.
export interface VocabularyValueDto {
  value: string;
  label: string;
  /** VOC1: the picked unit on a numeric list, null on the others. */
  unit: string | null;
  /** KEGMATH1. Ounces in one keg, ONLY on the 'keg_size' vocabulary (a decimal string); null elsewhere. */
  volumeOz?: string | null;
  /** B7 Slice 1 (migration 0103). Cents, and ONLY on the 'shelf_tier'
   * vocabulary; null on every other vocabulary. */
  priceCents: number | null;
  /** VOC1-F4 (docs/build-briefs/VOC1-F4-drink-modifier-catalog.md, Section
   * 2.1; DEC-173). Cents, zero or positive, and ONLY on the
   * 'drink_modifier' vocabulary; null on every other vocabulary. */
  priceAdjustmentCents: number | null;
  /** ITEM1 Slice 3 (migration 0111). Which item kind this row's category
   * belongs to, and ONLY on the 'item_category' vocabulary; null on every
   * other vocabulary. */
  itemKindScope: string | null;
  /** ITEM2 Slice 1 (migration 0115, DEC-158). 'per_ounce' or 'whole_unit',
   * and ONLY on the 'item_kind' vocabulary; null on every other
   * vocabulary, and null on 'item_kind' itself until a manager picks one
   * (the reserved 'liquor' row stays null forever, Section 3.5 of the
   * ITEM2 brief). */
  saleMechanism: string | null;
  /** ITEM2-F7-S3. The canonical allowed methods for an item kind. */
  saleMechanisms?: ProductSaleMechanism[];
  /** ITEM2-F7-S3. False preserves the S2 compatibility screen during rollout. */
  policyEnforcementActive?: boolean;
  /** ITEM2 Slice 3 (migration 0116, DEC-158; QA-lead Ruling B). Whether
   * this tenant already has a sold order_lines row resolving to this
   * value, so the picker can render already disabled on first paint
   * rather than only after a failed write. Meaningful only on the
   * 'item_kind' vocabulary; false on every other vocabulary and every
   * other row. */
  hasSoldLines: boolean;
  /** ITEM2 Slice 4 deploy 2 (migration 0120, DEC-166). The pour_volume
   * VALUES this row's own item_kind is currently assigned
   * (item_kind_pour_volumes joined to pour_volume), in assignment order.
   * Meaningful only on the 'item_kind' vocabulary (liquor included, since
   * DEC-166 joins liquor onto the one location-wide list); an empty array
   * on every other vocabulary and every other row. */
  assignedPourVolumeValues: string[];
  /** P9-F2 (DEC-367). Version of assignedPourVolumeValues, sent back on
   * the pour-volumes save; item_kind rows only. */
  pourVolumesVersion?: string;
  sortOrder: number;
  active: boolean;
}

export function getVocabularies(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<{ vocabularies: Record<string, VocabularyValueDto[]> } & Partial<ApiErrorBody>>
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/vocabularies`);
}

export function addVocabularyValue(
  baseUrl: string,
  tenantId: string,
  vocabulary: string,
  // VOC1 (DEC-044): a numeric list sends the tapped amount and the picked
  // unit; every other one sends the display text. No caller sends a label:
  // the server derives it. B7-S3: shelf_tier sends the display text AND a
  // price (tenant_vocabulary_values_price_cents_check forbids a null
  // price_cents on a shelf_tier row), mirroring the server's own
  // AddVocabularyValueParams three-shape union (vocabularies.ts).
  params:
    | { amount: string; unit: string }
    | { displayText: string; priceCents: number }
    | { displayText: string; itemKindScope: string }
    | { displayText: string; saleMechanisms: ProductSaleMechanism[] }
    // VOC1-F4 (Section 2.1; DEC-173): the 'drink_modifier' vocabulary's own
    // shape, a non-negative integer cents adjustment, mirroring
    // priceCents' shape above one vocabulary over.
    | { displayText: string; priceAdjustmentCents: number }
    | { displayText: string },
): Promise<ApiResult<{ value: VocabularyValueDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vocabularies/${encodeURIComponent(vocabulary)}`,
    params,
  );
}

export function setVocabularyValueActive(
  baseUrl: string,
  tenantId: string,
  vocabulary: string,
  value: string,
  active: boolean,
): Promise<ApiResult<{ value: VocabularyValueDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vocabularies/${encodeURIComponent(vocabulary)}/${encodeURIComponent(value)}`,
    { active },
  );
}

// B7 Slice 1 (migration 0103, ADR-009 Section 4). Renames and/or reprices
// a value already in the list, in place; the stored identifier never
// changes. `priceCents` is only legal on the 'shelf_tier' vocabulary
// (src/domain/vocabularies.ts's updateVocabularyValue is the real
// enforcement).
export function updateVocabularyValue(
  baseUrl: string,
  tenantId: string,
  vocabulary: string,
  value: string,
  // VOC1-F4 (Section 2.1): priceAdjustmentCents is only legal on the
  // 'drink_modifier' vocabulary, the same one-vocabulary restriction
  // priceCents already carries for 'shelf_tier' (src/domain/
  // vocabularies.ts's updateVocabularyValue is the real enforcement).
  params: { label?: string; priceCents?: number; priceAdjustmentCents?: number },
): Promise<ApiResult<{ value: VocabularyValueDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vocabularies/${encodeURIComponent(vocabulary)}/${encodeURIComponent(value)}`,
    params,
  );
}

// ITEM2 Slice 2 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
// in-settings.md Section 4, "Slice 2"; DEC-158): a manager picks a new
// item kind's sale mechanism, per ounce (Wine's shape) or whole unit
// (Packaged's shape). A dedicated route, kept separate from
// updateVocabularyValue above; see src/api/server.ts's own comment on the
// route for why. There is no "unset" verb, matching the server's own
// contract.
export function updateItemKindSaleMechanism(
  baseUrl: string,
  tenantId: string,
  value: string,
  saleMechanism: "per_ounce" | "whole_unit",
): Promise<ApiResult<{ value: VocabularyValueDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/item-kinds/${encodeURIComponent(value)}/sale-mechanism`,
    { saleMechanism },
  );
}

/** ITEM2-F7-S3: atomically replaces the manager-added kind's complete
 * canonical policy set through the existing policy route. */
export function updateItemKindSaleMechanisms(
  baseUrl: string,
  tenantId: string,
  value: string,
  saleMechanisms: ProductSaleMechanism[],
): Promise<ApiResult<{ value: VocabularyValueDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/item-kinds/${encodeURIComponent(value)}/sale-mechanism`,
    { saleMechanisms },
  );
}

// ITEM2 Slice 4 deploy 2 / Slice 5 (docs/build-briefs/ITEM2-a-kind-sale-
// mechanism-is-picked-in-settings.md Section 4's "Slice 5" paragraph;
// DEC-166). Replaces one item kind's WHOLE assigned pour-volume set,
// atomically: the full ordered list of values every call, mirroring how a
// permission grid is usually saved as one call rather than one add/remove
// call per toggle. Same permission as updateItemKindSaleMechanism above.
export function setItemKindPourVolumes(
  baseUrl: string,
  tenantId: string,
  itemKind: string,
  pourVolumeValues: string[],
  expectedVersion?: string,
): Promise<
  ApiResult<
    { pourVolumes: VocabularyValueDto[]; pourVolumesVersion: string } & Partial<ApiErrorBody>
  >
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/item-kinds/${encodeURIComponent(itemKind)}/pour-volumes`,
    expectedVersion === undefined ? { pourVolumeValues } : { pourVolumeValues, expectedVersion },
  );
}

// INV27 (docs/build-briefs/INV27-item-editor-redesign.md; DEC-227 item 3).
// Settings > Inventory's default target markup % per item kind. Structural
// twin of setItemKindPourVolumes immediately above: same permission, same
// per-item-kind route shape.
export interface ItemKindTargetMarkupDto {
  itemKind: string;
  label: string;
  targetMarkupPercent: number | null;
}

export function getItemKindTargetMarkups(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ targetMarkups: ItemKindTargetMarkupDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/item-kind-target-markups`);
}

export function setItemKindTargetMarkup(
  baseUrl: string,
  tenantId: string,
  itemKind: string,
  targetMarkupPercent: number | null,
): Promise<ApiResult<{ targetMarkups: ItemKindTargetMarkupDto[] } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/item-kind-target-markups/${encodeURIComponent(itemKind)}`,
    { targetMarkupPercent },
  );
}

export function resetPin(
  baseUrl: string,
  tenantId: string,
  userId: string,
  // T9: chosenPin omitted means the generated PIN this has always
  // produced. temporaryPin comes back only on the generated path, since a
  // chosen one is already known to the manager who typed it.
  params: { idempotencyKey: string; chosenPin?: string },
): Promise<
  ApiResult<
    { user: AdminUserDto; temporaryPin?: string; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/reset-pin`,
    params,
  );
}

export function unlockUser(
  baseUrl: string,
  tenantId: string,
  userId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ user: AdminUserDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/unlock`,
    params,
  );
}

/** RBAC track (DEC-023): Settings > Users' multi-role chip group. Sends
 * the COMPLETE intended role set (never a diff), matching the
 * multi-select control's own "here is everything now checked" semantics;
 * see src/domain/permissions.ts's setUserRoles for the server-side
 * validation (at least one role required) this call can 400 against. */
export function setUserRoles(
  baseUrl: string,
  tenantId: string,
  userId: string,
  roles: string[],
): Promise<ApiResult<{ user: AdminUserDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/roles`,
    { roles },
  );
}

// ---------------------------------------------------------------------
// U1/U2: Edit User, the User Information tab.
//
// Every field is optional and follows the server's own contract exactly:
// an ABSENT key leaves that field alone, an explicit null CLEARS it, and
// a string sets it. That distinction is why these are typed
// `string | null` rather than `string | undefined`; collapsing the two
// would make "clear my phone number" indistinguishable from "I did not
// touch the phone number."
// ---------------------------------------------------------------------

export interface UpdateUserProfileInput {
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export function updateUserProfile(
  baseUrl: string,
  tenantId: string,
  userId: string,
  params: UpdateUserProfileInput,
): Promise<ApiResult<{ user: AdminUserDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    params,
  );
}

// ---------------------------------------------------------------------
// U3/U5: Edit User, the User Permissions tab.
//
// roleGrant and override are deliberately kept apart rather than
// collapsed into effectiveGrant: `source` is what the row's badge
// renders, and an Admin who cannot see whether access came from a role
// or from a person-specific exception cannot tell WHY someone has it.
// ---------------------------------------------------------------------

export type PermissionGrantSource = "role" | "individual";

export interface UserPermissionRowDto {
  permissionId: string;
  group: string;
  groupLabel: string;
  label: string;
  planned: boolean;
  roleGrant: PermissionGrant;
  /** R7: the held role keys whose own setting supplies `roleGrant`, so the Access tab can
   * say "From Lounger". Presentation only. */
  roleGrantFrom: string[];
  override: PermissionGrant | null;
  effectiveGrant: PermissionGrant;
  source: PermissionGrantSource;
  /** DEC-025's escalation cap as the CURRENT signed-in admin would hit
   * it. Presentation only: the UI disables what the server would refuse,
   * and the server refuses it again regardless of what the UI showed. */
  actorMayGrant: boolean;
  /** False when this row may not be touched at all, because the person
   * being edited holds it as "allowed" and the signed-in admin does not
   * (independent-review Finding F1). Same presentation-only caveat. */
  actorMayModify: boolean;
  /** AU17: false when "Needs manager approval" does not exist for this row (reading a screen). */
  allowsApproval: boolean;
}

/** RT2: one of the tenant's own roles (migration 0047), replacing the
 * five hardcoded names. `active` false is a RETIRED role: still readable
 * so historical records keep rendering its label, not offered in any
 * picker, and not assignable. */
export interface TenantRoleDto {
  id: string;
  role: string;
  label: string;
  active: boolean;
  /** The one role that cannot be switched off. */
  isSystem: boolean;
  /** R3-S3: one of the roles this product ships ("Built in"). Its name cannot be changed. */
  isShipped: boolean;
  /** R5: the role this one was copied from, when it was made by Make a copy. */
  createdFromRoleId: string | null;
  createdBy: string | null;
}

export function listTenantRoles(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ roles: TenantRoleDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/roles`);
}

/** Creating a role whose name matches a RETIRED one switches that one
 * back on instead (DEC-033), which is why the response carries
 * `restored`: the confirmation should say which happened rather than
 * announcing a new role that is actually an old one returning. */
export function createTenantRole(
  baseUrl: string,
  tenantId: string,
  role: string,
  label: string,
): Promise<ApiResult<{ role: TenantRoleDto; restored: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/roles`, { role, label });
}

/** R5 (DEC-313): the people a refused switch-off names. */
export interface RoleHolderDto {
  userId: string;
  displayName: string;
  otherRoles: { role: string; label: string }[];
}

export interface RoleErrorBody extends Partial<ApiErrorBody> {
  /** 409 role_held: everyone still holding the role. */
  holders?: RoleHolderDto[];
  /** 403 role_copy_over_grant: the tasks the copier cannot give out, in plain words. */
  taskLabels?: string[];
  /** 409 role_name_taken: which role owns the name, and whether it is switched on. */
  takenByRole?: string;
  takenByActive?: boolean;
  /** 409 confirmation_stale: how many people hold the role now. */
  affectedCount?: number;
}

/** R5: a role people hold is switched off only when every one of them is moved in the same
 * request: `moveAllTo` for everyone, or `moves` (person id to role) per person. */
export function retireTenantRole(
  baseUrl: string,
  tenantId: string,
  role: string,
  moves?: { moveAllTo?: string; moves?: Record<string, string> },
): Promise<
  ApiResult<
    { role: TenantRoleDto; strippedUserIds: string[]; movedUserIds: string[] } & RoleErrorBody
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/retire`,
    moves ?? {},
  );
}

export function renameTenantRole(
  baseUrl: string,
  tenantId: string,
  role: string,
  label: string,
): Promise<ApiResult<{ role: TenantRoleDto; changed: boolean } & RoleErrorBody>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}`,
    { label },
  );
}

export function copyTenantRole(
  baseUrl: string,
  tenantId: string,
  role: string,
  label: string,
): Promise<ApiResult<{ role: TenantRoleDto } & RoleErrorBody>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/copy`,
    { label },
  );
}

export interface RoleSwitchOffPlanDto {
  role: string;
  label: string;
  canSwitchOff: boolean;
  holders: (RoleHolderDto & { leftWithNoAccess: boolean })[];
  destinations: { role: string; label: string; assignable: boolean }[];
}

export function getRoleSwitchOffPlan(
  baseUrl: string,
  tenantId: string,
  role: string,
): Promise<ApiResult<RoleSwitchOffPlanDto & RoleErrorBody>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/holders`,
  );
}

/** RT1: one role's permission template. Deliberately NOT the same shape
 * as UserPermissionRowDto: a role has no override axis and no
 * effective-versus-role distinction, so offering those fields would
 * invite a caller to reason about a role as if it were a person. */
export interface RolePermissionRowDto {
  permissionId: string;
  group: string;
  groupLabel: string;
  label: string;
  planned: boolean;
  grant: PermissionGrant;
  /** DEC-025's cap as the CURRENT signed-in admin would hit it.
   * Presentation only: the server re-derives it on every write. */
  actorMayGrant: boolean;
  /** False when this row may not be touched at all, because the role
   * grants it and the signed-in admin does not hold it. Same caveat. */
  actorMayModify: boolean;
  /** AU17: false when "Needs manager approval" does not exist for this row (reading a screen). */
  allowsApproval: boolean;
}

export function getRolePermissions(
  baseUrl: string,
  tenantId: string,
  role: string,
): Promise<
  ApiResult<{ role: string; permissions: RolePermissionRowDto[] } & Partial<ApiErrorBody>>
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/permissions`,
  );
}

/** No `null` here, unlike setUserPermissionOverride: an override has a
 * value to fall back to, a role's grant IS the baseline. */
export function setRolePermission(
  baseUrl: string,
  tenantId: string,
  role: string,
  permissionId: string,
  grant: PermissionGrant,
): Promise<
  ApiResult<{ role: string; permissionId: string; grant: PermissionGrant } & Partial<ApiErrorBody>>
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/permissions/${encodeURIComponent(permissionId)}`,
    { grant },
  );
}

export type RoleChangeDirection = "widen" | "approval" | "narrow";

export interface RolePermissionChangePreviewDto {
  role: string;
  roleLabel: string;
  holders: { userId: string; displayName: string }[];
  changes: {
    permissionId: string;
    label: string;
    groupLabel: string;
    from: PermissionGrant;
    to: PermissionGrant;
    direction: RoleChangeDirection;
  }[];
  personalSettingsStillWin: {
    userId: string;
    displayName: string;
    permissionIds: string[];
    taskLabels: string[];
  }[];
}

/** R6 (DEC-313 ruling 2): what saving the staged changes would do. Read-only. */
export function previewRolePermissionChanges(
  baseUrl: string,
  tenantId: string,
  role: string,
  changes: { permissionId: string; grant: PermissionGrant }[],
): Promise<ApiResult<RolePermissionChangePreviewDto & RoleErrorBody>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/permissions/preview`,
    { changes },
  );
}

/** R6: the one atomic save. `confirmedAffectedCount` is the number of people the confirmation
 * showed; the server refuses a save whose count no longer matches. */
export function saveRolePermissions(
  baseUrl: string,
  tenantId: string,
  role: string,
  changes: { permissionId: string; grant: PermissionGrant }[],
  confirmedAffectedCount: number,
): Promise<
  ApiResult<
    {
      role: string;
      applied: { permissionId: string; from: PermissionGrant; to: PermissionGrant }[];
      affectedCount: number;
    } & RoleErrorBody
  >
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(role)}/permissions`,
    { changes, confirmedAffectedCount },
  );
}

export interface AddRolePreviewDto {
  userId: string;
  displayName: string;
  addRole: string;
  addRoleLabel: string;
  currentRoles: { role: string; label: string }[];
  gained: {
    permissionId: string;
    label: string;
    groupLabel: string;
    now: PermissionGrant;
    withRole: PermissionGrant;
  }[];
  personalSettingsStillWin: { permissionId: string; label: string; grant: PermissionGrant }[];
  actorMayAssign: boolean;
}

/** R6: what adding one role to one person would let them do. Read-only. */
export function previewAddRoleToUser(
  baseUrl: string,
  tenantId: string,
  userId: string,
  addRole: string,
): Promise<ApiResult<AddRolePreviewDto & RoleErrorBody>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/roles/preview?addRole=${encodeURIComponent(addRole)}`,
  );
}

export function getUserPermissions(
  baseUrl: string,
  tenantId: string,
  userId: string,
): Promise<ApiResult<{ permissions: UserPermissionRowDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/permissions`,
  );
}

export interface ReportAiPersonDto {
  allowed: boolean;
  capDollars: string | null;
  month: string;
  usedCents: number;
  reservedCents: number;
  canManage: boolean;
  permissionOverride?: boolean | null;
}

export function getUserReportAi(
  baseUrl: string,
  tenantId: string,
  userId: string,
): Promise<ApiResult<ReportAiPersonDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/report-ai`,
  );
}

export function setUserReportAi(
  baseUrl: string,
  tenantId: string,
  userId: string,
  capDollars: string | null,
): Promise<ApiResult<ReportAiPersonDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/report-ai`,
    { capDollars },
  );
}

/** `grant: null` CLEARS the individual override so the permission falls
 * back to whatever this person's roles give them. A grid with no way
 * back to "inherit" is a trap, which is why this is one call rather than
 * a separate delete endpoint the UI could forget to offer. */
export function setUserPermissionOverride(
  baseUrl: string,
  tenantId: string,
  userId: string,
  permissionId: string,
  grant: PermissionGrant | null,
): Promise<
  ApiResult<
    {
      permissionId: string;
      grant: PermissionGrant | null;
      roleGrant: PermissionGrant;
      effectiveGrant: PermissionGrant;
      source: PermissionGrantSource;
    } & Partial<ApiErrorBody>
  >
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/permissions/${encodeURIComponent(permissionId)}`,
    { grant },
  );
}

// ---------------------------------------------------------------------
// Discounts, slice A. The tenant's own configurable discounts and reason
// vocabulary, and putting one on a ticket or a line.
//
// ApprovalRetryParams is on the apply and remove calls because
// money.discount.apply resolves approval_required for the Assistant
// Manager tier, AND because a discount's own requires_approval flag can
// demand a second person from anyone at all. Both arrive at the client as
// the same 403 approval_required, so main.ts's requestApproval drives
// both without needing to know which one it is.
// ---------------------------------------------------------------------

/** DSC5. One clause of a discount's attribute scope.
 *
 * The dimension is ALWAYS carried with the value and never dropped:
 * "beer" is a legal value of both item_kind and line_kind, and
 * "liquor" is an item_kind that is NOT a line_kind, so a bare value could
 * not be resolved. See src/domain/discountScopes.ts. */
export interface DiscountScopeClauseDto {
  dimension: "shelf_tier" | "category" | "item_kind" | "line_kind";
  value: string;
}

export interface DiscountScheduleDto {
  weekdays: number[];
  dateFrom: string | null;
  dateTo: string | null;
  repeatYearly: boolean;
  timeFrom: string | null;
  timeTo: string | null;
}

export interface DiscountSpecialScopeEntryDto {
  kind: "category" | "item_kind" | "menu_drink" | "liquor_product";
  /** The category or item kind value, or the drink or product id. */
  value: string;
  /** Present for the two named kinds. */
  name?: string;
}

/** DSC35-S6. The list status, decided on the server in the location's zone. */
export interface DiscountScheduleStatusDto {
  state: "running" | "opens" | "ended" | "off" | "manual";
  label: string;
}

export interface DiscountDto {
  retiredByNetwork?: boolean;
  savedActive?: boolean;
  /** DSC35-S6. The schedule in words (null for a staff-applied discount).
   * Present on the settings list, absent on the floor's list. */
  scheduleSummary?: string | null;
  /** DSC35-S6. "Running now", "Opens Fri 10:00 PM", "Off", "No schedule". */
  scheduleStatus?: DiscountScheduleStatusDto;
  /** DSC35-S1. Null or absent: a staff-applied discount, no schedule. */
  schedule?: DiscountScheduleDto | null;
  /** DSC35-S2. What a scheduled special covers (a line matching ANY entry).
   * Empty or absent covers nothing. */
  specialScope?: DiscountSpecialScopeEntryDto[];
  id: string;
  tenant_id: string;
  name: string;
  active: boolean;
  markdown_type: "percentage" | "amount";
  markdown_basis_points: number | null;
  markdown_amount_cents: number | null;
  applies_to_tickets: boolean;
  applies_to_items: boolean;
  requires_approval: boolean;
  requires_reason: boolean;
  sort_order: number;
  is_shipped_default: boolean;
  /** CL3. True only for "Walkout" today: applying this discount to a
   * ticket that fully settles it also ends that ticket, before any tip is
   * offered. EDITABLE through updateDiscount (adversarial-gate fix,
   * 2026-08-29): a plain per-discount flag with no CHECK constraint
   * behind it, unlike member_daily_comp below. Owner ruling A3 (a zero
   * balance must not close a tab while a tip is unresolved) is the real
   * guard against misusing this flag and is NOT built yet
   * (docs/backlog.md CL5), so the admin UI states the consequence in
   * visible text, not a hover tooltip. */
  closes_ticket: boolean;
  /** DSC4. Applies to a member's ticket with no bartender tap. Editable
   * through updateDiscount, guarded server-side by
   * discounts_member_standing_shape_check: turning this on for a 100%
   * discount that is not the daily comp is refused. */
  auto_apply_on_membership: boolean;
  /** DSC2. Marks the one bundled-daily-drink discount. Read-only here;
   * see src/domain/discounts.ts's own comment on why a free-form toggle
   * would mostly produce constraint violations an Admin could not act
   * on. Its name, amount and active flag are the editable surface. */
  member_daily_comp: boolean;
  /** DSC5. ALWAYS present; an EMPTY array means the discount applies to
   * everything, which is what every discount meant before scoping
   * existed. */
  scope: DiscountScopeClauseDto[];
  /** TEN18. Set only on a copy the location adopted from its franchise network's published discount; null on a discount
   * the location made itself. A copy's terms are read-only and only on and off may change. */
  network_publication_id?: string | null;
  /** DSC21. Who owns the definition: Corporate for a copy of the network's published discount, else Local. */
  kind?: DiscountKind;
  /** TEN18. Present on the settings list: the network's name and whether it withdrew the publication, for a copy;
   * null for the location's own discount. */
  networkSource?: {
    networkName: string;
    withdrawn: boolean;
    required?: boolean;
    locationNames?: string[];
    /** TEN18-F5. The network's central date window (whole local dates) and stacking declaration, read-only. */
    windowStartsOn?: string | null;
    windowEndsOn?: string | null;
    membershipComposition?: "sequential" | "additive" | "exclusive";
    windowPosition?: "before" | "open" | "after" | null;
  } | null;
  created_at: string;
  updated_at: string;
}

export type DiscountKind = "local" | "corporate";

export interface DiscountReasonDto {
  id: string;
  tenant_id: string;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface DiscountApplicationDto {
  id: string;
  order_id: string;
  order_line_id: string | null;
  discount_id: string;
  discount_name_at_apply: string;
  markdown_type_at_apply: string;
  discount_reason_label_at_apply: string | null;
  applied_at: string;
  removed_at: string | null;
}

/** One live application as the order-discount read returns it, already
 * carrying what it actually took off after the clamp. */
export interface OrderDiscountApplicationDto {
  id: string;
  orderLineId: string | null;
  discountId: string;
  discountName: string;
  markdownType: string;
  markdownBasisPoints: number | null;
  markdownAmountCents: number | null;
  discountReasonLabel: string | null;
  appliedAt: string;
  /** DSC35-S7. 'scheduled' for a special bound at ring time. */
  origin: "manual" | "scheduled";
  reductionCents: number;
}

/** DSC2. A member benefit is DERIVED on every pricing pass and has no
 * discount_applications row behind it, so it is not an application and
 * carries no application id to remove. TF1: the floor lists these
 * alongside the real applications, but without a remove control, because
 * there is nothing to remove and the next pricing pass would rebuild it
 * anyway. */
/** DSC35-S7b. A scheduled special a manager took off a drink, as recorded
 * (name at apply, reason label at removal). Informational: not in any total. */
export interface RemovedScheduledSpecialDto {
  applicationId: string;
  orderLineId: string | null;
  discountName: string;
  reasonLabel: string;
}

export interface OrderMemberBenefitDto {
  kind: string;
  guestId: string;
  discountId: string;
  discountName: string;
  /** DSC29: null for a ticket-scoped standing amount benefit, which has
   * no single line. discountTargetLabel already renders null as "Whole
   * ticket", same as a manual ticket-level discount application. */
  orderLineId: string | null;
  reductionCents: number;
}

/** AU14-F5 / DEC-276. A manual discount taken off this ticket, as recorded: what it
 * was taking, who took it off, who approved, when, and why. Informational: not in any total. */
export interface RemovedManualDiscountDto {
  applicationId: string;
  orderLineId: string | null;
  discountName: string;
  reductionCents: number;
  at: string;
  byName: string | null;
  approvedByName: string | null;
  reason: string;
}

export interface OrderDiscountsDto {
  applications: OrderDiscountApplicationDto[];
  /** DSC35-S7b. */
  removedSpecials: RemovedScheduledSpecialDto[];
  /** AU14-F5. Newest first. */
  removedManual: RemovedManualDiscountDto[];
  /** M5. The drinks whose price was changed at the till, server-resolved. */
  priceChanges: LinePriceChangeDto[];
  memberBenefits: OrderMemberBenefitDto[];
  /** DUALPRICE1. Both totals when dual pricing is on; null when off. */
  dualPricing?: {
    basisPoints: number;
    cardPriceCents: number;
    cardTaxCents: number;
    cashTotalCents: number;
    cardTotalCents: number;
  } | null;
  lines: Array<{
    orderLineId: string;
    grossCents: number;
    reductionCents: number;
    netCents: number;
  }>;
  totals: {
    subtotalBeforeDiscountCents: number;
    discountCents: number;
    /** DSC35-S7. The two halves of discountCents, from the server. */
    scheduledDiscountCents: number;
    otherDiscountCents: number;
    subtotalCents: number;
    taxCents: number;
    totalCents: number;
    tipBaseCents: number;
    /** M5. Net of the changed drinks (negative when lowered). The subtotal
     * already reflects it; this only feeds the "(includes price changes
     * ...)" note, and it is never a discount. */
    priceChangesCents: number;
  };
}

/** Defaults to active discounts only, which is what the floor picker
 * wants. Location settings passes activeOnly:false so an Admin can see
 * and switch a retired seasonal promotion back on. */
export function listDiscounts(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean; forFloor?: boolean },
): Promise<
  ApiResult<
    {
      discounts: DiscountDto[];
      businessDayTimezone?: string;
      localDiscountPolicy?: { allowed: boolean; version: number };
    } & Partial<ApiErrorBody>
  >
> {
  const pairs: string[] = [];
  if (params?.activeOnly === false) {
    pairs.push("activeOnly=false");
  }
  // DSC35-S1: the floor's manual picker omits scheduled discounts.
  if (params?.forFloor === true) {
    pairs.push("forFloor=true");
  }
  const query = pairs.length > 0 ? `?${pairs.join("&")}` : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/discounts${query}`);
}

/** TEN18. One discount the franchise network published to this location, with the location's answer so far. */
export interface NetworkOfferDto {
  publicationId: string;
  name: string;
  markdownType: "percentage" | "amount";
  markdownBasisPoints: number | null;
  markdownAmountCents: number | null;
  appliesToTickets: boolean;
  appliesToItems: boolean;
  requiresApproval: boolean;
  requiresReason: boolean;
  state: "pending" | "adopted" | "declined";
  decidedAt: string | null;
  discountId: string | null;
}

export function listNetworkOffers(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<{ network: { name: string } | null; offers: NetworkOfferDto[] } & Partial<ApiErrorBody>>
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/network-publications`);
}

export function adoptNetworkOffer(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
): Promise<ApiResult<{ offer: NetworkOfferDto; discount: DiscountDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/network-publications/${encodeURIComponent(publicationId)}/adopt`,
    {},
  );
}

export function declineNetworkOffer(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
): Promise<ApiResult<{ offer: NetworkOfferDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/network-publications/${encodeURIComponent(publicationId)}/decline`,
    {},
  );
}

/** TEN19. The franchise network's recipes offered to this location (see networkRecipeOffersModel.ts for the shape). */
export function listNetworkRecipeOffers(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<{ network: { name: string } | null; offers: unknown[] } & Partial<ApiErrorBody>>
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/network-recipe-offers`);
}

export function answerNetworkRecipeOffer(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
  kind: "accept" | "decline",
): Promise<ApiResult<{ offer: unknown } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/network-recipe-offers/${encodeURIComponent(publicationId)}/${kind}`,
    {},
  );
}

export function mapNetworkRecipeOffer(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
  body: {
    priceCents?: number;
    expectedContentRevision?: number;
    mappings: { position: number; liquorProductId: string }[];
    /** MAPCREATE1 (DEC-594): slot positions to map to a new item, and the exact names the person approved adding. */
    createSlots?: number[];
    approvedCreate?: string[];
    addonMappings?: {
      optionKey: string;
      inventoryItemId: string;
      quantityPerUse?: string;
      unit?: string;
      priceAdjustmentCents?: number;
    }[];
  },
): Promise<ApiResult<{ offer: unknown; menuDrink: MenuDrinkDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/network-recipe-offers/${encodeURIComponent(publicationId)}/map`,
    body,
  );
}

/** REC7-S2. A recipient clones a recipe another franchisee shared: an ordinary, unlocked drink of its own. */
export function cloneNetworkRecipeOffer(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
  body: {
    priceCents: number;
    mappings: { position: number; liquorProductId: string }[];
    addonMappings?: {
      optionKey: string;
      inventoryItemId: string;
      quantityPerUse?: string;
      unit?: string;
      priceAdjustmentCents?: number;
    }[];
  },
): Promise<ApiResult<{ offer: unknown; menuDrink: MenuDrinkDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/network-recipe-offers/${encodeURIComponent(publicationId)}/clone`,
    body,
  );
}

/** REC7-S2. The franchisees this one can share with, and the recipes it shared (see recipeShareModel.ts). */
export function listRecipeShares(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<{ inNetwork: boolean; peers: unknown[]; shares: unknown[] } & Partial<ApiErrorBody>>
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/recipe-shares`);
}

export function shareRecipe(
  baseUrl: string,
  tenantId: string,
  body: Record<string, unknown>,
): Promise<ApiResult<{ shares: unknown[] } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/recipe-shares`, body);
}

export function withdrawRecipeShare(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
): Promise<ApiResult<{ shares: unknown[] } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/recipe-shares/${encodeURIComponent(publicationId)}/withdraw`,
    {},
  );
}

/** REC7-S1. The franchisee's own locations and the recipes it published to them (see ownRecipePublishingModel.ts). */
export function listOwnRecipePublications(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ locations: unknown[]; publications: unknown[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/recipe-publications`);
}

export function publishOwnRecipe(
  baseUrl: string,
  tenantId: string,
  body: Record<string, unknown>,
): Promise<ApiResult<{ publications: unknown[] } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/recipe-publications`, body);
}

export function listRecipePublicationSources(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ sources: unknown[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/recipe-publication-sources`);
}

export function shareSavedRecipe(
  baseUrl: string,
  tenantId: string,
  menuDrinkId: string,
  body: Record<string, unknown>,
): Promise<ApiResult<{ publicationIds: string[] } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/${encodeURIComponent(menuDrinkId)}/sharing`,
    body,
  );
}

export function withdrawOwnedRecipePublication(
  baseUrl: string,
  tenantId: string,
  publicationId: string,
): Promise<ApiResult<{ withdrawn: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/recipe-publications/${encodeURIComponent(publicationId)}/withdraw`,
    {},
  );
}

export function createDiscount(
  baseUrl: string,
  tenantId: string,
  params: {
    name: string;
    markdownType: "percentage" | "amount";
    markdownBasisPoints?: number;
    markdownAmountCents?: number;
    appliesToTickets: boolean;
    appliesToItems: boolean;
    requiresApproval?: boolean;
    requiresReason?: boolean;
    /** DSC5. Omitted means unscoped: applies to everything. */
    scope?: DiscountScopeClauseDto[];
  },
): Promise<ApiResult<{ discount: DiscountDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/discounts`, params);
}

/** Rename, re-value, retarget, and turn on or off. There is deliberately
 * no deleteDiscount: a discount that past applications reference is
 * retired, never removed, and no delete route exists to call. */
export function updateDiscount(
  baseUrl: string,
  tenantId: string,
  discountId: string,
  params: {
    name?: string;
    active?: boolean;
    markdownType?: "percentage" | "amount";
    markdownBasisPoints?: number;
    markdownAmountCents?: number;
    appliesToTickets?: boolean;
    appliesToItems?: boolean;
    requiresApproval?: boolean;
    requiresReason?: boolean;
    /** DSC4, settings rebuild SET1/SET2/SET3. Guarded server-side by
     * discounts_member_standing_shape_check; see DiscountDto's own
     * comment on auto_apply_on_membership. */
    autoApplyOnMembership?: boolean;
    /** CL3, adversarial-gate fix 2026-08-29. Unguarded server-side: see
     * DiscountDto's own comment on closes_ticket for why. */
    closesTicket?: boolean;
    /** DSC5. REPLACES the whole scope when sent; an empty array clears it
     * back to "everything". Omitted leaves it alone. */
    scope?: DiscountScopeClauseDto[];
    /** DSC35-S1. An object replaces the schedule, null returns the discount
     * to a staff-applied one, omitted leaves it alone. */
    schedule?: DiscountScheduleDto | null;
    /** DSC35-S2. An array replaces what a special covers, null clears it,
     * omitted leaves it alone. Only kind and value are sent. */
    specialScope?: Array<{ kind: DiscountSpecialScopeEntryDto["kind"]; value: string }> | null;
  },
): Promise<ApiResult<{ discount: DiscountDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/discounts/${encodeURIComponent(discountId)}`,
    params,
  );
}

export function listDiscountReasons(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean },
): Promise<ApiResult<{ discountReasons: DiscountReasonDto[] } & Partial<ApiErrorBody>>> {
  const query = params?.activeOnly === false ? "?activeOnly=false" : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/discount-reasons${query}`);
}

export function createDiscountReason(
  baseUrl: string,
  tenantId: string,
  params: { label: string },
): Promise<ApiResult<{ discountReason: DiscountReasonDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/discount-reasons`, params);
}

export function updateDiscountReason(
  baseUrl: string,
  tenantId: string,
  discountReasonId: string,
  params: { label?: string; active?: boolean },
): Promise<ApiResult<{ discountReason: DiscountReasonDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/discount-reasons/${encodeURIComponent(discountReasonId)}`,
    params,
  );
}

/** What is currently on THIS ticket and what it does to the money.
 * Computed over the order's ACTIVE lines, which is what the ticket's own
 * totals block is about; the pay sheet asks the same question about the
 * UNCOVERED lines through getPaymentPreview. */
export function getOrderDiscounts(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<OrderDiscountsDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/discounts`,
  );
}

/** Omit orderLineId for a TICKET-level discount; pass one for a single
 * drink. The discount's own "Apply to" checkboxes decide whether the
 * level asked for is allowed, and the server is the enforcement point. */
export function applyOrderDiscount(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: {
    idempotencyKey: string;
    discountId: string;
    orderLineId?: string;
    discountReasonId?: string;
    note?: string;
  } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      discountApplication: DiscountApplicationDto;
      replayed: boolean;
      settlementClose?: SettlementCloseDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/discounts`,
    params,
  );
}

/** DSC35-S7b. A manager takes one auto-applied special off one drink, with a
 * reason from the tenant's discount reasons. The server is the authority on
 * who may (money.scheduled_special.remove) and recomputes the totals. */
export function removeScheduledSpecial(
  baseUrl: string,
  tenantId: string,
  discountApplicationId: string,
  params: { idempotencyKey: string; discountReasonId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    { discountApplication: DiscountApplicationDto; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/discount-applications/${encodeURIComponent(discountApplicationId)}/remove-scheduled-special`,
    params,
  );
}

// ---------------------------------------------------------------------
// M5 (docs/build-briefs/M5-till-price-change.md; DEC-282): change a drink's
// price at the till, and put it back. The server is the authority on every
// rule; the floor only reflects it.
// ---------------------------------------------------------------------

export interface PriceChangeReasonDto {
  id: string;
  tenant_id: string;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------
// AU14-F4 / DEC-276: the shared lists of reasons for the actions that now
// require one ("reopen", "stock_override" for an 86 sold, and "movement_reversal"
// for undoing a stock movement). The server is the authority; the screens only
// offer the active ones and send the id.
// ---------------------------------------------------------------------

export type ActionReasonKind =
  | "reopen"
  | "stock_override"
  | "movement_reversal"
  | "discount_removal"
  | "settlement_difference"
  | "drawer_variance"
  | "drawer_paid_in"
  | "drawer_paid_out"
  | "drawer_drop";

export interface ActionReasonDto {
  id: string;
  tenant_id: string;
  kind: ActionReasonKind;
  label: string;
  sort_order: number;
  active: boolean;
  is_shipped_default: boolean;
  created_at: string;
  updated_at: string;
}

export function listActionReasons(
  baseUrl: string,
  tenantId: string,
  params: { kind: ActionReasonKind; activeOnly?: boolean },
): Promise<ApiResult<{ actionReasons: ActionReasonDto[] } & Partial<ApiErrorBody>>> {
  const query = `?kind=${encodeURIComponent(params.kind)}${params.activeOnly === false ? "&activeOnly=false" : ""}`;
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/action-reasons${query}`);
}

export function createActionReason(
  baseUrl: string,
  tenantId: string,
  params: { kind: ActionReasonKind; label: string },
): Promise<ApiResult<{ actionReason: ActionReasonDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/action-reasons`, params);
}

export function updateActionReason(
  baseUrl: string,
  tenantId: string,
  actionReasonId: string,
  params: { label?: string; active?: boolean },
): Promise<ApiResult<{ actionReason: ActionReasonDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/action-reasons/${encodeURIComponent(actionReasonId)}`,
    params,
  );
}

/** What the "Price changed" strip draws, resolved on the server. */
export interface LinePriceChangeDto {
  orderLineId: string;
  quotedPriceCents: number;
  priceCents: number;
  /** priceCents minus quotedPriceCents: negative when lowered. */
  changeCents: number;
  reason: string;
  setBy: string | null;
  /** Null when the person who set it could approve alone. */
  approvedBy: string | null;
  at: string;
}

/** M5-R10. One ledger row of a drink's price history, oldest first. Every flag
 * was decided on the server from the row's own snapshot. */
export interface LinePriceHistoryRowDto {
  id: string;
  kind: "set" | "restore";
  at: string;
  previousPriceCents: number;
  newPriceCents: number;
  /** newPriceCents minus previousPriceCents: negative when lowered. */
  differenceCents: number;
  quotedPriceCents: number;
  setBy: string | null;
  /** Null when the row recorded no approver (a put-back of a lowered price). */
  approvedBy: string | null;
  /** Null when the row recorded no reason (a put-back of a lowered price). */
  reason: string | null;
  halfOffOrMore: boolean;
  wentUp: boolean;
  belowCost: boolean;
  drinkCostCents: number | null;
  drinkCostBasis: "full" | "lower_bound" | "unknown" | null;
}

export interface LinePriceHistoryDto {
  orderLineId: string;
  orderId: string;
  orderName: string | null;
  itemReference: string;
  menuPriceCents: number | null;
  nowPriceCents: number | null;
  rows: LinePriceHistoryRowDto[];
}

/** M5-R10 (DEC-355). A drink's whole price change history, oldest first.
 * Read only; allowed for anyone who can read the ticket (pos.tab.open). */
export function getLinePriceHistory(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  orderLineId: string,
): Promise<ApiResult<{ history: LinePriceHistoryDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/price-history`,
  );
}

/** The reasons for a till price change, plus the tenant's "half off or more"
 * threshold in basis points of the menu price (a setting, never a number
 * baked into the browser). */
export function listPriceChangeReasons(
  baseUrl: string,
  tenantId: string,
  params?: { activeOnly?: boolean },
): Promise<
  ApiResult<
    {
      priceChangeReasons: PriceChangeReasonDto[];
      flagOffBasisPoints: number;
    } & Partial<ApiErrorBody>
  >
> {
  const query = params?.activeOnly === false ? "?activeOnly=false" : "";
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/price-change-reasons${query}`);
}

export function createPriceChangeReason(
  baseUrl: string,
  tenantId: string,
  params: { label: string },
): Promise<ApiResult<{ priceChangeReason: PriceChangeReasonDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/price-change-reasons`, params);
}

export function updatePriceChangeReason(
  baseUrl: string,
  tenantId: string,
  priceChangeReasonId: string,
  params: { label?: string; active?: boolean },
): Promise<ApiResult<{ priceChangeReason: PriceChangeReasonDto } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/price-change-reasons/${encodeURIComponent(priceChangeReasonId)}`,
    params,
  );
}

/** Change one drink's price. 403 approval_required for an assistant manager
 * (retry with ApprovalRetryParams and the same idempotencyKey). */
export function changeLinePrice(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  orderLineId: string,
  params: {
    idempotencyKey: string;
    newPriceCents: number;
    reasonId: string;
  } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      change: unknown;
      orderLine: OrderLineDto;
      priceChange: LinePriceChangeDto | null;
      replayed: boolean;
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/price-change`,
    params,
  );
}

/** M5-F1 (DEC-282). What the line's drink costs to make, for the Change
 * price sheet's below-cost warning. `known: false` carries no figure. Gated
 * like the sheet (money.price_override); 403 for a role without it. */
export interface RecipeDraftCostPreviewDto {
  locationId: string;
  locationName: string;
  complete: boolean;
  /** RECIPECOST2: lines left out because the item is not tracked, not sold, and has no cost. */
  untrackedCount?: number;
  totalCostCents: number | null;
  knownSubtotalCents: number | null;
  components: Array<{
    costCents: number | null;
    reason: "known" | "guest_choice" | "missing_item" | "missing_cost" | "not_tracked";
    source?: "received" | "vendor_price" | null;
  }>;
}

export function getRecipeDraftCost(
  baseUrl: string,
  tenantId: string,
  components: Array<{
    quantity: string;
    unit: string;
    liquorProductId: string | null;
    isLiquorSlot: boolean;
  }>,
  locationId?: string,
): Promise<ApiResult<RecipeDraftCostPreviewDto & Partial<ApiErrorBody>>> {
  const query = locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`;
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/menu-drinks/recipe-cost-preview${query}`,
    { components },
  );
}

export function getLineDrinkCost(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  orderLineId: string,
): Promise<
  ApiResult<{ known: boolean; partial?: boolean; costCents?: number } & Partial<ApiErrorBody>>
> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/drink-cost`,
  );
}

/** Put a changed drink back to its menu price. A lowered price goes back with
 * no reason and no second person (DEC-282). A RAISED price needs `reasonId`
 * and manager approval (DEC-333, M5-R2): 403 approval_required for an
 * assistant manager, retried with ApprovalRetryParams and the same key. */
export function restoreLinePrice(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  orderLineId: string,
  params: { idempotencyKey: string; reasonId?: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      change: unknown;
      orderLine: OrderLineDto;
      priceChange: null;
      replayed: boolean;
      holdIncrement?: HoldIncrementOutcomeDto;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/price-change/restore`,
    params,
  );
}

export function removeOrderDiscount(
  baseUrl: string,
  tenantId: string,
  discountApplicationId: string,
  // AU14-F5 / DEC-276: a reason from the tenant's discount_removal list is required; the
  // same reason rides the first attempt and the retry after a manager's PIN.
  params: { idempotencyKey: string; reasonId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    { discountApplication: DiscountApplicationDto; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/discount-applications/${encodeURIComponent(discountApplicationId)}/remove`,
    params,
  );
}

// TAX2-S1-F1 (docs/build-briefs/TAX2-S1-F1-tax-rate-settings.md; DEC-304, DEC-307). The sales tax
// rate as named parts with a start day. Both routes need admin.tax_rate.manage; a session without
// it never calls them (the Settings screen shows the combined rate from the ordinary settings read).
// The wire unit stays here and in ui/src/taxRateSettingsModel.ts; no screen text shows it.
export interface TaxRateComponentDto {
  component: string;
  basisPoints: number;
}

export interface TaxRateSetDto {
  locationId: string;
  rateSetId: string;
  rateSetNo: number;
  effectiveFrom: string;
  components: TaxRateComponentDto[];
  totalBasisPoints: number;
  recordedBy: string;
  recordedAt: string;
  /** First name and last initial, or null for the system or a recorder who no longer resolves. */
  recordedByName: string | null;
}

export interface TaxRatesDto {
  inEffect: {
    locationId: string | null;
    rateSetId: string | null;
    rateSetNo: number | null;
    effectiveFrom: string | null;
    components: TaxRateComponentDto[];
    totalBasisPoints: number;
    source: string;
  } | null;
  sets: TaxRateSetDto[];
}

export function getTaxRates(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<ApiResult<TaxRatesDto & Partial<ApiErrorBody>>> {
  const query = locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`;
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tax-rates${query}`);
}

// ONBW1-S2 (DEC-417): the sales tax rate and time zone suggested from one location's own address (admin.tax_rate.manage). Read only: it
// records nothing. The owner confirms through putTaxRates. `tax` and `timeZone` are read by ownerWizardModel's taxSuggestionOf and
// zoneSuggestionOf, which hold the shapes; a service that cannot answer is `{ available: false }`, never an error.
export interface TaxSuggestionAnswerDto {
  locationId: string;
  tax: unknown;
  timeZone: unknown;
}

export function getTaxSuggestion(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<TaxSuggestionAnswerDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/tax-suggestion`,
  );
}

// ONBW1-S3 (DEC-416): what the location owner's setup wizard remembers for this device's location, and the help request it can raise.
export interface SetupWizardProgressDto {
  locationId: string;
  skippedSteps: string[];
  reviewedSteps: string[];
  pausedAt: string | null;
  finishedAt: string | null;
}

export function getSetupWizard(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ progress: SetupWizardProgressDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/setup-wizard`);
}

export function putSetupWizard(
  baseUrl: string,
  tenantId: string,
  body: {
    skippedSteps?: string[];
    reviewedSteps?: string[];
    paused?: boolean;
    finished?: boolean;
  },
): Promise<ApiResult<{ progress: SetupWizardProgressDto } & Partial<ApiErrorBody>>> {
  return putJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/setup-wizard`, body);
}

export function postHelpRequest(
  baseUrl: string,
  tenantId: string,
  kind: "receipt_printer",
): Promise<
  ApiResult<{ helpRequest: { id: string; kind: string; status: string } } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/help-requests`, { kind });
}

// LEGAL1-F1: whether this device's location owner has agreed to the Terms of Service and Privacy Policy in force, and the agreement.
export interface LegalAcceptanceDto {
  termsVersion: string | null;
  acceptedAt: string | null;
  current: boolean;
}

export function getLegalAcceptance(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ acceptance: LegalAcceptanceDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/legal-acceptance`);
}

export function postLegalAcceptance(
  baseUrl: string,
  tenantId: string,
  termsVersion: string,
): Promise<ApiResult<{ acceptance: LegalAcceptanceDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/legal-acceptance`, {
    termsVersion,
  });
}

// RCPT1-S1 (DEC-401): where this location's card tips are taken, on the reader or on the printed receipt.
export function getTipMode(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<{ mode: "reader" | "receipt" } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/tip-mode`,
  );
}

export function putTipMode(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  mode: "reader" | "receipt",
): Promise<ApiResult<{ mode: "reader" | "receipt" } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/tip-mode`,
    { mode },
  );
}

// PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md, section 3.5). Settings > Tabs and payment > Card
// payments. The location is the tablet's: no location, account or URL is ever sent. The onboarding link comes back
// once and is handed straight to the browser by ui/src/cardPayments.ts.
export function getCardPayments(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<CardPaymentsForLocationDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/card-payments`);
}

// TERM8-F1 (docs/build-briefs/TERM8-F1-reader-tip-settings-screen.md): the card reader tip choices of one location, over
// TERM8's GET and PUT /tenants/:tenantId/locations/:locationId/reader-tip-settings.
export interface ReaderTipChoicesDto {
  percentages: number[];
  fixedAmountsCents: number[];
  smartThresholdCents: number;
}

export type ReaderTipProviderStateDto = "simulated" | "not_registered" | "pending" | "applied";

/** TIPONE1 (DEC-541): the reader takes the location's tablet tip choices, so the screen has only the switches. */
export interface ReaderTipSettingsDto {
  cartPauseEnabled?: boolean;
  locationId: string;
  /** What the reader receives, from the location's tip choices. */
  settings: ReaderTipChoicesDto;
  /** The location's tip choices in basis points, as stored. */
  tipChoicesBasisPoints: number[];
  enabled: boolean;
  providerState: ReaderTipProviderStateDto;
  /** PUT only. */
  syncOutcome?: string;
  syncWarning?: string;
}

/** What a PUT carries: the switch (with the cart pause optional), the cart pause alone, or a return to on. */
export type ReaderTipSettingsPutBody =
  | { enabled: boolean; cartPauseEnabled?: boolean }
  | { reset: true }
  | { cartPauseEnabled: boolean }
  | { resetCartPause: true };

export function getReaderTipSettings(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<ReaderTipSettingsDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/reader-tip-settings`,
  );
}

export function putReaderTipSettings(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: ReaderTipSettingsPutBody,
): Promise<ApiResult<ReaderTipSettingsDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/reader-tip-settings`,
    body,
  );
}

// DEPCHECKUI1 (DEC-612): the stored dependency checks of one location (GET) and a run now (POST). Shapes are
// LocationDependencyChecks in src/domain/dependencyChecks.ts.
export type SystemCheckStatusDto = "working" | "failing" | "not_set_up";
export type SystemCheckKeyDto =
  | "mytime_client_list"
  | "mytime_connection"
  | "card_payments"
  | "card_reader"
  | "background_jobs"
  | "email_delivery"
  | "tax_and_business_day"
  | "product_lookup_ai";
export interface SystemCheckDto {
  key: SystemCheckKeyDto;
  status: SystemCheckStatusDto;
  detail: string;
  checkedAt: string;
  subjectId: string | null;
  statusSince: string;
}
export interface LocationSystemChecksDto {
  locationId: string;
  locationName: string;
  checks: SystemCheckDto[];
  failing: number;
  notSetUp: number;
  oldestCheckedAt: string | null;
  stale: boolean;
}

export function getSystemChecks(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<LocationSystemChecksDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/system-checks`,
  );
}

export function runSystemChecksNow(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<LocationSystemChecksDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/system-checks/run`,
    {},
  );
}

export function postCardPaymentsOnboardingLink(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url: string; expiresAt: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/card-payments/onboarding-link`,
    {},
  );
}

export function postCardPaymentsReturn(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<CardPaymentsForLocationDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/card-payments/return`, {});
}

// PAY2-S6 (docs/build-briefs/PAY2-S6-royalty-collection-authorization.md; DEC-406). A franchise location's ACH
// authorization for royalty collection. The location is the device's and the network is the server's: nothing but the
// session is sent, and no account, customer or payment method id ever comes back. `applies: false` carries nothing else.
export interface RoyaltyAuthorizationDto {
  applies: boolean;
  status?: "not_set_up" | "pending" | "active" | "needs_reauthorization";
  updating?: boolean;
  bankLast4?: string | null;
  acceptedAt?: string | null;
  signerName?: string | null;
  mode?: "test" | "live";
  mandate?: { version: string; text: string };
}

export function getRoyaltyAuthorization(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<RoyaltyAuthorizationDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/royalty-authorization`);
}

export function postRoyaltyAuthorizationStart(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url?: string; status?: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/royalty-authorization/start`,
    {},
  );
}

export function postRoyaltyAuthorizationReturn(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<RoyaltyAuthorizationDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/royalty-authorization/return`,
    {},
  );
}

/** BANK1 (DEC-509, self-serve deposits): creates this location's connected account through the same stand-up Snip & Sip Admin uses,
 * then returns Stripe's hosted onboarding link, once. Nothing but the session is sent. */
export function postCardPaymentsSetUp(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url?: string } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/card-payments/set-up`, {});
}

// BANK1 (docs/build-briefs/BANK1-bank-accounts-in-app.md; DEC-494, DEC-509). The customer's ACH debit authorization for the
// Snip & Sip subscription invoice. Same shape and rules as the royalty authorization above: nothing but the session is sent, and
// no customer, payment method or mandate id ever comes back. `applies: false` carries nothing else.
export type SubscriptionDebitDto = RoyaltyAuthorizationDto;

export function getSubscriptionDebitAuthorization(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<SubscriptionDebitDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/subscription-debit-authorization`,
  );
}

export function postSubscriptionDebitAuthorizationStart(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url?: string; status?: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/subscription-debit-authorization/start`,
    {},
  );
}

export function postSubscriptionDebitAuthorizationReturn(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<SubscriptionDebitDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/subscription-debit-authorization/return`,
    {},
  );
}

// ONB4 slice 3 (docs/build-briefs/ONB4-S3-wizard-card-steps.md; docs/build-briefs/ONB4-S2-test-sale.md). The setup
// wizard's $1.00 test sale on the location's reader. The location is the device's: no location or account is ever sent. Start
// waits for the reader's outcome; the poll never does.
export interface CardTestSaleDto {
  testSaleId: string;
  status:
    | "waiting"
    | "approved_refunded"
    | "declined"
    | "timed_out"
    | "cancelled"
    | "refund_pending"
    | "refund_failed";
  amountCents: number;
  /** LIVETEST1: the sale moved real money. */
  live: boolean;
  message: string;
}

export function startCardTestSale(
  baseUrl: string,
  tenantId: string,
  body: { testSaleId: string; terminalId: string },
): Promise<ApiResult<{ testSale: CardTestSaleDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/card-payments/test-sale`,
    body,
  );
}

export function getCardTestSale(
  baseUrl: string,
  tenantId: string,
  testSaleId: string,
): Promise<ApiResult<{ testSale: CardTestSaleDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/card-payments/test-sale/${encodeURIComponent(testSaleId)}`,
  );
}

export interface PutTaxRatesInput {
  locationId?: string;
  effectiveFrom: string;
  components: TaxRateComponentDto[];
  idempotencyKey: string;
}

export interface LocationMoneyConfirmationDto {
  locationId: string;
  kind: "tax_rate" | "business_day";
  fingerprint: string | null;
  confirmed: boolean;
  inherited: boolean;
  display: {
    basisPoints?: number;
    effectiveFrom?: string;
    timezone?: string;
    cutoff?: string;
  };
}

function locationMoneyConfirmationPath(
  tenantId: string,
  locationId: string,
  kind: "tax_rate" | "business_day",
): string {
  return `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/money-confirmations/${kind}`;
}

export function getLocationMoneyConfirmation(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  kind: "tax_rate" | "business_day",
  rateSetId?: string,
) {
  const query = rateSetId === undefined ? "" : `?rateSetId=${encodeURIComponent(rateSetId)}`;
  return getJson<LocationMoneyConfirmationDto & Partial<ApiErrorBody>>(
    baseUrl,
    `${locationMoneyConfirmationPath(tenantId, locationId, kind)}${query}`,
  );
}

export function postLocationMoneyConfirmation(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  kind: "tax_rate" | "business_day",
  rateSetId?: string,
) {
  const query = rateSetId === undefined ? "" : `?rateSetId=${encodeURIComponent(rateSetId)}`;
  return postJson<LocationMoneyConfirmationDto & Partial<ApiErrorBody>>(
    baseUrl,
    `${locationMoneyConfirmationPath(tenantId, locationId, kind)}${query}`,
    {},
  );
}

export function putTaxRates(
  baseUrl: string,
  tenantId: string,
  input: PutTaxRatesInput,
): Promise<ApiResult<{ replayed: boolean; set: TaxRateSetDto } & Partial<ApiErrorBody>>> {
  return putJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/tax-rates`, input);
}

// AU17 (docs/build-briefs/AU17-change-history.md; DEC-309). The Change history list and its CSV,
// both read only and both gated by admin.change_history.view on the server. Mirrors
// src/domain/changeHistory.ts's page shape. Every value is already written for a person and already
// masked: the client draws it and never formats or unmasks anything.
export interface ChangeHistoryChangeDto {
  field: string;
  label: string;
  /** The item this one change is about, when an entry lists several items (a price file). */
  subject: string | null;
  was: string | null;
  now: string | null;
  /** True: the value is not shown. was and now are null. */
  hidden: boolean;
}

export interface ChangeHistoryHeadlineDto {
  before: string;
  subject: string | null;
  mid: string;
  was: string | null;
  now: string | null;
  tail: string | null;
  locked: boolean;
}

export interface ChangeHistoryEntryDto {
  id: string;
  at: string;
  atLocal: string;
  dayKey: string;
  dayLabel: string;
  timeLabel: string;
  whenLabel: string;
  actor: { id: string | null; kind: "user" | "support" | "system"; name: string; initials: string };
  area: { id: string; label: string };
  headline: ChangeHistoryHeadlineDto;
  summary: string;
  title: string;
  moreCount: number;
  changes: ChangeHistoryChangeDto[];
  facts: { label: string; value: string }[];
  note: string | null;
  recordId: string;
  recordShort: string;
}

export interface ChangeHistoryPageDto {
  entries: ChangeHistoryEntryDto[];
  nextCursor: string | null;
  total: number | null;
  timeZone: string;
  timeZoneName: string;
  from: string;
  to: string;
  todayLocal: string;
  historyStartsOn: string | null;
  areas: { id: string; label: string }[];
  people: { key: string; name: string; kind: "user" | "support" | "system" }[];
  notYetRecorded: string;
}

export function getChangeHistory(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<ApiResult<ChangeHistoryPageDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/change-history?${query}`);
}

export function getChangeHistoryCsv(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/change-history?${query}`);
}

// AU18 (docs/build-briefs/AU18-location-audit-log.md). The Audit log of one location and its CSV, read only,
// gated by admin.change_history.view. Mirrors src/domain/locationAuditLog.ts. Every value is already
// written for a person; the client draws it and formats nothing.
export interface AuditLogActorDto {
  id: string | null;
  kind: "user" | "support" | "system" | "unknown";
  name: string;
}

export interface AuditLogLinkDto {
  kind: "ticket" | "void-history" | "closeout" | "count";
  value: string;
}

export interface AuditLogEntryDto {
  id: string;
  at: string;
  atLocal: string;
  dayKey: string;
  dayLabel: string;
  timeLabel: string;
  actor: AuditLogActorDto;
  approver: AuditLogActorDto | null;
  action: { id: string; label: string };
  family: { id: string; label: string };
  summary: string;
  item: string | null;
  detail: string | null;
  amountCents: number | null;
  ticket: { id: string; name: string | null; status: string; businessDate: string } | null;
  link: AuditLogLinkDto | null;
}

export interface AuditLogPageDto {
  entries: AuditLogEntryDto[];
  nextCursor: string | null;
  /** Null when the person may read no location. */
  locationId: string | null;
  locationName?: string;
  timeZone?: string;
  timeZoneName?: string;
  from?: string;
  to?: string;
  todayLocal?: string;
  locations: { id: string; name: string; isThisTablet: boolean }[];
  people?: { key: string; name: string; kind: "user" | "support" }[];
  families?: { id: string; label: string }[];
  actions?: { id: string; label: string; family: string }[];
}

export function getAuditLog(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<ApiResult<AuditLogPageDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/audit-log?${query}`);
}

/** TEN11-S5: one support session a Software Owner opened at the location. */
export interface SupportSessionDto {
  sessionId: string;
  who: string;
  supportUserId: string;
  startedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  changeCount: number;
  byFranchisor: boolean;
  changes: { at: string; routeKind: string; subjectKind: string | null }[];
  fromDay: string;
  toDay: string;
}

export interface SupportSessionsDto {
  locationId: string | null;
  timeZone: string | null;
  sessions: SupportSessionDto[];
  locations: { id: string; name: string; isThisTablet: boolean }[];
}

export function getSupportSessions(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<SupportSessionsDto & Partial<ApiErrorBody>>> {
  const query = locationId === "" ? "" : `?locationId=${encodeURIComponent(locationId)}`;
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/support-sessions${query}`);
}

/** TEN11-F2: the location's Admin ends a live franchisor support session. */
export function endFranchisorSupportSession(
  baseUrl: string,
  tenantId: string,
  sessionId: string,
): Promise<ApiResult<{ ended: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/support-sessions/${encodeURIComponent(sessionId)}/end`,
    {},
  );
}

/** TEN11-F2: the franchisors with a live support session at this customer, for the in-app notice. */
export function getSupportNotice(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ franchisors: string[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/support-notice`);
}

/** NETBILLPAUSE1: why service is paused for non-payment (one sentence naming the paying account), for the notice above the app. */
export function getBillingNotice(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      paused?: boolean;
      message?: string | null;
      billingPath?: string | null;
    } & Partial<ApiErrorBody>
  >
> {
  // TEN11-F3: read on a timer, not by the person.
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/billing-notice`, {
    headers: BACKGROUND_REQUEST_HEADERS,
  });
}

export function getAuditLogCsv(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<BinaryFileResult> {
  return getBinaryFile(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/audit-log?${query}`);
}

// ---------------------------------------------------------------------
// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md): setup codes, devices and the tablet's own
// redemption. The routes are the S1 ones (src/api/server.ts "SEC2-S1" block).
// ---------------------------------------------------------------------

/** What POST /device-setup/redeem answers on success. */
export interface RedeemBody {
  deviceId: string;
  tenantId: string;
  locationId: string;
  locationName: string;
  businessName: string;
  displayName: string;
  /** ONBW1-S1: true when the owner's PIN is still to be chosen, so the device goes to the welcome screens. */
  welcome?: boolean;
}

/**
 * A tablet redeeming a setup code. Anonymous by design: the code is the authority, and no tenant is named in
 * the path (the code names it). It reads Retry-After itself because a throttled source (429) is told how long
 * to wait in that header. The code and secret are sent in the body and never logged here.
 */
export async function redeemSetupCode(
  baseUrl: string,
  params: { code: string; deviceSecret: string },
): Promise<
  ApiResult<(RedeemBody & Partial<ApiErrorBody>) | ApiErrorBody> & { retryAfter: string | null }
> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/device-setup/redeem`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: params.code, deviceSecret: params.deviceSecret }),
      cache: "no-store",
    });
  } catch (cause) {
    return { ...networkErrorResult<ApiErrorBody>(cause), retryAfter: null };
  }
  const parsed = (await readJsonBody(response)) as
    (RedeemBody & Partial<ApiErrorBody>) | ApiErrorBody;
  noteResponse(response.status, parsed, "revoked");
  return {
    status: response.status,
    body: parsed,
    networkError: false,
    retryAfter: response.headers.get("retry-after"),
  };
}

/**
 * ONBW1-S1 (DEC-416): a device opening its welcome link. Anonymous by design: the link is the authority and names no
 * tenant. The token is sent in the BODY (it reached this page in the address fragment, never a query string) and is
 * never logged here. Same answers as redeemSetupCode, plus `welcome` when the owner's PIN is still to be chosen.
 */
export async function redeemWelcomeLink(
  baseUrl: string,
  params: { token: string; deviceSecret: string },
): Promise<
  ApiResult<(RedeemBody & Partial<ApiErrorBody>) | ApiErrorBody> & { retryAfter: string | null }
> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/device-setup/welcome`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token: params.token, deviceSecret: params.deviceSecret }),
      cache: "no-store",
    });
  } catch (cause) {
    return { ...networkErrorResult<ApiErrorBody>(cause), retryAfter: null };
  }
  const parsed = (await readJsonBody(response)) as
    (RedeemBody & Partial<ApiErrorBody>) | ApiErrorBody;
  noteResponse(response.status, parsed, "revoked");
  return {
    status: response.status,
    body: parsed,
    networkError: false,
    retryAfter: response.headers.get("retry-after"),
  };
}

/** What the PIN choice screen shows. */
export interface WelcomePendingDto {
  ownerName: string;
  businessName: string;
  locationName: string;
  deviceName: string;
  pinMinLength: number;
  pinMaxLength: number;
}

/** ONBW1-S1: the device asks whether a welcome is waiting for its owner's PIN. Authorized by the device credential
 * alone (header only). 409 `welcome_not_pending` means nothing is waiting; 401 means the credential is not recognised. */
export function getWelcomePending(
  baseUrl: string,
  tenantId: string,
  deviceToken: string,
): Promise<ApiResult<{ welcome: WelcomePendingDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/device-setup/welcome`, {
    anonymousOk: true,
    headers: { [DEVICE_TOKEN_HEADER]: deviceToken },
  });
}

/** ONBW1-S1: the owner's chosen PIN. Answers as a sign-in does (a session token), with no forced change after. The PIN
 * travels in the body only and is never logged. */
export function chooseWelcomePin(
  baseUrl: string,
  tenantId: string,
  deviceToken: string,
  pin: string,
): Promise<ApiResult<SignInBody & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/device-setup/welcome/pin`,
    { pin },
    undefined,
    { anonymousOk: true, headers: { [DEVICE_TOKEN_HEADER]: deviceToken } },
  );
}

/**
 * UX89: a device that has just been set up names itself, once. Authorized by its own device credential (the
 * X-Device-Token header, never the body or a URL), with the tenant in the path. There is no session yet, so this
 * is not patchJson. The credential and the name are never logged. A 409 `device_naming_closed` means the window
 * (15 minutes, or the first sign-in) is over.
 */
export async function nameDeviceAtSetup(
  baseUrl: string,
  tenantId: string,
  deviceToken: string,
  displayName: string,
): Promise<
  ApiResult<({ device: DeviceDto; replayed: boolean } & Partial<ApiErrorBody>) | ApiErrorBody>
> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/tenants/${encodeURIComponent(tenantId)}/device-setup/name`, {
      method: "PATCH",
      headers: { "content-type": "application/json", [DEVICE_TOKEN_HEADER]: deviceToken },
      body: JSON.stringify({ displayName }),
      cache: "no-store",
    });
  } catch (cause) {
    return networkErrorResult<ApiErrorBody>(cause);
  }
  const parsed = (await readJsonBody(response)) as
    ({ device: DeviceDto; replayed: boolean } & Partial<ApiErrorBody>) | ApiErrorBody;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

export interface TabletDevicesBody {
  devices: DeviceDto[];
  locations: LocationDto[];
  businessName: string;
}

export function listTabletDevices(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<TabletDevicesBody & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/devices`);
}

export function listTabletSetupCodes(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ setupCodes: SetupCodeDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/device-setup-codes`);
}

export function issueTabletSetupCode(
  baseUrl: string,
  tenantId: string,
  params: { idempotencyKey: string; locationId: string; displayName: string },
): Promise<
  ApiResult<
    {
      setupCode: SetupCodeDto;
      /** XXXXX-XXXXX, only on the call that made the code. */
      code: string | null;
      replayed: boolean;
      message?: string;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/device-setup-codes`, params);
}

export function revokeTabletSetupCode(
  baseUrl: string,
  tenantId: string,
  codeId: string,
): Promise<ApiResult<{ setupCode: SetupCodeDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/device-setup-codes/${encodeURIComponent(codeId)}/revoke`,
    {},
  );
}

export function revokeTablet(
  baseUrl: string,
  tenantId: string,
  deviceId: string,
): Promise<ApiResult<{ device: DeviceDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/devices/${encodeURIComponent(deviceId)}/revoke`,
    {},
  );
}

/** UX89: a manager renames a device (Settings > Devices > Rename). Needs a signed-in session. */
export function renameTablet(
  baseUrl: string,
  tenantId: string,
  deviceId: string,
  displayName: string,
): Promise<ApiResult<{ device: DeviceDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return patchJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/devices/${encodeURIComponent(deviceId)}`,
    { displayName },
  );
}

/** TEN12 (DEC-338): the customer, location and device names the server holds for this device's credential. Device
 * credential only (no session), so it works on the sign-in screen; a 401 means the credential is not recognised. */
export function getDeviceIdentity(
  baseUrl: string,
  tenantId: string,
  deviceToken: string,
): Promise<
  ApiResult<
    { businessName: string; locationName: string; deviceName: string } & Partial<ApiErrorBody>
  >
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/device-identity`, {
    anonymousOk: true,
    headers: { [DEVICE_TOKEN_HEADER]: deviceToken },
  });
}

/** LOC2: add a location (POST .../locations, admin.location.manage). A location starts clean and takes the customer's tax rate,
 * time zone and tip presets. */
export interface AddLocationInput {
  idempotencyKey: string;
  name: string;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export function addLocation(
  baseUrl: string,
  tenantId: string,
  params: AddLocationInput,
): Promise<
  ApiResult<{ location: TenantLocationNameDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/locations`, params);
}

/** LOC2: set the COMPLETE list of locations a person works at (PUT .../users/:id/locations, admin.location.manage). */
export function setUserLocations(
  baseUrl: string,
  tenantId: string,
  userId: string,
  locationIds: string[],
): Promise<
  ApiResult<{ userId: string; locationIds: string[]; changed: boolean } & Partial<ApiErrorBody>>
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/locations`,
    { locationIds },
  );
}

/** TEN12 (DEC-338): a tenant's locations by name (GET .../locations, admin.user.view). */
export interface TenantLocationNameDto {
  id: string;
  name: string;
  isMain: boolean;
  /** LOC3: the location's address as one line, or null when it has none. */
  address?: string | null;
}

export function listTenantLocations(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ locations: TenantLocationNameDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/locations`);
}

// INVXFER1-S2 (docs/build-briefs/INVXFER1-stock-transfers-between-bars.md section 9): the transfer routes.
export interface TransferLocationDto {
  id: string;
  name: string;
  isThisDevice: boolean;
}

export interface TransferRowDto {
  id: string;
  from_location_id: string;
  to_location_id: string;
  state: "sent" | "received" | "cancelled";
  note: string | null;
  sent_by_user_id: string;
  sent_at: string;
  received_at: string | null;
  cancelled_at: string | null;
  line_count: number;
  sent_by_display_name: string | null;
}

export interface TransferLineDto {
  id: string;
  liquorProductId: string;
  componentReference: string;
  quantity: string;
  unit: string;
  enteredQuantity: string | null;
  enteredUnit: string | null;
  receivedQuantity: string | null;
  exception: { shortQuantity: string; note: string | null } | null;
}

export interface TransferViewDto {
  transfer: TransferRowDto;
  lines: TransferLineDto[];
}

function transfersPath(tenantId: string): string {
  return `/tenants/${encodeURIComponent(tenantId)}/inventory/transfers`;
}

export function listTransferLocations(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ locations: TransferLocationDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `${transfersPath(tenantId)}/locations`);
}

export function listTransfers(
  baseUrl: string,
  tenantId: string,
  params: { state?: "sent" | "received" | "cancelled"; background?: boolean } = {},
): Promise<ApiResult<{ transfers: TransferRowDto[] } & Partial<ApiErrorBody>>> {
  const query = params.state === undefined ? "" : `?state=${params.state}`;
  return getJson(
    baseUrl,
    `${transfersPath(tenantId)}${query}`,
    params.background === true ? { headers: BACKGROUND_REQUEST_HEADERS } : {},
  );
}

export function getTransferDetail(
  baseUrl: string,
  tenantId: string,
  transferId: string,
): Promise<ApiResult<{ transfer: TransferViewDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `${transfersPath(tenantId)}/${encodeURIComponent(transferId)}`);
}

export function sendTransfer(
  baseUrl: string,
  tenantId: string,
  body: {
    idempotencyKey: string;
    toLocationId: string;
    lines: Array<{ liquorProductId: string; quantity: string; unit: string }>;
  },
): Promise<ApiResult<{ transfer: TransferViewDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, transfersPath(tenantId), body);
}

export function receiveTransfer(
  baseUrl: string,
  tenantId: string,
  transferId: string,
  body: {
    idempotencyKey: string;
    lines: Array<{ lineId: string; receivedQuantity: string }>;
    exceptionNote?: string;
  },
): Promise<ApiResult<{ transfer: TransferViewDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `${transfersPath(tenantId)}/${encodeURIComponent(transferId)}/receive`,
    body,
  );
}

export function cancelTransfer(
  baseUrl: string,
  tenantId: string,
  transferId: string,
  body: { idempotencyKey: string },
): Promise<ApiResult<{ transfer: TransferViewDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `${transfersPath(tenantId)}/${encodeURIComponent(transferId)}/cancel`,
    body,
  );
}

// CASH1-S1-U1 (docs/build-briefs/CASH1-S1-U1-drawer-screens.md): the cash drawer routes of
// CASH1-S1. A drawer belongs to the session's bound device, so no device or location id travels in a body. The counted
// amount is built from the stepper counts (denominations, cents to a count) and never typed.

export interface CashDrawerSessionDto {
  id: string;
  deviceId: string;
  custodian: { id: string; name: string };
  businessDate: string;
  openedAt: string;
  status: "open" | "closed";
  openingFloatCents: number;
  standardFloatCents: number;
  floatVarianceCents: number;
  /** True once the one recount has been used; the next submission commits. */
  recountUsed: boolean;
  closedAt: string | null;
  countedCents: number | null;
  /** The next three exist only for a session holding drawer.view_expected. */
  expectedCents?: number | null;
  varianceCents?: number | null;
  toleranceCents?: number | null;
}

export type CashDrawerCountBody = {
  idempotencyKey: string;
  amountCents: number;
  denominations: Record<string, number>;
};

export function getCurrentCashDrawer(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      session: CashDrawerSessionDto | null;
      /** CASH1-S2's per-location switch. Absent today; a missing or non-true value is read as off. */
      cashEnabled?: unknown;
      /** RCPT1-S5: "none", "pending", "ok" or "did_not_open" for the open drawer's newest kick. */
      drawerKick?: unknown;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/current`);
}

/** RCPT1-S5. "Open drawer": a kick on demand for this device's open drawer. Needs drawer.open; the server audits it. */
export function kickCashDrawer(
  baseUrl: string,
  tenantId: string,
  body: { idempotencyKey: string },
): Promise<ApiResult<{ jobId: string; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/kick`, body);
}

/** CASH1-S2-U1-F1. What Pay needs, behind the pay permission: the location's cash switch and whether a drawer is open here. */
export function getCashTenderState(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ cashEnabled?: unknown; drawerOpen?: unknown } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/tender-state`);
}

export function openCashDrawer(
  baseUrl: string,
  tenantId: string,
  body: CashDrawerCountBody,
): Promise<
  ApiResult<{ session: CashDrawerSessionDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/sessions`, body);
}

export function closeCashDrawer(
  baseUrl: string,
  tenantId: string,
  sessionId: string,
  body: CashDrawerCountBody & { reasonId?: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      outcome: "closed" | "recount_required";
      session: CashDrawerSessionDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/sessions/${encodeURIComponent(sessionId)}/close`,
    body,
  );
}

// CASH1-S2-U1 (docs/build-briefs/CASH1-S2-U1-pay-screens.md): this location's cash switch in Settings, Location. The read is the
// existing settings read (admin.tenant_settings.view); the write is admin.location_settings.manage and is audited by the server.
export interface CashDrawerSettingsDto {
  locationId: string;
  locationName: string;
  standardFloatCents: number;
  toleranceCents: number;
  paidOutApprovalCents: number;
  cashEnabled: boolean;
}

export function getCashDrawerSettings(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<CashDrawerSettingsDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/cash-drawer-settings`,
  );
}

export function putCashEnabled(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: { cashEnabled: boolean },
): Promise<ApiResult<CashDrawerSettingsDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/cash-drawer-settings`,
    body,
  );
}

/** CASH1-S5: the three drawer figures, saved together over the same route. */
export function putCashDrawerSettings(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: { standardFloatCents: number; toleranceCents: number; paidOutApprovalCents: number },
): Promise<ApiResult<CashDrawerSettingsDto & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/cash-drawer-settings`,
    body,
  );
}

// AUTOPAR1 (docs/build-briefs/AUTOPAR1-automatic-par-levels.md; PRD P0.12; DEC-412): automatic par levels for one location.
export interface AutoParSettingsDto {
  enabled: boolean;
  bufferBasisPoints: number;
  lookbackWeeks: number;
  historyWeeks: number;
  enabledAt: string | null;
  lastRunAt: string | null;
}

export interface AutoParVendorDto {
  id: string;
  name: string;
  paceDays: number | null;
  orderWeekday: string | null;
  leadDays: number;
  itemCount: number;
}

export interface AutoParOverviewDto {
  settings: AutoParSettingsDto;
  vendors: AutoParVendorDto[];
  offPrompt: { keepCount: number; restoreCount: number; noEarlierCount: number };
  heldCount: number;
  canSetPace: boolean;
}

export type AutoParChangeTagDto = "up" | "down" | "same" | "held" | "would_be" | "na";

export interface AutoParItemDto {
  liquorProductId: string;
  preferredVendorId?: string | null;
  eligible?: boolean;
  name: string;
  size: string | null;
  onHand: string | null;
  countUnit: string | null;
  parNow: string | null;
  usedPerWeek: string | null;
  autoPar: string | null;
  status: string;
  historyDays: number | null;
  tag: AutoParChangeTagDto;
  change: string | null;
  heldPar: string | null;
  locked: boolean;
}

function autoParPath(tenantId: string, locationId: string, rest = ""): string {
  return `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/auto-par${rest}`;
}

export function getAutoPar(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<AutoParOverviewDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, autoParPath(tenantId, locationId));
}

export function getAutoParItems(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  vendorId?: string,
): Promise<ApiResult<{ items: AutoParItemDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    autoParPath(
      tenantId,
      locationId,
      vendorId === undefined ? "/items" : `/items?vendorId=${encodeURIComponent(vendorId)}`,
    ),
  );
}

export function putAutoParSettings(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  body: {
    enabled?: boolean;
    restore?: boolean;
    bufferBasisPoints?: number;
    lookbackWeeks?: number;
    historyWeeks?: number;
  },
): Promise<ApiResult<{ settings: AutoParSettingsDto } & Partial<ApiErrorBody>>> {
  return putJson(baseUrl, autoParPath(tenantId, locationId, "/settings"), body);
}

export function postAutoParRecalculate(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<
  ApiResult<{ summary: { ran: boolean; applied: number; held: number } } & Partial<ApiErrorBody>>
> {
  return postJson(baseUrl, autoParPath(tenantId, locationId, "/recalculate"), {});
}

export function putAutoParItemLock(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  liquorProductId: string,
  locked: boolean,
): Promise<ApiResult<{ locked: boolean } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    autoParPath(tenantId, locationId, `/items/${encodeURIComponent(liquorProductId)}/lock`),
    { locked },
  );
}

export function postAutoParAccept(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  liquorProductId: string,
  heldPar: string,
): Promise<ApiResult<{ parLevel: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    autoParPath(tenantId, locationId, `/items/${encodeURIComponent(liquorProductId)}/accept`),
    { heldPar },
  );
}

/** AUTOPAR1: a vendor's order pace, usual order weekday and lead time. `paceDays: null` clears the pace. */
export function putVendorOrderPace(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
  body: { paceDays?: number | null; orderWeekday?: string | null; leadDays?: number },
): Promise<ApiResult<{ vendor: VendorDto } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/vendors/${encodeURIComponent(vendorId)}/order-pace`,
    body,
  );
}

// CASH1-S3 (docs/build-briefs/CASH1-S3-drawer-moves-and-open-at-close.md): cash paid in, paid out or dropped to the safe on the
// device's open drawer. The amount is built from the stepper counts and never typed. A paid out over the location's threshold answers
// 403 approval_required and is retried with a manager's approval.
export type CashDrawerMovePath = "paid-in" | "paid-out" | "drop";

export function postCashDrawerMove(
  baseUrl: string,
  tenantId: string,
  sessionId: string,
  kind: CashDrawerMovePath,
  body: CashDrawerCountBody & { reasonId: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      move: { id: string; kind: string; amountCents: number; reasonLabel: string | null };
      session: CashDrawerSessionDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/cash-drawer/sessions/${encodeURIComponent(sessionId)}/${kind}`,
    body,
  );
}

// ---------------------------------------------------------------------
// RCPT1-S3 (docs/build-briefs/RCPT1-S3-receipts-and-awaiting-tip.md). Printed receipts and the tip written on them. See
// src/domain/receiptTip.ts (the authorization) and src/domain/receiptPrint.ts (the rest).
// ---------------------------------------------------------------------

export type ReceiptAuthorizationOutcomeDto =
  | { outcome: "authorized"; replayed: boolean }
  | {
      outcome: "reader";
      reason:
        "tip_mode_reader" | "not_whole_ticket" | "overcapture_unsupported" | "provider_unsupported";
      replayed: boolean;
    };

/** Authorizes a whole-ticket card sale for a tip to be written on the receipt. `reader` says to take the sale the ordinary way. */
export function authorizeReceiptSale(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; terminalId?: string },
): Promise<ApiResult<ReceiptAuthorizationOutcomeDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-authorization`,
    params,
  );
}

export type ReceiptAuthorizationStateDto =
  | { awaiting: false }
  | {
      awaiting: true;
      orderId: string;
      authorizedCents: number;
      subtotalCents: number;
      taxCents: number;
      balanceCents: number;
      tipCapCents: number;
      cardBrand: string | null;
      cardLast4: string | null;
      captureBeforeAt: string | null;
    };

export function getReceiptAuthorization(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<ReceiptAuthorizationStateDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-authorization`,
  );
}

export interface LocationPrinterChoiceDto {
  id: string;
  name: string;
  online: boolean;
}

export type ReceiptPrintOutcomeDto =
  | { outcome: "queued"; printer: { id: string; name: string } }
  | {
      outcome: "printer_offline";
      reason: "no_printer" | "offline";
      printers: LocationPrinterChoiceDto[];
    };

/** Prints both copies to the location's online printer, or to `printerId`. `printer_offline` queues nothing. */
export function printReceipt(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; printerId?: string },
): Promise<ApiResult<ReceiptPrintOutcomeDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-print`,
    params,
  );
}

/** Reprints one copy. The merchant copy needs receipt.reprint (a manager's PIN for some roles), so it accepts the approval retry. */
export function reprintReceipt(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  copy: "customer" | "merchant",
  params: { idempotencyKey: string; printerId?: string } & ApprovalRetryParams,
): Promise<ApiResult<ReceiptPrintOutcomeDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-reprint/${copy}`,
    params,
  );
}

/** A manager agrees to charge the tip limit instead of a larger tip written on the receipt. Returns the limit. */
export function approveReceiptTipCap(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string } & ApprovalRetryParams,
): Promise<ApiResult<{ tipCapCents: number } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-tip-approval`,
    params,
  );
}

/** RCPT1-S6. A manager accepts a tip the guest wrote above the limit: records the figure and the reason. The server charges the limit later
 * through the payments route and keeps the rest for RCPT1-S3-F1. `uncollectedTipCents` is the server's figure for the remainder. */
export function recordReceiptTipOverride(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; writtenTipCents: number; reason: string } & ApprovalRetryParams,
): Promise<
  ApiResult<
    {
      id: string;
      orderId: string;
      writtenTipCents: number;
      capCents: number;
      chargedTipCents: number;
      uncollectedTipCents: number;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-tip-override`,
    params,
  );
}

/** RCPT1-S3-F1 (DEC-489). The part of an approved over-limit tip still to collect. */
export interface ReceiptTipRemainderStateDto {
  orderId: string;
  state: "none" | "cap_not_charged" | "open" | "pending" | "collected" | "left_uncollected";
  overrideId: string | null;
  remainderCents: number;
  writtenTipCents: number;
  capCents: number;
  failedAttempts: number;
  leaveReason: "guest_left" | "cancelled" | null;
  collectedPaymentAttemptId: string | null;
}

export function getReceiptTipRemainder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<ReceiptTipRemainderStateDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-tip-remainder`,
  );
}

/** Sends a card-present charge for exactly the remainder (the server reads the figure; none is sent) to the terminal's remembered reader. */
export function collectReceiptTipRemainder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; terminalId?: string },
): Promise<
  ApiResult<
    {
      paymentAttempt: PaymentAttemptDto;
      failureKind?: "declined" | "customer_canceled" | "reader_error";
      tipSplits: PaymentTipSplitDto[];
      state: ReceiptTipRemainderStateDto;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-tip-remainder/collect`,
    params,
  );
}

/** Cancel or Guest has left: the remainder is recorded as not collected, with who and why. */
export function leaveReceiptTipRemainder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  params: { idempotencyKey: string; reason: "guest_left" | "cancelled" },
): Promise<
  ApiResult<{ state: ReceiptTipRemainderStateDto; replayed: boolean } & Partial<ApiErrorBody>>
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/receipt-tip-remainder/leave`,
    params,
  );
}

// INVIMP1 (docs/build-briefs/INVIMP1-inventory-import.md): import current inventory from a CSV or XLSX file. The server reads
// the file at every step, so the file is sent again each time and no parsed rows ever travel from the browser.
export interface InventoryImportColumnDto {
  index: number;
  heading: string;
  samples: string[];
  distinctValues?: string[];
}

export interface InventoryImportInspectDto {
  fileName: string;
  sheets: Array<{ name: string; rowCount: number }>;
  sheet: { name: string; rowCount: number; columns: InventoryImportColumnDto[] };
  suggestedMapping: Record<string, number>;
  suggestions?: Record<string, { fieldId: string | null; reason: string }>;
  /** BACKBAR1: the headings are a Backbar export's, so the mapping was filled in from them. */
  backbarRecognized?: boolean;
  fields: Array<{
    id: string;
    label: string;
    required: boolean;
    examples?: string[];
    examplesState?: "ready" | "empty" | "unavailable";
    vocabulary?: Array<{ value: string; label: string }>;
  }>;
  kinds: Array<{ value: string; label: string }>;
}

export interface InventoryImportNewThingDto {
  kind: "category" | "container type" | "vendor";
  label: string;
}

export interface InventoryImportPreviewRowDto {
  rowNumber: number;
  status: "new" | "update" | "error";
  name: string;
  kind: string | null;
  size: string | null;
  par: string | null;
  costCents: number | null;
  stock: string | null;
  matchedBy: "barcode" | "name" | null;
  reasons: string[];
  notes: string[];
  newThings: InventoryImportNewThingDto[];
}

export interface InventoryImportPreviewDto {
  uploadId: string;
  counts: { rows: number; new: number; update: number; error: number };
  newThings: InventoryImportNewThingDto[];
  rows: InventoryImportPreviewRowDto[];
  rowsTruncated: boolean;
  startingStock: { rowsToWrite: number; rowsSkipped: number };
  shelf?: {
    pricingModel: "shelf" | "per_ounce";
    rowsWithoutShelf: number;
    tiers: Array<{ value: string; label: string }>;
  };
}

export interface InventoryImportApplyDto {
  uploadId: string;
  fileName: string;
  sheet: string;
  added: number;
  updated: number;
  failed: number;
  needsPricing?: number;
  needsShelf?: number;
  stockSet: number;
  stockSkipped: number;
  newThings: InventoryImportNewThingDto[];
  failedRows: Array<{ rowNumber: number; name: string; reason: string }>;
  failedRowsTruncated: boolean;
  failedRowsCsv: string;
  replayed: boolean;
}

export interface InventoryImportChoices {
  locationId?: string;
  sheet?: string | undefined;
  mapping: Record<string, number>;
  defaultItemKind?: string | undefined;
  defaultShelfTier?: string | undefined;
  setStartingStock: boolean;
}

const inventoryImportBase = (tenantId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}/inventory-import`;

function inventoryImportForm(
  file: File,
  choices: Partial<InventoryImportChoices> & { uploadId?: string },
): FormData {
  const form = new FormData();
  if (choices.locationId !== undefined) form.append("locationId", choices.locationId);
  if (choices.sheet !== undefined) form.append("sheet", choices.sheet);
  if (choices.mapping !== undefined) form.append("mapping", JSON.stringify(choices.mapping));
  if (choices.defaultItemKind !== undefined)
    form.append("defaultItemKind", choices.defaultItemKind);
  if (choices.defaultShelfTier !== undefined)
    form.append("defaultShelfTier", choices.defaultShelfTier);
  if (choices.setStartingStock !== undefined) {
    form.append("setStartingStock", choices.setStartingStock ? "true" : "false");
  }
  if (choices.uploadId !== undefined) form.append("uploadId", choices.uploadId);
  form.append("file", file, file.name);
  return form;
}

export function inspectInventoryImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  sheet?: string,
): Promise<ApiResult<InventoryImportInspectDto & Partial<ApiErrorBody>>> {
  return postForm(
    baseUrl,
    `${inventoryImportBase(tenantId)}/inspect`,
    inventoryImportForm(file, sheet === undefined ? {} : { sheet }),
  );
}

export function previewInventoryImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  choices: InventoryImportChoices,
): Promise<ApiResult<InventoryImportPreviewDto & Partial<ApiErrorBody>>> {
  return postForm(
    baseUrl,
    `${inventoryImportBase(tenantId)}/preview`,
    inventoryImportForm(file, choices),
  );
}

export function applyInventoryImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  choices: InventoryImportChoices,
  uploadId: string,
): Promise<ApiResult<InventoryImportApplyDto & Partial<ApiErrorBody>>> {
  return postForm(
    baseUrl,
    `${inventoryImportBase(tenantId)}/apply`,
    inventoryImportForm(file, { ...choices, uploadId }),
  );
}

// RECIPEAI1 (DEC-590; docs/build-briefs/RECIPEAI1-ai-recipe-import.md): the AI recipe import on Settings, Recipes. The server reads
// the file at every step (the browser sends it again each time and never parsed rows); the AI only chooses columns.
export interface RecipeImportMappingDto {
  layout: "line_per_row" | "recipe_per_row";
  recipeName: number;
  category: number | null;
  buildCharge: number | null;
  defaultCategory: string | null;
  defaultUnit: string | null;
  ingredient: number | null;
  amount: number | null;
  unit: number | null;
  ingredientGroups: Array<{ item: number; amount: number | null; unit: number | null }>;
  ingredientText: number | null;
}

export interface RecipeImportStatusDto {
  available: boolean;
  message: string;
}

export interface RecipeImportMapDto {
  fileName: string;
  sheets: Array<{ name: string; rowCount: number }>;
  sheet: string;
  headers: string[];
  rowCount: number;
  mapping: RecipeImportMappingDto;
}

export interface RecipeImportSkippedDto {
  rowNumber: number | null;
  recipe: string | null;
  reason: string;
}

export interface RecipeImportPreviewDto {
  mapping: RecipeImportMappingDto;
  categories: Array<{ value: string; label: string }>;
  example: null | {
    index: number;
    name: string;
    category: string;
    categoryFromFile: boolean;
    buildChargeCents: number;
    lines: Array<{
      key: string;
      text: string;
      amount: number | null;
      unit: string | null;
      item: { id: string; name: string } | null;
      /** RECIPEIMPORT2: why this line is left out of the saved recipe, or null. */
      problem: string | null;
    }>;
  };
  counts: {
    recipesFound: number;
    recipesToImport: number;
    recipesSkipped: number;
    lines: number;
    linesFlagged: number;
    unmatchedNames: number;
    unmatchedLines: number;
    rowsSkipped: number;
  };
  unmatched: Array<{
    key: string;
    name: string;
    lineCount: number;
    candidates: Array<{ id: string; name: string }>;
  }>;
  skipped: RecipeImportSkippedDto[];
  skippedTruncated: boolean;
  flaggedLines: RecipeImportFlaggedLineDto[];
  flaggedTruncated: boolean;
  items: Array<{ id: string; name: string }>;
  itemsTruncated: boolean;
}

export interface RecipeImportFlaggedLineDto {
  rowNumber: number;
  recipe: string;
  text: string;
  reason: string;
}

export interface RecipeImportApplyDto {
  uploadId: string;
  created: number;
  skipped: number;
  linesSkipped: number;
  /** RECIPEIMPORT2. Absent on an answer stored before. */
  flaggedLines?: RecipeImportFlaggedLineDto[];
  /** LIQSEL1. Recipes saved with liquor selection off, and why. */
  liquorSelectionOff?: Array<{ recipe: string; reason: string }>;
  createdRecipes: Array<{ id: string; name: string }>;
  skippedRecipes: RecipeImportSkippedDto[];
  /** RECIPEAI1-F1 (DEC-593). Absent on an answer stored before "Create it" existed. */
  itemsCreated?: number;
  itemsReused?: number;
  createdItems?: Array<{ id: string; name: string }>;
  replayed: boolean;
}

const recipeImportBase = (tenantId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}/recipe-import`;

export function recipeImportStatus(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<RecipeImportStatusDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `${recipeImportBase(tenantId)}/status`);
}

function recipeImportForm(file: File, fields: Record<string, string | undefined>): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields))
    if (value !== undefined) form.append(name, value);
  form.append("file", file, file.name);
  return form;
}

export function mapRecipeImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  sheet?: string,
): Promise<ApiResult<RecipeImportMapDto & Partial<ApiErrorBody>>> {
  return postForm(baseUrl, `${recipeImportBase(tenantId)}/map`, recipeImportForm(file, { sheet }));
}

export function previewRecipeImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  choices: { sheet?: string; mapping: RecipeImportMappingDto; example: number },
): Promise<ApiResult<RecipeImportPreviewDto & Partial<ApiErrorBody>>> {
  return postForm(
    baseUrl,
    `${recipeImportBase(tenantId)}/preview`,
    recipeImportForm(file, {
      sheet: choices.sheet,
      mapping: JSON.stringify(choices.mapping),
      example: String(choices.example),
    }),
  );
}

export function applyRecipeImport(
  baseUrl: string,
  tenantId: string,
  file: File,
  choices: {
    sheet?: string;
    mapping: RecipeImportMappingDto;
    resolutions: Record<string, string>;
    approvedCreate: string[];
  },
  uploadId: string,
): Promise<ApiResult<RecipeImportApplyDto & Partial<ApiErrorBody>>> {
  return postForm(
    baseUrl,
    `${recipeImportBase(tenantId)}/apply`,
    recipeImportForm(file, {
      sheet: choices.sheet,
      mapping: JSON.stringify(choices.mapping),
      resolutions: JSON.stringify(choices.resolutions),
      approvedCreate: JSON.stringify(choices.approvedCreate),
      uploadId,
    }),
  );
}

// RCPT1-S7 (docs/build-briefs/RCPT1-S7-printers-settings-screen.md): the Receipt printers section in Settings. Every call needs
// printer.manage. A pairing code or password comes back on the answer to the call that made it and nowhere else.
export type PrinterStatusDto = "pairing" | "paired" | "unpaired";

export interface PrinterDto {
  id: string;
  locationId: string;
  name: string;
  status: PrinterStatusDto;
  pairingExpiresAt: string | null;
  lastPollAt: string | null;
  online: boolean;
  openJobs: number;
  passwordExpired: boolean;
  passwordRefusedFrom: string | null;
  passwordChangePending: boolean;
  passwordChangeExpiresAt: string | null;
}

export function listPrinters(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<{ printers: PrinterDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/printers?locationId=${encodeURIComponent(locationId)}`,
  );
}

export function addPrinter(
  baseUrl: string,
  tenantId: string,
  params: { idempotencyKey: string; locationId: string; name: string },
): Promise<
  ApiResult<
    {
      printer: PrinterDto;
      pairingCode: string | null;
      cloudPrntPath: string;
      replayed: boolean;
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/printers`, params);
}

export function changePrinterPassword(
  baseUrl: string,
  tenantId: string,
  printerId: string,
  params: { idempotencyKey: string },
): Promise<
  ApiResult<
    { printer: PrinterDto; password: string | null; replayed: boolean } & Partial<ApiErrorBody>
  >
> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/printers/${encodeURIComponent(printerId)}/password`,
    params,
  );
}

export function unpairPrinterRequest(
  baseUrl: string,
  tenantId: string,
  printerId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ printer: PrinterDto; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/printers/${encodeURIComponent(printerId)}/unpair`,
    params,
  );
}

export function printTestPage(
  baseUrl: string,
  tenantId: string,
  printerId: string,
  params: { idempotencyKey: string },
): Promise<ApiResult<{ replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/printers/${encodeURIComponent(printerId)}/test-page`,
    params,
  );
}

// PO7-S5 (docs/build-briefs/PO7-S5-invoice-scan-receive.md; DEC-409). Scan an invoice to receive. Every route is
// inv.receipt.record alone and carries no price, total or tax; the browser records what the person accepted through the
// ordinary receipt command (postScanReceipt below), never through the invoice.

export type ReceiveScanReadingStatus = "pending" | "drafted" | "failed" | "unavailable";

export interface ReceiveScanOrderLineTargetDto {
  type: "order_line";
  orderLineId: string;
  liquorProductId: string;
  productName: string;
  orderedQuantity: string;
  receivedQuantity: string;
  outstandingQuantity: string;
  orderUnit: string | null;
  bottlesPerOrderUnit: string | null;
}

export interface ReceiveScanItemTargetDto {
  type: "item";
  liquorProductId: string;
  productName: string;
  orderUnit: string | null;
  bottlesPerOrderUnit: string | null;
}

export type ReceiveScanTargetDto = ReceiveScanOrderLineTargetDto | ReceiveScanItemTargetDto;

export interface ReceiveScanLineDto {
  lineNumber: number;
  description: string | null;
  vendorItemCode: string | null;
  quantity: string | null;
  lowConfidence: boolean;
  target: ReceiveScanTargetDto | null;
  candidate: ReceiveScanTargetDto | null;
}

export interface ReceiveScanDraftDto {
  vendorReadName: string | null;
  vendorLowConfidence: boolean;
  orderVendorId: string | null;
  orderVendorName: string | null;
  suggestedVendorId: string | null;
  vendors: Array<{ id: string; name: string }>;
  invoiceNumber: string | null;
  invoiceNumberLowConfidence: boolean;
  invoiceDate: string | null;
  receiveReference: string | null;
  duplicateRecorded: boolean;
  duplicateOnOrder: boolean;
  lines: ReceiveScanLineDto[];
}

export interface ReceiveScanDto {
  attachmentId: string;
  orderId: string | null;
  reading: {
    status: ReceiveScanReadingStatus;
    message: string | null;
    retryable: boolean | null;
  };
  draft: ReceiveScanDraftDto | null;
}

/** Uploads one invoice file for a delivery with no purchase order (an order's own invoice goes through
 * uploadInvoiceAttachment, PO7-S1, unchanged). The idempotency key names the page set, so a repeat is a replay. */
export async function uploadReceiveScan(
  baseUrl: string,
  tenantId: string,
  file: Blob,
  filename: string,
  contentType: string,
  idempotencyKey: string,
): Promise<ApiResult<{ attachmentId: string; replayed: boolean } & Partial<ApiErrorBody>>> {
  if (authToken === undefined) {
    return missingTokenResult();
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/tenants/${encodeURIComponent(tenantId)}/receiving/scans`, {
      method: "POST",
      headers: {
        "content-type": contentType,
        "idempotency-key": idempotencyKey,
        "x-invoice-filename": encodeURIComponent(filename),
        ...authHeaders(),
      },
      body: file,
    });
  } catch (cause) {
    return networkErrorResult(cause);
  }
  const parsed = (await readJsonBody(response)) as never;
  noteResponse(response.status, parsed, "revoked");
  return { status: response.status, body: parsed, networkError: false };
}

export function getReceiveScan(
  baseUrl: string,
  tenantId: string,
  attachmentId: string,
  vendorId?: string,
): Promise<ApiResult<{ scan: ReceiveScanDto } & Partial<ApiErrorBody>>> {
  const query = vendorId === undefined ? "" : `?vendorId=${encodeURIComponent(vendorId)}`;
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/receiving/scans/${encodeURIComponent(attachmentId)}${query}`,
  );
}

export function retryReceiveScanReading(
  baseUrl: string,
  tenantId: string,
  attachmentId: string,
): Promise<ApiResult<{ status: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/receiving/scans/${encodeURIComponent(attachmentId)}/reading/retry`,
    {},
  );
}

/** One line of a scanned invoice, posted through the existing receipt command. The server decides every refusal. */
export function postScanReceipt(
  baseUrl: string,
  tenantId: string,
  params: Record<string, string | number>,
): Promise<ApiResult<{ replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/inventory/receipts`, params);
}

/** LOC3 (DEC-413, provisional): one location with its own address. */
export interface LocationWithAddressDto extends LocationAddressDto {
  id: string;
  name: string;
  isMain: boolean;
}

/** GET .../location-addresses (admin.tenant_settings.view): the locations with their addresses, the signed-in location first. */
export function getLocationAddresses(
  baseUrl: string,
  tenantId: string,
): Promise<
  ApiResult<
    {
      locations: LocationWithAddressDto[];
      signedInLocationId: string | null;
    } & Partial<ApiErrorBody>
  >
> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/location-addresses`);
}

/** PUT .../locations/:id/address (admin.location_settings.manage): one location's address. */
export function putLocationAddress(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  input: Partial<LocationAddressDto>,
): Promise<
  ApiResult<{ location: LocationWithAddressDto; syncWarning?: string } & Partial<ApiErrorBody>>
> {
  return putJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/address`,
    input,
  );
}

// CRB6: design-only templates. Every operation rechecks the current recipient server-side.
export interface ReportTemplateSummaryDto {
  id: string;
  publicationVersionId: string;
  title: string;
  dataset: string;
  datasetLabel: string;
  versionNumber: number;
  publishedAt: string;
}
export interface ReportTemplateDetailDto extends ReportTemplateSummaryDto {
  spec: Record<string, unknown>;
  specHash: string;
}
function templatePath(tenantId: string, id?: string): string {
  return `/tenants/${encodeURIComponent(tenantId)}/reports/custom/templates${id === undefined ? "" : `/${encodeURIComponent(id)}`}`;
}
export function listReportTemplates(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ items: ReportTemplateSummaryDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, templatePath(tenantId));
}
export function getReportTemplate(
  baseUrl: string,
  tenantId: string,
  id: string,
): Promise<ApiResult<ReportTemplateDetailDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, templatePath(tenantId, id));
}
export function runReportTemplate(
  baseUrl: string,
  tenantId: string,
  id: string,
  publicationVersionId: string,
): Promise<ApiResult<CustomReportResultDto & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `${templatePath(tenantId, id)}/run`, { publicationVersionId });
}
export function copyReportTemplate(
  baseUrl: string,
  tenantId: string,
  id: string,
  publicationVersionId: string,
  requestId: string,
): Promise<ApiResult<{ id: string } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `${templatePath(tenantId, id)}/copy`, {
    publicationVersionId,
    requestId,
  });
}

// CRB7: tenant transport shares the ordinary authenticated JSON path.
export async function reportAiRequest<T>(
  baseUrl: string,
  tenantId: string,
  method: "GET" | "POST",
  suffix: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  const path = `/tenants/${encodeURIComponent(tenantId)}/reports/custom/ai${suffix}`;
  return method === "GET" ? getJson<T>(baseUrl, path) : postJson<T>(baseUrl, path, body ?? {});
}

// BILLSTMT1: the location Billing page's balances and statement history (billing.ai.manage). The server shapes it.
export function customerBillingOverview(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<unknown>> {
  return getJson<unknown>(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/billing/overview`);
}

// BILLSTMT1: Stripe's page for the customer's payment method (allowed while billing is paused or suspended, DEC-492).
export function customerBillingPortal(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url?: string }>> {
  return postJson<{ url?: string }>(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/billing/portal`,
    {},
  );
}

// CRB10: personal report tiles use the ordinary authenticated request path.
export async function reportTileRequest<T>(
  baseUrl: string,
  tenantId: string,
  method: "GET" | "POST",
  suffix: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  const path = `/tenants/${encodeURIComponent(tenantId)}/reports/custom${suffix}`;
  return method === "GET" ? getJson<T>(baseUrl, path) : postJson<T>(baseUrl, path, body ?? {});
}

// RCPT2-F6: manager access to the current client preference is bound to this exact ticket payer.
export function getClientReceiptEmail(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  paymentAttemptId?: string,
): Promise<ApiResult<ClientReceiptEmailDto & Partial<ApiErrorBody>>> {
  const query =
    paymentAttemptId === undefined
      ? ""
      : `?paymentAttemptId=${encodeURIComponent(paymentAttemptId)}`;
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/client-receipt-email${query}`,
  );
}
export function saveClientReceiptEmail(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  input: ({ email: string | null; optOut?: never } | { optOut: true; email?: never }) & {
    expectedIdentity: string;
    expectedRevision: number;
    idempotencyKey: string;
    paymentAttemptId?: string;
  },
): Promise<ApiResult<ClientReceiptEmailDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/client-receipt-email`,
    input,
  );
}

export function setGuestServiceToday(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  guestId: string,
  params: { serviceToday: boolean; idempotencyKey: string },
): Promise<ApiResult<{ guests: GuestDto[]; replayed: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/orders/${encodeURIComponent(orderId)}/guests/${encodeURIComponent(guestId)}/service-today`,
    params,
  );
}

export interface CalculatedCatalogPricePreviewDto {
  priceCents: number | null;
  previewToken: string | null;
  targetMarkupPercent: number | null;
}
export function previewCalculatedCatalogPrice(
  baseUrl: string,
  tenantId: string,
  productId: string,
  params: {
    targetMarkupPercent?: number;
    sizeValue?: number;
    sizeUnit?: string;
    locationId?: string;
    sellingVersion?: number;
  },
): Promise<ApiResult<CalculatedCatalogPricePreviewDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/${encodeURIComponent(productId)}/calculated-price-preview`,
    params,
  );
}

export interface PossibleDuplicateClientDto {
  id: string;
  displayName: string;
  phoneLastFour: string | null;
  originLocationName: string | null;
  lastVisitAtUtc: string | null;
  lastVisitLocationName: string | null;
  lastVisitTimeZone: string | null;
  membershipActive: boolean;
}
export function getPossibleDuplicateClients(
  baseUrl: string,
  tenantId: string,
  afterClientId?: string,
  snapshotId?: string,
): Promise<
  ApiResult<
    {
      available: boolean;
      snapshotId: string | null;
      results: PossibleDuplicateClientDto[];
      nextAfterClientId: string | null;
      freshness: { lastSyncedAt: string | null };
    } & Partial<ApiErrorBody>
  >
> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/clients/possible-duplicates`, {
    ...(afterClientId === undefined ? {} : { afterClientId }),
    ...(snapshotId === undefined ? {} : { snapshotId }),
  });
}

// TEN8-F1-S3: permit metadata and masks only. Replacement numbers are transient request input.
export interface LocationPermitDto {
  id: string;
  locationId: string;
  version: number;
  recordedAt: string;
  issuingAuthority: string;
  permitType: string;
  holderEntityId: string;
  holderName: string;
  expiresOn: string | null;
  status: "recorded" | "inactive" | "unknown";
  number: { recorded: true; masked: string };
}
export interface LocationPermitContextDto {
  locations: Array<{ id: string; name: string }>;
  holderEntities: Array<{ id: string; name: string }>;
  canManage: boolean;
}
export interface LocationPermitListDto {
  permits: LocationPermitDto[];
  holderEntities: Array<{ id: string; name: string }>;
  canManage: boolean;
}
export interface LocationPermitInput {
  issuingAuthority: string;
  permitType: string;
  holderEntityId: string;
  holderName: string;
  expiresOn: string | null;
  status: "recorded" | "inactive" | "unknown";
  expectedVersion: number;
  idempotencyKey: string;
  number: { kind: "keep" } | { kind: "replace"; value: string };
}
export function locationPermitRequest<T>(
  baseUrl: string,
  tenantId: string,
  method: "GET" | "POST",
  suffix: string,
  body?: LocationPermitInput,
): Promise<ApiResult<T & Partial<ApiErrorBody>>> {
  const path = `/tenants/${encodeURIComponent(tenantId)}${suffix}`;
  return method === "GET" ? getJson(baseUrl, path) : postJson(baseUrl, path, body);
}

// NETBANNER1: counts are null for categories this person cannot answer.
export function readNetworkOfferNotice(
  baseUrl: string,
  tenantId: string,
  locationId?: string,
): Promise<ApiResult<unknown>> {
  const query = locationId === undefined ? "" : `?locationId=${encodeURIComponent(locationId)}`;
  // TEN11-F3: read on a timer, not by the person.
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/network-offer-notice${query}`, {
    headers: BACKGROUND_REQUEST_HEADERS,
  });
}

export function readLocalDiscountPolicy(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ policy?: { allowed: boolean; version: number } } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/discount-policy`);
}

/** RPTCAT0 fixed renditions: the same current tenant auth and device headers as existing reports. */
export function cannedReportRequest<T>(
  baseUrl: string,
  path: string,
  method: "GET" | "POST",
  body?: unknown,
): Promise<ApiResult<T>> {
  return method === "GET" ? getJson<T>(baseUrl, path) : postJson<T>(baseUrl, path, body ?? {});
}
export async function cannedReportFile(
  baseUrl: string,
  path: string,
  body: unknown,
  networkToken?: string,
): Promise<{ status: number; blob?: Blob; filename?: string; message?: string }> {
  try {
    const response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(networkToken === undefined
          ? authHeaders()
          : { authorization: `Bearer ${networkToken}` }),
      },
      body: JSON.stringify(body),
      cache: "no-store",
    });
    if (response.status !== 200) {
      const error = await readJsonBody(response);
      if (networkToken === undefined) noteResponse(response.status, error ?? {}, "revoked");
      return {
        status: response.status,
        message:
          typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof error.message === "string"
            ? error.message
            : "Could not download this report.",
      };
    }
    const filename =
      /filename="([^"/\\]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ??
      "report.csv";
    return { status: 200, blob: await response.blob(), filename };
  } catch {
    return { status: 0, message: "Could not reach the server." };
  }
}

/** CATNET1: location is an actual planning scope, never a catalog/account selector. */
export function productCatalogRequest(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  method: "GET" | "POST",
  path: string,
  body?: unknown,
): Promise<ApiResult<unknown>> {
  const route = `/tenants/${encodeURIComponent(tenantId)}/product-catalog${path}?locationId=${encodeURIComponent(locationId)}`;
  return method === "GET" ? getJson(baseUrl, route) : postJson(baseUrl, route, body);
}

/** ADDITEM2 (DEC-610, DEC-611): the server's numbers for the Add item steps (POST .../liquor-products/item-math). */
export interface ItemMathRowDto {
  ounces: string;
  poursPerContainer: number | null;
  pourCostCents: string | null;
  suggestedPriceCents: number | null;
  chargedPriceCents: number | null;
}
export interface ItemMathDto {
  ouncesPerContainer: string | null;
  wasteBasisPoints: number;
  tenantDefaultWasteBasisPoints: number;
  usableOuncesPerContainer: string | null;
  costPerUsableOunceCents: string | null;
  costPerUnitCents: string | null;
  targetMarkupPercent: number | null;
  rows: ItemMathRowDto[];
  suggestedUnitPriceCents: number | null;
  typed: {
    chargedPriceCents: number;
    markupPercent: number | null;
    markupToSave: number | null;
    belowCost: boolean;
  } | null;
}
export interface ItemMathRequest {
  locationId?: string;
  itemKind: string;
  saleMechanism: "per_ounce" | "whole_unit";
  containerType?: string;
  defaultKegSize?: string;
  sizeValue?: number;
  sizeUnit?: string;
  kegWasteBasisPoints?: number | null;
  pourOunces: string[];
  costCents?: number;
  orderUnit?: string;
  packSize?: string;
  targetMarkupPercent?: number;
  typedPrice?: { ounces?: string; priceCents: number };
}
export function postItemMath(
  baseUrl: string,
  tenantId: string,
  body: ItemMathRequest,
): Promise<ApiResult<ItemMathDto & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/liquor-products/item-math`,
    body,
  );
}

/** CATALOG1 (DEC-613, DEC-614): the shared product catalog every customer reads. Product facts only. */
export interface SharedCatalogProductDto {
  id: string;
  producer: string;
  productName: string;
  itemKind: string;
  category: string | null;
  abvBasisPoints: number | null;
  countryRegion: string | null;
  upc: string | null;
  ncCode: string | null;
  source: "seed" | "confirmed" | "ai";
  /** The customer's own active item with this producer and name; absent on a confirm response. */
  onYourList?: { itemId: string; name: string } | null;
}
export function searchSharedCatalog(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<ApiResult<{ products: SharedCatalogProductDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/shared-catalog/search?q=${encodeURIComponent(query)}`,
  );
}
/** CATALOGAI1 (DEC-615): what the AI found for a product the catalog does not have. Nothing is saved by asking. */
export interface CatalogAiLookupDto {
  found: boolean;
  product: {
    producer: string;
    productName: string;
    itemKind: string;
    category: string | null;
    abvBasisPoints: number | null;
    countryRegion: string | null;
    upc: string | null;
  } | null;
  sources: { url: string; title: string }[];
}
export function lookupProductWithAi(
  baseUrl: string,
  tenantId: string,
  query: string,
): Promise<ApiResult<{ lookup: CatalogAiLookupDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/shared-catalog/lookup`, {
    query,
  });
}
export interface SharedCatalogConfirmRequest {
  producer: string;
  productName: string;
  itemKind: string;
  category?: string;
  abvBasisPoints?: number;
  countryRegion?: string;
  upc?: string;
  ncCode?: string;
  source?: "confirmed" | "ai";
}
export function confirmSharedCatalogProduct(
  baseUrl: string,
  tenantId: string,
  body: SharedCatalogConfirmRequest,
): Promise<ApiResult<{ product: SharedCatalogProductDto } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/shared-catalog/confirm`, body);
}

export interface CatalogLocalItemSetupInput extends Pick<
  InventoryItemFieldsInput,
  | "trackStock"
  | "stockSettings"
  | "defaultKegSize"
  | "abvBasisPoints"
  | "producer"
  | "countryRegion"
  | "descriptionNotes"
> {
  productId: string;
  expectedVersion: number;
  requestId: string;
  localSaleMechanism: "per_ounce" | "whole_unit";
  unitPriceCents?: number;
  pourOunceOptions?: number[];
  shelfTier?: string;
  hideOnMenu?: boolean;
}
export function createCatalogLocalItem(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  input: CatalogLocalItemSetupInput,
): Promise<ApiResult<{ liquorProduct: LiquorProductDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/product-catalog/items?locationId=${encodeURIComponent(locationId)}`,
    input,
  );
}

// SUPPORT1 (DEC-514 item 4): Get help and Report a problem for the location console. One call to the person's own support
// requests under the signed-in tenant: "" lists or creates, "/summary" counts replies waiting, "/<id>" reads one and
// "/<id>/messages" adds to it. The person, customer and location come from the session on the server, never from here.
export function supportRequestCall<T>(
  baseUrl: string,
  tenantId: string,
  method: "GET" | "POST",
  suffix: string,
  body?: unknown,
): Promise<ApiResult<T & Partial<ApiErrorBody>>> {
  const path = `/tenants/${encodeURIComponent(tenantId)}/support-requests${suffix}`;
  return method === "GET" ? getJson(baseUrl, path) : postJson(baseUrl, path, body ?? {});
}

// AIHELP1 (DEC-574): Willy, the AI support chat. "/status" (GET) says whether Willy can answer now; "/turn" (POST) sends the
// conversation and screen and returns one answer. The person and location come from the session on the server.
export function willyCall<T>(
  baseUrl: string,
  tenantId: string,
  method: "GET" | "POST",
  suffix: string,
  body?: unknown,
): Promise<ApiResult<T & Partial<ApiErrorBody>>> {
  const path = `/tenants/${encodeURIComponent(tenantId)}/willy${suffix}`;
  return method === "GET" ? getJson(baseUrl, path) : postJson(baseUrl, path, body ?? {});
}

// HELPDOCS1: the written Help guides the Help button opens ("" is the list, "/<slug>" one guide), read with the session token.
export function helpGuidesCall<T>(
  baseUrl: string,
  tenantId: string,
  suffix: string,
): Promise<ApiResult<T & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/help${suffix}`);
}

/** HELPDOCS1: one guide screenshot as a blob (the page turns it into a URL, because an image tag cannot carry the token). */
export async function helpImageCall(
  baseUrl: string,
  tenantId: string,
  name: string,
): Promise<Blob | null> {
  const result = await getBinaryFile(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/help/images/${encodeURIComponent(name)}`,
  );
  return result.status === 200 ? (result.blob ?? null) : null;
}

// PAYEXPORT1 (docs/build-briefs/PAYEXPORT1-gusto-tips-connection.md). Settings > Users > Payroll (Gusto). The location is the
// signed-in device's: nothing here names a location, a company or a token.
export interface PayrollConnectionDto {
  state: "not_connected" | "connected" | "needs_reconnect";
  provider: string;
  companyName: string | null;
  connectedAt: string | null;
  connectedByName: string | null;
  problem: string | null;
}

export interface PayrollStatusDto {
  environment: string;
  canManage: boolean;
  canSend: boolean;
  connection: PayrollConnectionDto;
}

export interface PayrollPeopleDto {
  staff: {
    employeeId: string;
    displayName: string;
    email: string | null;
    match: {
      personRef: string;
      personName: string | null;
      source: "email" | "manual";
      providerState: string;
    } | null;
  }[];
  unmatchedPeople: { ref: string; name: string; email: string | null }[];
  problem: string | null;
}

export interface PayrollSendDto {
  id: string;
  attemptNo: number;
  status: "pending" | "sent" | "failed";
  totalCents: number;
  personCount: number;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
  payrollRef?: string;
  periodStart?: string;
  periodEnd?: string;
  actorName?: string | null;
}

export interface PayrollPeriodDto {
  payrollRef: string;
  periodStart: string;
  periodEnd: string;
  checkDate: string | null;
  send: PayrollSendDto | null;
}

export interface PayrollPreviewDto {
  period: {
    payrollRef: string;
    periodStart: string;
    periodEnd: string;
    checkDate: string | null;
    dayCount: number;
  };
  state: "ready" | "blocked" | "sending" | "sent";
  blocks: {
    code: string;
    message: string;
    days?: string[];
    people?: { employeeId: string | null; displayName: string }[];
  }[];
  lines: {
    employeeId: string | null;
    displayName: string;
    amountCents: number;
    daysWithTips: number;
    match: {
      state: "matched" | "unmatched" | "inactive";
      personRef: string | null;
      personName: string | null;
    };
  }[];
  totalCents: number | null;
  closeoutTipsCents: number | null;
  reconciled: boolean;
  send: PayrollSendDto | null;
}

const payrollPath = (tenantId: string, rest = ""): string =>
  `/tenants/${encodeURIComponent(tenantId)}/payroll/gusto${rest}`;

export function getPayrollGusto(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<PayrollStatusDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, payrollPath(tenantId));
}
export function postPayrollConnect(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ url?: string } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, payrollPath(tenantId, "/connect"), {});
}
export function postPayrollDisconnect(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ disconnected?: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, payrollPath(tenantId, "/disconnect"), {});
}
export function getPayrollPeople(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<PayrollPeopleDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, payrollPath(tenantId, "/people"));
}
export function postPayrollAutoMatch(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ matched: number } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, payrollPath(tenantId, "/people/auto-match"), {});
}
export function putPayrollMatch(
  baseUrl: string,
  tenantId: string,
  employeeId: string,
  personRef: string | null,
): Promise<ApiResult<{ matched?: boolean } & Partial<ApiErrorBody>>> {
  return putJson(
    baseUrl,
    payrollPath(tenantId, `/people/${encodeURIComponent(employeeId)}/match`),
    { personRef },
  );
}
export function getPayrollPayPeriods(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ periods: PayrollPeriodDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, payrollPath(tenantId, "/pay-periods"));
}
export function getPayrollPreview(
  baseUrl: string,
  tenantId: string,
  payrollRef: string,
): Promise<ApiResult<PayrollPreviewDto & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    payrollPath(tenantId, `/pay-periods/${encodeURIComponent(payrollRef)}/preview`),
  );
}
export function postPayrollSend(
  baseUrl: string,
  tenantId: string,
  payrollRef: string,
  idempotencyKey: string,
): Promise<ApiResult<{ send?: PayrollSendDto; replayed?: boolean } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    payrollPath(tenantId, `/pay-periods/${encodeURIComponent(payrollRef)}/send`),
    { idempotencyKey },
  );
}
export function getPayrollSends(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ sends: PayrollSendDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, payrollPath(tenantId, "/sends"));
}

// ACCTEXPORT1 (docs/build-briefs/ACCTEXPORT1-quickbooks-connection.md; DEC-514). The location's QuickBooks connection card. The
// location is a path value the server proves belongs to the session's tenant. Nothing secret ever comes back: no token, no code, no
// company id. While the feature is off every call answers 404 and the card stays hidden.
export type AccountingEntryStatusDto =
  "sent" | "sending" | "will_retry" | "needs_attention" | "waiting" | "nothing" | "not_applicable";

export interface AccountingDayDto {
  businessDate: string;
  salesBeforeTaxCents: number;
  taxCents: number;
  tipsCents: number;
  sales: { status: AccountingEntryStatusDto; reason: string | null };
  fees: { status: AccountingEntryStatusDto; reason: string | null };
  canSend: boolean;
}

export interface AccountingStatusDto {
  enabled: boolean;
  connection: {
    status: "not_connected" | "connected" | "needs_reconnect" | "disconnected";
    companyName: string | null;
    connectedAt: string | null;
  };
  mapping: {
    lines: {
      key: string;
      label: string;
      hint: string;
      accountId: string | null;
      accountName: string | null;
    }[];
    complete: boolean;
  };
  days: AccountingDayDto[];
}

export interface AccountingAccountDto {
  id: string;
  name: string;
  type: string;
}

function accountingPath(tenantId: string, locationId: string): string {
  return `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/accounting/quickbooks`;
}

export function getAccountingStatus(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<AccountingStatusDto & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, accountingPath(tenantId, locationId));
}

export function postAccountingConnectionStart(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<{ authorizationUrl?: string } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `${accountingPath(tenantId, locationId)}/connection/start`, {});
}

export function postAccountingDisconnect(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<{ revoked?: boolean } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `${accountingPath(tenantId, locationId)}/connection/disconnect`, {});
}

export function getAccountingAccounts(
  baseUrl: string,
  tenantId: string,
  locationId: string,
): Promise<ApiResult<{ accounts?: AccountingAccountDto[] } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `${accountingPath(tenantId, locationId)}/accounts`);
}

export function putAccountingMapping(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  accounts: Record<string, string>,
): Promise<ApiResult<Partial<ApiErrorBody>>> {
  return putJson(baseUrl, `${accountingPath(tenantId, locationId)}/mapping`, { accounts });
}

export function postAccountingSendDay(
  baseUrl: string,
  tenantId: string,
  locationId: string,
  businessDate: string,
): Promise<ApiResult<Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `${accountingPath(tenantId, locationId)}/days/${encodeURIComponent(businessDate)}/send`,
    {},
  );
}

// INVDESIGN1-O1 (docs/build-briefs/INVDESIGN1-ORDER-ordering-screens.md). The four ordering screens' reads and writes. Shapes
// mirror src/domain/orderingScreens.ts; dates are calendar days ("2026-11-04") and instants are ISO strings.

export interface OrderingScheduleDto {
  orderByDate: string;
  orderByTime: string;
  arrivalDate: string;
  past: boolean;
}

export interface OnOrderDto {
  quantity: string;
  orderUnit: string | null;
  arrivalDate: string | null;
  orderNumber: number | null;
}

export interface WhatToOrderLineDto {
  liquorProductId: string;
  productName: string;
  sizeText: string | null;
  onHand: string | null;
  par: string | null;
  usedLastWeek: string | null;
  onOrder: OnOrderDto | null;
  quantity: string;
  orderUnit: string;
  unitCostCents: number | null;
  lineEstimateCents: number | null;
}

export interface WhatToOrderVendorDto {
  vendorId: string;
  vendorName: string;
  lowCount: number;
  totalCents: number;
  schedule: OrderingScheduleDto | null;
  draftOrderId: string | null;
  lines: WhatToOrderLineDto[];
}

export interface WhatToOrderDto {
  today: string;
  timeZone: string;
  lastCount: { date: string; byName: string | null } | null;
  vendors: WhatToOrderVendorDto[];
  needsSetup: { liquorProductId: string; productName: string; reason: string }[];
  coveredByOpenOrders: number;
}

export function getWhatToOrder(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ whatToOrder: WhatToOrderDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/ordering/what-to-order`);
}

export interface OrderableItemDto {
  liquorProductId: string;
  productName: string;
  sizeText: string | null;
  orderUnit: string | null;
  packSize: string | null;
  unitCostCents: number | null;
}

export function getVendorOrderableItems(
  baseUrl: string,
  tenantId: string,
  vendorId: string,
): Promise<ApiResult<{ items: OrderableItemDto[] } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/ordering/vendors/${encodeURIComponent(vendorId)}/items`,
  );
}

export interface OrderingOrderLineDto {
  purchaseOrderLineId: string;
  liquorProductId: string;
  productName: string;
  sizeText: string | null;
  orderUnit: string | null;
  bottlesPerOrderUnit: string | null;
  quantity: string;
  unitCostCents: number | null;
  lineEstimateCents: number | null;
  receivedQuantity: string;
  outstandingQuantity: string;
}

export interface OrderingOrderDto {
  id: string;
  orderNumber: number | null;
  status: string;
  vendorId: string;
  vendorName: string;
  contactName: string | null;
  contactEmail: string | null;
  orderMethod: string | null;
  leadDays: number;
  createdAt: string;
  sentAt: string | null;
  sentVia: string | null;
  timeZone: string;
  estimatedTotalCents: number;
  lines: OrderingOrderLineDto[];
  schedule: OrderingScheduleDto | null;
  approvalBlockers: string[];
  sendChannel: { available: false; reason: string } | { available: true; via: "email" };
  needsApprovalToSend: boolean;
  thresholdCents: number;
}

export function getOrderingOrder(
  baseUrl: string,
  tenantId: string,
  orderId: string,
): Promise<ApiResult<{ order: OrderingOrderDto } & Partial<ApiErrorBody>>> {
  return getJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/ordering/orders/${encodeURIComponent(orderId)}`,
  );
}

export interface OpenOrderDto {
  id: string;
  orderNumber: number | null;
  vendorName: string;
  status: string;
  sentAt: string | null;
  createdAt: string;
  lineCount: number;
  lines: { productName: string; quantity: string; orderUnit: string | null }[];
  estimatedTotalCents: number;
  arrivalDate: string | null;
  orderByDate: string | null;
  orderByTime: string | null;
  orderByPast: boolean;
}

export interface ClosedOrderDto {
  id: string;
  orderNumber: number | null;
  vendorName: string;
  closedShort: boolean;
  receivedCents: number;
  orderedCents: number;
  shortLines: { productName: string; shortQuantity: string; orderUnit: string | null }[];
  arrivedDate: string | null;
}

export interface GlanceRowDto {
  date: string;
  vendorName: string;
  kind: "sent" | "draft" | "not_built";
  lineCount: number | null;
}

export interface OpenOrdersDto {
  today: string;
  timeZone: string;
  orders: OpenOrderDto[];
  glance: GlanceRowDto[];
  recentlyClosed: ClosedOrderDto[];
}

export function getOpenOrders(
  baseUrl: string,
  tenantId: string,
): Promise<ApiResult<{ openOrders: OpenOrdersDto } & Partial<ApiErrorBody>>> {
  return getJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/ordering/open-orders`);
}

export function saveOrderDraft(
  baseUrl: string,
  tenantId: string,
  params: {
    vendorId: string;
    lines: { liquorProductId: string; quantity: string }[];
    replaceOrderId?: string;
  },
): Promise<ApiResult<{ order: { id: string; orderNumber: number | null } } & Partial<ApiErrorBody>>> {
  return postJson(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/purchasing/ordering/drafts`, params);
}

export function markOrderSent(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  idempotencyKey: string,
): Promise<ApiResult<{ order: OrderingOrderDto } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/mark-sent`,
    { idempotencyKey },
  );
}

export function closeOrderShortRequest(
  baseUrl: string,
  tenantId: string,
  orderId: string,
  idempotencyKey: string,
): Promise<ApiResult<{ status: string } & Partial<ApiErrorBody>>> {
  return postJson(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/purchasing/orders/${encodeURIComponent(orderId)}/close-short`,
    { idempotencyKey },
  );
}
